import * as PIXI from "pixi.js";
import { ApiClient } from "./api.js";
import { assetPaths, audioPaths, collidableRectFeatureKinds, debugFightEnemies, enemyGroupHitRadius, itemIconPaths, movementSpeed, navigationGridSize, playerCollisionRadius, playerSprite, windowTitles, worldScale } from "./constants.js";
import { button, clear, panel, resourceBar, setButtonClickHandler, setUiTextScale, suppressButtonTap, text } from "./components.js";
import { clamp, currentLanguage, directionFromDelta, distance, setLanguage } from "./utils.js";
import { calculateViewportProfile, clampToSafeRect, phoneWindowLayout } from "./layout.js";
import { combatMethods } from "./combat/index.js";
import { playerSkeletalVisuals } from "./player-visuals.js";
import { sampleSkeletonPose, skeletonWorldTransforms } from "./skeleton/runtime.js";
import { skeletalEquipmentSkins, skeletalEquipmentSkinPath, sortEquipmentForSkinning } from "./equipment-skins.js";
import { PixiSkeletonView, sampleClipPose } from "../skeleton/index.ts";

class TextInputBridge {
  constructor(input, wrapper, doneButton, cancelButton, profileProvider) {
    this.input = input;
    this.wrapper = wrapper;
    this.profileProvider = profileProvider;
    this.onSubmit = null;
    this.layout = null;
    this.input.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        this.commit();
      }
      if (event.key === "Escape") {
        event.preventDefault();
        this.hide();
      }
    });
    this.input.addEventListener("blur", () => {
      if (!this.wrapper.classList.contains("hidden") && !this.profileProvider()?.phoneLandscape) this.commit();
    });
    [doneButton, cancelButton].forEach((control) => control.addEventListener("pointerdown", (event) => event.preventDefault()));
    doneButton.addEventListener("click", () => this.commit());
    cancelButton.addEventListener("click", () => this.hide());
  }

  show({ x, y, width, value = "", placeholder = "", type = "text", autocomplete = "off", inputMode = "text", onSubmit }) {
    this.onSubmit = onSubmit;
    this.layout = { x, y, width };
    this.input.type = type;
    this.input.autocomplete = autocomplete;
    this.input.inputMode = inputMode;
    this.input.value = value;
    this.input.placeholder = placeholder;
    this.wrapper.classList.remove("hidden");
    this.reposition();
    this.input.focus();
    this.input.select();
  }

  reposition() {
    if (!this.layout || this.wrapper.classList.contains("hidden")) return;
    const profile = this.profileProvider?.();
    const visualOffsetX = window.visualViewport?.offsetLeft || 0;
    const visualOffsetY = window.visualViewport?.offsetTop || 0;
    const safe = profile?.safeRect || { x: 0, y: 0, right: window.innerWidth, bottom: window.innerHeight, width: window.innerWidth, height: window.innerHeight };
    const requestedWidth = profile?.phoneLandscape ? Math.max(360, this.layout.width) : Math.max(300, this.layout.width + 142);
    const rect = clampToSafeRect({
      x: this.layout.x + visualOffsetX,
      y: this.layout.y + visualOffsetY,
      width: requestedWidth,
      height: 58,
    }, {
      ...safe,
      x: safe.x + visualOffsetX,
      y: safe.y + visualOffsetY,
      right: safe.right + visualOffsetX,
      bottom: safe.bottom + visualOffsetY,
    }, 8);
    this.wrapper.style.left = `${Math.round(rect.x)}px`;
    this.wrapper.style.top = `${Math.round(rect.y)}px`;
    this.wrapper.style.width = `${Math.round(rect.width)}px`;
  }

  commit() {
    const callback = this.onSubmit;
    const value = this.input.value;
    this.hide();
    callback?.(value);
  }

  hide() {
    this.wrapper.classList.add("hidden");
    this.input.type = "text";
    this.onSubmit = null;
    this.layout = null;
  }
}

class GameClient {
  constructor() {
    this.root = document.querySelector("#pixi-root");
    this.orientationGate = document.querySelector("#phone-orientation-gate");
    this.state = {
      viewportProfile: this.readViewportProfile(),
    };
    this.bridge = new TextInputBridge(
      document.querySelector("#pixi-text-input"),
      document.querySelector("#pixi-input-bridge"),
      document.querySelector("#pixi-input-done"),
      document.querySelector("#pixi-input-cancel"),
      () => this.state.viewportProfile,
    );
    this.api = new ApiClient((line) => this.log(line), (metric) => this.recordApiMetric(metric));
    this.app = new PIXI.Application();
    this.state = {
      ...this.state,
      world: null,
      map: null,
      player: null,
      combat: null,
      activeWindow: null,
      selectedNpc: null,
      selectedInteractable: null,
      selectedEnemyGroup: null,
      selectedPlayer: null,
      partyInvite: null,
      friendRequest: null,
      conversationLine: "",
      hoveredEnemyGroup: null,
      hoveredAggroThreat: null,
      hoveredMoveTarget: null,
      hoveredCharacterStat: null,
      hoveredInventoryItemId: null,
      hoveredShopItemKey: null,
      hoveredInteractable: null,
      selectedShop: null,
      selectedCardFusion: null,
      shopTab: "buy",
      shopScrollOffsets: { buyItems: 0, buyCards: 0, sell: 0 },
      cardFusionScrollOffset: 0,
      cardFusionSlots: [],
      cardFusionResult: null,
      hoveredCardFusionKey: null,
      hoveredCardFusionButton: false,
      cardFusionDrag: null,
      questBranch: null,
      activePartyVote: null,
      selectedCombatCardInstanceId: null,
      selectedCombatTargetId: null,
      selectedCombatAllyTargetId: null,
      hoveredCombatCardInstanceId: null,
      hoveredCombatTargetId: null,
      hoveredCombatStatus: null,
      retargetCombatCardInstanceId: null,
      localCombatPlan: null,
      combatReplay: null,
      combatReplayKey: null,
      combatReplayPromise: null,
      combatReplaySeenKeys: new Set(),
      combatDrag: null,
      combatPilePopup: null,
      combatPileShuffleAnimations: [],
      hoveredCombatQueueIndex: null,
      combatTimeoutRequestKey: null,
      lastCombatTimerSecond: null,
      authMode: "dev",
      authReady: false,
      authScreen: "login",
      authNick: localStorage.getItem("vibe-auth-nick") || "",
      authPassword: "",
      authMessage: "",
      buildSha: "local",
      devCheatsEnabled: false,
      combatTimeoutRetryAt: 0,
      combatChatPopups: [],
      suppressCombatCardTapInstanceId: null,
      dismissedCombatSummaryId: null,
      shownCombatRewardId: null,
      cardDeckView: null,
      deckDetailCardId: null,
      hoveredDeckCardKey: null,
      hoveredEquipmentSlot: null,
      equipmentDrag: null,
      deckScrollOffsets: { deck: 0, collection: 0 },
      socialTab: "nearby",
      logs: [],
      chatChannel: localStorage.getItem("vibe-chat-channel") || "map",
      chatHistories: { map: [], party: [], global: [], private: [] },
      chatUnread: { map: 0, party: 0, global: 0, private: 0 },
      chatScrollOffsets: { map: 0, party: 0, global: 0, private: 0 },
      phoneChatOpen: false,
      chatTargetPlayerId: null,
      privateChatRecentPlayers: this.loadPrivateChatRecentPlayers(),
      privateFriendDropdownOpen: false,
      showCollisionObjects: false,
      renderPositions: new Map(),
      tweens: new Map(),
      playerFacings: new Map(),
      restPopups: [],
      rewardPopups: [],
      levelUpPopups: [],
      rewardSummary: null,
      itemTakePopup: null,
      gatheringPopups: [],
      gathering: null,
      gatheringRequestPending: false,
      nextRestRefreshAt: 0,
      nextRestPopupAt: 0,
      movementSequence: 0,
      movementToken: 0,
      movementAbort: null,
      movementActive: false,
      performance: {
        fps: 0,
        lastFrameMs: 0,
        worstFrameMs: 0,
        spikeCount: 0,
        lastSpikeAt: 0,
        lastLogAt: 0,
        lastApiMs: 0,
        worstApiMs: 0,
        slowApiCount: 0,
        lastApiPath: "",
        lastApiLogAt: 0,
      },
      characterScrollOffset: 0,
      characterTab: localStorage.getItem("vibe-character-tab") || "profile",
      mapZoom: clamp(Number(localStorage.getItem("vibe-map-zoom") ?? 1), 0.7, 1.8),
      worldMapZoom: clamp(Number(localStorage.getItem("vibe-world-map-zoom") ?? 1), 0.7, 2.6),
      worldMapOffset: { x: 0, y: 0 },
      worldMapDrag: null,
      pinnedQuestId: localStorage.getItem("vibe-pinned-quest-id") || null,
      questTrackerVisible: localStorage.getItem("vibe-quest-tracker-visible") !== "false",
      inventoryTab: localStorage.getItem("vibe-inventory-tab") || "items",
      inventoryFilter: localStorage.getItem("vibe-inventory-filter") || "all",
      inventorySort: localStorage.getItem("vibe-inventory-sort") || "rarity",
      inventoryScrollOffset: 0,
      craftFilter: localStorage.getItem("vibe-craft-filter") || "ready",
      journalFilter: localStorage.getItem("vibe-journal-filter") || "all",
      worldMapTargetPins: localStorage.getItem("vibe-world-map-target-pins") !== "false",
      tutorialIntroSlide: null,
      tutorialGuideExpanded: false,
      tutorialBlockedPulseAt: 0,
      conversationScrollOffset: 0,
      socket: null,
      socketMapId: null,
      pendingName: localStorage.getItem("vibe-player-name") || "Adventurer",
      viewport: { width: 1, height: 1 },
    };
    this.layers = {};
    this.assetTextures = new Map();
    this.playerSkeletonTemplate = null;
    this.playerSkeletonClipIds = new Set();
    this.playerEquipmentSkinTextures = new Map();
    this.playerSkeletonBoneIndices = new Map();
    this.playerSkeletonGroundY = 0;
    this.textureRegions = new Map();
    this.audioCache = new Map();
    this.settings = {
      musicVolume: clamp(Number(localStorage.getItem("vibe-music-volume") ?? 0.7), 0, 1),
      sfxVolume: clamp(Number(localStorage.getItem("vibe-sfx-volume") ?? 0.8), 0, 1),
      uiVolume: clamp(Number(localStorage.getItem("vibe-ui-volume") ?? 0), 0, 1),
      uiScale: clamp(Number(localStorage.getItem("vibe-ui-scale") ?? 1.08), 0.8, 1.2),
    };
    this.currentMusic = null;
    this.currentMusicKey = "";
    this.currentMusicBaseVolume = 0.5;
    this.musicRetryAt = 0;
    this.audioUnlocked = false;
    this.combatReplayAudioMarks = new Set();
    this.ready = false;
    this.readyPromise = null;
    setUiTextScale(this.settings.uiScale);
    setButtonClickHandler(() => this.playUiSound(audioPaths.uiClick, 0.62));
  }

  async start() {
    this.readyPromise = this.startInternal();
    await this.readyPromise;
  }

  async startInternal() {
    const initialProfile = this.readViewportProfile();
    await this.app.init({
      resizeTo: this.root,
      background: "#080907",
      antialias: true,
      preference: "webgl",
      resolution: initialProfile.phoneLike ? Math.min(2, window.devicePixelRatio || 1) : window.devicePixelRatio || 1,
      autoDensity: true,
    });
    this.root.appendChild(this.app.canvas);
    this.stage = new PIXI.Container();
    this.app.stage.addChild(this.stage);
    this.renderLoading("Loading Pixi client...");
    await this.loadCoreAssets();
    this.createLayers();
    this.bindEvents();
    this.app.ticker.add((ticker) => this.frame(ticker.deltaMS));
    this.resize();
    window.addEventListener("resize", () => this.resize());
    window.addEventListener("orientationchange", () => this.resize());
    window.visualViewport?.addEventListener("resize", () => this.resize());
    window.visualViewport?.addEventListener("scroll", () => this.bridge.reposition());
    this.ready = true;
    this.log("PixiJS client ready.");
    await this.initializeAuth();
    this.render();
  }

  async initializeAuth() {
    try {
      const config = await this.api.request("/auth/config");
      this.state.authMode = config?.mode === "simple" ? "simple" : "dev";
      this.state.buildSha = config?.build_sha || "local";
      this.state.devCheatsEnabled = config?.dev_cheats_enabled === true || config?.dev_cheats_enabled === "true";
    } catch (error) {
      this.log(error.message);
      this.state.authMode = "dev";
      this.state.devCheatsEnabled = false;
    }
    this.state.authReady = true;
    if (this.state.authMode === "simple" && this.api.sessionToken) {
      await this.restoreAccountSession();
    }
  }

  async waitUntilReady() {
    if (!this.ready && this.readyPromise) {
      await this.readyPromise;
    }
  }

  async loadCoreAssets() {
    const entries = Object.entries({ ...assetPaths, ...itemIconPaths }).map(([key, src]) => ({ alias: key, src }));
    await PIXI.Assets.load(entries);
    entries.forEach(({ alias }) => this.assetTextures.set(alias, PIXI.Assets.get(alias)));
    await this.loadPlayerSkeleton();
  }

  async loadPlayerSkeleton() {
    const bundleUrl = "/static/assets/player/skeleton/rig/compiled.json?v=card-rig-1";
    try {
      const response = await fetch(bundleUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const definition = await response.json();
      const template = await PixiSkeletonView.load(definition, new URL(bundleUrl, window.location.href).toString());
      const measurement = template.createInstance();
      measurement.apply(sampleClipPose(definition, "idle", 0));
      this.playerSkeletonGroundY = measurement.container.getLocalBounds().maxY;
      measurement.destroy();
      this.playerSkeletonTemplate = template;
      this.playerSkeletonClipIds = new Set(definition.clips.map((clip) => clip.id));
      this.playerSkeletonBoneIndices = new Map(definition.bones.map((bone, index) => [bone.id, index]));
      const equipmentAtlases = await Promise.all(Object.keys(skeletalEquipmentSkins).map(async (itemId) => {
        const atlas = await PIXI.Assets.load(skeletalEquipmentSkinPath(itemId));
        if (!atlas?.textures) throw new Error(`Equipment skin ${itemId} did not load as a Pixi spritesheet`);
        return [itemId, atlas.textures];
      }));
      this.playerEquipmentSkinTextures = new Map(equipmentAtlases);
    } catch (error) {
      this.playerSkeletonTemplate = null;
      this.playerEquipmentSkinTextures = new Map();
      console.warn("Compiled player skeleton failed to load; using legacy renderer.", error);
    }
  }

  async ensureTexture(path) {
    if (!path) {
      return null;
    }
    if (!this.assetTextures.has(path)) {
      this.assetTextures.set(path, await PIXI.Assets.load(path));
    }
    return this.assetTextures.get(path);
  }

  audioFor(path, { loop = false, volume = 0.5 } = {}) {
    if (!path) {
      return null;
    }
    if (!this.audioCache.has(path)) {
      const audio = new Audio(path);
      audio.preload = "auto";
      this.audioCache.set(path, audio);
    }
    const audio = this.audioCache.get(path);
    audio.loop = loop;
    audio.volume = volume;
    return audio;
  }

  itemFallbackIconId(item) {
    if (item?.quest_item) return "quest_item";
    if (item?.type === "equipment") return "equipment";
    if (item?.type === "consumable") return "item";
    return "chest";
  }

  itemIconTexture(item) {
    const itemIconId = item?.item_id ? `item_${item.item_id}` : "";
    return this.assetTextures.get(itemIconId) || this.assetTextures.get(this.itemFallbackIconId(item)) || this.assetTextures.get("item");
  }

  musicVolume(baseVolume = this.currentMusicBaseVolume) {
    return clamp(baseVolume * this.settings.musicVolume, 0, 1);
  }

  sfxVolume(baseVolume = 1) {
    return clamp(baseVolume * this.settings.sfxVolume, 0, 1);
  }

  uiVolume(baseVolume = 1) {
    return clamp(baseVolume * this.settings.uiVolume, 0, 1);
  }

  setAudioSetting(key, value, render = true) {
    const min = key === "uiScale" ? 0.8 : 0;
    const max = key === "uiScale" ? 1.2 : 1;
    this.settings[key] = clamp(value, min, max);
    if (key === "musicVolume") {
      localStorage.setItem("vibe-music-volume", String(this.settings.musicVolume));
      if (this.currentMusic) {
        this.currentMusic.volume = this.musicVolume();
      }
    }
    if (key === "sfxVolume") {
      localStorage.setItem("vibe-sfx-volume", String(this.settings.sfxVolume));
    }
    if (key === "uiVolume") {
      localStorage.setItem("vibe-ui-volume", String(this.settings.uiVolume));
    }
    if (key === "uiScale") {
      localStorage.setItem("vibe-ui-scale", String(this.settings.uiScale));
      setUiTextScale(this.settings.uiScale);
    }
    if (render) {
      this.renderUi();
    }
  }

  unlockAudio() {
    this.audioUnlocked = true;
    this.updateActiveMusic(true);
  }

  tryPlayMusic(music, force = false) {
    if (!music || !this.audioUnlocked) {
      return;
    }
    const now = performance.now();
    if (!force && now < this.musicRetryAt) {
      return;
    }
    const playPromise = music.play();
    if (playPromise?.catch) {
      playPromise
        .then(() => {
          this.musicRetryAt = 0;
        })
        .catch(() => {
          this.musicRetryAt = performance.now() + 800;
        });
    }
  }

  playSound(path, volume = 0.55) {
    const effectiveVolume = this.sfxVolume(volume);
    this.playRawSound(path, effectiveVolume);
  }

  playUiSound(path, volume = 0.55) {
    const effectiveVolume = this.uiVolume(volume);
    this.playRawSound(path, effectiveVolume);
  }

  playRawSound(path, effectiveVolume) {
    const cached = this.audioFor(path, { volume: effectiveVolume });
    if (!cached) {
      return;
    }
    const sound = cached.cloneNode();
    sound.volume = effectiveVolume;
    sound.play().catch(() => {});
  }

  switchMusic(key, path, volume = 0.22, force = false) {
    if (!path) {
      return;
    }
    if (this.currentMusicKey === key) {
      if (this.currentMusic?.paused) {
        this.tryPlayMusic(this.currentMusic, force);
      }
      return;
    }
    if (this.currentMusic) {
      this.currentMusic.pause();
    }
    this.currentMusicBaseVolume = volume;
    const music = this.audioFor(path, { loop: true, volume: this.musicVolume(volume) });
    this.currentMusic = music;
    this.currentMusicKey = key;
    this.musicRetryAt = 0;
    if (music) {
      music.volume = this.musicVolume(volume);
      music.currentTime = 0;
      this.tryPlayMusic(music, true);
    }
  }

  musicMapId(mapId) {
    if (!mapId) {
      return this.state.map?.base_map_id || this.state.map?.id || this.state.player?.map_id || "city_wharf";
    }
    if (mapId.startsWith("instance:")) {
      return this.state.map?.base_map_id || "city_wharf";
    }
    if (mapId.startsWith("tutorial:")) {
      return this.state.map?.base_map_id || mapId.split(":").at(-1) || "tutorial_ash_village";
    }
    return mapId;
  }

  updateActiveMusic(force = false) {
    const combat = this.state.combat;
    if (this.state.activeWindow === "combat" && combat) {
      const mapId = this.musicMapId(combat.map_id);
      const path = audioPaths.combatMusic[mapId] || audioPaths.combatMusic.city_wharf;
      this.switchMusic(`combat:${path}`, path, 0.62, force);
      return;
    }
    const activeMapId = this.state.map?.id || this.state.player?.map_id;
    if (!activeMapId) {
      return;
    }
    const mapId = this.musicMapId(activeMapId);
    const path = audioPaths.mapMusic[mapId] || audioPaths.mapMusic.city_wharf;
    if (path) {
      const volume = mapId.startsWith("tutorial_") ? 0.58 : 0.52;
      this.switchMusic(`map:${path}`, path, volume, force);
    }
  }

  createLayers() {
    clear(this.stage);
    const names = ["map", "features", "debug", "objects", "actors", "effects", "ui", "modal", "tooltip"];
    names.forEach((name) => {
      this.layers[name] = new PIXI.Container();
      this.stage.addChild(this.layers[name]);
    });
  }

  renderLoading(message) {
    clear(this.stage);
    const width = this.app.screen.width || this.root.clientWidth || 800;
    const height = this.app.screen.height || this.root.clientHeight || 600;
    const background = new PIXI.Graphics().rect(0, 0, width, height).fill(0x080907);
    const box = panel(360, 112, { fill: 0x10100d, alpha: 0.96, stroke: 0xa9854b });
    box.position.set(width / 2 - 180, height / 2 - 56);
    const title = text("Vibe MMORPG", { fontSize: 24, fill: 0xf2e6c7, fontWeight: "900" });
    title.anchor.set(0.5);
    title.position.set(width / 2, height / 2 - 16);
    const status = text(message, { fontSize: 14, fill: 0xcdbf9b });
    status.anchor.set(0.5);
    status.position.set(width / 2, height / 2 + 22);
    this.stage.addChild(background, box, title, status);
  }

  bindEvents() {
    this.app.stage.eventMode = "static";
    this.app.stage.hitArea = this.app.screen;
    this.app.stage.on("pointermove", (event) => this.handlePointerMove(event));
    this.app.stage.on("pointerup", (event) => { this.finishEquipmentDrag(event); this.finishCombatCardDrag(event); });
    this.app.stage.on("pointerupoutside", (event) => { this.cancelEquipmentDrag(); this.finishCombatCardDrag(event); });
    this.app.stage.on("pointertap", (event) => this.handlePointerTap(event));
    this.app.canvas.addEventListener("wheel", (event) => this.handleWheel(event), { passive: false });
    this.bindPhonePinchGesture();
    [this.root, this.app.canvas].forEach((surface) => {
      surface.addEventListener("contextmenu", (event) => event.preventDefault());
      surface.addEventListener("dblclick", (event) => event.preventDefault());
      surface.addEventListener("dragstart", (event) => event.preventDefault());
      surface.addEventListener("selectstart", (event) => {
        if (event.target !== this.bridge.input) event.preventDefault();
      });
    });
    window.addEventListener("pointerdown", () => this.unlockAudio(), { capture: true });
    window.addEventListener("keydown", (event) => {
      this.unlockAudio();
      if (event.key === "Escape") {
        if (this.state.player?.tutorial) {
          this.showTutorialBlocked();
          return;
        }
        this.closeTopSurface();
      }
    });
  }

  bindPhonePinchGesture() {
    const pointers = new Map();
    let pinch = null;
    const distanceBetweenTouches = () => {
      const [first, second] = [...pointers.values()];
      return first && second ? Math.hypot(second.x - first.x, second.y - first.y) : 0;
    };
    this.app.canvas.addEventListener("pointerdown", (event) => {
      if (event.pointerType !== "touch" || !this.isPhoneLandscape()) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (pointers.size === 2 && (!this.state.activeWindow || this.state.activeWindow === "world-map")) {
        pinch = {
          distance: Math.max(1, distanceBetweenTouches()),
          zoom: this.state.activeWindow === "world-map" ? this.state.worldMapZoom : this.state.mapZoom,
        };
      }
    }, { passive: true });
    this.app.canvas.addEventListener("pointermove", (event) => {
      if (!pointers.has(event.pointerId)) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (!pinch || pointers.size < 2) return;
      event.preventDefault();
      this.state.suppressWorldTapUntil = performance.now() + 350;
      const scale = distanceBetweenTouches() / pinch.distance;
      if (this.state.activeWindow === "world-map") {
        this.state.worldMapZoom = clamp(pinch.zoom * scale, 0.7, 2.6);
        localStorage.setItem("vibe-world-map-zoom", String(this.state.worldMapZoom));
        this.renderUi();
      } else {
        const next = clamp(pinch.zoom * scale, 0.7, 1.8);
        this.state.mapZoom = Number(next.toFixed(2));
        localStorage.setItem("vibe-map-zoom", String(this.state.mapZoom));
        this.render();
      }
    }, { passive: false });
    const release = (event) => {
      pointers.delete(event.pointerId);
      if (pointers.size < 2) pinch = null;
    };
    this.app.canvas.addEventListener("pointerup", release, { passive: true });
    this.app.canvas.addEventListener("pointercancel", release, { passive: true });
  }

  resize() {
    this.state.viewport = { width: this.app.screen.width, height: this.app.screen.height };
    this.state.viewportProfile = this.readViewportProfile(this.state.viewport.width, this.state.viewport.height);
    this.updateOrientationGate();
    this.bridge.reposition();
    this.app.stage.hitArea = this.app.screen;
    this.render();
  }

  readViewportProfile(width = window.innerWidth, height = window.innerHeight) {
    const styles = getComputedStyle(document.documentElement);
    const cssNumber = (name) => Number.parseFloat(styles.getPropertyValue(name)) || 0;
    const screenOrientation = window.screen?.orientation?.type || "";
    const orientation = screenOrientation.startsWith("portrait")
      ? "portrait"
      : screenOrientation.startsWith("landscape") ? "landscape" : width >= height ? "landscape" : "portrait";
    return calculateViewportProfile({
      width,
      height,
      visualWidth: window.visualViewport?.width || width,
      visualHeight: window.visualViewport?.height || height,
      safeArea: {
        top: cssNumber("--safe-top"),
        right: cssNumber("--safe-right"),
        bottom: cssNumber("--safe-bottom"),
        left: cssNumber("--safe-left"),
      },
      coarsePointer: window.matchMedia?.("(pointer: coarse)").matches || navigator.maxTouchPoints > 0,
      orientation,
    });
  }

  updateOrientationGate() {
    const blocked = Boolean(this.state.viewportProfile?.orientationBlocked);
    this.orientationGate?.classList.toggle("hidden", !blocked);
    this.root.setAttribute("aria-hidden", blocked ? "true" : "false");
    if (blocked) this.bridge.hide();
  }

  isPhoneLandscape() {
    return Boolean(this.state.viewportProfile?.phoneLandscape);
  }

  isCompactPhone() {
    return Boolean(this.state.viewportProfile?.phoneCompact);
  }

  safeRect() {
    return this.state.viewportProfile?.safeRect || {
      x: 0,
      y: 0,
      width: this.state.viewport.width,
      height: this.state.viewport.height,
      right: this.state.viewport.width,
      bottom: this.state.viewport.height,
    };
  }

  async enterWorld(name) {
    await this.waitUntilReady();
    try {
      const player = await this.api.request("/players", {
        method: "POST",
        body: JSON.stringify({ name: name || this.state.pendingName || "Adventurer" }),
      });
      this.api.setSessionToken(player.session_token);
      localStorage.setItem("vibe-player-name", player.name);
      this.state.player = player;
      this.syncPlayer(player);
      this.log(`Entered ${player.map_id} as ${player.name}.`);
      await this.refreshWorld();
      await this.loadMap(player.map_id);
    } catch (error) {
      this.log(error.message);
    }
  }

  async restoreAccountSession() {
    try {
      const account = await this.api.request("/account/characters");
      const player = account.characters?.[0];
      if (!player) {
        this.state.authMessage = "No character on this account yet.";
        this.renderUi();
        return false;
      }
      this.state.player = player;
      this.syncPlayer(player);
      localStorage.setItem("vibe-player-name", player.name);
      await this.refreshWorld();
      await this.loadMap(player.map_id);
      this.log(`Entered ${player.map_id} as ${player.name}.`);
      return true;
    } catch (error) {
      this.api.setSessionToken("");
      this.state.player = null;
      this.state.authMessage = "Session expired. Log in again.";
      this.renderUi();
      return false;
    }
  }

  async loginAccount() {
    const username = this.state.authNick.trim();
    const password = this.state.authPassword;
    const validation = this.validateAuthFields(username, password);
    if (validation) {
      this.state.authMessage = validation;
      this.renderUi();
      return;
    }
    try {
      const session = await this.api.request("/sessions", {
        method: "POST",
        body: JSON.stringify({ username, password }),
      });
      this.api.setSessionToken(session.session_token);
      localStorage.setItem("vibe-auth-nick", username);
      this.state.authPassword = "";
      this.state.authMessage = "";
      await this.restoreAccountSession();
    } catch (error) {
      this.state.authMessage = error.message;
      this.renderUi();
    }
  }

  async registerAccount() {
    const username = this.state.authNick.trim();
    const password = this.state.authPassword;
    const validation = this.validateAuthFields(username, password);
    if (validation) {
      this.state.authMessage = validation;
      this.renderUi();
      return;
    }
    try {
      const account = await this.api.request("/accounts", {
        method: "POST",
        body: JSON.stringify({ username, password }),
      });
      const session = await this.api.request("/sessions", {
        method: "POST",
        body: JSON.stringify({ username, password }),
      });
      this.api.setSessionToken(session.session_token);
      const player = await this.api.request(`/accounts/${account.id}/characters`, {
        method: "POST",
        body: JSON.stringify({ name: username }),
      });
      this.state.authPassword = "";
      this.state.authMessage = "";
      localStorage.setItem("vibe-auth-nick", username);
      localStorage.setItem("vibe-player-name", player.name);
      this.state.player = player;
      this.syncPlayer(player);
      await this.refreshWorld();
      await this.loadMap(player.map_id);
      this.log(`Registered ${player.name}.`);
    } catch (error) {
      this.state.authMessage = error.message;
      this.renderUi();
    }
  }

  logoutAccount() {
    this.api.setSessionToken("");
    this.state.player = null;
    this.state.map = null;
    this.state.world = null;
    this.state.authPassword = "";
    this.state.authMessage = "";
    if (this.state.socket) {
      this.state.socket.close();
      this.state.socket = null;
      this.state.socketMapId = null;
    }
    this.render();
  }

  validateAuthFields(username, password) {
    if (!username || !password) {
      return "Enter nick and password.";
    }
    if (username.length < 3) {
      return "Nick must be at least 3 characters.";
    }
    if (password.length < 8) {
      return "Password must be at least 8 characters.";
    }
    return "";
  }

  async refreshWorld() {
    this.state.world = await this.api.request("/world");
    if (this.state.player) {
      const player = this.state.world.players.find((candidate) => candidate.id === this.state.player.id);
      if (player) {
        this.state.player = { ...this.state.player, ...player };
        this.syncPlayer(this.state.player);
      }
      await this.syncActiveCombat();
    }
  }

  async refreshWorldAndCurrentMap() {
    const previousMapId = this.state.map?.id;
    await this.refreshWorld();
    const mapId = this.state.player?.map_id || previousMapId;
    if (mapId) {
      await this.loadMap(mapId);
    }
  }

  async syncActiveCombat(openWindow = false) {
    const combatId = this.state.player?.combat_id;
    if (!combatId) {
      if (!this.state.combatReplay) {
        this.exitCombatView();
      }
      return;
    }
    const worldCombat = (this.state.world?.combats || []).find((combat) => combat.id === combatId);
    this.state.combat = worldCombat || await this.api.request(`/combats/${combatId}`);
    if (openWindow || !this.state.activeWindow) {
      this.state.activeWindow = "combat";
    }
  }

  async loadMap(mapId) {
    this.state.map = await this.api.request(`/maps/${mapId}`);
    this.normalizeMapPlayers();
    await this.ensureTexture(this.state.map.background_image);
    this.state.selectedNpc = null;
    this.state.selectedInteractable = null;
    this.state.selectedEnemyGroup = null;
    this.connectSocket(mapId);
    this.render();
  }

  async applyMapSnapshot(snapshot) {
    if (!snapshot?.id) {
      return;
    }
    const previousMapId = this.state.map?.id;
    const previousPlayer = this.state.player ? { ...this.state.player, position: { ...this.state.player.position } } : null;
    this.state.map = snapshot;
    this.normalizeMapPlayers();
    const localPlayer = (snapshot.players || []).find((player) => player.id === this.state.player?.id);
    if (localPlayer) {
      this.state.player = { ...this.state.player, ...localPlayer };
      this.syncPlayer(this.state.player);
      if (snapshot.id === previousMapId) {
        this.syncPlayerRenderPosition(this.state.player, previousPlayer);
      } else {
        this.setRenderPosition(this.state.player.id, this.state.player.position);
      }
    }
    await this.ensureTexture(snapshot.background_image);
    if (snapshot.id !== previousMapId) {
      this.state.selectedNpc = null;
      this.state.selectedInteractable = null;
      this.state.selectedEnemyGroup = null;
      this.state.hoveredEnemyGroup = null;
      this.state.hoveredInteractable = null;
      this.state.hoveredMoveTarget = null;
      this.state.hoveredAggroThreat = null;
      this.cancelGathering();
      this.cancelMovement({ snapToServer: true });
      this.connectSocket(snapshot.id);
    }
    this.render();
  }

  connectSocket(mapId) {
    if (this.state.socket && this.state.socketMapId === mapId && this.state.socket.readyState <= WebSocket.OPEN) {
      return;
    }
    if (this.state.socket) {
      this.state.socket.close();
    }
    const socket = new WebSocket(this.api.websocketUrl(mapId));
    this.state.socket = socket;
    this.state.socketMapId = mapId;
    socket.addEventListener("open", () => {
      if (this.api.sessionToken) {
        socket.send(JSON.stringify({ type: "identify", session_token: this.api.sessionToken }));
      }
      this.log(`Connected to ${mapId}.`);
    });
    socket.addEventListener("message", (event) => {
      this.handleSocketMessage(JSON.parse(event.data)).catch((error) => this.log(error.message));
    });
    socket.addEventListener("close", () => this.log(`Disconnected from ${mapId}.`));
  }

  async handleSocketMessage(message) {
    const payload = message.payload;
    if (message.event === "map_snapshot") {
      await this.applyMapSnapshot(payload);
      return;
    }
    if (["player_joined", "player_moved", "player_entered_map", "player_updated"].includes(message.event)) {
      this.mergeMapPlayer(payload);
      if (payload.id === this.state.player?.id) {
        this.state.player = { ...this.state.player, ...payload };
        if (this.state.player.combat_id) {
          this.syncActiveCombat(true).then(() => this.render()).catch((error) => this.log(error.message));
          return;
        }
        if (!this.state.combatReplay) {
          this.exitCombatView();
        }
      }
      this.render();
      return;
    }
    if (message.event === "player_left") {
      const playerId = payload?.id;
      if (playerId && this.state.map?.players) {
        this.state.map.players = this.state.map.players.filter((player) => player.id !== playerId);
        this.state.renderPositions.delete(playerId);
        this.state.tweens.delete(playerId);
        if (this.state.selectedPlayer?.id === playerId) {
          this.state.selectedPlayer = null;
        }
        this.render();
      }
      return;
    }
    if (["combat_started", "combat_updated"].includes(message.event)) {
      if (!this.isLocalCombatPayload(payload)) {
        return;
      }
      const previousCombat = this.state.combat?.id === payload?.id ? structuredClone(this.state.combat) : null;
      if (message.event === "combat_updated" && (payload?.combat_events || []).length) {
        this.state.combat = payload;
        this.state.localCombatPlan = null;
        if (payload?.id && payload.status === "active") {
          this.state.activeWindow = "combat";
        }
        this.playCombatReplay(payload, previousCombat).then(async () => {
          await this.refreshWorldAndCurrentMap();
          await this.finishResolvedCombatView(payload);
          this.render();
        }).catch((error) => this.log(error.message));
        return;
      }
      this.state.combat = payload;
      if (payload?.id && payload.status === "active") {
        this.clearConversation({ render: false });
        this.state.activeWindow = "combat";
      }
      if (payload?.id && payload.status !== "active" && this.state.player?.combat_id === null && !this.state.combatReplay) {
        this.exitCombatView();
      }
      this.render();
      return;
    }
    if (message.event === "chat_message") {
      if (payload.channel === "combat") {
        this.addCombatChatPopup(payload);
        return;
      }
      this.addChatMessage(payload);
      return;
    }
    if (message.event === "system_message") {
      this.log(payload.text);
      return;
    }
    if (message.event === "party_invite_updated") {
      if (payload.invitee_id === this.state.player?.id) {
        this.state.partyInvite = payload;
        this.renderUi();
      }
      return;
    }
    if (message.event === "friend_request_updated") {
      if (payload.target_id === this.state.player?.id) {
        this.state.friendRequest = payload;
        this.mergePendingFriendRequest(payload);
        this.renderUi();
      }
      return;
    }
    if (["group_created", "group_updated"].includes(message.event)) {
      this.refreshWorld().then(() => this.render()).catch((error) => this.log(error.message));
      return;
    }
  }

  isLocalCombatPayload(combat) {
    if (!combat?.id || !this.state.player?.id) {
      return false;
    }
    if (this.state.player.combat_id === combat.id || this.state.combat?.id === combat.id) {
      return true;
    }
    if ((combat.participant_ids || []).includes(this.state.player.id)) {
      return true;
    }
    if ((combat.pending_participant_ids || []).includes(this.state.player.id)) {
      return true;
    }
    return [...(combat.players || []), ...(combat.pending_players || [])].some((player) => player.id === this.state.player.id);
  }

  exitCombatView({ force = false } = {}) {
    if (this.state.combatReplay && !force) {
      return;
    }
    this.state.combat = null;
    this.state.combatReplay = null;
    this.state.combatReplayKey = null;
    this.state.combatReplayPromise = null;
    this.state.combatPilePopup = null;
    this.state.combatPileShuffleAnimations = [];
    this.state.combatTimeoutRequestKey = null;
    this.state.combatTimeoutRetryAt = 0;
    this.state.selectedCombatCardInstanceId = null;
    this.state.selectedCombatTargetId = null;
    this.state.selectedCombatAllyTargetId = null;
    this.state.hoveredCombatCardInstanceId = null;
    this.state.retargetCombatCardInstanceId = null;
    this.state.localCombatPlan = null;
    if (this.state.activeWindow === "combat") {
      this.state.activeWindow = null;
    }
  }

  mergeMapPlayer(player) {
    if (!this.state.map?.players) {
      return;
    }
    if (player.map_id !== this.state.map.id) {
      this.state.map.players = this.state.map.players.filter((candidate) => candidate.id !== player.id);
      this.state.renderPositions.delete(player.id);
      this.state.tweens.delete(player.id);
      return;
    }
    const index = this.state.map.players.findIndex((candidate) => candidate.id === player.id);
    const previous = index >= 0 ? this.state.map.players[index] : null;
    if (index >= 0) {
      this.state.map.players[index] = { ...this.state.map.players[index], ...player };
    } else {
      this.state.map.players.push(player);
    }
    const merged = index >= 0 ? this.state.map.players[index] : player;
    if (merged.id === this.state.player?.id) {
      this.state.player = { ...this.state.player, ...merged };
    }
    this.syncPlayer(merged);
    if (previous && Number(merged.level || 0) > Number(previous.level || 0)) {
      this.showLevelUpEffect(merged.level, merged.id, { log: merged.id === this.state.player?.id });
    }
    this.syncPlayerRenderPosition(merged, previous);
    this.normalizeMapPlayers();
  }

  normalizeMapPlayers() {
    const map = this.state.map;
    if (!map?.id) {
      return;
    }
    const playersById = new Map();
    (map.players || []).forEach((player) => {
      if (!player?.id || player.map_id !== map.id) {
        return;
      }
      playersById.set(player.id, { ...(playersById.get(player.id) || {}), ...player });
    });
    if (this.state.player?.id && this.state.player.map_id === map.id) {
      playersById.set(this.state.player.id, { ...(playersById.get(this.state.player.id) || {}), ...this.state.player });
    }
    map.players = [...playersById.values()];
    const visibleIds = new Set(map.players.map((player) => player.id));
    for (const playerId of this.state.renderPositions.keys()) {
      if (!visibleIds.has(playerId)) {
        this.state.renderPositions.delete(playerId);
        this.state.tweens.delete(playerId);
        this.state.playerFacings.delete(playerId);
      }
    }
  }

  syncPlayer(player) {
    if (!player?.position) {
      return;
    }
    if (player.id === this.state.player?.id && Number.isFinite(player.movement_sequence)) {
      this.state.movementSequence = Math.max(this.state.movementSequence, Number(player.movement_sequence));
    }
    if (!this.state.renderPositions.has(player.id)) {
      this.state.renderPositions.set(player.id, { ...player.position });
    }
    this.ensureTutorialIntro(player);
  }

  ensureTutorialIntro(player) {
    if (player.id !== this.state.player?.id || player.tutorial?.stage_id !== "origin") return;
    const key = `vibe-tutorial-intro-seen:${player.id}`;
    if (localStorage.getItem(key) !== "true" && this.state.tutorialIntroSlide === null) {
      this.state.tutorialIntroSlide = 0;
    }
  }

  log(line) {
    const message = String(line);
    this.state.logs.push(message);
    this.state.logs = this.state.logs.slice(-80);
    this.addSystemChatLine(message);
    this.renderUi();
  }

  frame(deltaMs) {
    this.updatePerformanceWatchdog(deltaMs);
    const tweensChanged = this.updateTweens(deltaMs);
    const restEffectsChanged = this.updateRestEffects();
    const gatheringChanged = this.updateGatheringEffects();
    this.updateCombatTimerDisplay();
    this.updateCombatTimeout();
    this.updateCombatChatPopups();
    this.updateCombatPileShuffleAnimations();
    this.updateActiveMusic();
    if (this.state.player?.tutorial?.target && this.state.tutorialIntroSlide === null) {
      this.renderEffects();
    }
    if (tweensChanged) {
      this.renderMap();
    }
    if (gatheringChanged) {
      this.renderUi();
    } else if (tweensChanged || restEffectsChanged) {
      this.renderEffects();
    }
    this.updateDeckAnimationPreview();
    this.updateCombatReplay();
  }

  updateDeckAnimationPreview() {
    const preview = this.deckAnimationPreviewState;
    if (!preview || this.state.activeWindow !== "deck" || !this.state.deckDetailCardId || preview.layer.destroyed || !preview.layer.parent) {
      this.deckAnimationPreviewState = null;
      return;
    }
    if (performance.now() - (preview.lastFrameAt || 0) < 33) {
      return;
    }
    preview.lastFrameAt = performance.now();
    clear(preview.layer);
    this.drawDeckAnimationPreviewScene(preview.layer, preview.card, preview.width, preview.safeHeight, (performance.now() % 1800) / 1800);
  }

  updatePerformanceWatchdog(deltaMs) {
    const perf = this.state.performance;
    const now = performance.now();
    perf.lastFrameMs = Math.round(deltaMs);
    perf.worstFrameMs = Math.max(perf.worstFrameMs || 0, perf.lastFrameMs);
    perf.fps = Math.round(1000 / Math.max(1, deltaMs));
    if (deltaMs >= 250) {
      perf.spikeCount += 1;
      perf.lastSpikeAt = now;
      if (now - (perf.lastLogAt || 0) > 10000) {
        perf.lastLogAt = now;
        this.log(`[Perf] Frame spike ${Math.round(deltaMs)}ms. Spikes this session: ${perf.spikeCount}.`);
      }
    }
  }

  recordApiMetric(metric) {
    if (!this.state?.performance) return;
    const perf = this.state.performance;
    const now = performance.now();
    perf.lastApiMs = metric.durationMs || 0;
    perf.worstApiMs = Math.max(perf.worstApiMs || 0, perf.lastApiMs);
    perf.lastApiPath = metric.path || "";
    if (metric.slow) {
      perf.slowApiCount += 1;
      if (now - (perf.lastApiLogAt || 0) > 10000) {
        perf.lastApiLogAt = now;
        this.log(`[Perf] Slow API ${perf.lastApiMs}ms ${perf.lastApiPath}. Slow requests: ${perf.slowApiCount}.`);
      }
    }
  }

  updateCombatTimeout() {
    const combat = this.state.combat;
    if (this.state.activeWindow !== "combat" || !combat || combat.status !== "active" || this.state.combatReplay) {
      return;
    }
    if (!combat.round_ends_at || Date.now() / 1000 < combat.round_ends_at) {
      return;
    }
    if (Date.now() < this.state.combatTimeoutRetryAt) {
      return;
    }
    const key = `${combat.id}:${combat.round}`;
    if (this.state.combatTimeoutRequestKey === key) {
      return;
    }
    this.state.combatTimeoutRequestKey = key;
    this.resolveTimedOutCombat(combat, key).catch((error) => {
      this.log(error.message);
      if (this.state.combatTimeoutRequestKey === key) {
        this.state.combatTimeoutRequestKey = null;
      }
    });
  }

  updateCombatTimerDisplay() {
    const combat = this.state.combat;
    if (this.state.activeWindow !== "combat" || !combat || combat.status !== "active" || this.state.combatReplay) {
      this.state.lastCombatTimerSecond = null;
      return;
    }
    const secondsLeft = Math.max(0, Math.ceil((combat.round_ends_at || 0) - Date.now() / 1000));
    const key = `${combat.id}:${combat.round}:${secondsLeft}`;
    if (this.state.lastCombatTimerSecond === key) {
      return;
    }
    this.state.lastCombatTimerSecond = key;
    this.renderUi();
  }

  updateCombatReplay() {
    if (!this.state.combatReplay) {
      return;
    }
    const replay = this.state.combatReplay;
    const elapsed = performance.now() - replay.startedAt;
    const totalDuration = replay.events.reduce((sum, event) => sum + this.combatEventDurationMs(event), 0) + Math.max(0, replay.events.length - 1) * replay.gapMs;
    replay.finished = elapsed >= totalDuration;
    this.handleCombatReplayAudio();
    this.renderUi();
  }

  handleCombatReplayAudio() {
    const step = this.currentCombatReplayStep();
    if (!step?.event || !step.active) {
      return;
    }
    const startKey = `${step.index}:start`;
    if (!this.combatReplayAudioMarks.has(startKey) && step.progress >= 0.01) {
      this.combatReplayAudioMarks.add(startKey);
      const action = String(step.event.action || "");
      const animationKey = String(step.event.animation?.key || "");
      this.playSound(audioPaths.cards[action] || audioPaths.cards[animationKey], 0.58);
    }
    const impactKey = `${step.index}:impact`;
    if (!this.combatReplayAudioMarks.has(impactKey) && step.progress >= this.combatEventHpApplyProgress(step.event)) {
      this.combatReplayAudioMarks.add(impactKey);
      const effect = this.combatReplayImpactSound(step.event);
      this.playSound(effect.path, effect.volume);
    }
  }

  combatReplayImpactSound(event) {
    const sfx = audioPaths.sfx || {};
    const harmfulStatuses = new Set(["burn", "bleed", "poison", "stun", "vulnerable", "shaken"]);
    const statuses = event?.statuses_applied || [];
    if (event?.result === "escaped" || event?.action === "flee") {
      return { path: sfx.combatFlee || audioPaths.cards.flee, volume: 0.58 };
    }
    if (event?.result === "block") {
      return { path: sfx.combatBlockFull || audioPaths.impact, volume: 0.58 };
    }
    if (Number(event?.blocked_damage || 0) > 0) {
      const fullBlock = Number(event?.damage || 0) <= 0;
      return { path: fullBlock ? sfx.combatBlockFull : sfx.combatBlockPartial, volume: fullBlock ? 0.62 : 0.58 };
    }
    if (event?.result === "status_tick" || statuses.some((status) => harmfulStatuses.has(status))) {
      return { path: sfx.combatDebuff || audioPaths.impact, volume: 0.5 };
    }
    if (event?.healing || statuses.length) {
      return { path: sfx.combatBuff || audioPaths.impact, volume: event?.healing ? 0.42 : 0.5 };
    }
    if (Number(event?.damage || 0) > 0) {
      if (event?.action_type === "ranged") {
        return { path: sfx.combatHitRanged || audioPaths.impact, volume: 0.6 };
      }
      if (event?.action_type === "mental") {
        return { path: sfx.combatHitMagic || audioPaths.impact, volume: 0.56 };
      }
      return { path: sfx.combatHitMelee || audioPaths.impact, volume: 0.62 };
    }
    return { path: audioPaths.impact, volume: 0.48 };
  }

  updateCombatPileShuffleAnimations() {
    const now = performance.now();
    const before = this.state.combatPileShuffleAnimations.length;
    this.state.combatPileShuffleAnimations = this.state.combatPileShuffleAnimations.filter((animation) => now - animation.startedAt < animation.duration);
    if (before !== this.state.combatPileShuffleAnimations.length) {
      this.renderUi();
    }
  }

  updateTweens(deltaMs) {
    let changed = false;
    this.state.tweens.forEach((tween, playerId) => {
      changed = true;
      const previousPosition = this.state.renderPositions.get(playerId) || tween.path?.[0] || tween.from;
      tween.elapsed += deltaMs;
      const progress = clamp(tween.elapsed / tween.duration, 0, 1);
      const nextPosition = tween.path
        ? this.positionAlongTweenPath(tween, tween.totalLength * progress)
        : {
            x: tween.from.x + (tween.to.x - tween.from.x) * progress,
            y: tween.from.y + (tween.to.y - tween.from.y) * progress,
          };
      this.state.playerFacings.set(playerId, directionFromDelta(nextPosition.x - previousPosition.x, nextPosition.y - previousPosition.y));
      this.state.renderPositions.set(playerId, nextPosition);
      if (progress >= 1) {
        this.state.renderPositions.set(playerId, { ...this.tweenFinalTarget(tween) });
        this.state.tweens.delete(playerId);
      }
    });
    return changed;
  }

  updateRestEffects() {
    const now = performance.now();
    const beforeCount = this.state.restPopups.length;
    this.state.restPopups = this.state.restPopups.filter((popup) => now - popup.createdAt < popup.duration);
    const beforeRewardCount = this.state.rewardPopups.length;
    this.state.rewardPopups = this.state.rewardPopups.filter((popup) => now - popup.createdAt < popup.duration);
    const beforeLevelUpCount = this.state.levelUpPopups.length;
    this.state.levelUpPopups = this.state.levelUpPopups.filter((popup) => now - popup.createdAt < popup.duration);
    let changed = beforeCount !== this.state.restPopups.length
      || beforeRewardCount !== this.state.rewardPopups.length
      || beforeLevelUpCount !== this.state.levelUpPopups.length
      || this.state.restPopups.length > 0
      || this.state.rewardPopups.length > 0
      || this.state.levelUpPopups.length > 0;
    if (!this.state.player?.is_resting) {
      return changed;
    }
    if (now >= this.state.nextRestRefreshAt) {
      this.state.nextRestRefreshAt = now + 1000;
      this.refreshWorld()
        .then(() => this.renderUi())
        .catch((error) => this.log(error.message));
    }
    if (now >= this.state.nextRestPopupAt) {
      this.state.nextRestPopupAt = now + 1000;
      ["hp", "mana", "stamina"].forEach((resource, index) => {
        this.state.restPopups.push({
          resource,
          value: 20,
          createdAt: now,
          duration: 1200,
          worldOffsetX: (Math.random() - 0.5) * 1.2,
          worldOffsetY: -0.7 - index * 0.22 - Math.random() * 0.3,
        });
      });
      changed = true;
    }
    return changed;
  }

  updateGatheringEffects() {
    const now = performance.now();
    const beforeCount = this.state.gatheringPopups.length;
    this.state.gatheringPopups = this.state.gatheringPopups.filter((popup) => now - popup.createdAt < popup.duration);
    let changed = beforeCount !== this.state.gatheringPopups.length || this.state.gatheringPopups.length > 0;
    const gathering = this.state.gathering;
    if (!gathering) {
      return changed;
    }
    changed = true;
    if (now - gathering.startedAt >= gathering.duration && !this.state.gatheringRequestPending) {
      this.state.gatheringRequestPending = true;
      this.gatherOnce(gathering.resourceId)
        .catch((error) => this.log(error.message))
        .finally(() => {
          this.state.gatheringRequestPending = false;
        });
    }
    return changed;
  }

  getCamera() {
    const map = this.state.map;
    const focus = this.getMapCameraFocusPosition();
    const scale = worldScale * this.state.mapZoom;
    const mapTop = this.mapViewportTop();
    const mapHeight = Math.max(1, this.state.viewport.height - mapTop);
    const visibleWidth = this.state.viewport.width / scale;
    const visibleHeight = mapHeight / scale;
    return {
      scale,
      x: clamp(focus.x - visibleWidth / 2, 0, Math.max(0, map.width - visibleWidth)),
      y: clamp(focus.y - visibleHeight / 2, 0, Math.max(0, map.height - visibleHeight)),
      offsetX: Math.max(0, (this.state.viewport.width - map.width * scale) / 2),
      offsetY: mapTop + Math.max(0, (mapHeight - map.height * scale) / 2),
      visibleWidth,
      visibleHeight,
    };
  }

  mapViewportTop() {
    return this.topNavHeight() + 14;
  }

  worldToScreen(position, camera = this.getCamera()) {
    return {
      x: camera.offsetX + (position.x - camera.x) * camera.scale,
      y: camera.offsetY + (position.y - camera.y) * camera.scale,
    };
  }

  screenToWorld(point, camera = this.getCamera()) {
    return {
      x: (point.x - camera.offsetX) / camera.scale + camera.x,
      y: (point.y - camera.offsetY) / camera.scale + camera.y,
    };
  }

  getPlayerRenderPosition(player) {
    return this.state.renderPositions.get(player.id) || player.position;
  }

  getMapCameraFocusPosition() {
    const map = this.state.map;
    if (!this.state.player) {
      return { x: map.width / 2, y: map.height / 2 };
    }
    const localParty = this.currentParty();
    if (localParty?.member_ids?.includes(this.state.player.id)) {
      const mapPlayers = this.state.map?.players || [];
      const leader = mapPlayers.find((player) => player.id === localParty.leader_id);
      if (leader) {
        return this.getPlayerRenderPosition(leader);
      }
    }
    return this.getPlayerRenderPosition(this.state.player);
  }

  render() {
    this.renderMap();
    this.renderEffects();
    this.renderUi();
  }

  renderMap() {
    clear(this.layers.map);
    clear(this.layers.features);
    clear(this.layers.debug);
    clear(this.layers.objects);
    clear(this.layers.actors);
    if (!this.state.map) {
      return;
    }
    this.normalizeMapPlayers();
    const camera = this.getCamera();
    this.drawMapBackground(camera);
    this.drawFeatures(camera);
    this.drawCollisionDebug(camera);
    this.drawPointObjects(camera);
    this.drawActors(camera);
  }

  drawMapBackground(camera) {
    const map = this.state.map;
    const color = map.background_image ? 0x050504 : map.id === "outer_fields" ? 0x263923 : 0x332f27;
    this.layers.map.addChild(new PIXI.Graphics().rect(0, 0, this.state.viewport.width, this.state.viewport.height).fill(color));
    if (map.background_image) {
      const backgroundTexture = this.assetTextures.get(map.background_image);
      if (!backgroundTexture) {
        return;
      }
      const sprite = new PIXI.Sprite(backgroundTexture);
      const topLeft = this.worldToScreen({ x: 0, y: 0 }, camera);
      sprite.position.set(topLeft.x, topLeft.y);
      sprite.width = map.width * camera.scale;
      sprite.height = map.height * camera.scale;
      this.layers.map.addChild(sprite);
    }
    this.drawWorldPhaseOverlay();
    this.drawWeatherOverlay();
  }

  drawWorldPhaseOverlay() {
    const phase = this.state.player?.world_time?.phase;
    const overlays = {
      dawn: { color: 0xffb36b, alpha: 0.07 },
      dusk: { color: 0xff8f5a, alpha: 0.11 },
      night: { color: 0x10244f, alpha: 0.24 },
    };
    const overlay = overlays[phase];
    if (!overlay) {
      return;
    }
    this.layers.map.addChild(new PIXI.Graphics()
      .rect(0, 0, this.state.viewport.width, this.state.viewport.height)
      .fill({ color: overlay.color, alpha: overlay.alpha }));
  }

  drawWeatherOverlay() {
    const condition = this.state.player?.weather?.condition;
    const width = this.state.viewport.width;
    const height = this.state.viewport.height;
    const overlays = {
      cloudy: { asset: "weather_cloudy_subtle", alpha: 0.16, tint: 0xd8dde3 },
      fog: { asset: "weather_fog_subtle", alpha: 0.18, tint: 0xe3e8e2 },
      rain: { asset: "weather_rain_subtle", alpha: 0.2, tint: 0xb8cce6 },
    };
    const overlay = overlays[condition];
    if (!overlay) return;
    const texture = this.assetTextures.get(overlay.asset);
    if (!texture) return;
    const sprite = new PIXI.Sprite(texture);
    sprite.position.set(0, 0);
    sprite.width = width;
    sprite.height = height;
    sprite.alpha = overlay.alpha;
    sprite.tint = overlay.tint;
    this.layers.map.addChild(sprite);
  }

  drawFeatures(camera) {
    const colors = {
      wall: 0x3f4648,
      water: 0x23475a,
      dock: 0x4b3524,
      market: 0x80652e,
      tavern: 0x6b3e34,
      shop: 0x41516a,
      blacksmith: 0x2e3335,
      inn: 0x51415f,
      gate: 0x433324,
      field: 0x263923,
      floor: 0x332f27,
      counter: 0x4f4232,
      tree: 0x223f2e,
      shrub: 0x2e4d2f,
      rock: 0x5a5d59,
      road: 0x58462e,
    };
    const featureLayer = (feature) => {
      if (["field", "floor", "water", "dock", "road"].includes(feature.kind)) return 0;
      if (["wall", "gate", "market", "tavern", "shop", "blacksmith", "inn", "counter"].includes(feature.kind)) return 1;
      return 2;
    };
    [...(this.state.map.features || [])]
      .filter((feature) => feature.visible === true)
      .sort((left, right) => featureLayer(left) - featureLayer(right))
      .forEach((feature) => {
        const screen = this.worldToScreen(feature.position, camera);
        if (!["tree", "gate", "herb", "cache"].includes(feature.kind)) {
          const graphic = new PIXI.Graphics()
            .rect(screen.x, screen.y, feature.width * camera.scale, feature.height * camera.scale)
            .fill(colors[feature.kind] ?? 0x3d3a32);
          this.layers.features.addChild(graphic);
        }
        const iconId = this.featureIconId(feature);
        if (iconId) {
          const sprite = this.iconSprite(iconId, feature.kind === "tree" ? 1.65 : 0.74, camera);
          sprite.position.set(screen.x + feature.width * camera.scale / 2, screen.y + feature.height * camera.scale / 2);
          this.layers.features.addChild(sprite);
        }
        if (!["field", "tree", "shrub", "rock", "road", "herb", "cache"].includes(feature.kind)) {
          const label = text(feature.name, { fontSize: 11, fill: 0xe8ddc3, stroke: { color: 0x050505, width: 3 } });
          label.position.set(screen.x + 4, screen.y + 4);
          this.layers.features.addChild(label);
        }
      });
  }

  drawCollisionDebug(camera) {
    if (!this.state.showCollisionObjects) {
      return;
    }
    const drawPolygon = (shape, color, alpha) => {
      const points = shape.points || [];
      if (!points.length) return;
      const graphic = new PIXI.Graphics();
      points.forEach((point, index) => {
        const screen = this.worldToScreen(point, camera);
        if (index === 0) graphic.moveTo(screen.x, screen.y);
        else graphic.lineTo(screen.x, screen.y);
      });
      graphic.closePath().fill({ color, alpha }).stroke({ color, width: 2, alpha: 0.9 });
      this.layers.debug.addChild(graphic);
    };
    (this.state.map.collision_map?.walkable_areas || []).forEach((area) => drawPolygon(area, 0x22c55e, 0.13));
    (this.state.map.collision_map?.objects || []).forEach((object) => {
      const graphic = new PIXI.Graphics();
      if (object.shape === "rect" && object.position) {
        const screen = this.worldToScreen(object.position, camera);
        graphic.rect(screen.x, screen.y, object.width * camera.scale, object.height * camera.scale);
      } else if (object.shape === "circle" && object.position) {
        const screen = this.worldToScreen(object.position, camera);
        graphic.circle(screen.x, screen.y, object.radius * camera.scale);
      } else if (object.shape === "polygon") {
        drawPolygon(object, 0xef754b, 0.22);
        return;
      }
      graphic.fill({ color: 0xef754b, alpha: 0.22 }).stroke({ color: 0xef754b, width: 2 });
      this.layers.debug.addChild(graphic);
    });
  }

  drawPointObjects(camera) {
    const drawSet = [
      ...(this.state.map.interactables || []).map((entry) => ({ kind: "interactable", entry, position: entry.position })),
      ...(this.state.map.portals || []).map((entry) => ({ kind: "portal", entry, position: entry.at })),
      ...(this.state.map.enemy_groups || []).map((entry) => ({ kind: "enemy", entry, position: entry.position })),
      ...(this.state.map.npcs || []).map((entry) => ({ kind: "npc", entry, position: entry.position })),
    ].sort((a, b) => a.position.y - b.position.y);
    drawSet.forEach((item) => {
      const screen = this.worldToScreen(item.position, camera);
      if (item.kind === "portal") this.drawPortal(item.entry, screen, camera);
      if (item.kind === "npc") this.drawNpc(item.entry, screen, camera);
      if (item.kind === "interactable") this.drawInteractable(item.entry, screen, camera);
      if (item.kind === "enemy") this.drawEnemyGroup(item.entry, screen, camera);
    });
  }

  drawActors(camera) {
    this.partyActorEntries()
      .sort((a, b) => this.getPlayerRenderPosition(a.leader).y - this.getPlayerRenderPosition(b.leader).y)
      .forEach((entry) => {
        const player = entry.leader;
        const members = entry.members;
        const isParty = members.length > 1;
        const localInParty = members.some((member) => member.id === this.state.player?.id);
        const position = this.getPlayerRenderPosition(player);
        const screen = this.worldToScreen(position, camera);
        const sameParty = Boolean(this.currentParty()?.member_ids.includes(player.id) && !localInParty);
        const isLocalPlayer = player.id === this.state.player?.id;
        if (!isLocalPlayer && !localInParty) {
          this.drawMapObjectPresence(this.layers.actors, screen, camera, { color: sameParty || isParty ? 0xf7f5ed : 0x8bbcff, scale: 0.92, ring: sameParty || isParty });
        }
        if (isLocalPlayer || localInParty) {
          this.layers.actors.addChild(new PIXI.Graphics()
            .ellipse(screen.x, screen.y + camera.scale * 0.08, camera.scale * 0.36, camera.scale * 0.18)
            .fill({ color: 0xffffff, alpha: 0.16 })
            .stroke({ color: 0xffffff, width: 2, alpha: 0.9 }));
        }
        if (isLocalPlayer && this.state.gathering) {
          const gathering = new PIXI.Sprite(this.assetTextures.get("player_gathering"));
          gathering.anchor.set(0.5, 0.82);
          gathering.position.set(screen.x, screen.y + Math.sin(performance.now() / 220) * camera.scale * 0.02);
          gathering.width = camera.scale * 1.72;
          gathering.height = camera.scale * 1.72;
          this.layers.actors.addChild(gathering);
        } else if (isLocalPlayer && this.state.player?.is_resting) {
          const rest = new PIXI.Sprite(this.assetTextures.get("player_rest"));
          rest.anchor.set(0.5, 0.82);
          rest.position.set(screen.x, screen.y + Math.sin(performance.now() / 240) * camera.scale * 0.025);
          rest.width = camera.scale * 1.72;
          rest.height = camera.scale * 1.72;
          this.layers.actors.addChild(rest);
        } else {
          this.drawPlayerSprite(player, screen, camera);
        }
        if (isParty) {
          this.drawPartyBadge(screen, camera, members.length);
          this.drawPartyMemberPips(screen, camera, members.length);
        } else if (sameParty) {
          this.layers.actors.addChild(new PIXI.Graphics().circle(screen.x, screen.y, camera.scale * 0.34).stroke({ color: 0xf7f5ed, width: 3 }));
        }
        const label = isParty ? `${player.name} +${members.length - 1}` : player.name;
        this.label(label, screen.x, screen.y - camera.scale * 0.92, this.layers.actors, 11, 0xf0e7d0, { background: true });
      });
  }

  partyActorEntries() {
    const players = this.state.map?.players || [];
    const byId = new Map(players.map((player) => [player.id, player]));
    const handled = new Set();
    const entries = [];
    (this.state.world?.groups || []).forEach((group) => {
      const members = (group.member_ids || []).map((memberId) => byId.get(memberId)).filter(Boolean);
      if (members.length <= 1) return;
      const leader = byId.get(group.leader_id) || members[0];
      members.forEach((member) => handled.add(member.id));
      entries.push({ leader, members, group });
    });
    players.forEach((player) => {
      if (!handled.has(player.id)) {
        entries.push({ leader: player, members: [player], group: null });
      }
    });
    return entries;
  }

  drawPartyBadge(screen, camera, count) {
    const radius = Math.max(13, camera.scale * 0.18);
    const x = screen.x + camera.scale * 0.34;
    const y = screen.y - camera.scale * 0.58;
    this.layers.actors.addChild(new PIXI.Graphics()
      .circle(x, y, radius)
      .fill({ color: 0x0d1724, alpha: 0.96 })
      .stroke({ color: 0x8fb7ff, width: 3, alpha: 0.95 }));
    const badge = text(`x${count}`, { fontSize: 12, fill: 0xf4f8ff, fontWeight: "900" });
    badge.anchor.set(0.5);
    badge.position.set(x, y);
    this.layers.actors.addChild(badge);
  }

  drawPartyMemberPips(screen, camera, count) {
    const total = Math.min(5, count);
    const startX = screen.x - (total - 1) * camera.scale * 0.08;
    for (let index = 0; index < total; index += 1) {
      this.layers.actors.addChild(new PIXI.Graphics()
        .circle(startX + index * camera.scale * 0.16, screen.y + camera.scale * 0.34, Math.max(4, camera.scale * 0.055))
        .fill({ color: index === 0 ? 0xf3d28a : 0x8fb7ff, alpha: 0.98 })
        .stroke({ color: 0x050606, width: 1.5, alpha: 0.85 }));
    }
  }

  drawPlayerSprite(player, screen, camera) {
    const texture = this.assetTextures.get("player_walk");
    if (!texture) return;
    const tween = this.state.tweens.get(player.id);
    const direction = this.state.playerFacings.get(player.id) || "down";
    const row = playerSprite.directions[direction] ?? playerSprite.directions.down;
    const frame = tween ? Math.floor(tween.elapsed / playerSprite.frameMs) % playerSprite.columns : 0;
    const frameTexture = this.textureRegion(
      "player_walk",
      frame * playerSprite.cellSize,
      row * playerSprite.cellSize,
      playerSprite.cellSize,
      playerSprite.cellSize,
    );
    const sprite = new PIXI.Sprite(frameTexture);
    const drawHeight = camera.scale * playerSprite.scale;
    sprite.anchor.set(0.5, 0.78);
    sprite.position.set(screen.x, screen.y);
    sprite.width = drawHeight;
    sprite.height = drawHeight;
    this.layers.actors.addChild(sprite);
  }

  drawPortal(portal, screen, camera) {
    const routeHint = (this.state.player?.route_hints || []).find((hint) => hint.portal_id === portal.id && hint.next_map_id === portal.to_map_id);
    const isRoutePortal = Boolean(routeHint);
    this.drawMapObjectPresence(this.layers.objects, screen, camera, { color: isRoutePortal ? 0xffdf7a : 0x7aa8ff, scale: isRoutePortal ? 1.38 : 1.12, ring: true, pulse: true });
    const sprite = this.iconSprite("portal", 1.1, camera);
    sprite.position.set(screen.x, screen.y);
    this.layers.objects.addChild(sprite);
    if (isRoutePortal) {
      const marker = new PIXI.Graphics()
        .poly([
          screen.x,
          screen.y - camera.scale * 0.95,
          screen.x - camera.scale * 0.18,
          screen.y - camera.scale * 0.58,
          screen.x + camera.scale * 0.18,
          screen.y - camera.scale * 0.58,
        ])
        .fill({ color: 0xffdf7a, alpha: 0.92 })
        .stroke({ color: 0x2b1c06, width: 2, alpha: 0.9 });
      this.layers.objects.addChild(marker);
      this.label("Route", screen.x, screen.y - camera.scale * 0.98, this.layers.objects, 11, 0xffdf7a, { background: true });
    }
    this.label(portal.instance_template_id ? "Instance" : portal.to_map_id.replaceAll("_", " "), screen.x, screen.y + camera.scale * 0.52, this.layers.objects, 11, 0xdde8ff, { background: true });
  }

  drawNpc(npc, screen, camera) {
    const actionNpc = Boolean(npc.actions?.length);
    const questNpc = npc.kind === "quest";
    this.drawMapObjectPresence(this.layers.objects, screen, camera, {
      color: questNpc ? 0xffd56d : 0xd7b063,
      scale: 1.05,
      ring: actionNpc || questNpc,
      pulse: actionNpc || questNpc,
    });
    const sprite = this.iconSprite(this.npcIconId(npc), 1.18, camera);
    sprite.position.set(screen.x, screen.y);
    this.layers.objects.addChild(sprite);
    if (npc.actions?.length) {
      this.layers.objects.addChild(new PIXI.Graphics().circle(screen.x + camera.scale * 0.29, screen.y - camera.scale * 0.29, camera.scale * 0.13).fill(0xd8cfb6));
      this.label("!", screen.x + camera.scale * 0.29, screen.y - camera.scale * 0.42, this.layers.objects, 12, 0x16120d, { background: false });
    }
    this.label(npc.name, screen.x, screen.y - camera.scale * 0.78, this.layers.objects, 11, 0xf4e2b2, { background: true });
  }

  drawInteractable(interactable, screen, camera) {
    const hovered = this.state.hoveredInteractable?.id === interactable.id;
    const questTarget = this.isActiveInteractableTarget(interactable.id);
    const accent = this.interactableAccentColor(interactable);
    const isSacrificeAltar = interactable.kind === "sacrifice_altar";
    const isIllustratedTutorialProp = interactable.id.startsWith("tutorial_");
    const presenceScale = isSacrificeAltar ? 1.24 : 1;
    const iconScale = isSacrificeAltar ? (hovered ? 1.42 : 1.24) : (hovered ? 0.86 : 0.74);
    if (hovered || questTarget) {
      this.layers.objects.addChild(new PIXI.Graphics()
        .ellipse(screen.x, screen.y + camera.scale * 0.05, camera.scale * 0.34 * presenceScale, camera.scale * 0.2 * presenceScale)
        .fill({ color: questTarget ? 0xffdf7a : accent, alpha: questTarget ? 0.34 : 0.24 }));
    }
    if (questTarget) {
      this.drawMapObjectPresence(this.layers.objects, screen, camera, {
        color: 0xffdf7a,
        scale: hovered ? 1.34 : 1.18,
        ring: true,
        pulse: true,
      });
    }
    if (isSacrificeAltar) {
      this.drawMapObjectPresence(this.layers.objects, screen, camera, {
        color: 0xffdf7a,
        scale: hovered ? 1.32 : 1.18,
        ring: true,
        pulse: hovered,
      });
    }
    if (isIllustratedTutorialProp) {
      const marker = new PIXI.Graphics()
        .ellipse(screen.x, screen.y + camera.scale * 0.12, camera.scale * (hovered ? 0.34 : 0.27), camera.scale * (hovered ? 0.18 : 0.14))
        .stroke({ color: questTarget ? 0xffdf7a : accent, width: Math.max(1.5, camera.scale * 0.045), alpha: hovered || questTarget ? 0.95 : 0.58 });
      this.layers.objects.addChild(marker);
    } else {
      const sprite = this.iconSprite(this.interactableIconId(interactable), iconScale, camera);
      sprite.position.set(screen.x, screen.y);
      sprite.alpha = hovered ? 1 : 0.92;
      this.layers.objects.addChild(sprite);
    }
    if (questTarget) {
      this.label(`CEL: ${interactable.name}`, screen.x, screen.y - camera.scale * 0.82, this.layers.objects, 11, 0xffdf7a, { background: true });
    }
  }

  isActiveInteractableTarget(interactableId) {
    const questTargets = (this.activeStoryQuests?.() || [])
      .flatMap((quest) => quest.objectives || [])
      .filter((objective) => !objective.complete && objective.type === "interact")
      .map((objective) => objective.target);
    const errandTargets = (this.state.player?.active_errands || [])
      .flatMap((errand) => errand.objectives || [])
      .filter((objective) => !objective.complete && ["interact", "workstation"].includes(objective.type))
      .map((objective) => objective.target_interactable_id);
    return [...questTargets, ...errandTargets].includes(interactableId);
  }

  drawEnemyGroup(enemyGroup, screen, camera) {
    const highlighted = enemyGroup.id === this.state.selectedEnemyGroup?.id || enemyGroup.id === this.state.hoveredEnemyGroup?.id || enemyGroup.id === this.state.hoveredAggroThreat?.enemyGroup?.id;
    this.drawMapObjectPresence(this.layers.objects, screen, camera, { color: 0xff6a45, scale: 0.96, ring: highlighted, pulse: highlighted, danger: true });
    const sprite = this.iconSprite(this.enemyIconId(enemyGroup), 0.9, camera);
    sprite.position.set(screen.x, screen.y);
    this.layers.objects.addChild(sprite);
    this.layers.objects.addChild(new PIXI.Graphics().circle(screen.x + camera.scale * 0.23, screen.y - camera.scale * 0.24, camera.scale * 0.14).fill(0xd8cfb6));
    this.label("X", screen.x + camera.scale * 0.23, screen.y - camera.scale * 0.36, this.layers.objects, 11, 0x16120d, { background: false });
    this.label(`${enemyGroup.name} Lv ${enemyGroup.level || 1}`, screen.x, screen.y - camera.scale * 0.62, this.layers.objects, 11, 0xffd4c8, { background: true, backgroundFill: 0x1a0805 });
  }

  renderEffects() {
    clear(this.layers.effects);
    if (!this.state.map) return;
    const camera = this.getCamera();
    this.drawTutorialTargetArrow(camera);
    if (this.state.hoveredAggroThreat && this.state.hoveredMoveTarget) {
      const enemyGroup = this.state.hoveredAggroThreat.enemyGroup;
      const enemyScreen = this.worldToScreen(enemyGroup.position, camera);
      const targetScreen = this.worldToScreen(this.state.hoveredMoveTarget, camera);
      this.layers.effects.addChild(new PIXI.Graphics()
        .circle(enemyScreen.x, enemyScreen.y, enemyGroup.aggro_radius * camera.scale)
        .fill({ color: 0xe53935, alpha: 0.16 })
        .stroke({ color: 0xe53935, alpha: 0.72, width: 2 }));
      this.layers.effects.addChild(new PIXI.Graphics().circle(targetScreen.x, targetScreen.y, 7).fill({ color: 0xe53935, alpha: 0.85 }));
    }
    if (this.state.player && (this.state.restPopups.length || this.state.rewardPopups.length || this.state.levelUpPopups.length || this.state.gatheringPopups.length)) {
      const position = this.getPlayerRenderPosition(this.state.player);
      const colors = { hp: 0x7ee36d, mana: 0x79a7ff, stamina: 0xf0c85e };
      this.state.restPopups.forEach((popup) => {
        const progress = (performance.now() - popup.createdAt) / popup.duration;
        const screen = this.worldToScreen({ x: position.x + popup.worldOffsetX, y: position.y + popup.worldOffsetY }, camera);
        const popupText = text(`+${popup.value}`, { fontSize: 14, fill: colors[popup.resource] || 0xf7f5ed, fontWeight: "900", stroke: { color: 0x050505, width: 4 } });
        popupText.anchor.set(0.5);
        popupText.alpha = Math.max(0, 1 - progress);
        popupText.position.set(screen.x, screen.y - progress * 34);
        this.layers.effects.addChild(popupText);
      });
      this.state.rewardPopups.forEach((popup) => {
        const progress = (performance.now() - popup.createdAt) / popup.duration;
        const screen = this.worldToScreen({ x: position.x + popup.worldOffsetX, y: position.y + popup.worldOffsetY }, camera);
        const popupText = text(popup.label, { fontSize: 16, fill: popup.color || 0xffdf7a, fontWeight: "900", stroke: { color: 0x050505, width: 4 } });
        popupText.anchor.set(0.5);
        popupText.alpha = Math.max(0, 1 - progress);
        popupText.position.set(screen.x, screen.y - progress * 38);
        this.layers.effects.addChild(popupText);
      });
      this.state.levelUpPopups.forEach((popup) => {
        const progress = clamp((performance.now() - popup.createdAt) / popup.duration, 0, 1);
        const target = this.mapPlayerForEffect(popup.playerId) || this.state.player;
        const targetPosition = this.getPlayerRenderPosition(target);
        const screen = this.worldToScreen({ x: targetPosition.x, y: targetPosition.y - 0.55 }, camera);
        const burst = new PIXI.Graphics()
          .circle(screen.x, screen.y, 20 + progress * 74)
          .stroke({ color: 0xffdf7a, width: 5, alpha: Math.max(0, 1 - progress) });
        const inner = new PIXI.Graphics()
          .circle(screen.x, screen.y, 9 + Math.sin(progress * Math.PI) * 18)
          .fill({ color: 0xfff1a6, alpha: 0.22 * Math.max(0, 1 - progress) });
        const label = text(`LEVEL UP ${popup.level ? `Lv ${popup.level}` : ""}`.trim(), {
          fontSize: 22,
          fill: 0xfff1a6,
          fontWeight: "900",
          stroke: { color: 0x2a1600, width: 6 },
        });
        label.anchor.set(0.5);
        label.alpha = Math.max(0, 1 - progress * 0.8);
        label.position.set(screen.x, screen.y - 46 - progress * 22);
        this.layers.effects.addChild(inner, burst, label);
      });
      this.state.gatheringPopups.forEach((popup) => {
        const progress = (performance.now() - popup.createdAt) / popup.duration;
        const screen = this.worldToScreen({ x: position.x + popup.worldOffsetX, y: position.y + popup.worldOffsetY }, camera);
        const popupText = text(popup.label, { fontSize: 15, fill: 0x7bd889, fontWeight: "900", stroke: { color: 0x050505, width: 4 } });
        popupText.anchor.set(0.5);
        popupText.alpha = Math.max(0, 1 - progress);
        popupText.position.set(screen.x, screen.y - progress * 36);
        this.layers.effects.addChild(popupText);
      });
    }
  }

  drawTutorialTargetArrow(camera) {
    const tutorial = this.state.player?.tutorial;
    const target = tutorial?.target;
    if (!target?.position || this.state.tutorialIntroSlide !== null || this.state.activeWindow === "combat") return;
    const screen = this.worldToScreen(target.position, camera);
    const safe = this.safeRect();
    const navBottom = safe.y + this.topNavHeight() + 26;
    const marker = this.isPhoneLandscape()
      ? {
          x: clamp(screen.x, safe.x + 34, safe.right - 34),
          y: clamp(screen.y, navBottom + 34, safe.bottom - 34),
        }
      : screen;
    const pulse = (Math.sin(performance.now() / 180) + 1) / 2;
    const y = marker.y - camera.scale * (0.82 + pulse * 0.18);
    const glow = new PIXI.Graphics()
      .circle(marker.x, marker.y + camera.scale * 0.08, camera.scale * (0.42 + pulse * 0.08))
      .fill({ color: 0xffe36d, alpha: 0.12 + pulse * 0.12 })
      .stroke({ color: 0xffdf55, width: 3 + pulse * 2, alpha: 0.78 });
    const arrow = new PIXI.Graphics()
      .poly([
        marker.x - 13, y - 30,
        marker.x + 13, y - 30,
        marker.x + 13, y - 7,
        marker.x + 25, y - 7,
        marker.x, y + 18,
        marker.x - 25, y - 7,
        marker.x - 13, y - 7,
      ])
      .fill({ color: 0xffe36d, alpha: 0.92 })
      .stroke({ color: 0x3b2400, width: 3, alpha: 0.95 });
    this.layers.effects.addChild(glow, arrow);
    this.label(target.label || tutorial.hint || "", marker.x, Math.max(navBottom, y - 44), this.layers.effects, 13, 0xffef9d, { background: true });
  }

  mapPlayerForEffect(playerId) {
    if (!playerId) {
      return this.state.player;
    }
    if (this.state.player?.id === playerId) {
      return this.state.player;
    }
    return (this.state.map?.players || []).find((player) => player.id === playerId) || null;
  }

  renderUi() {
    clear(this.layers.ui);
    clear(this.layers.modal);
    clear(this.layers.tooltip);
    this.drawTopNav();
    this.drawHud();
    this.drawTutorialGuide();
    this.drawQuestTracker();
    this.drawRewardSummary();
    this.drawMapZoomControls();
    this.drawChat();
    this.drawSelectionPanels();
    this.drawActiveWindow();
    this.drawItemTakePopup();
    this.drawBuildStamp();
    this.drawCombatDragGhost();
    this.drawEquipmentDragGhost();
    this.drawTutorialIntro();
  }

  drawBuildStamp() {
    if (this.state.player) {
      return;
    }
    const sha = String(this.state.buildSha || "local").slice(0, 12);
    const label = text(`build ${sha}`, { fontSize: 9, fill: 0x8f8775, alpha: 0.72 });
    label.anchor.set(1, 1);
    label.position.set(this.state.viewport.width - 8, this.state.viewport.height - 6);
    this.layers.ui.addChild(label);
  }

  drawTopNav() {
    if (this.isPhoneLandscape()) {
      this.drawPhoneTopNav();
      return;
    }
    const width = Math.min(this.state.viewport.width - 28, 1180);
    const labels = this.topNavLabels();
    const compact = this.state.viewport.width < 760;
    const topHeight = this.topNavHeight();
    const top = panel(width, topHeight, { fill: 0x0d0f0d, alpha: 0.88, stroke: 0x6f5d3d });
    top.position.set(14, 14);
    this.layers.ui.addChild(top);
    const title = text("Vibe MMORPG", { fontSize: compact ? 19 : 21, fill: 0xf2e6c7, fontWeight: "900" });
    title.position.set(28, 24);
    this.layers.ui.addChild(title);
    const buttonWidths = {
      character: compact ? 78 : 88,
      inventory: compact ? 78 : 92,
      deck: compact ? 78 : 72,
      combat: compact ? 78 : 82,
      quests: compact ? 78 : 82,
      party: compact ? 78 : 78,
      "world-map": compact ? 78 : 100,
      social: compact ? 78 : 80,
      settings: compact ? 78 : 108,
    };
    const compactLabels = {
      character: "Char",
      inventory: "Inv",
      deck: "Deck",
      combat: "Fight",
      quests: "Quest",
      party: "Party",
      "world-map": "Map",
      social: "Social",
      settings: "Set",
    };
    let x = compact ? 28 : 274;
    let y = compact ? 58 : 28;
    const maxX = 14 + width - 8;
    labels.forEach((name) => {
      const itemWidth = buttonWidths[name] || 88;
      if (compact && x + itemWidth > maxX) {
        x = 28;
        y += 40;
      }
      const item = this.iconMenuButton(compact ? compactLabels[name] : windowTitles[name] || name, this.menuIconKey(name), itemWidth, 34, () => {
        this.state.activeWindow = name;
        if (name === "deck") {
          this.loadCardDeck();
        }
        this.renderUi();
      }, this.state.activeWindow === name);
      item.position.set(x, y);
      const isTutorialTarget = this.state.player?.tutorial?.target?.ui === name;
      if (isTutorialTarget) {
        const pulse = (Math.sin(performance.now() / 180) + 1) / 2;
        const highlight = new PIXI.Graphics()
          .roundRect(x - 5, y - 5, itemWidth + 10, 44, 8)
          .stroke({ color: 0xffdf55, width: 3 + pulse * 2, alpha: 0.9 });
        this.layers.ui.addChild(highlight);
      }
      x += item.widthHint + 8;
      this.layers.ui.addChild(item);
    });
    const lang = this.iconMenuButton(currentLanguage() === "pl" ? "PL" : "EN", "menu_icon_world_map", 58, 34, () => {
      setLanguage(currentLanguage() === "pl" ? "en" : "pl");
      this.refreshWorld().then(() => this.state.player ? this.loadMap(this.state.player.map_id) : this.render());
    });
    if (x + lang.widthHint > maxX) {
      x = 28;
      y += 40;
    }
    lang.position.set(x, y);
    this.layers.ui.addChild(lang);
  }

  topNavHeight() {
    if (this.isPhoneLandscape()) return 52;
    const width = Math.min(this.state.viewport.width - 28, 1180);
    const compact = this.state.viewport.width < 760;
    const items = [...this.topNavLabels().map((name) => this.topNavButtonWidth(name, compact)), 58];
    let x = compact ? 28 : 274;
    let rows = 1;
    const maxX = 14 + width - 8;
    items.forEach((itemWidth) => {
      if (x + itemWidth > maxX) {
        x = 28;
        rows += 1;
      }
      x += itemWidth + 8;
    });
    if (!compact) {
      return rows <= 1 ? 62 : 62 + (rows - 1) * 40;
    }
    return 58 + rows * 40 + 12;
  }

  topNavLabels() {
    const combat = this.state.combat || this.state.player?.combat_id ? ["combat"] : [];
    if (this.state.player?.tutorial) {
      if (combat.length) return combat;
      const targetUi = this.state.player.tutorial.target?.ui;
      return targetUi ? [targetUi] : [];
    }
    return ["character", "inventory", "deck", ...combat, "quests", "party", "world-map", "social", "settings"];
  }

  drawPhoneTopNav() {
    const safe = this.safeRect();
    const labels = this.topNavLabels();
    const entries = [...labels, "language"];
    const gap = 4;
    const innerWidth = safe.width - 12;
    const itemWidth = clamp(Math.floor((innerWidth - gap * Math.max(0, entries.length - 1)) / Math.max(1, entries.length)), 44, 60);
    const rowWidth = entries.length * itemWidth + Math.max(0, entries.length - 1) * gap;
    const frame = panel(Math.min(safe.width, rowWidth + 12), 52, { fill: 0x0d0f0d, alpha: 0.95, stroke: 0x6f5d3d, radius: 5 });
    frame.position.set(safe.x + 4, safe.y);
    this.layers.ui.addChild(frame);
    const compactLabels = {
      character: "Char", inventory: "Inv", deck: "Deck", combat: "Fight", quests: "Quest",
      party: "Party", "world-map": "Map", social: "Social", settings: "Set",
    };
    let x = safe.x + 10;
    entries.forEach((name) => {
      const isLanguage = name === "language";
      const label = isLanguage ? (currentLanguage() === "pl" ? "PL" : "EN") : compactLabels[name] || name;
      const action = isLanguage
        ? () => {
            setLanguage(currentLanguage() === "pl" ? "en" : "pl");
            this.refreshWorld().then(() => this.state.player ? this.loadMap(this.state.player.map_id) : this.render());
          }
        : () => {
            this.state.activeWindow = name;
            this.state.phoneChatOpen = false;
            if (name === "deck") this.loadCardDeck();
            this.renderUi();
          };
      const item = this.iconMenuButton(label, isLanguage ? "menu_icon_world_map" : this.menuIconKey(name), itemWidth, 44, action, this.state.activeWindow === name);
      item.position.set(x, safe.y + 4);
      this.layers.ui.addChild(item);
      x += itemWidth + gap;
    });
  }

  drawTutorialGuide() {
    const tutorial = this.state.player?.tutorial;
    const phone = this.isPhoneLandscape();
    if (!tutorial || ((phone || this.state.viewport.width < 760) && this.state.activeWindow)) return;
    const compact = phone || this.state.viewport.width < 760;
    const safe = this.safeRect();
    const width = phone
      ? Math.min(310, Math.max(250, safe.width * 0.42))
      : compact
      ? Math.max(260, this.state.viewport.width - 28)
      : Math.min(390, Math.max(290, this.state.viewport.width - 620));
    const x = phone ? safe.x + 8 : 14;
    const topY = (phone ? safe.y : 0) + this.topNavHeight() + 8;
    const contentWidth = width - 28;
    const chapter = text(`${currentLanguage() === "pl" ? "PROLOG" : "PROLOGUE"}  ${tutorial.chapter}/${tutorial.chapter_count}`, { fontSize: 11, fill: 0xffdf7a, fontWeight: "900" });
    chapter.position.set(x + 14, topY + 11);
    const expanded = Boolean(this.state.tutorialGuideExpanded);
    const toggleWidth = currentLanguage() === "pl" ? 82 : 76;
    const toggle = button(
      expanded
        ? (currentLanguage() === "pl" ? "Zwiń" : "Collapse")
        : (currentLanguage() === "pl" ? "Rozwiń" : "Expand"),
      toggleWidth,
      28,
      () => {
        this.state.tutorialGuideExpanded = !this.state.tutorialGuideExpanded;
        this.renderUi();
      },
      { fontSize: 11 },
    );
    toggle.position.set(x + width - toggleWidth - 8, topY + 7);
    if (!expanded) {
      const height = 44;
      const y = phone ? topY : compact ? Math.max(topY, this.state.viewport.height - height - 14) : topY;
      const offsetY = y - topY;
      chapter.y += offsetY;
      toggle.y += offsetY;
      const box = panel(width, height, { fill: 0x11120f, alpha: 0.97, stroke: 0xe0b95b, strokeAlpha: 0.95 });
      box.position.set(x, y);
      this.layers.ui.addChild(box, chapter, toggle);
      return;
    }
    const title = text(tutorial.title || "Tutorial", { fontSize: 16, fill: 0xf2e6c7, fontWeight: "900", wordWrap: true, wordWrapWidth: width - 28 });
    title.position.set(x + 14, topY + 31);
    const story = text(tutorial.story || "", { fontSize: phone ? 11 : 12, fill: 0xcfc2a4, fontStyle: "italic", wordWrap: true, wordWrapWidth: contentWidth, lineHeight: phone ? 14 : 16 });
    story.position.set(x + 14, title.y + title.height + 8);
    const hintY = tutorial.story ? story.y + story.height + 10 : story.y;
    const hint = text(`${currentLanguage() === "pl" ? "TERAZ: " : "NOW: "}${tutorial.target?.label || tutorial.hint || ""}`, { fontSize: 12, fill: 0xffe695, fontWeight: "900", wordWrap: true, wordWrapWidth: contentWidth, lineHeight: 16 });
    hint.position.set(x + 14, hintY);
    const privateLabel = text(currentLanguage() === "pl" ? "Prywatna historia • postęp zapisuje się automatycznie" : "Private story • progress saves automatically", { fontSize: 10, fill: 0x8fb7ff });
    privateLabel.position.set(x + 14, hint.y + hint.height + 12);
    const naturalHeight = privateLabel.y + privateLabel.height + 12 - topY;
    const maxHeight = phone ? Math.max(120, safe.bottom - topY - 8) : naturalHeight;
    const height = Math.min(naturalHeight, maxHeight);
    if (phone && naturalHeight > height) {
      const scale = Math.max(0.78, (height - 28) / Math.max(1, naturalHeight - 28));
      [title, story, hint, privateLabel].forEach((entry) => entry.scale.set(scale));
    }
    const y = phone ? topY : compact ? Math.max(topY, this.state.viewport.height - height - 14) : topY;
    const offsetY = y - topY;
    [chapter, toggle, title, story, hint, privateLabel].forEach((entry) => { entry.y += offsetY; });
    const box = panel(width, height, { fill: 0x11120f, alpha: 0.97, stroke: 0xe0b95b, strokeAlpha: 0.95 });
    box.position.set(x, y);
    this.layers.ui.addChild(box, chapter, toggle, title, story, hint, privateLabel);
  }

  drawTutorialIntro() {
    const index = this.state.tutorialIntroSlide;
    if (index === null || !this.state.player?.tutorial) return;
    const polish = currentLanguage() === "pl";
    const slides = polish ? [
      {
        texture: "tutorial_intro_1",
        title: "Noc bez świadków",
        body: "Napastnicy wyglądali jak bandyci. Rabowali domy i pędzili zwierzęta, lecz ogień podłożyli z kilku stron. Linę studni przecięli, zanim ktokolwiek zdążył podnieść alarm.",
      },
      {
        texture: "tutorial_intro_2",
        title: "Wschodnia brama",
        body: "Rutka otworzyła boczne wyjście. Nie wie, czego szukają napastnicy — tylko że zabijają również tych, którzy oddali wszystko bez oporu. Jasiek czeka z barką dla ocalałych.",
      },
      {
        texture: "tutorial_intro_3",
        title: "Cena przeprawy",
        body: "Uchodźcy zapłacili za miejsce tym, co uratowali z ognia. Nie wiedzą, że Moryc sprzedał już ich miejsca po raz drugi — prywatnej przystani, która płaci za ludzi bez dokumentów.",
      },
    ] : [
      {
        texture: "tutorial_intro_1",
        title: "A Night Without Witnesses",
        body: "The attackers looked like bandits. They robbed homes and drove off livestock, but set fires from several directions. They cut the well rope before anyone could raise an alarm.",
      },
      {
        texture: "tutorial_intro_2",
        title: "The East Gate",
        body: "Rutka opened the side gate. She does not know what the attackers want—only that they kill even those who surrender everything. Jasiek waits with a barge for the survivors.",
      },
      {
        texture: "tutorial_intro_3",
        title: "The Price of Passage",
        body: "The refugees paid with whatever they saved from the fire. They do not know Moryc has already sold their berths a second time—to a private landing that pays for undocumented labor.",
      },
    ];
    const slide = slides[clamp(index, 0, slides.length - 1)];
    const overlay = new PIXI.Container();
    overlay.eventMode = "static";
    overlay.hitArea = new PIXI.Rectangle(0, 0, this.state.viewport.width, this.state.viewport.height);
    overlay.on("pointerdown", (event) => event.stopPropagation?.());
    overlay.on("pointertap", (event) => event.stopPropagation?.());
    overlay.addChild(new PIXI.Graphics().rect(0, 0, this.state.viewport.width, this.state.viewport.height).fill({ color: 0x030403, alpha: 0.96 }));
    const advance = () => {
      if (index < slides.length - 1) {
        this.state.tutorialIntroSlide = index + 1;
      } else {
        localStorage.setItem(`vibe-tutorial-intro-seen:${this.state.player.id}`, "true");
        this.state.tutorialIntroSlide = null;
      }
      this.render();
    };
    if (this.isPhoneLandscape()) {
      const safe = this.safeRect();
      const x = safe.x + 8;
      const y = safe.y + 8;
      const frameWidth = safe.width - 16;
      const frameHeight = safe.height - 16;
      const imageWidth = Math.floor(frameWidth * 0.43);
      const contentX = x + imageWidth + 24;
      const contentWidth = frameWidth - imageWidth - 40;
      const frame = panel(frameWidth, frameHeight, { fill: 0x0b0b09, alpha: 1, stroke: 0xc59645, strokeAlpha: 0.95, radius: 8 });
      frame.position.set(x, y);
      overlay.addChild(frame);
      const texture = this.assetTextures.get(slide.texture);
      if (texture) {
        const image = new PIXI.Sprite(texture);
        const imageHeight = frameHeight - 24;
        const scale = Math.min((imageWidth - 20) / texture.width, imageHeight / texture.height);
        image.width = texture.width * scale;
        image.height = texture.height * scale;
        image.position.set(x + 10 + (imageWidth - 20 - image.width) / 2, y + 12 + (imageHeight - image.height) / 2);
        overlay.addChild(image);
      }
      const divider = new PIXI.Graphics().rect(x + imageWidth + 8, y + 12, 1, frameHeight - 24).fill({ color: 0xc59645, alpha: 0.45 });
      const counter = text(`${index + 1} / ${slides.length}`, { fontSize: 11, fill: 0xe0b95b, fontWeight: "900" });
      counter.position.set(contentX, y + 18);
      const title = text(slide.title, { fontSize: 20, fill: 0xffe7af, fontWeight: "900", wordWrap: true, wordWrapWidth: contentWidth, lineHeight: 22 });
      title.position.set(contentX, y + 40);
      const body = text(slide.body, { fontSize: 13, fill: 0xd8cfb6, wordWrap: true, wordWrapWidth: contentWidth, lineHeight: 17 });
      body.position.set(contentX, title.y + title.height + 10);
      const next = button(index === slides.length - 1 ? (polish ? "Zacznij ucieczkę" : "Begin the escape") : (polish ? "Dalej" : "Continue"), Math.min(180, contentWidth), 44, advance, {
        fill: 0x4a3212, stroke: 0xffd86b, fillText: 0xffefc2, fontSize: 13,
      });
      next.position.set(x + frameWidth - next.widthHint - 16, y + frameHeight - 58);
      overlay.addChild(divider, counter, title, body, next);
      this.layers.modal.addChild(overlay);
      return;
    }
    const frameWidth = Math.min(1040, this.state.viewport.width - 40);
    const frameHeight = Math.min(700, this.state.viewport.height - 40);
    const x = (this.state.viewport.width - frameWidth) / 2;
    const y = (this.state.viewport.height - frameHeight) / 2;
    const frame = panel(frameWidth, frameHeight, { fill: 0x0b0b09, alpha: 1, stroke: 0xc59645, strokeAlpha: 0.95, radius: 8 });
    frame.position.set(x, y);
    overlay.addChild(frame);
    const texture = this.assetTextures.get(slide.texture);
    const imageHeight = Math.max(180, frameHeight - 190);
    if (texture) {
      const image = new PIXI.Sprite(texture);
      const scale = Math.min((frameWidth - 24) / texture.width, imageHeight / texture.height);
      image.width = texture.width * scale;
      image.height = texture.height * scale;
      image.position.set(x + (frameWidth - image.width) / 2, y + 12 + (imageHeight - image.height) / 2);
      overlay.addChild(image);
    }
    const shade = new PIXI.Graphics().rect(x + 12, y + imageHeight - 66, frameWidth - 24, 76).fill({ color: 0x050504, alpha: 0.72 });
    overlay.addChild(shade);
    const counter = text(`${index + 1} / ${slides.length}`, { fontSize: 12, fill: 0xe0b95b, fontWeight: "900" });
    counter.position.set(x + 24, y + imageHeight + 18);
    const title = text(slide.title, { fontSize: 25, fill: 0xffe7af, fontWeight: "900", wordWrap: true, wordWrapWidth: frameWidth - 180 });
    title.position.set(x + 24, y + imageHeight + 38);
    const body = text(slide.body, { fontSize: 16, fill: 0xd8cfb6, wordWrap: true, wordWrapWidth: frameWidth - 48, lineHeight: 22 });
    body.position.set(x + 24, y + imageHeight + 76);
    const next = button(index === slides.length - 1 ? (polish ? "Zacznij ucieczkę" : "Begin the escape") : (polish ? "Dalej" : "Continue"), 180, 38, advance, { fill: 0x4a3212, stroke: 0xffd86b, fillText: 0xffefc2 });
    next.position.set(x + frameWidth - 204, y + frameHeight - 54);
    overlay.addChild(counter, title, body, next);
    this.layers.modal.addChild(overlay);
  }

  topNavButtonWidth(name, compact = this.state.viewport.width < 760) {
    const buttonWidths = {
      character: compact ? 78 : 88,
      inventory: compact ? 78 : 92,
      deck: compact ? 78 : 72,
      combat: compact ? 78 : 82,
      quests: compact ? 78 : 82,
      party: compact ? 78 : 78,
      "world-map": compact ? 78 : 100,
      social: compact ? 78 : 80,
      settings: compact ? 78 : 108,
    };
    return buttonWidths[name] || 88;
  }

  menuButton(name, width, height, onPress) {
    return this.iconMenuButton(windowTitles[name] || name, this.menuIconKey(name), width, height, onPress, this.state.activeWindow === name);
  }

  menuIconKey(name) {
    if (name === "party") return "menu_icon_social";
    return `menu_icon_${name.replaceAll("-", "_")}`;
  }

  iconMenuButton(label, iconKey, width, height, onPress, selected = false) {
    const container = new PIXI.Container();
    container.eventMode = "static";
    container.cursor = "pointer";
    container.hitArea = new PIXI.Rectangle(0, 0, width, height);
    const bg = panel(width, height, {
      fill: selected ? 0x3b2a17 : 0x2a2117,
      stroke: selected ? 0xd7aa5f : 0x967341,
      alpha: 0.95,
      radius: 5,
    });
    const texture = this.assetTextures.get(iconKey);
    if (texture) {
      const icon = new PIXI.Sprite(texture);
      icon.anchor.set(0.5);
      icon.position.set(18, height / 2);
      icon.width = 22;
      icon.height = 22;
      container.addChild(icon);
    }
    const labelText = text(label, {
      fontSize: 12,
      fill: selected ? 0xfff4cf : 0xf3dfad,
      fontWeight: "800",
    });
    labelText.anchor.set(0, 0.5);
    labelText.position.set(34, height / 2);
    container.addChild(bg, labelText);
    if (texture) {
      const icon = container.children.find((child) => child instanceof PIXI.Sprite);
      if (icon) {
        container.setChildIndex(icon, 1);
      }
    }
    const consume = (event) => event.stopPropagation?.();
    const applyHover = () => {
      bg.tint = 0xffc85f;
      labelText.style.fill = 0xfff4cf;
    };
    const clearHover = () => {
      bg.tint = 0xffffff;
      labelText.style.fill = selected ? 0xfff4cf : 0xf3dfad;
    };
    container.on("pointerdown", (event) => {
      consume(event);
      bg.tint = 0xffa83d;
    });
    container.on("pointerup", (event) => {
      consume(event);
      applyHover();
    });
    container.on("pointerover", (event) => {
      consume(event);
      applyHover();
    });
    container.on("pointerout", (event) => {
      consume(event);
      clearHover();
    });
    container.on("pointertap", (event) => {
      consume(event);
      onPress?.();
    });
    container.widthHint = width;
    container.heightHint = height;
    return container;
  }

  drawHud() {
    if (this.isPhoneLandscape()) {
      this.drawPhoneHud();
      return;
    }
    if (this.state.viewport.width < 760 && this.state.activeWindow) {
      return;
    }
    const hudWidth = this.state.player ? 238 : 260;
    const right = this.state.viewport.width - hudWidth - 14;
    const hudX = Math.max(14, right);
    const hudY = this.topNavHeight() + 16;
    const authHudHeight = this.state.authMode === "simple" ? 318 : 166;
    const hud = panel(hudWidth, this.state.player ? 210 : authHudHeight, { fill: 0x0a0b0a, alpha: 0.94, stroke: 0xb78f49 });
    hud.position.set(hudX, hudY);
    this.layers.ui.addChild(hud);
    if (!this.state.player) {
      if (this.state.authMode === "simple") {
        this.drawAuthHud(hud, hudX, hudY, hudWidth);
        return;
      }
      const title = text("Adventurer", { fontSize: 18, fill: 0xf2e6c7, fontWeight: "900" });
      title.position.set(hudX + 16, hudY + 16);
      const nameBox = button(this.state.pendingName || "Adventurer", 150, 34, () => {
        this.bridge.show({
          x: hudX + 16,
          y: hudY + 52,
          width: 150,
          value: this.state.pendingName,
          placeholder: "Character name",
          onSubmit: (value) => {
            this.state.pendingName = value.trim() || "Adventurer";
            this.renderUi();
          },
        });
      });
      nameBox.position.set(hudX + 16, hudY + 52);
      const enter = button("Enter World", 150, 38, () => this.enterWorld(this.state.pendingName), { fill: 0x3a2b19 });
      enter.position.set(hudX + 16, hudY + 96);
      this.layers.ui.addChild(title, nameBox, enter);
      return;
    }
    const hudElements = [];
    const profileSheet = this.assetTextures.get("profiles");
    const portraitTexture = this.textureRegion("profiles", 0, 0, profileSheet.width / 6, profileSheet.height / 3);
    const portrait = new PIXI.Sprite(portraitTexture);
    portrait.position.set(hudX + 12, hudY + 12);
    portrait.width = 46;
    portrait.height = 46;
    hudElements.push(portrait);
    const name = text(this.state.player.name, { fontSize: 13, fill: 0xf2e6c7, fontWeight: "900", wordWrap: true, wordWrapWidth: 158 });
    name.position.set(hudX + 68, hudY + 13);
    const classLabel = this.state.player.class_summary?.label || this.formatIdLabel(this.state.player.class_name);
    const klass = text(`${classLabel} | Lv ${this.state.player.level}`, { fontSize: 11, fill: 0xb6a680, wordWrap: true, wordWrapWidth: 158 });
    klass.position.set(hudX + 68, hudY + 35);
    hudElements.push(name, klass);
    const headerBottom = Math.max(
      hudY + 64,
      name.y + name.height,
      klass.y + klass.height,
    );
    const goldRow = this.hudGoldRow(this.state.player.gold || 0);
    goldRow.position.set(hudX + 16, headerBottom + 2);
    hudElements.push(goldRow);
    const bars = [
      resourceBar("HP", this.state.player.hp, this.state.player.max_hp, 0x62c461, 206),
      resourceBar("Mana", this.state.player.mana ?? 0, this.state.player.max_mana ?? 1, 0x6292ff, 206),
      resourceBar("Stamina", this.state.player.stamina ?? 0, this.state.player.max_stamina ?? 1, 0xe5b64f, 206),
    ];
    let cursorY = headerBottom + 34;
    bars.forEach((bar, index) => {
      bar.position.set(hudX + 16, cursorY + index * 25);
      hudElements.push(bar);
    });
    cursorY += 3 * 25 + 10;
    const rest = button(this.state.player.is_resting ? "Stop Resting" : "Rest", 128, 30, () => this.toggleResting(), { fill: this.state.player.is_resting ? 0x12301d : 0x2a1f12, fontSize: 11 });
    rest.position.set(hudX + 16, cursorY);
    const logout = button("Logout", 64, 30, () => this.logoutAccount(), { fill: 0x2a1717, stroke: 0xb85a43, fontSize: 10 });
    logout.position.set(hudX + 158, cursorY);
    hudElements.push(rest, logout);
    cursorY += 38;
    const resource = this.nearbyGatherable();
    if (resource) {
      const gathering = this.state.gathering?.resourceId === resource.id ? this.state.gathering : null;
      const iconTexture = this.assetTextures.get(resource.icon_kind || "herb") || this.assetTextures.get("herb");
      const icon = new PIXI.Sprite(iconTexture);
      icon.anchor.set(0.5);
      icon.position.set(hudX + 28, cursorY + 15);
      icon.width = 22;
      icon.height = 22;
      const gather = button(gathering ? "Stop" : "Gather", 118, 30, () => this.toggleGathering(resource), { fill: gathering ? 0x12301d : 0x1f2a19, stroke: 0x7bd889, fontSize: 11 });
      gather.position.set(hudX + 52, cursorY);
      const craft = button("Craft", 46, 30, () => {
        this.state.activeWindow = "inventory";
        this.renderUi();
      }, { fill: 0x2b2212, stroke: 0xd7aa5f, fontSize: 10 });
      craft.position.set(hudX + 176, cursorY);
      hudElements.push(icon, gather, craft);
      const owned = (this.state.player.inventory || []).find((item) => item.item_id === resource.item_id)?.quantity || 0;
      const label = text(`${resource.item_name || resource.name} | Owned ${owned}`, { fontSize: 10, fill: 0xc8b698, fontWeight: "900", wordWrap: true, wordWrapWidth: 206 });
      label.position.set(hudX + 16, cursorY + 34);
      hudElements.push(label);
      cursorY += 34 + label.height + 7;
      if (gathering) {
        const progress = this.gatheringProgress(gathering);
        const bar = new PIXI.Graphics();
        bar.roundRect(hudX + 16, cursorY, 206, 8, 4).fill({ color: 0x2d241e, alpha: 0.96 }).stroke({ color: 0x070605, width: 1 });
        bar.roundRect(hudX + 17, cursorY + 1, Math.max(2, 204 * progress), 6, 3).fill(0x7bd889);
        hudElements.push(bar);
        cursorY += 16;
      }
    }
    hud.clear();
    hud.roundRect(0, 0, hudWidth, Math.max(138, cursorY - hudY + 14), 6)
      .fill({ color: 0x0a0b0a, alpha: 0.94 })
      .stroke({ color: 0xb78f49, width: 1, alpha: 1 });
    this.layers.ui.addChild(...hudElements);
  }

  drawPhoneHud() {
    if (this.state.activeWindow || this.state.phoneChatOpen || this.state.tutorialIntroSlide !== null) return;
    const safe = this.safeRect();
    if (!this.state.player) {
      const width = Math.min(this.state.authMode === "simple" ? 420 : 360, safe.width - 24);
      const height = this.state.authMode === "simple" ? Math.min(318, safe.height - 64) : 132;
      const x = safe.x + (safe.width - width) / 2;
      const y = safe.y + 58 + Math.max(0, (safe.height - 58 - height) / 2);
      const hud = panel(width, height, { fill: 0x0a0b0a, alpha: 0.96, stroke: 0xb78f49 });
      hud.position.set(x, y);
      this.layers.ui.addChild(hud);
      if (this.state.authMode === "simple") {
        this.drawAuthHud(hud, x, y, width);
        return;
      }
      const title = text("Adventurer", { fontSize: 18, fill: 0xf2e6c7, fontWeight: "900" });
      title.position.set(x + 16, y + 12);
      const nameBox = button(this.state.pendingName || "Adventurer", width - 32, 44, () => this.bridge.show({
        x: x + 16, y: y + 46, width: width - 32, value: this.state.pendingName, placeholder: "Character name",
        autocomplete: "nickname",
        onSubmit: (value) => { this.state.pendingName = value.trim() || "Adventurer"; this.renderUi(); },
      }));
      nameBox.position.set(x + 16, y + 42);
      const enter = button("Enter World", 132, 44, () => this.enterWorld(this.state.pendingName), { fill: 0x3a2b19 });
      enter.position.set(x + width - 148, y + 88);
      this.layers.ui.addChild(title, nameBox, enter);
      return;
    }
    const width = Math.min(326, safe.width - 24);
    const height = 64;
    const x = safe.right - width - 8;
    const y = safe.y + 58;
    const hud = panel(width, height, { fill: 0x0a0b0a, alpha: 0.9, stroke: 0xb78f49 });
    hud.position.set(x, y);
    const name = text(`${this.state.player.name} · Lv ${this.state.player.level}`, { fontSize: 12, fill: 0xf2e6c7, fontWeight: "900" });
    name.position.set(x + 10, y + 8);
    const resources = text(`HP ${this.state.player.hp}/${this.state.player.max_hp}  MP ${this.state.player.mana ?? 0}/${this.state.player.max_mana ?? 0}  ST ${this.state.player.stamina ?? 0}/${this.state.player.max_stamina ?? 0}`, {
      fontSize: 10, fill: 0xc8b698, fontWeight: "800",
    });
    resources.position.set(x + 10, y + 29);
    const rest = button(this.state.player.is_resting ? "Stop" : "Rest", 58, 44, () => this.toggleResting(), { fontSize: 10 });
    rest.position.set(x + width - 126, y + 10);
    const logout = button("Out", 54, 44, () => this.logoutAccount(), { fill: 0x2a1717, stroke: 0xb85a43, fontSize: 10 });
    logout.position.set(x + width - 62, y + 10);
    this.layers.ui.addChild(hud, name, resources, rest, logout);
  }

  hudGoldRow(amount) {
    const container = new PIXI.Container();
    const width = 206;
    const bg = panel(width, 24, { fill: 0x17130f, alpha: 0.9, stroke: 0xe0b95b, strokeAlpha: 0.72, radius: 5 });
    container.addChild(bg);
    const texture = this.assetTextures.get("character_gold");
    if (texture) {
      const icon = new PIXI.Sprite(texture);
      icon.width = 18;
      icon.height = 18;
      icon.position.set(8, 3);
      container.addChild(icon);
    }
    const label = text(`${amount} gold`, { fontSize: 12, fill: 0xfff0c8, fontWeight: "900", wordWrap: true, wordWrapWidth: width - 42 });
    label.position.set(34, 6);
    container.addChild(label);
    return container;
  }

  drawAuthHud(hud, hudX, hudY, hudWidth) {
    const elements = [];
    const isRegister = this.state.authScreen === "register";
    const contentX = hudX + 16;
    const contentWidth = hudWidth - 32;
    const title = text("Account", { fontSize: 18, fill: 0xf2e6c7, fontWeight: "900" });
    title.position.set(contentX, hudY + 14);
    elements.push(title);

    const subtitle = text(
      isRegister ? "Create a new account and character." : "Log in to continue your character.",
      { fontSize: 11, fill: 0xcdbf9b, wordWrap: true, wordWrapWidth: contentWidth }
    );
    subtitle.position.set(contentX, hudY + 39);
    elements.push(subtitle);

    const loginTab = button("Login", contentWidth / 2 - 4, 32, () => {
      this.state.authScreen = "login";
      this.state.authMessage = "";
      this.renderUi();
    }, {
      fill: isRegister ? 0x201a14 : 0x4a321d,
      stroke: isRegister ? 0x5c4a31 : 0xd39a48,
      fillText: isRegister ? 0xb9a785 : 0xfff0bf,
      fontSize: 12,
    });
    loginTab.position.set(contentX, hudY + 68);
    elements.push(loginTab);

    const registerTab = button("Register", contentWidth / 2 - 4, 32, () => {
      this.state.authScreen = "register";
      this.state.authMessage = "";
      this.renderUi();
    }, {
      fill: isRegister ? 0x4a321d : 0x201a14,
      stroke: isRegister ? 0xd39a48 : 0x5c4a31,
      fillText: isRegister ? 0xfff0bf : 0xb9a785,
      fontSize: 12,
    });
    registerTab.position.set(contentX + contentWidth / 2 + 4, hudY + 68);
    elements.push(registerTab);

    const nickLabel = text("Nick", { fontSize: 11, fill: 0xb9a785, fontWeight: "900" });
    nickLabel.position.set(contentX, hudY + 112);
    elements.push(nickLabel);
    const nick = button(this.state.authNick || "Enter nick", contentWidth, 36, () => {
      this.bridge.show({
        x: contentX,
        y: hudY + 130,
        width: contentWidth,
        value: this.state.authNick,
        placeholder: "Nick",
        onSubmit: (value) => {
          this.state.authNick = value.trim();
          this.renderUi();
        },
      });
    }, { fill: 0x16130f, stroke: 0x6f5632, fillText: this.state.authNick ? 0xf2e6c7 : 0x9c8b70 });
    nick.position.set(contentX, hudY + 130);
    elements.push(nick);

    const passwordFieldLabel = text("Password", { fontSize: 11, fill: 0xb9a785, fontWeight: "900" });
    passwordFieldLabel.position.set(contentX, hudY + 174);
    elements.push(passwordFieldLabel);
    const passwordLabel = this.state.authPassword ? "Password entered" : "Password";
    const password = button(passwordLabel, contentWidth, 36, () => {
      this.bridge.show({
        x: contentX,
        y: hudY + 192,
        width: contentWidth,
        value: this.state.authPassword,
        placeholder: "Password",
        type: "password",
        onSubmit: (value) => {
          this.state.authPassword = value;
          this.renderUi();
        },
      });
    }, { fill: 0x16130f, stroke: 0x6f5632, fillText: this.state.authPassword ? 0xf2e6c7 : 0x9c8b70 });
    password.position.set(contentX, hudY + 192);
    elements.push(password);

    const helper = text(
      isRegister ? "Password must be at least 8 characters." : "Use the nick and password you registered with.",
      { fontSize: 10, fill: 0x9f9076, wordWrap: true, wordWrapWidth: contentWidth }
    );
    helper.position.set(contentX, hudY + 235);
    elements.push(helper);

    const primary = button(isRegister ? "Create account" : "Log in", contentWidth, 38, () => (
      isRegister ? this.registerAccount() : this.loginAccount()
    ), { fill: 0x4f341b, stroke: 0xe0a64e, fillText: 0xfff0bf, fontSize: 13 });
    primary.position.set(contentX, hudY + 260);
    elements.push(primary);

    if (this.state.authMessage) {
      const message = text(this.state.authMessage, { fontSize: 11, fill: 0xffb3a1, wordWrap: true, wordWrapWidth: contentWidth });
      message.position.set(contentX, hudY + 304);
      elements.push(message);
    }
    hud.clear();
    hud.roundRect(0, 0, hudWidth, this.state.authMessage ? 352 : 318, 6)
      .fill({ color: 0x0a0b0a, alpha: 0.84 })
      .stroke({ color: 0xb78f49, width: 1, alpha: 1 });
    this.layers.ui.addChild(...elements);
  }

  activeStoryQuests() {
    return (this.state.player?.quest_log || []).filter((quest) => quest.status !== "completed" && quest.status !== "failed");
  }

  activeQuestOrErrandTarget() {
    const active = this.activeStoryQuests();
    const pinned = active.find((quest) => quest.id === this.state.pinnedQuestId || quest.quest_id === this.state.pinnedQuestId);
    if (pinned) return { kind: "quest", entry: pinned };
    if (active.length) return { kind: "quest", entry: active[0] };
    const errand = (this.state.player?.active_errands || []).find((entry) => (entry.current || 0) < (entry.required || 1))
      || (this.state.player?.active_errands || [])[0];
    return errand ? { kind: "errand", entry: errand } : null;
  }

  activeRouteHintForTarget(target) {
    if (!target) return null;
    const markerMapId = target.entry?.marker_map_id;
    return (this.state.player?.route_hints || []).find((hint) => (
      hint.kind === target.kind && (!markerMapId || hint.target_map_id === markerMapId)
    )) || null;
  }

  drawQuestTracker() {
    const phone = this.isPhoneLandscape();
    if (!this.state.player || this.state.player.tutorial || !this.state.questTrackerVisible || ((phone || this.state.viewport.width < 760) && this.state.activeWindow)) {
      return;
    }
    const target = this.activeQuestOrErrandTarget();
    if (!target) return;
    const entry = target.entry;
    const safe = this.safeRect();
    const width = Math.min(292, Math.max(230, (phone ? safe.width : this.state.viewport.width) - 340));
    const x = phone ? safe.x + 8 : 14;
    const y = (phone ? safe.y : 0) + this.topNavHeight() + 8;
    const trackerHeight = 96;
    const tracker = panel(width, trackerHeight, { fill: 0x0c0f0d, alpha: 0.95, stroke: target.kind === "quest" ? 0xe0b95b : 0x8fb7ff, strokeAlpha: 0.86 });
    tracker.position.set(x, y);
    const eyebrow = text(target.kind === "quest" ? "Tracked Quest" : "Tracked Errand", { fontSize: 10, fill: target.kind === "quest" ? 0xffdf7a : 0x9fc2ff, fontWeight: "900" });
    eyebrow.position.set(x + 12, y + 9);
    const close = button("Hide", 44, 22, () => {
      this.state.questTrackerVisible = false;
      localStorage.setItem("vibe-quest-tracker-visible", "false");
      this.renderUi();
    }, { fontSize: 10, fill: 0x201b14 });
    close.position.set(x + width - 54, y + 7);
    const title = text(entry.title || this.formatIdLabel(entry.id || entry.quest_id), { fontSize: 13, fill: 0xf2e6c7, fontWeight: "900", wordWrap: true, wordWrapWidth: width - 24 });
    title.position.set(x + 12, y + 28);
    if (title.height > 20) {
      title.scale.set(Math.max(0.82, 20 / title.height));
    }
    const note = text(entry.next_step || entry.objective_text || "Destination", {
      fontSize: 10,
      fill: 0xc8b698,
      wordWrap: true,
      wordWrapWidth: width - 24,
      lineHeight: 13,
    });
    note.position.set(x + 12, y + 49);
    if (note.height > 16) {
      note.scale.set(Math.max(0.78, 16 / note.height));
    }
    const location = text(`Destination: ${entry.target_location || entry.marker_map_name || entry.marker_label || "unknown location"}`, {
      fontSize: 11,
      fill: 0xffdf7a,
      fontWeight: "900",
      wordWrap: true,
      wordWrapWidth: width - 24,
    });
    location.position.set(x + 12, y + 69);
    this.layers.ui.addChild(tracker, eyebrow, close, title, note);
    this.layers.ui.addChild(location);
  }

  drawRewardSummary() {
    const summary = this.state.rewardSummary;
    const phone = this.isPhoneLandscape();
    if (!summary || ((phone || this.state.viewport.width < 760) && this.state.activeWindow)) return;
    const age = performance.now() - summary.createdAt;
    if (age > summary.duration) {
      this.state.rewardSummary = null;
      return;
    }
    const safe = this.safeRect();
    const width = Math.min(260, safe.width - 16);
    const lines = summary.lines || [];
    const height = Math.min(142, 54 + lines.length * 18);
    const x = phone ? safe.right - width - 8 : Math.max(14, this.state.viewport.width - width - 14);
    const y = phone ? safe.bottom - height - 8 : this.state.player ? 338 : 282;
    const box = panel(width, height, { fill: 0x10130d, alpha: 0.94, stroke: 0xe0b95b, strokeAlpha: 0.82 });
    box.position.set(x, y);
    const title = text(summary.title || "Rewards", { fontSize: 14, fill: 0xffdf7a, fontWeight: "900", wordWrap: true, wordWrapWidth: width - 24 });
    title.position.set(x + 12, y + 10);
    const body = text(lines.join("\n"), { fontSize: 12, fill: 0xf2e6c7, wordWrap: true, wordWrapWidth: width - 24, lineHeight: 17 });
    body.position.set(x + 12, y + 34);
    this.layers.ui.addChild(box, title, body);
  }

  drawItemTakePopup() {
    const popup = this.state.itemTakePopup;
    if (!popup?.items?.length) return;
    const phone = this.isPhoneLandscape();
    const safe = this.safeRect();
    const alongsideStory = Boolean(
      this.state.player?.tutorial
      && popup.alongsideStory
      && !phone
      && (this.state.selectedNpc || this.state.selectedInteractable),
    );
    const width = Math.min(alongsideStory ? 310 : 430, (phone ? safe.width : this.state.viewport.width) - 32);
    const rowHeight = 54;
    const height = Math.min(phone ? safe.height - 16 : 470, 94 + popup.items.length * rowHeight);
    const x = alongsideStory
      ? Math.max(16, this.state.viewport.width - width - 16)
      : phone ? safe.x + (safe.width - width) / 2 : this.state.viewport.width / 2 - width / 2;
    const y = alongsideStory
      ? Math.max(this.topNavHeight() + 72, 96)
      : phone ? safe.y + (safe.height - height) / 2 : this.state.viewport.height / 2 - height / 2;
    const frame = panel(width, height, { fill: 0x10100d, alpha: 0.98, stroke: 0xe0b95b, strokeAlpha: 0.9, radius: 6 });
    frame.position.set(x, y);
    frame.eventMode = "static";
    const title = text(popup.title || "Items found", { fontSize: 20, fill: 0xffdf7a, fontWeight: "900" });
    title.position.set(x + 18, y + 14);
    if (!alongsideStory) {
      const blocker = new PIXI.Graphics().rect(0, 0, this.state.viewport.width, this.state.viewport.height).fill({ color: 0x000000, alpha: 0.58 });
      blocker.eventMode = "static";
      this.layers.modal.addChild(blocker);
    }
    this.layers.modal.addChild(frame, title);
    const visibleItemCount = Math.max(1, Math.floor((height - 94) / rowHeight));
    popup.items.slice(0, Math.min(6, visibleItemCount)).forEach((item, index) => {
      const rowY = y + 48 + index * rowHeight;
      const row = panel(width - 32, 46, { fill: 0x1b1711, alpha: 0.9, stroke: 0x6f5d3d, strokeAlpha: 0.7, radius: 4 });
      row.position.set(x + 16, rowY);
      const texture = this.itemIconTexture?.(item);
      if (texture) {
        const icon = new PIXI.Sprite(texture);
        icon.anchor.set(0.5);
        icon.position.set(x + 42, rowY + 23);
        icon.width = 34;
        icon.height = 34;
        this.layers.modal.addChild(icon);
      }
      const name = text(`${item.name || this.formatIdLabel(item.item_id)} x${item.quantity || 1}`, { fontSize: 14, fill: 0xf2e6c7, fontWeight: "900", wordWrap: true, wordWrapWidth: width - 104 });
      name.position.set(x + 68, rowY + 14);
      this.layers.modal.addChild(row, name);
    });
    const take = button("Take all", 112, 34, () => {
      const activePopup = this.state.itemTakePopup;
      const taken = activePopup?.items || [];
      this.state.itemTakePopup = null;
      taken.forEach((item, index) => this.state.rewardPopups.push({
        label: `+${item.quantity || 1} ${item.name || this.formatIdLabel(item.item_id)}`,
        color: 0x7bd889,
        createdAt: performance.now() + index * 120,
        duration: 1800,
        worldOffsetX: (index - taken.length / 2) * 0.22,
        worldOffsetY: -1.1 - index * 0.16,
      }));
      if (activePopup?.closeConversationOnTake) {
        this.clearConversation({ render: false });
      }
      this.renderUi();
    }, { fill: 0x1f2a19, stroke: 0x7bd889, fontSize: 12 });
    take.position.set(x + width - 128, y + height - 42);
    this.layers.modal.addChild(take);
  }

  queueItemTakePopup(items, title = "Items found", options = {}) {
    if (!items?.length) return;
    const inventory = new Map((this.state.player?.inventory || []).map((item) => [item.item_id, item]));
    const merged = new Map();
    items.forEach((item) => {
      if (!item?.item_id) return;
      const existing = merged.get(item.item_id) || { ...(inventory.get(item.item_id) || {}), item_id: item.item_id, quantity: 0 };
      existing.quantity += Number(item.quantity || 1);
      merged.set(item.item_id, existing);
    });
    if (merged.size) {
      this.state.itemTakePopup = { title, items: [...merged.values()], ...options };
    }
  }

  grantedItemsFromResult(result) {
    const direct = [...(result?.granted_items || [])];
    const nested = [];
    this.flattenQuestEvents(result?.quest_events || []).forEach((event) => {
      if (event.type === "item_granted" && event.item_id) nested.push({ item_id: event.item_id, quantity: event.quantity || 1 });
      (event.granted_items || []).forEach((item) => nested.push(item));
    });
    for (const event of [result?.errand_event, ...(result?.errand_events || [])]) {
      (event?.granted_items || []).forEach((item) => nested.push(item));
    }
    return [...direct, ...nested];
  }

  drawMapZoomControls() {
    const phone = this.isPhoneLandscape();
    if ((phone || this.state.viewport.width < 760) && this.state.activeWindow) {
      return;
    }
    if (!this.state.map) {
      return;
    }
    const width = 112;
    const height = 30;
    const x = Math.max(14, this.state.viewport.width - width - 14);
    const y = 14;
    const zone = this.currentMapZone();
    const zoneColor = this.zoneColorValue(zone);
    if (phone) {
      const safe = this.safeRect();
      const phoneWidth = 144;
      const phoneHeight = 44;
      const phoneX = safe.x + 8;
      const phoneY = safe.bottom - phoneHeight - 8;
      const phoneFrame = panel(phoneWidth, phoneHeight, { fill: 0x0a0b0a, alpha: 0.9, stroke: 0x6f5d3d, radius: 5 });
      phoneFrame.position.set(phoneX, phoneY);
      const minus = button("-", 44, 44, () => this.setMapZoom(this.state.mapZoom - 0.1), { fontSize: 20, fill: 0x201b14 });
      minus.position.set(phoneX, phoneY);
      const zoom = text(`${Math.round(this.state.mapZoom * 100)}%`, { fontSize: 11, fill: zoneColor, fontWeight: "900" });
      zoom.anchor.set(0.5);
      zoom.position.set(phoneX + phoneWidth / 2, phoneY + phoneHeight / 2);
      const plus = button("+", 44, 44, () => this.setMapZoom(this.state.mapZoom + 0.1), { fontSize: 20, fill: 0x201b14 });
      plus.position.set(phoneX + phoneWidth - 44, phoneY);
      this.layers.ui.addChild(phoneFrame, zoom, minus, plus);
      return;
    }
    const frame = panel(width, height, { fill: 0x0a0b0a, alpha: 0.82, stroke: 0x6f5d3d, radius: 5 });
    frame.position.set(x, y);
    const position = this.state.player?.position || { x: 0, y: 0 };
    const infoLabel = `${this.state.map.name || this.formatIdLabel(this.state.map.id)} ${this.formatCoordinate(position.x)}, ${this.formatCoordinate(position.y)}`;
    const infoWidth = Math.min(Math.max(160, this.state.viewport.width - width - 42), 360);
    const infoX = Math.max(14, x - infoWidth - 8);
    const infoFrame = panel(infoWidth, height, { fill: 0x0a0b0a, alpha: 0.82, stroke: 0x6f5d3d, radius: 5 });
    infoFrame.position.set(infoX, y);
    let zoneIcon;
    const iconTexture = this.assetTextures.get("zone_blob");
    if (iconTexture) {
      zoneIcon = new PIXI.Sprite(iconTexture);
      zoneIcon.tint = zoneColor;
      zoneIcon.anchor.set(0.5);
      zoneIcon.position.set(infoX + 16, y + height / 2);
      zoneIcon.width = 16;
      zoneIcon.height = 16;
    } else {
      zoneIcon = new PIXI.Graphics().circle(infoX + 16, y + height / 2, 6).fill(zoneColor);
    }
    const infoText = text(infoLabel, {
      fontSize: 11,
      fill: zoneColor,
      fontWeight: "900",
      wordWrap: true,
      wordWrapWidth: infoWidth - 40,
    });
    infoText.position.set(infoX + 32, y + 8);
    const minus = button("-", 24, 24, () => this.setMapZoom(this.state.mapZoom - 0.1), { fontSize: 15, fill: 0x201b14 });
    minus.position.set(x + 4, y + 3);
    const label = text(`${Math.round(this.state.mapZoom * 100)}%`, { fontSize: 11, fill: 0xd8cfb6, fontWeight: "900" });
    label.anchor.set(0.5);
    label.position.set(x + width / 2, y + height / 2);
    const plus = button("+", 24, 24, () => this.setMapZoom(this.state.mapZoom + 0.1), { fontSize: 15, fill: 0x201b14 });
    plus.position.set(x + width - 28, y + 3);
    this.layers.ui.addChild(infoFrame, zoneIcon, infoText, frame, label, minus, plus);
  }

  currentMapZone() {
    return this.state.map?.current_zone || this.state.player?.current_zone || {
      type: "green",
      name: "Safe Zone",
      color: "#56d879",
      rest_allowed: true,
      pvp_allowed: false,
    };
  }

  zoneColorValue(zone) {
    const fallback = {
      green: 0x56d879,
      yellow: 0xe0c85a,
      red: 0xe05a4f,
      black: 0x9b8cff,
    }[zone?.type || "green"] || 0x56d879;
    const configured = String(zone?.color || "").replace("#", "");
    if (/^[0-9a-fA-F]{6}$/.test(configured)) {
      return parseInt(configured, 16);
    }
    return fallback;
  }

  formatCoordinate(value) {
    const numeric = Number(value || 0);
    return numeric.toFixed(Math.abs(numeric) >= 10 ? 1 : 2);
  }

  chatLayout() {
    const phone = this.isPhoneLandscape();
    const safe = this.safeRect();
    const width = phone ? Math.min(560, safe.width - 16) : Math.min(520, this.state.viewport.width - 28);
    const isPrivate = this.effectiveChatChannel() === "private";
    const height = phone
      ? Math.max(210, safe.height - this.topNavHeight() - 12)
      : Math.min(isPrivate ? 260 : 216, Math.max(isPrivate ? 202 : 158, this.state.viewport.height * 0.32));
    const x = phone ? safe.x + 8 : 14;
    const y = phone ? safe.bottom - height - 6 : this.state.viewport.height - height - 14;
    const inputHeight = phone ? 44 : 30;
    const inputY = y + height - inputHeight - 12;
    const listY = y + (isPrivate ? 82 : 34);
    const rowHeight = 18;
    const listHeight = Math.max(42, inputY - listY - 12);
    const visibleRows = Math.max(1, Math.floor(listHeight / rowHeight));
    return { x, y, width, height, inputHeight, inputY, listY, rowHeight, listHeight, visibleRows };
  }

  maxChatScrollOffset() {
    const channel = this.effectiveChatChannel();
    const { visibleRows } = this.chatLayout();
    return Math.max(0, this.chatHistory(channel).length - visibleRows);
  }

  clampChatScroll() {
    const channel = this.effectiveChatChannel();
    this.state.chatScrollOffsets[channel] = clamp(Math.round(this.state.chatScrollOffsets[channel] || 0), 0, this.maxChatScrollOffset());
  }

  drawChat() {
    if (this.isPhoneLandscape() && !this.state.player) return;
    if (this.state.player?.tutorial || this.state.activeWindow === "combat" || ((this.isPhoneLandscape() || this.state.viewport.width < 760) && this.state.activeWindow)) {
      return;
    }
    if (this.isPhoneLandscape() && this.state.player && !this.state.phoneChatOpen) {
      const safe = this.safeRect();
      const unread = Object.values(this.state.chatUnread || {}).reduce((sum, value) => sum + Number(value || 0), 0);
      const launcher = button(unread ? `Chat ${unread}` : "Chat", 84, 44, () => { this.state.phoneChatOpen = true; this.renderUi(); }, {
        fill: unread ? 0x3b2a17 : 0x171817,
        stroke: unread ? 0xffdf7a : 0x6f5d3d,
      });
      launcher.position.set(safe.right - 92, safe.bottom - 52);
      this.layers.ui.addChild(launcher);
      return;
    }
    this.clampChatScroll();
    const { x, y, width, height, inputY, listY, rowHeight, visibleRows } = this.chatLayout();
    const chat = panel(width, height, { fill: 0x070807, alpha: 0.78, stroke: 0x4f4532 });
    chat.position.set(x, y);
    this.layers.ui.addChild(chat);
    const title = text("Log & Chat", { fontSize: 13, fill: 0xd8cfb6, fontWeight: "900" });
    title.position.set(x + 12, y + 10);
    this.layers.ui.addChild(title);
    if (this.isPhoneLandscape()) {
      const close = button("×", 44, 44, () => { this.state.phoneChatOpen = false; this.renderUi(); }, { fontSize: 22 });
      close.position.set(x + width - 50, y + 4);
      this.layers.ui.addChild(close);
    }
    const activeChatChannel = this.effectiveChatChannel();
    this.drawChatModeButtons(this.isPhoneLandscape() ? x + 96 : x + width - 274, y + 7, activeChatChannel);
    if (activeChatChannel === "private") {
      this.drawPrivateChatSelector(x + 12, y + 34, width - 24);
    }
    const history = this.chatHistory(activeChatChannel);
    const offset = this.state.chatScrollOffsets[activeChatChannel] || 0;
    const end = history.length - offset;
    const start = Math.max(0, end - visibleRows);
    if (activeChatChannel === "private" && !this.state.chatTargetPlayerId) {
      const empty = text("Choose a recent player or friend to view a private conversation.", { fontSize: 12, fill: 0x9f9276, wordWrap: true, wordWrapWidth: width - 26 });
      empty.position.set(x + 12, listY);
      this.layers.ui.addChild(empty);
    }
    history.slice(start, end).forEach((entry, index) => {
      const line = this.chatLine(entry);
      const row = text(line, { fontSize: 12, fill: 0xbfb495, wordWrap: true, wordWrapWidth: width - 26 });
      row.position.set(x + 12, listY + index * rowHeight);
      this.layers.ui.addChild(row);
    });
    if (offset > 0) {
      const badge = text(`${offset} newer`, { fontSize: 11, fill: 0xe7be69, fontWeight: "900" });
      badge.anchor.set(1, 0);
      badge.position.set(x + width - 14, y + 10);
      this.layers.ui.addChild(badge);
    }
    const input = button("Type message...", width - 24, 30, () => {
      this.openChatInput();
    }, { fill: 0x171817, stroke: 0x6f5d3d, fillText: 0xbeb395 });
    input.position.set(x + 12, inputY);
    this.layers.ui.addChild(input);
  }

  openChatInput(targetPlayer = null) {
    if (targetPlayer) {
      this.selectPrivateChatTarget(targetPlayer);
    }
    const target = this.chatTargetPlayer();
    const layout = this.chatLayout();
    const width = Math.min(520, this.state.viewport.width - 28);
    const x = this.state.viewport.width < 760 && this.state.activeWindow
      ? 14
      : layout.x + 12;
    const y = this.state.viewport.width < 760 && this.state.activeWindow
      ? this.state.viewport.height - 46
      : layout.inputY;
    this.bridge.show({
      x,
      y,
      width: this.state.viewport.width < 760 && this.state.activeWindow ? width : layout.width - 24,
      value: target ? `/w ${target.name} ` : "",
      placeholder: target ? `Whisper to ${target.name}` : this.chatPlaceholder(),
      onSubmit: (value) => this.sendChat(value),
    });
  }

  setChatChannel(channel) {
    const next = this.availableChatChannel(channel);
    this.state.chatChannel = next;
    this.state.chatUnread[next] = 0;
    if (next !== "private") {
      this.state.privateFriendDropdownOpen = false;
    }
    localStorage.setItem("vibe-chat-channel", next);
    this.renderUi();
  }

  effectiveChatChannel() {
    return this.availableChatChannel(this.state.chatChannel);
  }

  availableChatChannel(channel) {
    if (["map", "party", "global", "private"].includes(channel)) return channel;
    return "map";
  }

  drawChatModeButtons(x, y, activeChannel) {
    const specs = [
      ["map", "Map", 54, 0xd7aa5f],
      ["party", "Party", 58, 0x8fb7ff],
      ["global", "Global", 66, 0x7bd889],
      ["private", "Private", 74, 0xcdb8ff],
    ];
    let cursorX = x;
    specs.forEach(([channel, label, width, stroke]) => {
      const disabled = false;
      const unread = this.state.chatUnread[channel] || 0;
      const buttonLabel = unread > 0 ? `${label} ${Math.min(9, unread)}` : label;
      const mode = button(buttonLabel, width, 22, disabled ? null : () => this.setChatChannel(channel), {
        fill: activeChannel === channel ? 0x1c2d3e : 0x171817,
        stroke: activeChannel === channel ? stroke : 0x6f5d3d,
        fontSize: 10,
        alpha: disabled ? 0.45 : 1,
      });
      mode.position.set(cursorX, y);
      this.layers.ui.addChild(mode);
      cursorX += width + 4;
    });
  }

  chatHistory(channel = this.effectiveChatChannel()) {
    const history = this.state.chatHistories[channel] || [];
    if (channel !== "private") {
      return history;
    }
    if (!this.state.chatTargetPlayerId) {
      return [];
    }
    return history.filter((entry) => this.privateChatPeerId(entry) === this.state.chatTargetPlayerId);
  }

  chatPlaceholder() {
    const channel = this.effectiveChatChannel();
    if (channel === "party") return "Message your party";
    if (channel === "global") return "Message global chat";
    if (channel === "private") return this.chatTargetPlayer() ? `Whisper to ${this.chatTargetPlayer().name}` : "Use /w name message or pick a player";
    return "Say something nearby";
  }

  chatLine(entry) {
    if (entry.system) {
      return entry.text;
    }
    if (entry.channel === "private") {
      const label = entry.direction === "to" ? `to ${entry.target_player_name || "Player"}` : `from ${entry.player_name || "Player"}`;
      return `[Private ${label}] ${entry.text}`;
    }
    return `[${this.formatIdLabel(entry.channel)}] ${entry.player_name || "System"}: ${entry.text}`;
  }

  drawPrivateChatSelector(x, y, width) {
    const target = this.chatTargetPlayer();
    const targetName = target?.name || "No conversation selected";
    const label = text("Private", { fontSize: 10, fill: 0xcdb8ff, fontWeight: "900" });
    label.position.set(x, y);
    this.layers.ui.addChild(label);
    const current = button(targetName, Math.min(164, Math.floor(width * 0.36)), 26, () => {
      this.state.privateFriendDropdownOpen = !this.state.privateFriendDropdownOpen;
      this.renderUi();
    }, { fill: 0x171817, stroke: target ? 0xcdb8ff : 0x6f5d3d, fontSize: 10 });
    current.position.set(x, y + 16);
    this.layers.ui.addChild(current);
    const recent = this.state.privateChatRecentPlayers || [];
    let cursorX = x + current.widthHint + 8;
    recent.slice(0, 3).forEach((player) => {
      const selected = player.id === this.state.chatTargetPlayerId;
      const chipWidth = Math.min(92, Math.max(58, 28 + String(player.name || "Player").length * 6));
      if (cursorX + chipWidth > x + width) return;
      const chip = button(player.name || "Player", chipWidth, 26, () => this.selectPrivateChatTarget(player), {
        fill: selected ? 0x1d2536 : 0x171817,
        stroke: selected ? 0x6fa8ff : 0x5c6f7c,
        fontSize: 10,
      });
      chip.position.set(cursorX, y + 16);
      this.layers.ui.addChild(chip);
      cursorX += chipWidth + 6;
    });
    if (this.state.privateFriendDropdownOpen) {
      const friends = (this.state.player?.friends || []).filter((friend) => friend.id);
      const dropdownWidth = Math.min(220, width);
      const dropdownHeight = Math.min(130, Math.max(42, friends.length * 28 + 10));
      const frame = panel(dropdownWidth, dropdownHeight, { fill: 0x0c0d0d, alpha: 0.97, stroke: 0xcdb8ff, strokeAlpha: 0.74 });
      frame.position.set(x, y + 46);
      this.layers.ui.addChild(frame);
      if (!friends.length) {
        const empty = text("No friends yet.", { fontSize: 11, fill: 0x9f9276, fontWeight: "800" });
        empty.position.set(x + 10, y + 58);
        this.layers.ui.addChild(empty);
      }
      friends.slice(0, 4).forEach((friend, index) => {
        const row = button(`${friend.name || "Friend"}${friend.online ? "" : " offline"}`, dropdownWidth - 12, 24, () => this.selectPrivateChatTarget(friend), {
          fill: friend.id === this.state.chatTargetPlayerId ? 0x1d2536 : 0x171817,
          stroke: friend.online ? 0x7bd889 : 0x6f5d3d,
          fontSize: 10,
        });
        row.position.set(x + 6, y + 52 + index * 28);
        this.layers.ui.addChild(row);
      });
    }
  }

  addSystemChatLine(line) {
    const channel = this.effectiveChatChannel();
    const wasAtBottom = (this.state.chatScrollOffsets[channel] || 0) <= 0;
    const entry = {
      id: `${Date.now()}:${Math.random()}`,
      channel,
      text: line,
      system: true,
      created_at: Date.now(),
    };
    this.state.chatHistories[channel] = [...(this.state.chatHistories[channel] || []), entry].slice(-100);
    this.state.chatScrollOffsets[channel] = wasAtBottom ? 0 : Math.min((this.state.chatScrollOffsets[channel] || 0) + 1, this.maxChatScrollOffset());
  }

  addChatMessage(payload) {
    const rawChannel = payload.channel === "whisper" ? "private" : String(payload.channel || "map");
    const channel = ["map", "party", "global", "private"].includes(rawChannel) ? rawChannel : "map";
    const entry = {
      id: `${Date.now()}:${Math.random()}`,
      channel,
      text: String(payload.text || ""),
      player_id: payload.player_id || null,
      player_name: payload.player_name || "",
      direction: payload.direction || null,
      target_player_id: payload.target_player_id || null,
      target_player_name: payload.target_player_name || "",
      created_at: Date.now(),
    };
    if (channel === "private") {
      const peer = this.privateChatPeerFromEntry(entry);
      if (peer?.id) {
        this.rememberPrivateChatPlayer(peer);
        if (!this.state.chatTargetPlayerId && this.effectiveChatChannel() === "private") {
          this.state.chatTargetPlayerId = peer.id;
        }
      }
    }
    const wasAtBottom = (this.state.chatScrollOffsets[channel] || 0) <= 0;
    this.state.chatHistories[channel] = [...(this.state.chatHistories[channel] || []), entry].slice(-100);
    if (this.effectiveChatChannel() === channel) {
      this.state.chatUnread[channel] = 0;
      this.state.chatScrollOffsets[channel] = wasAtBottom ? 0 : Math.min((this.state.chatScrollOffsets[channel] || 0) + 1, this.maxChatScrollOffset());
    } else {
      this.state.chatUnread[channel] = Math.min(99, (this.state.chatUnread[channel] || 0) + 1);
    }
    this.renderUi();
  }

  privateChatPeerId(entry) {
    if (entry.direction === "to") {
      return entry.target_player_id || null;
    }
    return entry.player_id || null;
  }

  privateChatPeerFromEntry(entry) {
    const id = this.privateChatPeerId(entry);
    if (!id || id === this.state.player?.id) {
      return null;
    }
    return {
      id,
      name: entry.direction === "to" ? (entry.target_player_name || "Player") : (entry.player_name || "Player"),
    };
  }

  selectPrivateChatTarget(player) {
    if (!player?.id) return;
    this.state.chatTargetPlayerId = player.id;
    this.state.privateFriendDropdownOpen = false;
    this.rememberPrivateChatPlayer(player);
    this.setChatChannel("private");
  }

  rememberPrivateChatPlayer(player) {
    if (!player?.id || player.id === this.state.player?.id) return;
    const entry = { id: player.id, name: player.name || this.formatIdLabel(player.id) };
    const recent = [entry, ...(this.state.privateChatRecentPlayers || []).filter((candidate) => candidate.id !== entry.id)].slice(0, 8);
    this.state.privateChatRecentPlayers = recent;
    localStorage.setItem("vibe-private-chat-recent", JSON.stringify(recent));
  }

  loadPrivateChatRecentPlayers() {
    try {
      const parsed = JSON.parse(localStorage.getItem("vibe-private-chat-recent") || "[]");
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter((entry) => entry && typeof entry.id === "string")
        .map((entry) => ({ id: entry.id, name: String(entry.name || entry.id) }))
        .slice(0, 8);
    } catch {
      return [];
    }
  }

  addCombatChatPopup(payload) {
    const combatId = String(payload.combat_id || "");
    const playerId = String(payload.player_id || "");
    if (!combatId || !playerId || combatId !== this.state.combat?.id) {
      return;
    }
    const now = performance.now();
    const popup = {
      id: `${combatId}:${playerId}:${now}`,
      combat_id: combatId,
      player_id: playerId,
      player_name: String(payload.player_name || ""),
      text: String(payload.text || "").slice(0, 160),
      created_at: now,
      expires_at: now + 3000,
    };
    this.state.combatChatPopups = [
      ...this.state.combatChatPopups.filter((entry) => entry.combat_id === combatId && entry.player_id !== playerId && entry.expires_at > now),
      popup,
    ].slice(-5);
    this.renderUi();
  }

  updateCombatChatPopups() {
    if (!this.state.combatChatPopups.length) {
      return;
    }
    const now = performance.now();
    const next = this.state.combatChatPopups.filter((popup) => popup.expires_at > now && popup.combat_id === this.state.combat?.id);
    if (next.length !== this.state.combatChatPopups.length) {
      this.state.combatChatPopups = next;
      this.renderUi();
      return;
    }
    if (this.state.activeWindow === "combat") {
      this.renderUi();
    }
  }

  drawSelectionPanels() {
    if (this.state.selectedPlayer) {
      this.drawSelectedPlayerPopup();
    }
    if (this.state.partyInvite) {
      this.drawPartyInvitePopup();
    }
    if (this.state.friendRequest) {
      this.drawFriendRequestPopup();
    }
    if (this.state.selectedEnemyGroup && !this.state.player?.combat_id) {
      const enemy = this.state.selectedEnemyGroup;
      const modal = panel(330, 108, { fill: 0x130d0c, alpha: 0.94, stroke: 0xb85a43 });
      modal.position.set(this.state.viewport.width / 2 - 165, this.state.viewport.height - 142);
      const name = text(enemy.name, { fontSize: 17, fill: 0xf0d6c3, fontWeight: "900" });
      name.position.set(modal.x + 16, modal.y + 14);
      const detail = text(`${enemy.enemy_template_ids.length} enemies | Lv ${enemy.level || 1}`, { fontSize: 13, fill: 0xc8b698 });
      detail.position.set(modal.x + 16, modal.y + 40);
      const fight = button("Fight", 108, 34, () => this.startEnemyCombat(enemy), { fill: 0x4a1d19, stroke: 0xc46d56 });
      fight.position.set(modal.x + 16, modal.y + 64);
      const cancel = button("Cancel", 108, 34, () => { this.state.selectedEnemyGroup = null; this.renderUi(); });
      cancel.position.set(modal.x + 136, modal.y + 64);
      this.layers.modal.addChild(modal, name, detail, fight, cancel);
    }
    if (this.state.selectedCardFusion) {
      this.drawCardFusionPanel();
    } else if (this.state.selectedShop) {
      this.drawShopPanel();
    } else if (this.state.selectedInteractable) {
      this.drawInteractablePopup();
    } else if (this.state.selectedNpc) {
      this.drawDialoguePanel();
    }
  }

  drawSelectedPlayerPopup() {
    const player = this.state.selectedPlayer;
    if (!player || player.id === this.state.player?.id || !this.state.map) {
      return;
    }
    const screen = this.worldToScreen(this.getPlayerRenderPosition(player));
    const width = 224;
    const height = 44;
    const x = clamp(screen.x - width / 2, 12, this.state.viewport.width - width - 12);
    const y = clamp(screen.y - 112, this.topNavHeight() + 10, this.state.viewport.height - height - 12);
    const frame = panel(width, height, { fill: 0x0e0d0b, alpha: 0.94, stroke: 0xa9854b, radius: 5 });
    frame.position.set(x, y);
    const inspect = button("i", 42, 32, () => this.inspectPlayer(player), { fill: 0x222026, stroke: 0xcdb8ff, fontSize: 18 });
    inspect.position.set(x + 10, y + 6);
    const whisper = button("💬", 42, 32, () => {
      this.openChatInput(player);
      this.state.selectedPlayer = null;
      this.renderUi();
    }, { fill: 0x1d2536, stroke: 0x6fa8ff, fontSize: 18 });
    whisper.position.set(x + 64, y + 6);
    const friend = button("☆", 42, 32, () => this.createFriendRequest(player), { fill: 0x2b2212, stroke: 0xd7aa5f, fontSize: 18 });
    friend.position.set(x + 118, y + 6);
    const invite = button("＋", 42, 32, () => this.createPartyInvite(player), { fill: 0x1f2a19, stroke: 0x7bd889, fontSize: 18 });
    invite.position.set(x + 172, y + 6);
    this.layers.modal.addChild(frame, inspect, whisper, friend, invite);
  }

  drawPartyInvitePopup() {
    const invite = this.state.partyInvite;
    if (!invite || invite.invitee_id !== this.state.player?.id) {
      return;
    }
    const width = Math.min(320, this.state.viewport.width - 32);
    const height = 106;
    const x = this.state.viewport.width / 2 - width / 2;
    const y = this.topNavHeight() + 22;
    const frame = panel(width, height, { fill: 0x0e0d0b, alpha: 0.96, stroke: 0x7bd889, radius: 6 });
    frame.position.set(x, y);
    const title = text("Party Invite", { fontSize: 16, fill: 0xf2e6c7, fontWeight: "900" });
    title.position.set(x + 16, y + 12);
    const detail = text(`${invite.inviter_name} wants to form a party.`, { fontSize: 13, fill: 0xc8b698, wordWrap: true, wordWrapWidth: width - 32 });
    detail.position.set(x + 16, y + 38);
    const accept = button("Accept", 94, 30, () => this.answerPartyInvite(invite, true), { fill: 0x1f2a19, stroke: 0x7bd889 });
    accept.position.set(x + 16, y + 68);
    const decline = button("Decline", 94, 30, () => this.answerPartyInvite(invite, false), { fill: 0x2a1717, stroke: 0xb85a43 });
    decline.position.set(x + 118, y + 68);
    this.layers.modal.addChild(frame, title, detail, accept, decline);
  }

  drawFriendRequestPopup() {
    const request = this.state.friendRequest;
    if (!request || request.target_id !== this.state.player?.id) {
      return;
    }
    const width = Math.min(340, this.state.viewport.width - 32);
    const height = 106;
    const x = this.state.viewport.width / 2 - width / 2;
    const y = this.topNavHeight() + 140;
    const frame = panel(width, height, { fill: 0x0e0d0b, alpha: 0.96, stroke: 0xd7aa5f, radius: 6 });
    frame.position.set(x, y);
    const title = text("Friend Request", { fontSize: 16, fill: 0xf2e6c7, fontWeight: "900" });
    title.position.set(x + 16, y + 12);
    const detail = text(`${request.requester_name} wants to add you as a friend.`, { fontSize: 13, fill: 0xc8b698, wordWrap: true, wordWrapWidth: width - 32 });
    detail.position.set(x + 16, y + 38);
    const accept = button("Accept", 94, 30, () => this.answerFriendRequest(request, true), { fill: 0x1f2a19, stroke: 0x7bd889 });
    accept.position.set(x + 16, y + 68);
    const decline = button("Decline", 94, 30, () => this.answerFriendRequest(request, false), { fill: 0x2a1717, stroke: 0xb85a43 });
    decline.position.set(x + 118, y + 68);
    this.layers.modal.addChild(frame, title, detail, accept, decline);
  }

  drawDialoguePanel() {
    const target = this.state.selectedInteractable || this.state.selectedNpc;
    const phone = this.isPhoneLandscape();
    const safe = this.safeRect();
    const conversationOptions = this.state.selectedNpc ? this.visibleConversationOptions(target) : [];
    const orderedConversationOptions = [...conversationOptions].sort((left, right) => {
      const priority = { quest: 0, errand: 1, shop: 2, talk: 3, end: 4 };
      return (priority[left.effect] ?? 3) - (priority[right.effect] ?? 3);
    });
    const hasEndOption = conversationOptions.some((option) => option.effect === "end");
    const miniInteractable = this.state.selectedInteractable?.label_visible === false;
    const storyLootAlongside = Boolean(
      this.state.player?.tutorial
      && this.state.itemTakePopup?.alongsideStory
      && !phone
      && this.state.viewport.width >= 760,
    );
    const sacrificeStatus = this.state.selectedInteractable?.kind === "sacrifice_altar" ? this.state.player?.temple_sacrifice || {} : null;
    const options = this.state.selectedInteractable
      ? this.state.selectedInteractable.kind === "sacrifice_altar"
        ? [
            {
              label: sacrificeStatus?.available ? "Sacrifice level" : `Available in ${this.formatClockDuration(sacrificeStatus?.seconds_until_available || 0)}`,
              action: sacrificeStatus?.available ? () => this.confirmSacrificeLevel() : null,
              variant: "quest",
              disabled: !sacrificeStatus?.available,
            },
            { label: "Inspect", action: () => this.interact(target) },
            { label: "Close", action: () => this.clearConversation() },
          ]
        : [{ label: miniInteractable ? "Inspect" : "Open", action: () => this.interact(target) }, { label: "Close", action: () => this.clearConversation() }]
      : [
          ...orderedConversationOptions.map((option) => ({
            label: option.text,
            action: option.available === false ? null : () => this.chooseNpcOption(target, option),
            variant: option.effect === "quest" ? "quest" : option.effect === "errand" ? "errand" : "normal",
            effect: option.effect || "talk",
            disabled: option.available === false,
            unavailableReason: option.unavailable_reason || "",
          })),
          ...(hasEndOption || this.state.player?.tutorial ? [] : [{ label: "Close", action: () => this.clearConversation() }]),
        ];
    const storyColumnWidth = storyLootAlongside
      ? this.state.viewport.width - Math.min(310, this.state.viewport.width - 32) - 48
      : this.state.viewport.width - 32;
    const width = phone
      ? safe.width - 16
      : miniInteractable
        ? Math.max(300, Math.min(440, storyColumnWidth))
        : Math.max(360, Math.min(storyLootAlongside ? 620 : 720, storyColumnWidth));
    const optionHeight = phone ? 44 : miniInteractable ? 32 : 40;
    const optionGap = 8;
    const desiredHeight = (miniInteractable ? 118 : 150) + options.length * (optionHeight + optionGap) + 14;
    const height = phone
      ? safe.height - this.topNavHeight() - 14
      : Math.min(this.state.viewport.height - 32, Math.max(miniInteractable ? 190 : 280, desiredHeight));
    const x = phone
      ? safe.x + 8
      : storyLootAlongside
      ? Math.max(16, (storyColumnWidth - width) / 2 + 16)
      : Math.max(16, Math.min(this.state.viewport.width - width - 16, this.state.viewport.width / 2 - width / 2));
    const y = phone
      ? safe.y + this.topNavHeight() + 6
      : Math.max(16, Math.min(this.state.viewport.height - height - 16, this.state.viewport.height - height - 24));
    const modal = panel(width, height, { fill: 0x0e0d0b, alpha: 0.96, stroke: 0xa9854b });
    modal.position.set(x, y);
    const icon = new PIXI.Sprite(this.assetTextures.get(this.state.selectedInteractable ? this.interactableIconId(target) : this.npcIconId(target)));
    icon.anchor.set(0.5);
    icon.position.set(x + 38, y + 38);
    icon.width = miniInteractable ? 34 : 42;
    icon.height = miniInteractable ? 34 : 42;
    const name = text(target.name, { fontSize: miniInteractable ? 18 : 22, fill: 0xf2e6c7, fontWeight: "900" });
    name.position.set(x + 72, y + (miniInteractable ? 18 : 20));
    let dialogue = this.state.conversationLine || target.description || target.dialogue?.[0] || target.kind || "";
    if (sacrificeStatus) {
      const playerLevel = Number(this.state.player?.level || 1);
      const availability = sacrificeStatus.available
        ? "Available today."
        : `Available in ${this.formatClockDuration(sacrificeStatus.seconds_until_available || 0)}.`;
      dialogue = `${dialogue} ${availability} Sacrifice level ${playerLevel} -> ${Math.max(1, playerLevel - 1)} for +1 permanent free stat point.`;
    }
    const line = text(dialogue, { fontSize: miniInteractable ? 14 : 17, fill: 0xcfc2a4, wordWrap: true, wordWrapWidth: width - 92, lineHeight: miniInteractable ? 18 : 22 });
    line.position.set(x + 72, y + (miniInteractable ? 48 : 54));
    const maxLineHeight = phone ? 50 : miniInteractable ? 54 : 82;
    if (line.height > maxLineHeight) {
      line.scale.set(Math.max(0.86, maxLineHeight / line.height));
    }
    this.layers.modal.addChild(modal, icon, name, line);
    const optionsTop = phone ? 122 : miniInteractable ? 112 : 140;
    const pagingHeight = phone && options.length > 1 ? 48 : 0;
    const maxOptions = Math.max(1, Math.floor((height - optionsTop - pagingHeight - 10) / (optionHeight + optionGap)));
    const pageCount = phone ? Math.max(1, Math.ceil(options.length / maxOptions)) : 1;
    const page = clamp(Number(this.state.conversationScrollOffset || 0), 0, pageCount - 1);
    this.state.conversationScrollOffset = page;
    options.slice(page * maxOptions, (page + 1) * maxOptions).forEach((option, index) => {
      const optionIndex = page * maxOptions + index;
      const optionButton = button(`${optionIndex + 1}. ${option.label}${option.disabled ? " (locked)" : ""}`, width - 32, optionHeight, option.action, {
        fill: option.variant === "quest" ? 0x3d2b0f : option.variant === "errand" ? 0x172638 : 0x211a12,
        stroke: option.variant === "quest" ? 0xe0b95b : option.variant === "errand" ? 0x8fb7ff : 0x967341,
        fillText: option.disabled ? 0x8f8067 : option.variant === "quest" ? 0xffe1a1 : option.variant === "errand" ? 0xd8ebff : 0xf3dfad,
        fontSize: 14,
      });
      optionButton.position.set(x + 16, y + optionsTop + index * (optionHeight + optionGap));
      this.layers.modal.addChild(optionButton);
      if (option.effect && !miniInteractable) {
        const chipColors = { quest: 0xe0b95b, errand: 0x8fb7ff, shop: 0x7bd889, end: 0x9f9276, talk: 0xc8b698 };
        const chip = this.summaryPill(this.formatIdLabel(option.effect), chipColors[option.effect] || 0xc8b698);
        chip.scale.set(0.82);
        chip.position.set(x + width - 32 - chip.widthHint * 0.82, optionButton.y + 8);
        this.layers.modal.addChild(chip);
      }
    });
    if (pageCount > 1) {
      const pagerY = y + height - 46;
      const previous = button("Previous", 104, 40, page > 0 ? () => {
        this.state.conversationScrollOffset = page - 1;
        this.renderUi();
      } : null, { fill: 0x171411, stroke: 0x6f5d3d, fontSize: 12 });
      previous.position.set(x + 16, pagerY);
      const pageLabel = text(`${page + 1} / ${pageCount}`, { fontSize: 12, fill: 0xc8b698, fontWeight: "900" });
      pageLabel.anchor.set(0.5);
      pageLabel.position.set(x + width / 2, pagerY + 20);
      const next = button("Next", 104, 40, page < pageCount - 1 ? () => {
        this.state.conversationScrollOffset = page + 1;
        this.renderUi();
      } : null, { fill: 0x211a12, stroke: 0x967341, fontSize: 12 });
      next.position.set(x + width - 120, pagerY);
      this.layers.modal.addChild(previous, pageLabel, next);
    }
  }

  drawInteractablePopup() {
    const interactable = this.state.selectedInteractable;
    if (!interactable || !this.state.map) {
      return;
    }
    const screen = this.worldToScreen(interactable.position);
    const width = Math.min(280, this.state.viewport.width - 24);
    const description = this.state.conversationLine || interactable.description || interactable.kind || "";
    const hasDescription = Boolean(description);
    const height = hasDescription ? 116 : 88;
    const x = clamp(screen.x - width / 2, 12, this.state.viewport.width - width - 12);
    const y = clamp(screen.y - height - 36, this.topNavHeight() + 8, this.state.viewport.height - height - 12);
    const frame = panel(width, height, { fill: 0x0e0d0b, alpha: 0.78, stroke: 0x8d7245, strokeAlpha: 0.42, radius: 5 });
    frame.position.set(x, y);
    const name = text(interactable.name, { fontSize: 13, fill: 0xf2e6c7, fontWeight: "900", wordWrap: true, wordWrapWidth: width - 24 });
    name.position.set(x + 12, y + 10);
    this.layers.modal.addChild(frame, name);
    let buttonY = y + 38;
    if (hasDescription) {
      const detail = text(description, { fontSize: 11, fill: 0xc8b698, wordWrap: true, wordWrapWidth: width - 24, lineHeight: 14 });
      detail.position.set(x + 12, y + 31);
      if (detail.height > 36) {
        detail.scale.set(Math.max(0.78, 36 / detail.height));
      }
      this.layers.modal.addChild(detail);
      buttonY = y + 72;
    }
    const inspect = button("Inspect", 92, 28, () => this.interact(interactable), { fill: 0x211a12, stroke: 0x967341, fontSize: 11 });
    inspect.position.set(x + 12, buttonY);
    this.layers.modal.addChild(inspect);
    if (!this.state.player?.tutorial) {
      const close = button("Close", 72, 28, () => this.clearConversation(), { fill: 0x171411, stroke: 0x6f5d3d, fontSize: 11 });
      close.position.set(x + width - 84, buttonY);
      this.layers.modal.addChild(close);
    }
  }

  drawShopPanel() {
    const shop = this.state.selectedShop;
    if (!shop) return;
    const phone = this.isPhoneLandscape();
    const safe = this.safeRect();
    const activeTab = this.state.shopTab === "sell" ? "sell" : "buy";
    const width = phone ? safe.width - 16 : Math.max(360, Math.min(900, this.state.viewport.width - 32));
    const y = phone ? safe.y + this.topNavHeight() + 6 : this.topNavHeight() + 18;
    const height = phone ? safe.bottom - y - 8 : Math.max(300, Math.min(660, this.state.viewport.height - this.topNavHeight() - 36));
    const x = phone ? safe.x + 8 : this.state.viewport.width / 2 - width / 2;
    const frame = panel(width, height, { fill: activeTab === "sell" ? 0x0d120d : 0x0f0e0b, alpha: 0.97, stroke: activeTab === "sell" ? 0x79b86d : 0xc0964f, radius: 6 });
    frame.position.set(x, y);
    const title = text(shop.npc_name || "Trade", { fontSize: 21, fill: 0xf2e6c7, fontWeight: "900", wordWrap: true, wordWrapWidth: width - 190 });
    title.position.set(x + 18, y + 14);
    const gold = this.summaryPill(`Gold ${shop.player_gold ?? this.state.player?.gold ?? 0}`, 0xe0b95b);
    gold.position.set(x + width - gold.widthHint - 104, y + 13);
    const close = button("Close", 82, 30, () => {
      this.state.selectedShop = null;
      this.state.hoveredShopItemKey = null;
      this.state.shopTab = "buy";
      this.state.shopScrollOffsets = { buyItems: 0, buyCards: 0, sell: 0 };
      this.renderUi();
    }, { fill: 0x171817, alpha: 0.9, fontSize: 12 });
    close.position.set(x + width - 94, y + 12);
    this.layers.modal.addChild(frame, title, gold, close);

    const contentX = x + 18;
    const tabsY = y + 56;
    const contentY = y + 98;
    const contentWidth = width - 36;
    const contentHeight = height - 118;
    const items = shop.for_sale || [];
    const cards = shop.cards_for_sale || [];
    const sellItems = shop.can_sell || [];
    const market = shop.merchant_state || {};
    this.drawShopTabs(contentX, tabsY, contentWidth, activeTab, sellItems);
    if (activeTab === "sell") {
      this.drawShopSellTab(this.sortedSellItems(sellItems), market, contentX, contentY, contentWidth, contentHeight);
      return;
    }
    this.drawShopBuyTab(items, cards, market, shop.player_gold ?? this.state.player?.gold ?? 0, contentX, contentY, contentWidth, contentHeight);
  }

  drawCardFusionPanel() {
    const fusion = this.state.selectedCardFusion;
    if (!fusion) return;
    const phone = this.isPhoneLandscape();
    const safe = this.safeRect();
    const width = phone ? safe.width - 16 : Math.max(380, Math.min(980, this.state.viewport.width - 32));
    const y = phone ? safe.y + this.topNavHeight() + 6 : this.topNavHeight() + 18;
    const height = phone ? safe.bottom - y - 8 : Math.max(340, Math.min(680, this.state.viewport.height - this.topNavHeight() - 36));
    const x = phone ? safe.x + 8 : this.state.viewport.width / 2 - width / 2;
    const frame = panel(width, height, { fill: 0x100f13, alpha: 0.98, stroke: 0xa576d6, radius: 6 });
    frame.position.set(x, y);
    const title = text("Card Fusion", { fontSize: 22, fill: 0xf2e6c7, fontWeight: "900" });
    title.position.set(x + 18, y + 14);
    const subtitle = text(`3 same cards -> next tier | +${fusion.tier_bonus_percent || 15}% numbers per tier`, {
      fontSize: 11,
      fill: 0xc8b698,
      fontWeight: "900",
      wordWrap: true,
      wordWrapWidth: width - 210,
    });
    subtitle.position.set(x + 18, y + 42);
    const close = button("Close", 82, 30, () => {
      this.state.selectedCardFusion = null;
      this.state.cardFusionSlots = [];
      this.state.cardFusionResult = null;
      this.state.hoveredCardFusionKey = null;
      this.renderUi();
    }, { fill: 0x171817, alpha: 0.9, fontSize: 12 });
    close.position.set(x + width - 94, y + 12);
    this.layers.modal.addChild(frame, title, subtitle, close);

    const contentX = x + 18;
    const contentY = y + 76;
    const contentWidth = width - 36;
    const contentHeight = height - 96;
    const compact = contentWidth < 760;
    const listWidth = compact ? Math.floor(contentWidth * 0.46) : Math.floor(contentWidth * 0.42);
    const forgeX = contentX + listWidth + 18;
    const forgeWidth = contentWidth - listWidth - 18;
    this.drawCardFusionStackList(fusion.stacks || [], contentX, contentY, listWidth, contentHeight);
    this.drawCardFusionForge(forgeX, contentY, forgeWidth, contentHeight);
  }

  drawCardFusionStackList(stacks, x, y, width, height) {
    const title = this.sectionTitle("Available cards");
    title.position.set(x, y);
    this.layers.modal.addChild(title);
    const rowHeight = 86;
    const listY = y + 34;
    const listHeight = Math.max(1, height - 34);
    const contentHeight = stacks.length * rowHeight;
    const maxOffset = Math.max(0, contentHeight - listHeight);
    const offset = clamp(Number(this.state.cardFusionScrollOffset || 0), 0, maxOffset);
    this.state.cardFusionScrollOffset = offset;
    const rowWidth = Math.max(1, width - (maxOffset > 0 ? 14 : 0));
    const viewport = new PIXI.Container();
    viewport.position.set(x, listY);
    const content = new PIXI.Container();
    content.position.set(0, -offset);
    const mask = new PIXI.Graphics().roundRect(0, 0, rowWidth, listHeight, 5).fill(0xffffff);
    content.mask = mask;
    stacks.forEach((stack, index) => {
      const row = this.cardFusionStackRow(stack, rowWidth, rowHeight - 8);
      row.position.set(0, index * rowHeight);
      content.addChild(row);
    });
    viewport.addChild(content, mask);
    if (maxOffset > 0) {
      this.drawShopScrollbar(viewport, width, listHeight, contentHeight, offset, 0xa576d6);
    }
    this.layers.modal.addChild(viewport);
  }

  cardFusionStackRow(stack, width, height) {
    const container = new PIXI.Container();
    const canFuse = Boolean(stack.can_fuse);
    const equipmentCount = Number(stack.equipment_count || 0);
    const permanentCount = Number(stack.permanent_count ?? stack.owned_count ?? 0);
    const equipmentOnly = !canFuse && equipmentCount > 0 && permanentCount <= 0;
    const selected = (this.state.cardFusionSlots || [])[0] === stack.card_key;
    container.addChild(panel(width, height, {
      fill: selected ? 0x241733 : equipmentOnly ? 0x202020 : 0x17131d,
      alpha: canFuse ? 0.94 : equipmentOnly ? 0.58 : 0.72,
      stroke: canFuse ? 0xa576d6 : equipmentOnly ? 0x777777 : 0x6f5d83,
      strokeAlpha: canFuse ? 0.82 : equipmentOnly ? 0.38 : 0.46,
      radius: 5,
    }));
    container.eventMode = "static";
    container.cursor = canFuse ? "grab" : "help";
    container.hitArea = new PIXI.Rectangle(0, 0, width, height);
    container.on("pointerdown", (event) => {
      event.stopPropagation?.();
      if (canFuse) {
        this.state.cardFusionDrag = { cardKey: stack.card_key };
      }
    });
    container.on("pointerup", (event) => {
      event.stopPropagation?.();
      if (canFuse && !this.state.cardFusionDrag) {
        this.addCardToFusionSlots(stack.card_key);
      }
      this.state.cardFusionDrag = null;
    });
    container.on("pointertap", (event) => {
      event.stopPropagation?.();
      if (canFuse) this.addCardToFusionSlots(stack.card_key);
    });
    this.drawCombatCardArt(container, stack.card, 8, 8, 52, height - 16);
    if (equipmentOnly) {
      const shade = new PIXI.Graphics().roundRect(8, 8, 52, height - 16, 4).fill({ color: 0x111111, alpha: 0.46 });
      container.addChild(shade);
    }
    this.drawCardTierBadge(container, stack.tier, 42, 11);
    const name = text(stack.card?.name || this.formatIdLabel(stack.base_card_id), {
      fontSize: 13,
      fill: canFuse ? 0xf2e6c7 : 0xa99d82,
      fontWeight: "900",
      wordWrap: true,
      wordWrapWidth: width - 150,
    });
    name.position.set(70, 9);
    const equipmentLabel = equipmentCount ? ` | equipment ${equipmentCount}` : "";
    const counts = text(`T${stack.tier} | available ${stack.available_count}/${permanentCount}${equipmentLabel}${stack.deck_count ? ` | deck ${stack.deck_count}` : ""}`, {
      fontSize: 11,
      fill: canFuse ? 0xd8b5ff : 0xc8b698,
      fontWeight: "900",
      wordWrap: true,
      wordWrapWidth: width - 150,
    });
    counts.position.set(70, 33);
    const odds = stack.odds ? `${stack.odds.success_percent}% -> T${stack.odds.target_tier}` : "";
    const sourceLabel = stack.equipment_source_label ? `From equipment - cannot fuse: ${stack.equipment_source_label}` : "";
    const unavailable = sourceLabel || stack.unavailable_reason || "Cannot fuse";
    const reason = text(canFuse ? odds : unavailable, {
      fontSize: 10,
      fill: canFuse ? 0x9de6a9 : equipmentOnly ? 0xbdb7aa : 0xffb09a,
      wordWrap: true,
      wordWrapWidth: width - 150,
    });
    reason.position.set(70, 55);
    const add = button("+", 30, 28, canFuse ? () => this.addCardToFusionSlots(stack.card_key) : null, {
      fill: canFuse ? 0x2a1f34 : 0x202020,
      stroke: canFuse ? 0xa576d6 : 0x555555,
      fontSize: 16,
    });
    add.position.set(width - 40, height / 2 - 14);
    container.addChild(name, counts, reason, add);
    return container;
  }

  drawCardFusionForge(x, y, width, height) {
    const title = this.sectionTitle("Forge slots");
    title.position.set(x, y);
    this.layers.modal.addChild(title);
    const slots = this.state.cardFusionSlots || [];
    const slotSize = Math.min(116, Math.max(86, Math.floor((width - 34) / 3)));
    const slotY = y + 44;
    for (let index = 0; index < 3; index += 1) {
      const slotX = x + index * (slotSize + 12);
      this.drawCardFusionSlot(slotX, slotY, slotSize, slots[index], index);
    }
    const selectedStack = slots.length ? this.cardFusionStackByKey(slots[0]) : null;
    const canFuse = slots.length === 3 && new Set(slots).size === 1 && selectedStack?.can_fuse;
    const fuse = button("Fuse", Math.min(180, width), 36, canFuse ? () => this.fuseSelectedCards() : null, {
      fill: canFuse ? 0x2a1f34 : 0x202020,
      stroke: canFuse ? 0xa576d6 : 0x555555,
      fontSize: 13,
    });
    fuse.position.set(x, slotY + slotSize + 20);
    fuse.eventMode = "static";
    fuse.on("pointerover", () => {
      this.state.hoveredCardFusionButton = true;
      this.renderUi();
    });
    fuse.on("pointerout", () => {
      this.state.hoveredCardFusionButton = false;
      this.renderUi();
    });
    this.layers.modal.addChild(fuse);
    if (this.state.hoveredCardFusionButton && selectedStack?.odds) {
      this.layers.modal.addChild(this.cardFusionOddsTooltip(selectedStack, x + fuse.widthHint + 10, slotY + slotSize + 18, width - fuse.widthHint - 10));
    }
    const resultY = slotY + slotSize + 76;
    const resultTitle = this.sectionTitle("Result");
    resultTitle.position.set(x, resultY);
    this.layers.modal.addChild(resultTitle);
    const result = this.state.cardFusionResult;
    const resultCard = result?.result_card || selectedStack?.result_card || null;
    const resultSlotWidth = Math.min(180, width);
    const resultSlotHeight = Math.min(236, Math.max(160, height - (resultY - y) - 32));
    const resultSlot = panel(resultSlotWidth, resultSlotHeight, {
      fill: result?.success ? 0x17251a : result && !result.success ? 0x2a1717 : 0x111014,
      alpha: 0.9,
      stroke: result?.success ? 0x7bd889 : result ? 0xb85a43 : 0x6f5d83,
      radius: 6,
    });
    resultSlot.position.set(x, resultY + 34);
    this.layers.modal.addChild(resultSlot);
    if (resultCard) {
      const cardWidth = Math.min(132, resultSlotWidth - 28);
      const cardHeight = Math.min(178, resultSlotHeight - 44);
      const preview = new PIXI.Container();
      preview.position.set(x + resultSlotWidth / 2, resultY + 42 + cardHeight / 2);
      this.drawCombatCardFace(preview, resultCard, { x: 0, y: 0, width: cardWidth, height: cardHeight, preview: true, compactText: true }, {});
      this.layers.modal.addChild(preview);
    }
    if (result?.message) {
      const message = text(result.message, { fontSize: 12, fill: result.success ? 0x9de6a9 : 0xffb09a, fontWeight: "900", wordWrap: true, wordWrapWidth: Math.min(260, width) });
      message.position.set(x + 192, resultY + 42);
      this.layers.modal.addChild(message);
    }
  }

  drawCardFusionSlot(x, y, size, cardKey, index) {
    const stack = cardKey ? this.cardFusionStackByKey(cardKey) : null;
    const slot = panel(size, size, { fill: stack ? 0x21182d : 0x111014, alpha: 0.92, stroke: stack ? 0xa576d6 : 0x6f5d83, radius: 6 });
    slot.position.set(x, y);
    slot.eventMode = "static";
    slot.cursor = this.state.cardFusionDrag ? "copy" : "pointer";
    slot.hitArea = new PIXI.Rectangle(0, 0, size, size);
    slot.on("pointerup", (event) => {
      event.stopPropagation?.();
      const dragKey = this.state.cardFusionDrag?.cardKey;
      if (dragKey) {
        this.addCardToFusionSlots(dragKey);
      } else if (stack) {
        this.removeCardFusionSlot(index);
      }
      this.state.cardFusionDrag = null;
    });
    this.layers.modal.addChild(slot);
    if (stack?.card) {
      const preview = new PIXI.Container();
      preview.position.set(x + size / 2, y + size / 2);
      this.drawCombatCardFace(preview, stack.card, { x: 0, y: 0, width: size - 18, height: size - 10, preview: true, compactText: true }, {});
      this.layers.modal.addChild(preview);
    } else {
      const label = text("Drop card", { fontSize: 12, fill: 0x9f9276, fontWeight: "900" });
      label.anchor.set(0.5);
      label.position.set(x + size / 2, y + size / 2);
      this.layers.modal.addChild(label);
    }
  }

  cardFusionOddsTooltip(stack, x, y, maxWidth) {
    const width = Math.max(220, Math.min(310, maxWidth || 280));
    const odds = stack.odds || {};
    const lines = [`Success: ${odds.success_percent ?? 0}%`];
    Object.entries(odds.destroy_chances || {}).forEach(([destroyed, chance]) => {
      lines.push(`Failure: destroy ${destroyed} card${Number(destroyed) === 1 ? "" : "s"} (${chance}%)`);
    });
    const body = text(lines.join("\n"), { fontSize: 12, fill: 0xf2e6c7, fontWeight: "900", wordWrap: true, wordWrapWidth: width - 20, lineHeight: 17 });
    const box = panel(width, body.height + 22, { fill: 0x09070c, alpha: 0.96, stroke: 0xa576d6, radius: 5 });
    box.position.set(clamp(x, 12, this.state.viewport.width - width - 12), clamp(y, this.topNavHeight() + 8, this.state.viewport.height - body.height - 34));
    body.position.set(box.x + 10, box.y + 10);
    const container = new PIXI.Container();
    container.addChild(box, body);
    return container;
  }

  drawCardTierBadge(container, tier, x, y, width = 34, height = 20) {
    if (Number(tier || 1) <= 1) return;
    const badgeWidth = Math.max(30, width);
    const badgeHeight = Math.max(18, height);
    const badge = panel(badgeWidth, badgeHeight, { fill: 0x2a1f34, alpha: 0.94, stroke: 0xd8b5ff, radius: Math.floor(badgeHeight / 2) });
    badge.position.set(x, y);
    const label = text(`T${tier}`, { fontSize: Math.max(10, Math.min(12, badgeHeight * 0.58)), fill: 0xf2e6c7, fontWeight: "900" });
    label.anchor.set(0.5);
    label.position.set(x + badgeWidth / 2, y + badgeHeight / 2);
    container.addChild(badge, label);
  }

  drawShopTabs(x, y, width, activeTab, sellItems) {
    const tabWidth = Math.min(132, Math.floor((width - 8) / 2));
    const tabs = [
      ["buy", "Buy", 0xe0b95b, 0x342717],
      ["sell", `Sell ${sellItems.length}`, 0x7bd889, 0x17301d],
    ];
    tabs.forEach(([tab, label, color, fill], index) => {
      const selected = activeTab === tab;
      const tabButton = button(label, tabWidth, 32, () => {
        this.state.shopTab = tab;
        this.state.hoveredShopItemKey = null;
        this.renderUi();
      }, {
        fill: selected ? fill : 0x17130f,
        stroke: selected ? color : 0x6f5d3d,
        strokeAlpha: selected ? 0.95 : 0.42,
        fontSize: 12,
      });
      tabButton.position.set(x + index * (tabWidth + 8), y);
      this.layers.modal.addChild(tabButton);
    });
    const activeLabel = activeTab === "sell" ? "Selling your inventory" : "Buying from merchant";
    const marker = text(activeLabel, {
      fontSize: 11,
      fill: activeTab === "sell" ? 0x9de6a9 : 0xffdf7a,
      fontWeight: "900",
      wordWrap: true,
      wordWrapWidth: Math.max(120, width - tabWidth * 2 - 28),
    });
    marker.position.set(x + tabWidth * 2 + 20, y + 8);
    this.layers.modal.addChild(marker);
  }

  drawShopBuyTab(items, cards, market, playerGold, contentX, contentY, contentWidth, contentHeight) {
    const hasCards = cards.length > 0;
    const itemWidth = hasCards ? Math.floor(contentWidth * 0.5) - 9 : contentWidth;
    const cardWidth = hasCards ? contentWidth - itemWidth - 18 : 0;
    const affordableItems = items.filter((item) => playerGold >= item.price).length;
    const affordableCards = cards.filter((card) => card.available && playerGold >= card.price).length;

    let hoveredShopItem = null;
    const itemTitle = this.sectionTitle("Items for sale");
    itemTitle.position.set(contentX, contentY);
    const itemSummary = this.summaryPill(`${affordableItems}/${items.length} affordable`, affordableItems ? 0x7bd889 : 0x9f9276);
    itemSummary.scale.set(0.82);
    itemSummary.position.set(contentX + 132, contentY + 1);
    this.layers.modal.addChild(itemTitle, itemSummary);
    if (market.mood || market.reputation_multiplier) {
      const marketText = text(`${market.mood || "Market"}${market.reputation_multiplier ? ` | reputation x${market.reputation_multiplier}` : ""}`, {
        fontSize: 10,
        fill: 0xc8b698,
        wordWrap: true,
        wordWrapWidth: Math.max(120, itemWidth - 20),
      });
      marketText.position.set(contentX, contentY + 21);
      this.layers.modal.addChild(marketText);
    }
    if (!items.length) {
      const empty = this.emptyState("No items for sale.", "This NPC only offers training or has no stock right now.");
      empty.position.set(contentX, contentY + 36);
      this.layers.modal.addChild(empty);
    } else {
      hoveredShopItem = this.drawShopItemGrid(this.sortedShopItems(items, playerGold), contentX, contentY + 42, itemWidth, contentHeight - 42, {
        scrollKey: "buyItems",
        mode: "buy",
      });
    }

    if (hasCards) {
      const cardX = contentX + itemWidth + 18;
      const cardTitle = this.sectionTitle("Training cards");
      cardTitle.position.set(cardX, contentY);
      const cardSummary = this.summaryPill(`${affordableCards}/${cards.length} trainable`, affordableCards ? 0x7bd889 : 0x9f9276);
      cardSummary.scale.set(0.82);
      cardSummary.position.set(cardX + 142, contentY + 1);
      this.layers.modal.addChild(cardTitle, cardSummary);
      const hoveredCard = this.drawShopCardList(this.sortedShopCards(cards, playerGold), cardX, contentY + 36, cardWidth, contentHeight - 36, "buyCards");
      if (hoveredCard) {
        const tooltipWidth = Math.min(390, Math.max(300, contentWidth));
        const tooltip = this.shopCardTooltip(hoveredCard.card, tooltipWidth, hoveredCard.x - tooltipWidth - 10, hoveredCard.y, this.state.viewport.width, this.state.viewport.height);
        this.layers.modal.addChild(tooltip);
      }
    }
    if (hoveredShopItem) {
      const tooltipWidth = Math.min(420, Math.max(300, contentWidth));
      const tooltip = this.inventoryItemTooltip(hoveredShopItem.item, tooltipWidth, hoveredShopItem.x + hoveredShopItem.width + 10, hoveredShopItem.y, this.state.viewport.width, this.state.viewport.height);
      this.layers.modal.addChild(tooltip);
    }
  }

  drawShopSellTab(sellItems, market, contentX, contentY, contentWidth, contentHeight) {
    const totalValue = sellItems.reduce((total, item) => total + (item.sell_price_total || 0), 0);
    const title = this.sectionTitle("Sell inventory");
    title.position.set(contentX, contentY);
    const summary = this.summaryPill(`${sellItems.length} sellable | ${totalValue}g`, 0x7bd889);
    summary.scale.set(0.82);
    summary.position.set(contentX + 126, contentY + 1);
    this.layers.modal.addChild(title, summary);
    const marketLine = text(`${market.mood || "Market"}${market.reputation_multiplier ? ` | reputation x${market.reputation_multiplier}` : ""}`, {
      fontSize: 11,
      fill: 0x9de6a9,
      fontWeight: "900",
      wordWrap: true,
      wordWrapWidth: contentWidth,
    });
    marketLine.position.set(contentX, contentY + 30);
    this.layers.modal.addChild(marketLine);
    const gridY = contentY + 54;
    const gridHeight = contentHeight - 54;
    if (!sellItems.length) {
      const empty = this.emptyState("Nothing to sell.", "Quest items, equipped gear, and items with no value stay out of this tab.");
      empty.position.set(contentX, gridY + 8);
      this.layers.modal.addChild(empty);
      return;
    }
    const hovered = this.drawShopItemGrid(sellItems, contentX, gridY, contentWidth, gridHeight, {
      scrollKey: "sell",
      mode: "sell",
    });
    if (hovered) {
      const tooltipWidth = Math.min(420, Math.max(300, contentWidth));
      const tooltip = this.inventoryItemTooltip(hovered.item, tooltipWidth, hovered.x + hovered.width + 10, hovered.y, this.state.viewport.width, this.state.viewport.height);
      this.layers.modal.addChild(tooltip);
    }
  }

  sortedShopItems(items, playerGold) {
    return [...items].sort((left, right) => Number(playerGold >= right.price) - Number(playerGold >= left.price) || left.price - right.price || String(left.name).localeCompare(String(right.name)));
  }

  sortedShopCards(cards, playerGold) {
    const availableScore = (card) => Number(Boolean(card.available && playerGold >= card.price));
    return [...cards].sort((left, right) => availableScore(right) - availableScore(left) || Number(left.owned) - Number(right.owned) || left.price - right.price || String(left.name).localeCompare(String(right.name)));
  }

  sortedSellItems(items) {
    return [...items].sort((left, right) => (right.sell_price_total || 0) - (left.sell_price_total || 0) || String(left.name).localeCompare(String(right.name)));
  }

  shopItemGridLayout(width, height, itemCount, mode = "buy") {
    const gridContentWidth = Math.max(1, width - 14);
    const gap = gridContentWidth < 360 ? 10 : 12;
    const targetCellWidth = gridContentWidth < 360 ? 150 : mode === "sell" ? 174 : 166;
    const columns = Math.max(1, Math.floor((gridContentWidth + gap) / (targetCellWidth + gap)));
    const cellWidth = Math.floor((gridContentWidth - gap * (columns - 1)) / columns);
    const cellHeight = mode === "sell" ? (gridContentWidth < 360 ? 214 : 222) : (gridContentWidth < 360 ? 196 : 204);
    const rows = Math.ceil(Math.max(0, itemCount) / columns);
    const contentHeight = rows ? rows * cellHeight + Math.max(0, rows - 1) * gap : 0;
    return {
      gap,
      columns,
      cellWidth,
      cellHeight,
      gridContentWidth,
      contentHeight,
      maxOffset: Math.max(0, contentHeight - height),
    };
  }

  drawShopItemGrid(items, x, y, width, height, options = {}) {
    const mode = options.mode || "buy";
    const scrollKey = options.scrollKey || "buyItems";
    const layout = this.shopItemGridLayout(width, height, items.length, mode);
    const offset = clamp(Number(this.state.shopScrollOffsets?.[scrollKey] || 0), 0, layout.maxOffset);
    this.state.shopScrollOffsets[scrollKey] = offset;
    const viewport = new PIXI.Container();
    viewport.position.set(x, y);
    const content = new PIXI.Container();
    content.position.set(0, -offset);
    const mask = new PIXI.Graphics().roundRect(0, 0, layout.gridContentWidth, height, 5).fill(0xffffff);
    content.mask = mask;
    let hovered = null;
    items.forEach((item, index) => {
      const hoverKey = `${scrollKey}:${item.item_id || item.id || "item"}:${index}`;
      const cell = mode === "sell"
        ? this.shopSellCell(item, layout.cellWidth, layout.cellHeight, hoverKey)
        : this.shopItemCell(item, layout.cellWidth, layout.cellHeight, hoverKey);
      const cellX = (index % layout.columns) * (layout.cellWidth + layout.gap);
      const cellY = Math.floor(index / layout.columns) * (layout.cellHeight + layout.gap);
      cell.position.set(cellX, cellY);
      content.addChild(cell);
      if (this.state.hoveredShopItemKey === hoverKey) {
        hovered = { item, x: x + cellX, y: y + cellY - offset, width: layout.cellWidth, height: layout.cellHeight };
      }
    });
    viewport.addChild(content, mask);
    if (layout.maxOffset > 0) {
      this.drawShopScrollbar(viewport, width, height, layout.contentHeight, offset, mode === "sell" ? 0x7bd889 : 0xd7aa5f);
    }
    this.layers.modal.addChild(viewport);
    return hovered;
  }

  shopItemCell(item, width, height, hoverKey) {
    const container = new PIXI.Container();
    const rarityColors = { common: 0x8a7a5a, uncommon: 0x6ea86b, rare: 0x608fd3, epic: 0x9c6bd3, quest: 0xe0b95b };
    const color = rarityColors[item.rarity] || 0x8a7a5a;
    container.addChild(panel(width, height, { fill: 0x18140f, alpha: 0.88, stroke: color, strokeAlpha: 0.74, radius: 5 }));
    container.eventMode = "static";
    container.cursor = "help";
    container.hitArea = new PIXI.Rectangle(0, 0, width, height);
    container.on("pointerover", (event) => {
      event.stopPropagation?.();
      if (this.state.hoveredShopItemKey !== hoverKey) {
        this.state.hoveredShopItemKey = hoverKey;
        this.renderUi();
      }
    });
    container.on("pointerout", (event) => {
      event.stopPropagation?.();
      if (this.state.hoveredShopItemKey === hoverKey) {
        this.state.hoveredShopItemKey = null;
        this.renderUi();
      }
    });
    const texture = this.itemIconTexture(item);
    if (texture) {
      const icon = new PIXI.Sprite(texture);
      icon.anchor.set(0.5, 0);
      const iconSize = Math.min(66, Math.max(56, Math.floor(width * 0.38)));
      icon.position.set(width / 2, 10);
      icon.width = iconSize;
      icon.height = iconSize;
      container.addChild(icon);
    }
    const name = text(item.name || this.formatIdLabel(item.item_id), { fontSize: 13, fill: 0xf2e6c7, fontWeight: "900", align: "center", wordWrap: true, wordWrapWidth: width - 16, lineHeight: 15 });
    name.anchor.set(0.5, 0);
    name.position.set(width / 2, 82);
    if (name.height > 34) {
      name.scale.set(34 / name.height);
    }
    const metaParts = [`${item.price}g`];
    if (item.required_level > 1) metaParts.push(`Lv ${item.required_level}`);
    const meta = text(metaParts.join(" | "), { fontSize: 11, fill: 0xc8b698, align: "center", wordWrap: true, wordWrapWidth: width - 16 });
    meta.anchor.set(0.5, 0);
    meta.position.set(width / 2, 122);
    const canAfford = (this.state.selectedShop?.player_gold ?? this.state.player?.gold ?? 0) >= item.price;
    const status = text(canAfford ? "Can buy" : "Need gold", { fontSize: 10, fill: canAfford ? 0x7bd889 : 0xffa07a, fontWeight: "900" });
    status.anchor.set(0.5, 0);
    status.position.set(width / 2, height - 60);
    const buy = button("Buy", Math.min(72, width - 16), 26, canAfford ? () => this.buyShopItem(item) : null, {
      fill: canAfford ? 0x1f2a19 : 0x2a2117,
      stroke: canAfford ? 0x7bd889 : 0x6f5d3d,
      fontSize: 11,
    });
    buy.position.set((width - buy.widthHint) / 2, height - 34);
    container.addChild(name, meta, status, buy);
    return container;
  }

  drawShopCardList(cards, x, y, width, height, scrollKey = "buyCards") {
    const rowHeight = 82;
    const contentHeight = cards.length * rowHeight;
    const maxOffset = Math.max(0, contentHeight - height);
    const offset = clamp(Number(this.state.shopScrollOffsets?.[scrollKey] || 0), 0, maxOffset);
    this.state.shopScrollOffsets[scrollKey] = offset;
    const rowWidth = Math.max(1, width - (maxOffset > 0 ? 14 : 0));
    const viewport = new PIXI.Container();
    viewport.position.set(x, y);
    const content = new PIXI.Container();
    content.position.set(0, -offset);
    const mask = new PIXI.Graphics().roundRect(0, 0, rowWidth, height, 5).fill(0xffffff);
    content.mask = mask;
    let hovered = null;
    cards.forEach((card, index) => {
      const hoverKey = `${scrollKey}:${card.card_id || "card"}:${index}`;
      const rowY = index * rowHeight;
      const row = this.shopCardRow(card, rowWidth, hoverKey);
      row.position.set(0, rowY);
      content.addChild(row);
      if (this.state.hoveredShopItemKey === hoverKey) {
        hovered = { card, x, y: y + rowY - offset, width: rowWidth, height: 74 };
      }
    });
    viewport.addChild(content, mask);
    if (maxOffset > 0) {
      this.drawShopScrollbar(viewport, width, height, contentHeight, offset, 0xd7aa5f);
    }
    this.layers.modal.addChild(viewport);
    return hovered;
  }

  shopCardRow(card, width, hoverKey) {
    const container = new PIXI.Container();
    const available = card.available && (this.state.selectedShop?.player_gold ?? this.state.player?.gold ?? 0) >= card.price;
    container.addChild(panel(width, 74, { fill: card.owned ? 0x151512 : 0x18140f, alpha: 0.9, stroke: available ? 0x7bd889 : 0x8d6c3d, strokeAlpha: available ? 0.82 : 0.54, radius: 5 }));
    container.eventMode = "static";
    container.cursor = "help";
    container.hitArea = new PIXI.Rectangle(0, 0, width, 74);
    container.on("pointerover", (event) => {
      event.stopPropagation?.();
      if (this.state.hoveredShopItemKey !== hoverKey) {
        this.state.hoveredShopItemKey = hoverKey;
        this.renderUi();
      }
    });
    container.on("pointerout", (event) => {
      event.stopPropagation?.();
      if (this.state.hoveredShopItemKey === hoverKey) {
        this.state.hoveredShopItemKey = null;
        this.renderUi();
      }
    });
    this.drawCombatCardArt(container, card, 8, 8, 48, 58);
    const name = text(card.name || this.formatIdLabel(card.card_id), { fontSize: 13, fill: card.owned ? 0xa99d82 : 0xf2e6c7, fontWeight: "900", wordWrap: true, wordWrapWidth: width - 168 });
    name.position.set(66, 8);
    const cardKind = `${this.formatIdLabel(card.card_category || "attack")} | ${this.formatIdLabel(card.type)}`;
    const status = card.unavailable_reason || `${cardKind}${card.owned ? " | owned" : ""}`;
    const meta = text(status, { fontSize: 11, fill: available ? 0x9de6a9 : 0xc8b698, wordWrap: true, wordWrapWidth: width - 154 });
    meta.position.set(66, 32);
    const coinTexture = this.assetTextures.get("character_gold");
    const coin = coinTexture ? new PIXI.Sprite(coinTexture) : null;
    if (coin) {
      coin.width = 16;
      coin.height = 16;
      coin.position.set(width - 82, 7);
    }
    const price = text(String(card.price || 0), { fontSize: 13, fill: 0xffdf7a, fontWeight: "900" });
    price.position.set(width - 61, 8);
    const requirements = [];
    if (card.required_level > 1) requirements.push(`Lv ${card.required_level}`);
    if (card.required_classes?.length) requirements.push(card.required_classes.map((value) => this.formatIdLabel(value)).join(", "));
    const req = text(requirements.join(" | "), { fontSize: 10, fill: 0x9f9276, wordWrap: true, wordWrapWidth: width - 154 });
    req.position.set(66, 52);
    const buy = button("Buy", 66, 28, available ? () => this.buyShopCard(card) : null, {
      fill: available ? 0x1f2a19 : 0x2a2117,
      stroke: available ? 0x7bd889 : 0x6f5d3d,
      fontSize: 11,
    });
    buy.position.set(width - 76, 23);
    container.addChild(name, meta, req, buy);
    if (coin) container.addChild(coin);
    container.addChild(price);
    return container;
  }

  shopCardTooltip(card, width, x, y, panelWidth, panelHeight) {
    const bodyWidth = width - 24;
    const content = new PIXI.Container();
    const title = text(card.name || this.formatIdLabel(card.card_id), { fontSize: 16, fill: 0xf2e6c7, fontWeight: "900", wordWrap: true, wordWrapWidth: bodyWidth });
    title.position.set(12, 10);
    content.addChild(title);
    let cursorY = 14 + title.height;
    const metaParts = [
      `${card.price || 0}g`,
      this.formatIdLabel(card.rarity || "common"),
      this.formatIdLabel(card.card_category || "card"),
      this.formatIdLabel(card.action_area || card.type || "action"),
    ];
    const meta = text(metaParts.join(" | "), { fontSize: 11, fill: 0xa9c5e8, fontWeight: "900", wordWrap: true, wordWrapWidth: bodyWidth });
    meta.position.set(12, cursorY);
    content.addChild(meta);
    cursorY += meta.height + 8;
    const description = text(card.description || "Training card.", { fontSize: 12, fill: 0xc8b698, fontStyle: "italic", wordWrap: true, wordWrapWidth: bodyWidth, lineHeight: 15 });
    description.position.set(12, cursorY);
    content.addChild(description);
    cursorY += description.height + 10;
    const requirements = [];
    if (card.required_level > 1) requirements.push(`Level ${card.required_level}`);
    if (card.required_classes?.length) requirements.push(`Class ${card.required_classes.map((value) => this.formatIdLabel(value)).join(", ")}`);
    requirements.push(card.owned ? "Already owned" : card.available ? "Available" : card.unavailable_reason || "Unavailable");
    const requirementText = text(requirements.join(" | "), {
      fontSize: 11,
      fill: card.available && !card.owned ? 0x9de6a9 : 0xffdf9a,
      fontWeight: "900",
      wordWrap: true,
      wordWrapWidth: bodyWidth,
    });
    requirementText.position.set(12, cursorY);
    content.addChild(requirementText);
    cursorY += requirementText.height + 10;
    const primary = this.deckFormulaBlock("Primary", card.primary_action, card, bodyWidth);
    primary.position.set(12, cursorY);
    content.addChild(primary);
    cursorY += primary.heightHint + 8;
    if (card.secondary_action && Object.keys(card.secondary_action).length) {
      const secondary = this.deckFormulaBlock("Secondary", card.secondary_action, card, bodyWidth);
      secondary.position.set(12, cursorY);
      content.addChild(secondary);
      cursorY += secondary.heightHint + 8;
    }
    const blockValues = Object.entries(card.block_values || card.defense || {}).filter(([, value]) => Number(value) > 0);
    if (blockValues.length) {
      const blockText = text(`Blocks: ${blockValues.map(([key, value]) => `${this.formatIdLabel(key)} ${value}`).join(" | ")}`, { fontSize: 11, fill: 0x9de6a9, fontWeight: "900", wordWrap: true, wordWrapWidth: bodyWidth });
      blockText.position.set(12, cursorY);
      content.addChild(blockText);
      cursorY += blockText.height + 8;
    }
    const height = Math.min(panelHeight - 20, Math.max(132, cursorY + 12));
    const tooltipX = clamp(x, 8, Math.max(8, panelWidth - width - 8));
    const tooltipY = y + height + 10 > panelHeight ? Math.max(8, panelHeight - height - 8) : Math.max(8, y);
    const container = new PIXI.Container();
    const frame = panel(width, height, { fill: 0x0f0d0a, alpha: 0.985, stroke: 0xd7aa5f, strokeAlpha: 0.86, radius: 6 });
    container.addChild(frame, content);
    container.position.set(tooltipX, tooltipY);
    return container;
  }

  drawShopScrollbar(container, width, height, contentHeight, offset, color) {
    const trackWidth = 8;
    const track = new PIXI.Graphics().roundRect(width - trackWidth, 0, trackWidth, height, 4).fill({ color: 0x1b1711, alpha: 0.82 });
    const thumbHeight = clamp((height / Math.max(height, contentHeight)) * height, 42, height);
    const thumbY = (height - thumbHeight) * (offset / Math.max(1, contentHeight - height));
    const thumb = new PIXI.Graphics().roundRect(width - trackWidth, thumbY, trackWidth, thumbHeight, 4).fill({ color, alpha: 0.88 });
    container.addChild(track, thumb);
  }

  shopSellCell(item, width, height, hoverKey) {
    const container = new PIXI.Container();
    const rarityColors = { common: 0x77a56f, uncommon: 0x7bd889, rare: 0x8fb7ff, epic: 0xd49cff, quest: 0xe0b95b };
    const color = rarityColors[item.rarity] || 0x77a56f;
    container.addChild(panel(width, height, { fill: 0x121a12, alpha: 0.9, stroke: color, strokeAlpha: 0.82, radius: 5 }));
    container.eventMode = "static";
    container.cursor = "help";
    container.hitArea = new PIXI.Rectangle(0, 0, width, height);
    container.on("pointerover", (event) => {
      event.stopPropagation?.();
      if (this.state.hoveredShopItemKey !== hoverKey) {
        this.state.hoveredShopItemKey = hoverKey;
        this.renderUi();
      }
    });
    container.on("pointerout", (event) => {
      event.stopPropagation?.();
      if (this.state.hoveredShopItemKey === hoverKey) {
        this.state.hoveredShopItemKey = null;
        this.renderUi();
      }
    });
    const texture = this.itemIconTexture(item);
    if (texture) {
      const icon = new PIXI.Sprite(texture);
      icon.anchor.set(0.5, 0);
      const iconSize = Math.min(66, Math.max(56, Math.floor(width * 0.38)));
      icon.position.set(width / 2, 10);
      icon.width = iconSize;
      icon.height = iconSize;
      container.addChild(icon);
    }
    const name = text(`${item.name || this.formatIdLabel(item.item_id)} x${item.quantity || 1}`, {
      fontSize: 13,
      fill: 0xf2e6c7,
      fontWeight: "900",
      align: "center",
      wordWrap: true,
      wordWrapWidth: width - 16,
      lineHeight: 15,
    });
    name.anchor.set(0.5, 0);
    name.position.set(width / 2, 82);
    if (name.height > 34) {
      name.scale.set(34 / name.height);
    }
    const breakdown = (item.sell_price_breakdown || []).slice(0, 3).join(" | ");
    const price = text(`${item.sell_price_each || 0}g each | ${item.sell_price_total || 0}g stack`, {
      fontSize: 11,
      fill: 0xc8b698,
      align: "center",
      wordWrap: true,
      wordWrapWidth: width - 16,
    });
    price.anchor.set(0.5, 0);
    price.position.set(width / 2, 122);
    const status = text(breakdown || "Sell value", { fontSize: 10, fill: 0x9de6a9, fontWeight: "900", align: "center", wordWrap: true, wordWrapWidth: width - 16 });
    status.anchor.set(0.5, 0);
    status.position.set(width / 2, height - 78);
    if (status.height > 28) {
      status.scale.set(28 / status.height);
    }
    const sellOne = button("Sell 1", Math.min(66, Math.floor((width - 24) / 2)), 26, () => this.sellShopItem(item, 1), { fill: 0x1f2a19, stroke: 0x7bd889, fontSize: 10 });
    const sellAll = button("All", Math.min(58, Math.floor((width - 24) / 2)), 26, () => this.sellShopItem(item, item.quantity || 1), { fill: 0x2a2117, stroke: 0xe0b95b, fontSize: 10 });
    const buttonsWidth = sellOne.widthHint + 8 + sellAll.widthHint;
    sellOne.position.set((width - buttonsWidth) / 2, height - 34);
    sellAll.position.set(sellOne.x + sellOne.widthHint + 8, height - 34);
    container.addChild(name, price, status, sellOne, sellAll);
    return container;
  }

  drawActiveWindow() {
    if (!this.state.activeWindow) return;
    if (this.state.activeWindow === "combat") {
      this.drawCombatSurface();
      return;
    }
    const { x, y, width, height, content } = this.activeWindowLayout();
    const frame = panel(width, height, { fill: 0x10100d, alpha: 0.96, stroke: 0xa9854b });
    frame.position.set(x, y);
    const phone = this.isPhoneLandscape();
    const title = text(this.state.activeWindow === "combat" ? "Combat" : windowTitles[this.state.activeWindow] || "Window", { fontSize: phone ? 17 : 22, fill: 0xf2e6c7, fontWeight: "900" });
    title.position.set(x + (phone ? 12 : 18), y + (phone ? 12 : 14));
    this.layers.modal.addChild(frame, title);
    if (!this.state.player?.tutorial) {
      const close = button(phone ? "×" : "Close", phone ? 48 : 82, phone ? 44 : 30, () => { this.state.activeWindow = null; this.renderUi(); }, { fontSize: phone ? 22 : 13 });
      close.position.set(x + width - (phone ? 56 : 98), y + (phone ? 4 : 12));
      this.layers.modal.addChild(close);
    }
    const contentLayer = this.windowContent(this.state.activeWindow, content.width, content.height);
    contentLayer.position.set(content.x, content.y);
    this.layers.modal.addChild(contentLayer);
  }

  activeWindowLayout() {
    if (this.isPhoneLandscape()) {
      const layout = phoneWindowLayout(this.state.viewportProfile, this.topNavHeight(), 6);
      return {
        ...layout,
        content: {
          x: layout.x + 10,
          y: layout.y + 50,
          width: Math.max(1, layout.width - 20),
          height: Math.max(1, layout.height - 56),
        },
      };
    }
    const width = Math.max(320, Math.min(1120, this.state.viewport.width - 28));
    const height = Math.max(260, Math.min(720, this.state.viewport.height - this.topNavHeight() - 48));
    const x = this.state.viewport.width / 2 - width / 2;
    const y = this.topNavHeight() + 18;
    return {
      x,
      y,
      width,
      height,
      content: {
        x: x + 18,
        y: y + 58,
        width: Math.max(1, width - 36),
        height: Math.max(1, height - 66),
      },
    };
  }

  windowContent(name, width, height) {
    const container = new PIXI.Container();
    if (name === "settings") {
      container.addChild(this.settingsPanel(width, height));
      return container;
    }
    if (!this.state.player) {
      container.addChild(text("Create a character first.", { fontSize: 16 }));
      return container;
    }
    if (name === "character") {
      const contentWidth = Math.max(1, width - 14);
      container.addChild(this.scrollPanel(this.characterPanel(contentWidth, height), width, height, "characterScrollOffset"));
      return container;
    }
    if (name === "inventory") {
      container.addChild(this.inventoryWindowPanel(width, height));
      return container;
    }
    if (name === "quests") {
      container.addChild(this.questsPanel(width, height));
      return container;
    }
    if (name === "world-map") {
      container.addChild(this.worldMapGraphic(width, height));
      return container;
    }
    if (name === "social") {
      container.addChild(this.socialPanel(width, height));
      return container;
    }
    if (name === "party") {
      container.addChild(this.partyPanel(width, height));
      return container;
    }
    if (name === "deck") {
      container.addChild(this.deckPanel(width, height));
      return container;
    }
    if (name === "combat") {
      container.addChild(this.combatPanel(width, height));
      return container;
    }
    return container;
  }

  characterPanel(width, height) {
    const container = new PIXI.Container();
    const player = this.state.player;
    const activeTab = this.state.characterTab === "achievements" ? "achievements" : "profile";
    const tabs = [
      ["profile", "Profile"],
      ["achievements", "Achievements"],
    ];
    tabs.forEach(([tab, label], index) => {
      const selected = activeTab === tab;
      const tabButton = button(label, 116, 30, () => {
        this.state.characterTab = tab;
        localStorage.setItem("vibe-character-tab", tab);
        this.state.characterScrollOffset = 0;
        this.renderUi();
      }, {
        fill: selected ? 0x342717 : 0x17130f,
        stroke: selected ? 0xe0b95b : 0x6f5d3d,
        strokeAlpha: selected ? 0.9 : 0.42,
        fontSize: 11,
      });
      tabButton.position.set(Math.max(0, width - 240) + index * 122, 0);
      container.addChild(tabButton);
    });
    if (activeTab === "achievements") {
      const achievementsPanel = this.characterAchievementsPanel(player, width, height);
      achievementsPanel.position.set(0, 44);
      container.addChild(achievementsPanel);
      container.contentHeight = Math.max(height, 44 + (achievementsPanel.contentHeight || 0));
      return container;
    }

    const portraitSheet = this.assetTextures.get("profiles");
    if (portraitSheet) {
      const portrait = new PIXI.Sprite(this.textureRegion("profiles", 0, 0, portraitSheet.width / 6, portraitSheet.height / 3));
      portrait.position.set(0, 44);
      portrait.width = 92;
      portrait.height = 92;
      const portraitFrame = panel(108, 108, { fill: 0x17130d, alpha: 0.84, stroke: 0x8a6d3f });
      portraitFrame.position.set(0, 44);
      container.addChild(portraitFrame, portrait);
      portrait.position.set(8, 52);
    }
    const name = text(player.name, { fontSize: 20, fill: 0xf5e9c9, fontWeight: "900", wordWrap: true, wordWrapWidth: width - 132 });
    name.position.set(126, 48);
    const classLabel = player.class_summary?.label || this.formatIdLabel(player.class_name);
    const subtitle = text(`${player.active_title?.label || classLabel} | ${classLabel} | Level ${player.level}`, { fontSize: 13, fill: 0xc8b698, fontWeight: "800", wordWrap: true, wordWrapWidth: width - 132 });
    subtitle.position.set(126, 76);
    container.addChild(name, subtitle);
    const progressWidth = Math.min(300, Math.max(180, width - 132));
    const xp = resourceBar("XP", player.xp, player.xp_to_next, 0xe0b95b, progressWidth);
    xp.position.set(126, 102);
    container.addChild(xp);

    const resources = [
      ["HP", player.hp, player.max_hp, 0x62c461],
      ["Mana", player.mana ?? 0, player.max_mana ?? 1, 0x6292ff],
      ["Stamina", player.stamina ?? 0, player.max_stamina ?? 1, 0xe5b64f],
    ];
    const rightColumnX = width >= 620 ? width - 258 : 0;
    const resourcesY = width >= 620 ? 46 : 166;
    resources.forEach(([label, current, max, color], index) => {
      const bar = resourceBar(label, current, max, color, Math.min(250, width - 24));
      bar.position.set(rightColumnX, resourcesY + index * 36);
      container.addChild(bar);
    });

    const chipY = width >= 620 ? 142 : 284;
    const chips = [
      this.characterSummaryChip("character_gold", `${player.gold || 0} gold`, 0xe0b95b),
      this.characterSummaryChip(null, `Stat points ${player.unspent_stat_points || 0}`, 0x7bd889),
      this.characterSummaryChip(null, `Skill points ${player.skill_points || 0}`, 0x8fb7ff),
    ];
    let chipX = 0;
    let chipRow = 0;
    chips.forEach((chip) => {
      if (chipX + chip.widthHint > width) {
        chipX = 0;
        chipRow += 1;
      }
      chip.position.set(chipX, chipY + chipRow * 36);
      chipX += chip.widthHint + 8;
      container.addChild(chip);
    });

    const classPanel = this.characterClassPanel(player, width);
    const classY = chipY + (chipRow + 1) * 36 + 8;
    classPanel.position.set(0, classY);
    container.addChild(classPanel);

    const statY = classY + (classPanel.contentHeight || 0) + 14;
    const statHeader = new PIXI.Container();
    const statTitle = this.sectionTitle("Stats");
    statTitle.position.set(0, 0);
    const freePoints = text(`Free stat points: ${player.unspent_stat_points || 0}`, { fontSize: 12, fill: 0x9de6a9, fontWeight: "900" });
    freePoints.anchor.set(1, 0);
    freePoints.position.set(width, 2);
    statHeader.position.set(0, statY);
    statHeader.addChild(statTitle, freePoints);
    container.addChild(statHeader);
    const descriptions = this.characterStatDescriptions();
    const baseStats = {
      strength: player.stats?.strength ?? 0,
      agility: player.stats?.agility ?? 0,
      mind: player.stats?.mind ?? 0,
      magical_power: player.stats?.magical_power ?? 0,
      intelligence: player.stats?.intelligence ?? 0,
      endurance: player.stats?.endurance ?? 0,
      speed: player.stats?.speed ?? 0,
    };
    const statRows = Object.entries(baseStats).map(([key, value]) => ({
      key,
      label: this.formatIdLabel(key),
      value: String(value),
      description: descriptions[key] || "",
      iconKey: this.characterStatIconKey(key),
      allocatable: true,
    }));
    const resourceStatRows = [
      {
        key: "max_hp",
        label: "Health",
        value: String(player.max_hp ?? 0),
        description: descriptions.max_hp,
        iconKey: this.characterStatIconKey("max_hp"),
        allocatable: true,
      },
      {
        key: "max_mana",
        label: "Mana",
        value: String(player.max_mana ?? 0),
        description: descriptions.max_mana,
        iconKey: this.characterStatIconKey("max_mana"),
        allocatable: true,
      },
      {
        key: "max_stamina",
        label: "Stamina",
        value: String(player.max_stamina ?? 0),
        description: descriptions.max_stamina,
        iconKey: this.characterStatIconKey("max_stamina"),
        allocatable: true,
      },
    ];
    const statColumns = width >= 520 ? 3 : 2;
    const statGridHeight = Math.ceil(statRows.length / statColumns) * 58;
    const statGrid = this.compactStatGrid(statRows, width, statGridHeight);
    statGrid.position.set(0, statY + 30);
    container.addChild(statGrid);
    const resourcesStatY = statY + 30 + statGridHeight + 14;
    const resourceStatHeader = this.sectionTitle("Resources");
    resourceStatHeader.position.set(0, resourcesStatY);
    container.addChild(resourceStatHeader);
    const resourceGridHeight = Math.ceil(resourceStatRows.length / statColumns) * 58 + 104;
    const resourceStatGrid = this.compactStatGrid(resourceStatRows, width, resourceGridHeight);
    resourceStatGrid.position.set(0, resourcesStatY + 30);
    container.addChild(resourceStatGrid);
    container.contentHeight = Math.max(resourcesStatY + 30 + resourceGridHeight, height);
    return container;
  }

  characterClassPanel(player, width) {
    const container = new PIXI.Container();
    const summary = player.class_summary || {};
    const classLabel = summary.label || this.formatIdLabel(player.class_name);
    const classHeight = width >= 620 ? 116 : 148;
    const bg = panel(width, classHeight, { fill: 0x15120e, alpha: 0.86, stroke: 0x8d7245, strokeAlpha: 0.5, radius: 5 });
    container.addChild(bg);
    const title = this.sectionTitle(`Class: ${classLabel}`);
    title.position.set(12, 10);
    container.addChild(title);
    const description = text(summary.description || "No class specialization selected yet.", {
      fontSize: 11,
      fill: 0xc8b698,
      wordWrap: true,
      wordWrapWidth: width >= 620 ? Math.floor(width * 0.48) : width - 24,
      lineHeight: 14,
    });
    description.position.set(12, 40);
    container.addChild(description);
    const primary = (summary.primary_stats || []).map((stat) => this.formatIdLabel(stat)).join(", ") || "Flexible";
    const growthParts = Object.entries(summary.growth || {}).map(([stat, value]) => `+${value} ${this.formatIdLabel(stat)}`);
    const passiveParts = Object.entries(summary.passive_bonuses || {}).map(([stat, value]) => `+${value} ${this.formatIdLabel(stat)}`);
    const rightX = width >= 620 ? Math.floor(width * 0.54) : 12;
    const rightY = width >= 620 ? 14 : 86;
    const lines = [
      `Primary: ${primary}`,
      `Growth: ${growthParts.length ? growthParts.slice(0, 5).join(", ") : "standard"}`,
      `Passives: ${passiveParts.length ? passiveParts.slice(0, 4).join(", ") : "none unlocked"}`,
      `Class cards: ${(summary.class_cards || []).length}${(summary.next_class_cards || []).length ? ` | next ${summary.next_class_cards.map((card) => card.name).join(", ")}` : ""}`,
    ];
    lines.forEach((line, index) => {
      const row = text(line, {
        fontSize: 11,
        fill: index === 0 ? 0xf2e6c7 : 0xc8b698,
        fontWeight: index === 0 ? "900" : "800",
        wordWrap: true,
        wordWrapWidth: width >= 620 ? width - rightX - 14 : width - 24,
      });
      row.position.set(rightX, rightY + index * 22);
      container.addChild(row);
    });
    container.contentHeight = classHeight;
    return container;
  }

  characterAchievementsPanel(player, width, height) {
    const container = new PIXI.Container();
    const achievements = [...(player.achievements || [])];
    const unlocked = achievements.filter((achievement) => achievement.unlocked).length;
    const title = this.sectionTitle(`Achievements ${unlocked}/${achievements.length}`);
    title.position.set(0, 0);
    container.addChild(title);
    if (!achievements.length) {
      const empty = this.emptyState("No achievements yet", "Explore, fight and finish quests to start filling this list.");
      empty.position.set(0, 36);
      container.addChild(empty);
      container.contentHeight = Math.max(height - 44, 160);
      return container;
    }
    achievements
      .sort((left, right) => Number(Boolean(right.unlocked)) - Number(Boolean(left.unlocked)) || (right.current / Math.max(1, right.required)) - (left.current / Math.max(1, left.required)))
      .forEach((achievement, index) => {
        const y = 36 + index * 78;
        const complete = Boolean(achievement.unlocked);
        const row = panel(width, 66, { fill: complete ? 0x182416 : 0x17130f, alpha: 0.86, stroke: complete ? 0x7bd889 : 0x6f5d3d, strokeAlpha: complete ? 0.72 : 0.42 });
        row.position.set(0, y);
        const status = text(complete ? "Done" : `${achievement.current || 0}/${achievement.required || 1}`, {
          fontSize: 12,
          fill: complete ? 0x9de6a9 : 0xffdf7a,
          fontWeight: "900",
        });
        status.position.set(12, y + 10);
        const label = text(achievement.title, {
          fontSize: 14,
          fill: 0xf2e6c7,
          fontWeight: "900",
          wordWrap: true,
          wordWrapWidth: width - 128,
        });
        label.position.set(84, y + 9);
        const detail = text(achievement.description || achievement.objective || "Progress tracked automatically.", {
          fontSize: 11,
          fill: 0xc8b698,
          fontWeight: "800",
          wordWrap: true,
          wordWrapWidth: width - 96,
        });
        detail.position.set(84, y + 32);
        container.addChild(row, status, label, detail);
      });
    container.contentHeight = Math.max(height - 44, 36 + achievements.length * 78 + 12);
    return container;
  }

  characterSummaryChip(iconKey, label, color) {
    const container = new PIXI.Container();
    const labelText = text(label, { fontSize: 12, fill: 0xfff0c8, fontWeight: "900" });
    const width = Math.max(118, labelText.width + (iconKey ? 54 : 28));
    const bg = panel(width, 30, { fill: 0x17130f, alpha: 0.9, stroke: color, strokeAlpha: 0.72 });
    container.addChild(bg);
    let textX = 14;
    const texture = iconKey ? this.assetTextures.get(iconKey) : null;
    if (texture) {
      const icon = new PIXI.Sprite(texture);
      icon.width = 22;
      icon.height = 22;
      icon.position.set(8, 4);
      container.addChild(icon);
      textX = 36;
    }
    labelText.position.set(textX, 8);
    container.addChild(labelText);
    container.widthHint = width;
    return container;
  }

  scrollPanel(content, width, height, offsetKey) {
    const container = new PIXI.Container();
    const contentHeight = Math.max(height, Number(content.contentHeight || content.height || height));
    const maxOffset = Math.max(0, contentHeight - height);
    let offset = clamp(Number(this.state[offsetKey] || 0), 0, maxOffset);
    this.state[offsetKey] = offset;
    content.position.set(0, -offset);
    const trackWidth = 8;
    const gutter = maxOffset > 0 ? 14 : 0;
    const contentWidth = Math.max(1, width - gutter);
    const mask = new PIXI.Graphics().roundRect(0, 0, contentWidth, height, 5).fill(0xffffff);
    content.mask = mask;
    container.addChild(content, mask);
    let thumb = null;
    let thumbHeight = height;
    if (maxOffset > 0) {
      const track = new PIXI.Graphics().roundRect(width - trackWidth, 0, trackWidth, height, 4).fill({ color: 0x1b1711, alpha: 0.82 });
      thumbHeight = clamp((height / contentHeight) * height, 42, height);
      const thumbY = (height - thumbHeight) * (offset / maxOffset);
      thumb = new PIXI.Graphics().roundRect(width - trackWidth, 0, trackWidth, thumbHeight, 4).fill({ color: 0xd7aa5f, alpha: 0.86 });
      thumb.y = thumbY;
      container.addChild(track, thumb);
      container.eventMode = "static";
      container.hitArea = new PIXI.Rectangle(0, 0, width, height);
      let drag = null;
      const updateOffset = (value) => {
        offset = clamp(value, 0, maxOffset);
        this.state[offsetKey] = offset;
        content.y = -offset;
        thumb.y = (height - thumbHeight) * (offset / maxOffset);
      };
      container.on("pointerdown", (event) => {
        if (event.pointerType !== "touch" && !this.isPhoneLandscape()) return;
        drag = { y: event.global.y, offset, moved: false };
      });
      container.on("pointermove", (event) => {
        if (!drag) return;
        const delta = event.global.y - drag.y;
        if (Math.abs(delta) > 6) {
          drag.moved = true;
          suppressButtonTap(350);
          event.stopPropagation?.();
        }
        updateOffset(drag.offset - delta);
      });
      const endDrag = (event) => {
        if (drag?.moved) event?.stopPropagation?.();
        drag = null;
      };
      container.on("pointerup", endDrag);
      container.on("pointerupoutside", endDrag);
      container.on("wheel", (event) => {
        event.stopPropagation?.();
        updateOffset(offset + event.deltaY);
      });
    }
    return container;
  }

  inventoryWindowPanel(width, height) {
    const container = new PIXI.Container();
    const guided = Boolean(this.state.player?.tutorial);
    const activeTab = guided ? "items" : this.state.inventoryTab === "crafting" ? "crafting" : "items";
    const tabs = guided ? [["items", currentLanguage() === "pl" ? "Wybierz wskazany przedmiot" : "Choose the highlighted item"]] : [
      ["items", "Items"],
      ["crafting", "Crafting"],
    ];
    tabs.forEach(([tab, label], index) => {
      const selected = activeTab === tab;
      const tabButton = button(label, 116, 30, () => {
        this.state.inventoryTab = tab;
        localStorage.setItem("vibe-inventory-tab", tab);
        this.state.hoveredInventoryItemId = null;
        this.renderUi();
      }, {
        fill: selected ? 0x342717 : 0x17130f,
        stroke: selected ? 0xe0b95b : 0x6f5d3d,
        strokeAlpha: selected ? 0.9 : 0.42,
        fontSize: 11,
      });
      tabButton.position.set(index * 122, 0);
      container.addChild(tabButton);
    });
    const contentY = 42;
    if (activeTab === "crafting") {
      container.addChild(this.inventoryCraftingPanel(width, height - contentY, contentY));
      return container;
    }
    const panel = this.inventoryPanel(width, height - contentY);
    panel.position.set(0, contentY);
    container.addChild(panel);
    return container;
  }

  inventoryCraftingPanel(width, height, y = 0) {
    const container = new PIXI.Container();
    container.position.set(0, y);
    const recipes = this.state.player.available_recipes || [];
    container.addChild(this.craftingPanel(recipes, width, height));
    if (!recipes.length) {
      const empty = this.emptyState("No recipes yet.", "Learn professions, gather materials, or visit trainers to unlock crafting.");
      empty.position.set(14, 82);
      container.addChild(empty);
    }
    return container;
  }

  inventoryPanel(width, height) {
    const container = new PIXI.Container();
    const rawInventory = this.state.player.inventory || [];
    const inventory = this.filteredInventory(rawInventory);
    const equipment = this.state.player.equipment || [];
    const loadoutWidth = Math.min(390, Math.max(300, Math.floor(width * 0.42)));
    const gridX = loadoutWidth + 18;
    const gridWidth = width - gridX;
    container.addChild(this.characterLoadoutPanel(loadoutWidth, height, equipment));

    const carried = Number(this.state.player.inventory_weight_kg || 0);
    const capacity = Number(this.state.player.carry_capacity_kg || 50);
    const totalValue = rawInventory.reduce((sum, item) => sum + Number(item.value || 0) * Number(item.quantity || 0), 0);
    const heaviest = rawInventory.reduce((best, item) => (Number(item.total_weight_kg || 0) > Number(best?.total_weight_kg || 0) ? item : best), null);
    const sellPick = (this.state.player.sell_recommendations || [])[0];
    const gold = this.summaryPill(`Gold ${this.state.player.gold || 0}`, 0xe0b95b);
    gold.position.set(gridX, 0);
    const count = this.summaryPill(`${inventory.length} item types`, 0x7bd889);
    count.position.set(gridX + gold.widthHint + 8, 0);
    const value = this.summaryPill(`Value ${totalValue}g`, 0x8fb7ff);
    value.position.set(gridX + gold.widthHint + count.widthHint + 16, 0);
    container.addChild(gold, count, value);
    const load = resourceBar("Carry", carried, capacity, carried > capacity ? 0xc45445 : 0xe0b95b, Math.min(250, gridWidth));
    load.position.set(gridX, 36);
    container.addChild(load);
    if (capacity > 0 && carried / capacity >= 0.8) {
      const warning = text(carried > capacity ? "Over capacity: lighten your pack." : "Pack nearly full.", { fontSize: 11, fill: carried > capacity ? 0xffa07a : 0xffdf7a, fontWeight: "900" });
      warning.position.set(gridX + Math.min(260, gridWidth), 42);
      container.addChild(warning);
    }
    if (heaviest) {
      const sale = sellPick ? ` | Sell: ${sellPick.name} ${sellPick.total_value}g` : "";
      const heavy = text(`Heaviest: ${heaviest.name || this.formatIdLabel(heaviest.item_id)} (${this.formatWeight(heaviest.total_weight_kg)})${sale}`, { fontSize: 11, fill: 0xc8b698, fontWeight: "900", wordWrap: true, wordWrapWidth: Math.max(220, gridWidth - 260) });
      heavy.position.set(gridX + Math.min(260, gridWidth), 58);
      container.addChild(heavy);
    }

    const title = this.sectionTitle("Inventory grid");
    title.position.set(gridX, 82);
    container.addChild(title);
    const filters = ["all", "equipment", "consumable", "material", "quest"];
    let filterX = gridX + 120;
    filters.forEach((filter) => {
      const selected = this.state.inventoryFilter === filter;
      const item = button(this.formatIdLabel(filter), filter === "consumable" ? 88 : 72, 24, () => {
        this.state.inventoryFilter = filter;
        this.state.inventoryScrollOffset = 0;
        this.state.hoveredInventoryItemId = null;
        localStorage.setItem("vibe-inventory-filter", filter);
        this.renderUi();
      }, { fontSize: 10, fill: selected ? 0x2d351c : 0x1b1712, stroke: selected ? 0x7bd889 : 0x6f5d3d });
      item.position.set(filterX, 78);
      filterX += item.widthHint + 6;
      if (filterX + 72 <= gridX + gridWidth) container.addChild(item);
    });
    const sortOptions = ["rarity", "weight", "value", "density", "quantity", "name"];
    let sortX = gridX;
    sortOptions.forEach((sort) => {
      const selected = this.state.inventorySort === sort;
      const item = button(`Sort: ${this.formatIdLabel(sort)}`, sort === "quantity" ? 104 : 92, 24, () => {
        this.state.inventorySort = sort;
        this.state.inventoryScrollOffset = 0;
        this.state.hoveredInventoryItemId = null;
        localStorage.setItem("vibe-inventory-sort", sort);
        this.renderUi();
      }, { fontSize: 10, fill: selected ? 0x2d351c : 0x1b1712, stroke: selected ? 0x7bd889 : 0x6f5d3d });
      item.position.set(sortX, 108);
      sortX += item.widthHint + 6;
      if (sortX <= gridX + gridWidth + 6) container.addChild(item);
    });

    const layout = this.inventoryGridLayout(width, height, inventory.length);
    const grid = this.inventoryGridViewport(inventory, layout);
    grid.position.set(layout.gridX, layout.gridTop);
    container.addChild(grid);
    const hovered = this.inventoryHoveredVisibleItem(inventory, layout);
    if (hovered) {
      const tooltip = this.inventoryItemTooltip(hovered.item, Math.min(420, Math.max(300, gridWidth)), hovered.x, hovered.y, width, height);
      container.addChild(tooltip);
    }
    if (!inventory.length) {
      const empty = this.emptyState(rawInventory.length ? "No items match this filter." : "Inventory is empty.", rawInventory.length ? "Change the inventory filter or sorting mode." : "Loot, quest rewards, and shop purchases will appear here.");
      empty.position.set(layout.gridX, layout.gridTop);
      container.addChild(empty);
    }
    return container;
  }

  inventoryGridLayout(width, height, itemCount = 0) {
    const loadoutWidth = Math.min(390, Math.max(300, Math.floor(width * 0.42)));
    const gridX = loadoutWidth + 18;
    const gridWidth = width - gridX;
    const scrollbarGutter = 14;
    const gridContentWidth = Math.max(1, gridWidth - scrollbarGutter);
    const gridTop = 140;
    const gridBottom = height;
    const gridHeight = Math.max(90, gridBottom - gridTop);
    const gap = gridContentWidth < 440 ? 10 : 12;
    const targetCellWidth = gridContentWidth < 440 ? 112 : 132;
    const columns = Math.max(1, Math.floor((gridContentWidth + gap) / (targetCellWidth + gap)));
    const cellWidth = Math.floor((gridContentWidth - gap * (columns - 1)) / columns);
    const cellHeight = gridContentWidth < 440 ? 128 : 140;
    const rows = Math.max(1, Math.ceil(Math.max(0, itemCount) / columns));
    const contentHeight = rows * cellHeight + Math.max(0, rows - 1) * gap;
    const maxOffset = Math.max(0, contentHeight - gridHeight);
    this.state.inventoryScrollOffset = clamp(Number(this.state.inventoryScrollOffset || 0), 0, maxOffset);
    return {
      gridX,
      gridTop,
      gridWidth,
      gridContentWidth,
      gridHeight,
      gap,
      columns,
      cellWidth,
      cellHeight,
      contentHeight,
      maxOffset,
      offset: this.state.inventoryScrollOffset,
    };
  }

  inventoryGridViewport(inventory, layout) {
    const container = new PIXI.Container();
    container._equipmentInventoryDrop = true;
    container.eventMode = "static";
    container.hitArea = new PIXI.Rectangle(0, 0, layout.gridWidth, layout.gridHeight);
    if (!(inventory || []).length) {
      return container;
    }
    const content = new PIXI.Container();
    content.position.set(0, -layout.offset);
    (inventory || []).forEach((item, index) => {
      const col = index % layout.columns;
      const row = Math.floor(index / layout.columns);
      const rowY = row * (layout.cellHeight + layout.gap);
      if (rowY + layout.cellHeight < layout.offset - layout.gap || rowY > layout.offset + layout.gridHeight + layout.gap) {
        return;
      }
      const cell = this.inventoryGridCell(item, layout.cellWidth, layout.cellHeight);
      cell.position.set(col * (layout.cellWidth + layout.gap), rowY);
      content.addChild(cell);
    });
    const mask = new PIXI.Graphics().roundRect(0, 0, layout.gridContentWidth, layout.gridHeight, 6).fill(0xffffff);
    content.mask = mask;
    container.addChild(content, mask);
    if (layout.maxOffset > 0) {
      const trackWidth = 8;
      const trackX = layout.gridWidth - trackWidth;
      const track = new PIXI.Graphics()
        .roundRect(trackX, 0, trackWidth, layout.gridHeight, 4)
        .fill({ color: 0x1b1711, alpha: 0.82 });
      const thumbHeight = clamp((layout.gridHeight / layout.contentHeight) * layout.gridHeight, 34, layout.gridHeight);
      const thumbY = (layout.gridHeight - thumbHeight) * (layout.offset / layout.maxOffset);
      const thumb = new PIXI.Graphics()
        .roundRect(trackX, thumbY, trackWidth, thumbHeight, 4)
        .fill({ color: 0xd7aa5f, alpha: 0.9 });
      container.addChild(track, thumb);
    }
    return container;
  }

  inventoryHoveredVisibleItem(inventory, layout) {
    const index = (inventory || []).findIndex((item) => item.item_id === this.state.hoveredInventoryItemId);
    if (index < 0) {
      return null;
    }
    const row = Math.floor(index / layout.columns);
    const col = index % layout.columns;
    const localY = row * (layout.cellHeight + layout.gap) - layout.offset;
    if (localY + layout.cellHeight < 0 || localY > layout.gridHeight) {
      return null;
    }
    return {
      item: inventory[index],
      x: layout.gridX + col * (layout.cellWidth + layout.gap),
      y: layout.gridTop + localY,
    };
  }

  filteredInventory(inventory) {
    const filter = this.state.inventoryFilter || "all";
    const rarityRank = { quest: 5, epic: 4, rare: 3, uncommon: 2, common: 1 };
    const filtered = filter === "all" ? [...inventory] : inventory.filter((item) => item.type === filter);
    const nameOf = (item) => (item.name || this.formatIdLabel(item.item_id)).toLowerCase();
    const sort = this.state.inventorySort || "rarity";
    return filtered.sort((left, right) => {
      if (sort === "weight") {
        return (right.total_weight_kg || right.weight_kg || 0) - (left.total_weight_kg || left.weight_kg || 0) || nameOf(left).localeCompare(nameOf(right));
      }
      if (sort === "quantity") {
        return (right.quantity || 0) - (left.quantity || 0) || nameOf(left).localeCompare(nameOf(right));
      }
      if (sort === "value") {
        return ((right.value || 0) * (right.quantity || 0)) - ((left.value || 0) * (left.quantity || 0)) || nameOf(left).localeCompare(nameOf(right));
      }
      if (sort === "density") {
        const density = (item) => ((item.value || 0) * (item.quantity || 0)) / Math.max(0.01, item.total_weight_kg || item.weight_kg || 0.01);
        return density(right) - density(left) || nameOf(left).localeCompare(nameOf(right));
      }
      if (sort === "name") {
        return nameOf(left).localeCompare(nameOf(right));
      }
      return (rarityRank[right.rarity] || 0) - (rarityRank[left.rarity] || 0) || nameOf(left).localeCompare(nameOf(right));
    });
  }

  questObjectiveSummary(quest) {
    const objectives = quest.objectives || [];
    if (!objectives.length) return "";
    return objectives
      .slice(0, 2)
      .map((objective) => `${objective.current || 0}/${objective.required || 1} ${objective.text || this.formatIdLabel(objective.type)}`)
      .join(" | ");
  }

  journeySummaryEntries(player) {
    const activeQuests = this.activeStoryQuests();
    const completedQuests = (player.quest_log || []).filter((quest) => quest.status === "completed").length;
    const activeErrands = player.active_errands || [];
    const readyErrands = activeErrands.filter((errand) => (errand.current || 0) >= (errand.required || 1)).length;
    const achievements = player.achievements || [];
    const unlockedAchievements = achievements.filter((achievement) => achievement.unlocked).length;
    const nextAchievement = achievements
      .filter((achievement) => !achievement.unlocked)
      .sort((left, right) => (right.current / right.required) - (left.current / left.required))[0];
    const target = this.activeQuestOrErrandTarget();
    const carryRatio = Number(player.carry_capacity_kg || 1) > 0 ? Number(player.inventory_weight_kg || 0) / Number(player.carry_capacity_kg || 1) : 0;
    const readiness = player.readiness || {};
    const warningCount = (readiness.warnings || []).length;
    const restedRatio = Number(player.rested_xp_cap || 0) > 0 ? Number(player.rested_xp_bonus || 0) / Number(player.rested_xp_cap || 1) : 0;
    const recommendation = (player.recommendations || [])[0];
    const daily = (player.daily_activities || [])[0];
    const weekly = (player.weekly_activities || [])[0];
    const nextUnlock = (player.next_unlocks || [])[0];
    const routeHint = (player.route_hints || [])[0];
    const ownRank = (player.leaderboard || []).find((entry) => entry.is_self);
    return [
      { label: target ? `Next: ${target.entry.target_location || target.entry.title || this.formatIdLabel(target.entry.id)}` : "No tracked objective", color: target ? 0xe0b95b : 0x6f5d3d },
      { label: recommendation ? `Do: ${recommendation.label}` : "No recommendation", color: recommendation ? 0xffdf7a : 0x6f5d3d },
      { label: player.world_time?.label ? `Time: ${player.world_time.label}` : "World time unknown", color: 0xc8b698 },
      { label: player.weather?.label ? `Weather: ${player.weather.label}` : "Weather unknown", color: 0x8fb7ff },
      { label: routeHint ? `Route: ${routeHint.instruction}` : "No route hint", color: routeHint ? 0x7bd889 : 0x6f5d3d },
      { label: nextUnlock ? `Unlock: ${nextUnlock.label} Lv ${nextUnlock.required_level}` : "No upcoming unlocks", color: nextUnlock ? 0x8fb7ff : 0x6f5d3d },
      { label: daily ? `Daily: ${daily.label} ${daily.current}/${daily.required}` : "No daily focus", color: daily?.complete ? 0x7bd889 : 0x8fb7ff },
      { label: weekly ? `Weekly: ${weekly.label} ${weekly.current}/${weekly.required}` : "No weekly goals", color: weekly?.complete ? 0x7bd889 : 0x8fb7ff },
      { label: `${player.unread_mail_count || 0} unread mail, ${(player.mail || []).length} total`, color: player.unread_mail_count ? 0xffdf7a : 0xc8b698 },
      { label: `Bank ${(player.bank || []).length} stacks, ${this.formatWeight(player.bank_weight_kg || 0)} stored`, color: (player.bank || []).length ? 0x8fb7ff : 0x9f9276 },
      { label: `Market ${(player.market_listings || []).length} active listings`, color: (player.market_listings || []).length ? 0xffdf7a : 0x9f9276 },
      { label: player.guild ? `Guild: ${player.guild.name} Lv ${player.guild.level || 1} (${player.guild.xp || 0}/${player.guild.xp_to_next || 100})` : "No guild", color: player.guild ? 0x7bd889 : 0x9f9276 },
      { label: player.guild ? `Guild bank ${(player.guild.bank || []).length} stacks, ${this.formatWeight(player.guild.bank_weight_kg || 0)}` : "No guild bank", color: player.guild?.bank?.length ? 0x8fb7ff : 0x9f9276 },
      { label: player.guild ? `Guild perks ${(player.guild.perks || []).filter((perk) => perk.active).map((perk) => perk.label).join(", ") || "none"}` : "No guild perks", color: player.guild?.perks?.some((perk) => perk.active) ? 0xffdf7a : 0x9f9276 },
      { label: ownRank ? `Leaderboard #${ownRank.rank}, score ${ownRank.score}` : "No leaderboard rank", color: ownRank ? 0xffdf7a : 0x9f9276 },
      { label: player.world_event ? `Event: ${player.world_event.label} +${player.world_event.bonus_xp_percent}% ${this.formatIdLabel(player.world_event.bonus_activity_id)}` : "No world event", color: 0xd49cff },
      { label: `Power ${readiness.power_score || 0}, ${warningCount} warnings`, color: warningCount ? 0xffa07a : 0x7bd889 },
      { label: `${activeQuests.length} active quests, ${completedQuests} completed`, color: 0xe0b95b },
      { label: `${activeErrands.length} active errands, ${readyErrands} ready`, color: readyErrands ? 0x7bd889 : 0x8fb7ff },
      { label: `${(player.discovered_map_ids || []).length || 0} regions discovered`, color: 0x7bd889 },
      { label: `${player.adventure_summary?.completed_quests || 0} quests, ${player.adventure_summary?.completed_errands || 0} errands done`, color: 0xe0b95b },
      { label: `${unlockedAchievements}/${achievements.length} achievements unlocked`, color: 0xd49cff },
      { label: nextAchievement ? `Near: ${nextAchievement.title} ${nextAchievement.current}/${nextAchievement.required}` : "All visible achievements complete", color: 0xd49cff },
      { label: `Carry ${Math.round(carryRatio * 100)}% full`, color: carryRatio >= 0.8 ? 0xffa07a : 0xc8b698 },
      { label: `${(player.unlocked_card_ids || []).length} cards unlocked`, color: 0x8fb7ff },
      { label: `Rested XP ${Math.round(restedRatio * 100)}%`, color: restedRatio > 0 ? 0x8fb7ff : 0x9f9276 },
      { label: `${player.active_title?.label || "No title"} title active`, color: 0xffdf7a },
      { label: `${(player.bestiary || []).reduce((sum, entry) => sum + Number(entry.kills || 0), 0)} enemies defeated`, color: 0xffa07a },
      { label: `${(player.professions || []).filter((profession) => profession.level > 1 || profession.xp > 0).length} professions started`, color: 0x7bd889 },
      { label: `${(player.reputation_standings || Object.keys(player.reputation || {})).length} factions met`, color: 0xffdf7a },
    ];
  }

  craftingPanel(recipes, width, height) {
    const container = new PIXI.Container();
    container.addChild(panel(width, height, { fill: 0x111612, alpha: 0.86, stroke: 0x6ea86b, strokeAlpha: 0.72 }));
    const title = this.sectionTitle("Crafting");
    title.position.set(12, 10);
    const readyCount = recipes.filter((recipe) => recipe.available).length;
    const readyToggle = button(`Ready ${readyCount}`, 78, 24, () => {
      this.state.craftFilter = "ready";
      localStorage.setItem("vibe-craft-filter", "ready");
      this.renderUi();
    }, { fontSize: 10, fill: this.state.craftFilter === "ready" ? 0x2d351c : 0x1b1712, stroke: this.state.craftFilter === "ready" ? 0x7bd889 : 0x6f5d3d });
    readyToggle.position.set(width - 168, 8);
    const allToggle = button(`All ${recipes.length}`, 70, 24, () => {
      this.state.craftFilter = "all";
      localStorage.setItem("vibe-craft-filter", "all");
      this.renderUi();
    }, { fontSize: 10, fill: this.state.craftFilter === "all" ? 0x2d351c : 0x1b1712, stroke: this.state.craftFilter === "all" ? 0x7bd889 : 0x6f5d3d });
    allToggle.position.set(width - 82, 8);
    const professions = (this.state.player.professions || []).map((profession) => `${profession.name || this.formatIdLabel(profession.id)} ${profession.level} (${profession.xp}/${profession.xp_to_next})`).join(" | ");
    const professionText = text(professions || "No professions", { fontSize: 11, fill: 0xc8b698, fontWeight: "800", wordWrap: true, wordWrapWidth: width - 24 });
    professionText.position.set(12, 30);
    const craftTip = (this.state.player.crafting_recommendations || [])[0];
    const tipText = text(craftTip ? `Tip: ${craftTip.name} - ${craftTip.reason}` : "Tip: gather materials to unlock craftable recipes.", {
      fontSize: 10,
      fill: craftTip?.available ? 0x7bd889 : 0xffdf7a,
      fontWeight: "900",
      wordWrap: true,
      wordWrapWidth: width - 24,
    });
    tipText.position.set(12, 45);
    container.addChild(title, readyToggle, allToggle, professionText, tipText);
    const filteredRecipes = (this.state.craftFilter === "all" ? [...recipes] : recipes.filter((recipe) => recipe.available))
      .sort((left, right) => Number(Boolean(right.available)) - Number(Boolean(left.available)) || String(left.name).localeCompare(String(right.name)));
    const visible = filteredRecipes.slice(0, Math.max(1, Math.floor((height - 74) / 48)));
    visible.forEach((recipe, index) => {
      const y = 74 + index * 48;
      const output = `${recipe.output_quantity || 1}x ${recipe.output_item_name || this.formatIdLabel(recipe.output_item_id)}`;
      const inputs = Object.entries(recipe.inputs || {}).map(([itemId, qty]) => `${qty} ${this.formatIdLabel(itemId)}`).join(", ");
      const name = text(`${recipe.name} -> ${output}`, { fontSize: 12, fill: 0xf2e6c7, fontWeight: "900", wordWrap: true, wordWrapWidth: width - 118 });
      name.position.set(12, y);
      const detail = text(`${this.formatIdLabel(recipe.profession)} Lv ${recipe.required_profession_level || 1} | +${recipe.xp_reward || 0} XP | ${inputs || "No inputs"}`, { fontSize: 11, fill: recipe.available ? 0xc8b698 : 0xffa07a, wordWrap: true, wordWrapWidth: width - 118 });
      detail.position.set(12, y + 19);
      const craft = button("Craft", 78, 28, recipe.available ? () => this.craftRecipe(recipe.id) : null, {
        fill: recipe.available ? 0x1f2a19 : 0x1b1712,
        stroke: recipe.available ? 0x7bd889 : 0x5a4d37,
        fontSize: 11,
      });
      craft.position.set(width - 92, y + 4);
      container.addChild(name, detail, craft);
    });
    if (!visible.length) {
      const empty = text("No ready recipes.", { fontSize: 12, fill: 0xc8b698, fontWeight: "900" });
      empty.position.set(12, 64);
      container.addChild(empty);
    }
    return container;
  }

  characterLoadoutPanel(width, height, equipment) {
    const container = new PIXI.Container();
    container.addChild(panel(width, height, { fill: 0x11100d, alpha: 0.78, stroke: 0x7b643d }));
    const title = this.sectionTitle("Character loadout");
    title.position.set(14, 12);
    container.addChild(title);
    const bonusSummary = this.equipmentBonusSummary(equipment);
    if (bonusSummary) {
      const bonuses = text(bonusSummary, { fontSize: 10, fill: 0xffdf7a, fontWeight: "900", wordWrap: true, wordWrapWidth: width - 28 });
      bonuses.position.set(14, 34);
      container.addChild(bonuses);
    }
    const portraitSheet = this.assetTextures.get("profiles");
    if (portraitSheet) {
      const portrait = new PIXI.Sprite(this.textureRegion("profiles", 0, 0, portraitSheet.width / 6, portraitSheet.height / 3));
      portrait.anchor.set(0.5);
      portrait.position.set(width / 2, Math.min(236, Math.max(154, height * 0.42)));
      portrait.width = Math.min(118, width * 0.32);
      portrait.height = Math.min(118, width * 0.32);
      container.addChild(portrait);
    }
    const slotWidth = Math.max(70, Math.min(88, Math.floor((width - 40) / 3)));
    const slotHeight = Math.max(52, Math.min(68, Math.floor((height - 86) / 5)));
    const centerX = width / 2 - slotWidth / 2;
    const rightX = width - slotWidth - 14;
    const available = Math.max(slotHeight * 5 + 32, height - 72);
    const rowStep = Math.max(slotHeight + 8, Math.floor((available - slotHeight) / 4));
    const rows = Array.from({ length: 5 }, (_, index) => 54 + index * rowStep);
    const slots = [
      ["head", centerX, rows[0]],
      ["neck", 14, rows[0]],
      ["shoulders", rightX, rows[0]],
      ["weapon", 14, rows[2]],
      ["offhand", rightX, rows[1]],
      ["body", rightX, rows[2]],
      ["hands", 14, rows[3]],
      ["ring", 14, rows[1]],
      ["belt", 14, rows[4]],
      ["legs", rightX, rows[4]],
      ["feet", centerX, rows[4]],
      ["trinket", rightX, rows[3]],
    ];
    slots.forEach(([slot, x, y]) => {
      const equipped = equipment.find((item) => item.slot === slot);
      const slotView = this.equipmentSlot(slot, equipped, slotWidth, slotHeight);
      slotView.position.set(x, y);
      container.addChild(slotView);
    });
    return container;
  }

  equipmentBonusSummary(equipment) {
    const totals = {};
    (equipment || []).forEach((item) => {
      Object.entries(item.stat_bonuses || {}).forEach(([stat, value]) => {
        totals[stat] = (totals[stat] || 0) + Number(value || 0);
      });
      Object.entries(item.item_bonuses || {}).forEach(([bonus, value]) => {
        totals[bonus] = (totals[bonus] || 0) + Number(value || 0);
      });
    });
    const parts = Object.entries(totals)
      .filter(([, value]) => value)
      .map(([stat, value]) => `${this.formatIdLabel(stat)} +${value}`);
    return parts.length ? `Equipped bonuses: ${parts.slice(0, 4).join(", ")}` : "";
  }

  equippedItemForSlot(slot) {
    return (this.state.player?.equipment || []).find((item) => item.slot === slot && item.item_id) || null;
  }

  weaponFamilyLabel(weaponFamily) {
    const labels = {
      bow: "bow",
      sling: "sling",
      blade: "blade",
      shield: "shield",
      staff: "staff",
      focus: "focus",
    };
    return labels[weaponFamily] || this.formatIdLabel(weaponFamily || "weapon");
  }

  equipmentUnequipRestrictionLines(item) {
    if (!item?.item_id) {
      return [];
    }
    const view = this.state.cardDeckView || this.fallbackCardDeckView();
    const deck = view.active_deck || this.state.player?.card_deck || [];
    const deckCounts = this.countById(deck);
    const lines = [];
    const granted = new Set((item.granted_cards || []).map((card) => card.card_id).filter(Boolean));
    const temporaryDeckCards = [...granted]
      .filter((cardId) => deckCounts[cardId] > 0)
      .map((cardId) => this.cardById(view, cardId)?.name || this.formatIdLabel(cardId));
    if (temporaryDeckCards.length) {
      lines.push(`removes temporary deck card: ${temporaryDeckCards.slice(0, 3).join(", ")}`);
    }
    const weaponFamily = item.weapon_family;
    if (weaponFamily) {
      const blockedCards = Object.keys(deckCounts)
        .map((cardId) => this.cardById(view, cardId))
        .filter((card) => card?.weapon_family === weaponFamily)
        .map((card) => card.name || this.formatIdLabel(card.card_id));
      if (blockedCards.length) {
        lines.push(`cards require equipped ${this.weaponFamilyLabel(weaponFamily)}: ${blockedCards.slice(0, 3).join(", ")}`);
      }
    }
    return lines;
  }

  equipmentReplaceRestrictionLines(item) {
    if (!item?.slot || item.equipped) {
      return [];
    }
    const equipped = this.equippedItemForSlot(item.slot);
    return this.equipmentUnequipRestrictionLines(equipped);
  }

  equipmentSlot(slot, item, width, height) {
    const container = new PIXI.Container();
    container._equipmentSlotId = slot;
    const unequipLines = this.equipmentUnequipRestrictionLines(item);
    container.eventMode = "static";
    container.cursor = item?.item_id ? "help" : "default";
    container.hitArea = new PIXI.Rectangle(0, 0, width, height);
    container.on("pointerover", (event) => {
      event.stopPropagation?.();
      if (item?.item_id && this.state.hoveredEquipmentSlot !== slot) {
        this.state.hoveredEquipmentSlot = slot;
        this.renderUi();
      }
    });
    container.on("pointerout", (event) => {
      event.stopPropagation?.();
      if (this.state.hoveredEquipmentSlot === slot) {
        this.state.hoveredEquipmentSlot = null;
        this.renderUi();
      }
    });
    if (item?.item_id) {
      container.cursor = "grab";
      container.on("pointerdown", (event) => this.startEquipmentDrag(event, "equipped", item, slot));
    }
    container.addChild(panel(width, height, { fill: item?.item_id ? 0x201a12 : 0x0c0c0a, alpha: 0.86, stroke: item?.item_id ? 0xe0b95b : 0x5c4a32, strokeAlpha: 0.7 }));
    const title = text(this.equipmentSlotLabel(slot), { fontSize: 10, fill: 0xc8b698, fontWeight: "900" });
    title.anchor.set(0.5, 0);
    title.position.set(width / 2, 6);
    if (item?.item_id) {
      const texture = this.itemIconTexture(item);
      if (texture) {
        const icon = new PIXI.Sprite(texture);
        icon.anchor.set(0.5);
        icon.position.set(20, Math.min(height - 18, 38));
        icon.width = Math.min(34, height - 24);
        icon.height = Math.min(34, height - 24);
        container.addChild(icon);
      }
    }
    const name = text(item?.name || (currentLanguage() === "pl" ? "Pusty" : "Empty"), {
      fontSize: 9,
      fill: item?.name ? 0xf2e6c7 : 0x7d735e,
      fontWeight: "800",
      align: item?.item_id ? "left" : "center",
      wordWrap: true,
      wordWrapWidth: item?.item_id ? width - 48 : width - 12,
      lineHeight: 10,
    });
    name.anchor.set(item?.item_id ? 0 : 0.5, 0);
    name.position.set(item?.item_id ? 40 : width / 2, 22);
    const condition = item?.condition ? this.formatIdLabel(item.condition) : "";
    const repair = item?.repair_cost ? ` | Repair ${item.repair_cost}g` : "";
    const conditionColor = {
      pristine: 0x9de6a9,
      good: 0x7bd889,
      worn: 0xffdf7a,
      poor: 0xffa07a,
      broken: 0xff6b6b,
      empty: 0x7d735e,
    }[item?.condition || "empty"] || 0x9f9276;
    const detail = text(item?.durability != null ? `${condition} ${item.durability}%${repair}` : "", {
      fontSize: 9,
      fill: conditionColor,
      fontWeight: "800",
      align: "center",
      wordWrap: true,
      wordWrapWidth: width - 8,
    });
    detail.anchor.set(0.5, 0);
    detail.position.set(item?.item_id ? 20 : width / 2, height - 13);
    container.addChild(title, name, detail);
    if (item?.item_id) {
      const off = button(currentLanguage() === "pl" ? "Zdejmij" : "Unequip", Math.min(45, width - 42), 18, () => this.unequipInventorySlot(slot), { fill: 0x351816, stroke: 0xb85a43, fontSize: 8 });
      off.position.set(width - off.widthHint - 3, height - 21);
      container.addChild(off);
    }
    if (unequipLines.length) {
      const warning = text("!", { fontSize: 13, fill: 0xffd0c8, fontWeight: "900", stroke: { color: 0x050505, width: 3 } });
      warning.anchor.set(0.5);
      warning.position.set(width - 9, 10);
      container.addChild(warning);
    }
    if (item?.item_id && this.state.hoveredEquipmentSlot === slot && unequipLines.length) {
      const tooltipWidth = Math.min(260, Math.max(190, width * 2.6));
      const tooltipHeight = 42 + unequipLines.length * 18;
      const tooltip = new PIXI.Container();
      tooltip.position.set(Math.min(0, width - tooltipWidth), -tooltipHeight - 6);
      tooltip.addChild(panel(tooltipWidth, tooltipHeight, { fill: 0x140b0b, alpha: 0.98, stroke: 0xff8a6b, strokeAlpha: 0.9, radius: 6 }));
      const heading = text("Unequip restrictions", { fontSize: 12, fill: 0xffd0c8, fontWeight: "900" });
      heading.position.set(10, 8);
      tooltip.addChild(heading);
      unequipLines.slice(0, 4).forEach((line, index) => {
        const row = text(line, { fontSize: 10, fill: 0xf2e6c7, fontWeight: "800", wordWrap: true, wordWrapWidth: tooltipWidth - 20 });
        row.position.set(10, 28 + index * 18);
        tooltip.addChild(row);
      });
      container.addChild(tooltip);
    }
    return container;
  }

  equipmentSlotLabel(slot) {
    const labels = {
      offhand: "Off hand",
      body: "Armor",
      hands: "Gloves",
      feet: "Boots",
    };
    return labels[slot] || this.formatIdLabel(slot);
  }

  inventoryGridCell(item, width, height) {
    const container = new PIXI.Container();
    const rarityColors = { common: 0x8a7a5a, uncommon: 0x6ea86b, rare: 0x608fd3, epic: 0x9c6bd3, quest: 0xe0b95b };
    const color = rarityColors[item.rarity] || 0x8a7a5a;
    const guidedTarget = this.state.player?.tutorial?.target;
    const highlighted = guidedTarget?.kind === "item" && guidedTarget.id === item.item_id;
    container.addChild(panel(width, height, { fill: highlighted ? 0x342b12 : 0x18140f, alpha: 0.9, stroke: highlighted ? 0xffdf55 : color, strokeAlpha: highlighted ? 1 : 0.74 }));
    if (highlighted) {
      container.addChild(new PIXI.Graphics().roundRect(3, 3, width - 6, height - 6, 5).stroke({ color: 0xffef8a, width: 4, alpha: 0.9 }));
    }
    container.eventMode = "static";
    container.cursor = "help";
    container.hitArea = new PIXI.Rectangle(0, 0, width, height);
    container.on("pointerover", (event) => {
      event.stopPropagation?.();
      if (this.state.hoveredInventoryItemId !== item.item_id) {
        this.state.hoveredInventoryItemId = item.item_id;
        this.renderUi();
      }
    });
    container.on("pointerout", (event) => {
      event.stopPropagation?.();
      if (this.state.hoveredInventoryItemId === item.item_id) {
        this.state.hoveredInventoryItemId = null;
        this.renderUi();
      }
    });
    if (item.type === "equipment" && (!this.state.player?.tutorial || highlighted)) {
      container.cursor = "grab";
      container.on("pointerdown", (event) => this.startEquipmentDrag(event, "inventory", item, null));
    }
    const texture = this.itemIconTexture(item);
    if (texture) {
      const icon = new PIXI.Sprite(texture);
      icon.anchor.set(0.5, 0);
      const iconSize = Math.min(62, Math.max(50, Math.floor(width * 0.44)));
      icon.position.set(width / 2, 10);
      icon.width = iconSize;
      icon.height = iconSize;
      container.addChild(icon);
    }
    const qty = text(`x${item.quantity}`, { fontSize: 12, fill: 0xfff0c8, fontWeight: "900" });
    qty.anchor.set(1, 0);
    qty.position.set(width - 8, 7);
    const name = text(item.name || this.formatIdLabel(item.item_id), { fontSize: 12, fill: 0xf2e6c7, fontWeight: "900", align: "center", wordWrap: true, wordWrapWidth: width - 14 });
    name.anchor.set(0.5, 0);
    name.position.set(width / 2, 76);
    const weight = text(this.formatWeight(item.total_weight_kg || item.weight_kg), { fontSize: 10, fill: 0x9f9276, fontWeight: "800" });
    weight.anchor.set(0.5, 0);
    weight.position.set(width / 2, height - 52);
    container.addChild(qty, name, weight);
    const valueDensity = ((item.value || 0) * (item.quantity || 0)) / Math.max(0.01, item.total_weight_kg || item.weight_kg || 0.01);
    if (valueDensity >= 10 && item.value && height >= 138) {
      const dense = text(`${Math.round(valueDensity)}g/kg`, { fontSize: 10, fill: 0xffdf7a, fontWeight: "900" });
      dense.anchor.set(0.5, 0);
      dense.position.set(width / 2, height - 38);
      container.addChild(dense);
    }
    const actionAllowed = !this.state.player?.tutorial || highlighted;
    if (item.type === "equipment" && actionAllowed) {
      if (item.equipped) {
        const equipped = this.summaryPill("Equipped", 0xe0b95b);
        equipped.position.set((width - equipped.widthHint) / 2, height - 30);
        container.addChild(equipped);
        return container;
      }
      const equip = button("Equip", Math.min(58, width - 10), 22, () => this.equipInventoryItem(item), { fill: 0x1f2a19, stroke: 0x7bd889, fontSize: 10 });
      equip.position.set((width - equip.widthHint) / 2, height - 28);
      container.addChild(equip);
    } else if (item.type === "consumable" && actionAllowed) {
      const use = button("Use", Math.min(58, width - 10), 22, () => this.useInventoryItem(item), { fill: 0x1f2a19, stroke: 0x7bd889, fontSize: 10 });
      use.position.set((width - use.widthHint) / 2, height - 28);
      container.addChild(use);
    }
    return container;
  }

  inventoryItemTooltip(item, width, x, y, panelWidth, panelHeight) {
    width = Math.min(width, Math.max(240, panelWidth));
    const container = new PIXI.Container();
    const rarityColors = { common: 0x8a7a5a, uncommon: 0x6ea86b, rare: 0x608fd3, epic: 0x9c6bd3, quest: 0xe0b95b };
    const color = rarityColors[item.rarity] || 0x8a7a5a;
    const padding = 12;
    const compact = width < 390;
    const previewSize = compact ? 104 : 122;
    const previewPanelSize = previewSize + 18;
    const bodyWidth = compact ? width - padding * 2 : width - previewPanelSize - padding * 3;
    const title = text(item.name || this.formatIdLabel(item.item_id), { fontSize: 14, fill: 0xf2e6c7, fontWeight: "900", wordWrap: true, wordWrapWidth: bodyWidth });
    const metaParts = [this.formatIdLabel(item.rarity), this.formatIdLabel(item.type)];
    if (item.slot) metaParts.push(this.equipmentSlotLabel(item.slot));
    if (item.equipped) metaParts.push("Equipped");
    const meta = text(metaParts.join(" | "), { fontSize: 11, fill: 0x9f9276, fontWeight: "900", wordWrap: true, wordWrapWidth: bodyWidth });
    const content = new PIXI.Container();
    const requirements = this.inventoryItemRequirementRows(item);
    const bonuses = this.inventoryItemBonusRows(item);
    const cards = item.granted_cards || [];
    const textX = compact ? padding : previewPanelSize + padding * 2;
    let detailY = compact ? previewPanelSize + padding * 2 : padding + 2;
    title.position.set(textX, detailY);
    meta.position.set(textX, detailY + title.height + 4);
    content.addChild(title, meta);
    detailY += title.height + meta.height + 14;
    if (item.description) {
      const description = text(item.description, { fontSize: 12, fill: 0xc8b698, wordWrap: true, wordWrapWidth: bodyWidth });
      description.position.set(textX, detailY);
      content.addChild(description);
      detailY += description.height + 12;
    }
    detailY = this.drawInventoryDetailRows(content, requirements, textX, detailY, bodyWidth, "Requirements");
    const replaceRestrictions = this.equipmentReplaceRestrictionLines(item).map((line) => ({
      iconKey: "equipment",
      label: "Replacing current item",
      value: line,
      color: 0xffa07a,
    }));
    detailY = this.drawInventoryDetailRows(content, replaceRestrictions, textX, detailY, bodyWidth, "Replacing / Unequipping");
    const weaponDamageRows = this.inventoryItemWeaponDamageRows(item);
    detailY = this.drawInventoryDetailRows(content, weaponDamageRows, textX, detailY, bodyWidth, "Weapon Damage");
    detailY = this.drawInventoryDetailRows(content, bonuses, textX, detailY, bodyWidth, "Bonuses");
    detailY = this.drawInventoryGrantedCards(content, cards, textX, detailY, bodyWidth);
    const valueRows = [
      item.value ? { iconKey: "character_gold", label: "Value", value: `${item.value} gold`, color: 0xc8b698 } : null,
      { iconKey: "item", label: "Weight", value: `${this.formatWeight(item.weight_kg)} each | ${this.formatWeight(item.total_weight_kg)} total`, color: 0xc8b698 },
      Number(item.quantity || 0) > 1 ? { iconKey: "chest", label: "Quantity", value: `x${item.quantity}`, color: 0xc8b698 } : null,
      item.source_hint ? { iconKey: "document", label: "Source", value: item.source_hint, color: 0xc8b698 } : null,
    ].filter(Boolean);
    detailY = this.drawInventoryDetailRows(content, valueRows, textX, detailY, bodyWidth, "Item");
    const contentHeight = compact ? detailY : Math.max(previewPanelSize + padding, detailY);
    const height = Math.min(panelHeight - 8, Math.max(180, padding * 2 + contentHeight));
    const tooltipX = Math.min(Math.max(0, x), Math.max(0, panelWidth - width));
    const tooltipY = y + height + 10 > panelHeight ? Math.max(0, y - height - 8) : y + 8;
    container.addChild(panel(width, height, { fill: 0x0f0d0a, alpha: 0.98, stroke: color, strokeAlpha: 0.88 }));
    const previewX = compact ? (width - previewPanelSize) / 2 : padding;
    const previewY = padding;
    const preview = panel(previewPanelSize, previewPanelSize, { fill: 0x080706, alpha: 0.98, stroke: color, strokeAlpha: 0.95, radius: 6 });
    preview.position.set(previewX, previewY);
    const glow = new PIXI.Graphics()
      .roundRect(previewX + 6, previewY + 6, previewPanelSize - 12, previewPanelSize - 12, 6)
      .stroke({ color, width: 2, alpha: 0.35 });
    container.addChild(preview, glow);
    const texture = this.itemIconTexture(item);
    if (texture) {
      const icon = new PIXI.Sprite(texture);
      icon.anchor.set(0.5);
      icon.width = previewSize;
      icon.height = previewSize;
      icon.position.set(previewX + previewPanelSize / 2, previewY + previewPanelSize / 2);
      container.addChild(icon);
    }
    container.position.set(tooltipX, tooltipY);
    container.addChild(content);
    return container;
  }

  inventoryItemRequirementRows(item) {
    if (item.type !== "equipment") {
      return [];
    }
    const player = this.state.player || {};
    const rows = [];
    const requiredLevel = Number(item.required_level || 1);
    if (requiredLevel > 1) {
      const met = Number(player.level || 1) >= requiredLevel;
      rows.push({
        iconKey: "character_stat_speed",
        label: "Level",
        value: `${player.level || 1}/${requiredLevel}`,
        color: met ? 0xc8b698 : 0xff6b6b,
      });
    }
    if ((item.required_classes || []).length) {
      const met = item.required_classes.includes(player.class_name);
      rows.push({
        iconKey: "equipment",
        label: "Class",
        value: item.required_classes.map((className) => this.formatIdLabel(className)).join(", "),
        color: met ? 0xc8b698 : 0xff6b6b,
      });
    }
    return rows;
  }

  inventoryItemBonusRows(item) {
    const rows = [];
    Object.entries(item.stat_bonuses || {}).forEach(([stat, value]) => {
      if (!value) return;
      rows.push({
        iconKey: this.characterStatIconKey(stat),
        label: this.formatIdLabel(stat),
        value: `${Number(value) > 0 ? "+" : ""}${value}`,
        color: 0x9de6a9,
      });
    });
    Object.entries(item.item_bonuses || {}).forEach(([bonus, value]) => {
      if (!value) return;
      if (String(bonus).startsWith("attack_")) return;
      rows.push({
        iconKey: this.itemBonusIconKey(bonus),
        label: this.itemBonusLabel(bonus),
        value: `${Number(value) > 0 ? "+" : ""}${value}`,
        color: 0x9fc2ff,
      });
    });
    return rows;
  }

  inventoryItemWeaponDamageRows(item) {
    if (item.slot !== "weapon") {
      return [];
    }
    return Object.entries(item.item_bonuses || {})
      .filter(([bonus, value]) => String(bonus).startsWith("attack_") && Number(value || 0) !== 0)
      .map(([bonus, value]) => ({
        iconKey: this.itemBonusIconKey(bonus),
        label: this.itemBonusLabel(bonus),
        value: `${Number(value) > 0 ? "+" : ""}${value}`,
        color: 0xffdf7a,
      }));
  }

  itemBonusLabel(key) {
    const labels = {
      attack_melee: "Melee damage",
      attack_ranged: "Ranged damage",
      attack_mental: "Mental damage",
      attack_sling: "Sling attacks",
      attack_bow: "Bow attacks",
      block_melee: "Melee block",
      block_ranged: "Ranged block",
      block_mental: "Mental block",
      armor_melee: "Melee armor",
      armor_ranged: "Ranged armor",
      armor_mental: "Mental armor",
      resist_fire: "Fire resistance",
      resist_frost: "Frost resistance",
      resist_poison: "Poison resistance",
      resist_shadow: "Shadow resistance",
      max_mana: "Mana",
      max_stamina: "Stamina",
    };
    return labels[key] || this.formatIdLabel(key);
  }

  itemBonusIconKey(key) {
    if (key === "max_mana" || key === "max_stamina") {
      return this.characterStatIconKey(key);
    }
    if (String(key).startsWith("attack_")) {
      return "card_tag_attack";
    }
    if (String(key).startsWith("block_") || String(key).startsWith("armor_") || String(key).startsWith("resist_")) {
      return "card_tag_defense";
    }
    return "equipment";
  }

  drawInventoryDetailRows(container, rows, x, y, width, heading) {
    if (!rows.length) {
      return y;
    }
    const header = text(heading, { fontSize: 11, fill: 0xe0b95b, fontWeight: "900" });
    header.position.set(x, y);
    container.addChild(header);
    y += 18;
    rows.slice(0, 8).forEach((row) => {
      const iconTexture = this.assetTextures.get(row.iconKey);
      if (iconTexture) {
        const icon = new PIXI.Sprite(iconTexture);
        icon.width = 18;
        icon.height = 18;
        icon.position.set(x, y - 1);
        container.addChild(icon);
      }
      const label = text(row.label, { fontSize: 11, fill: 0x9f9276, fontWeight: "800", wordWrap: true, wordWrapWidth: Math.max(80, width * 0.5) });
      label.position.set(x + 24, y + 1);
      const value = text(row.value, { fontSize: 11, fill: row.color || 0xc8b698, fontWeight: "900", align: "right", wordWrap: true, wordWrapWidth: Math.max(90, width - 118) });
      value.anchor.set(1, 0);
      value.position.set(x + width, y + 1);
      container.addChild(label, value);
      y += Math.max(22, label.height + 4, value.height + 4);
    });
    return y + 6;
  }

  drawInventoryGrantedCards(container, cards, x, y, width) {
    if (!cards.length) {
      return y;
    }
    const header = text(cards.length === 1 ? "Granted card" : "Granted cards", { fontSize: 11, fill: 0xe0b95b, fontWeight: "900" });
    header.position.set(x, y);
    container.addChild(header);
    y += 20;
    cards.slice(0, 2).forEach((card) => {
      const rowHeight = 54;
      const row = panel(width, rowHeight, { fill: 0x14100d, alpha: 0.92, stroke: 0x6f5d3d, strokeAlpha: 0.55, radius: 5 });
      row.position.set(x, y);
      container.addChild(row);
      const texture = this.assetTextures.get(`card_${card.icon || card.card_id}`) || this.assetTextures.get(`card_${card.card_id}`) || this.assetTextures.get("card_default");
      if (texture) {
        const icon = new PIXI.Sprite(texture);
        icon.width = 42;
        icon.height = 42;
        icon.position.set(x + 6, y + 6);
        container.addChild(icon);
      }
      const cardName = text(card.name || this.formatIdLabel(card.card_id), { fontSize: 12, fill: 0xf2e6c7, fontWeight: "900", wordWrap: true, wordWrapWidth: width - 60 });
      cardName.position.set(x + 56, y + 7);
      const tags = [card.card_category, card.action_area || card.type, card.target].filter(Boolean).map((value) => this.formatIdLabel(value)).join(" | ");
      const values = [];
      if (Number(card.action_value || 0) > 0) values.push(`Value ${card.action_value}`);
      const blockValues = Object.entries(card.block_values || {}).filter(([, value]) => Number(value) > 0).map(([zone, value]) => `${this.formatIdLabel(zone)} ${value}`);
      if (blockValues.length) values.push(`Block ${blockValues.join(", ")}`);
      const cardMeta = text(`${tags}${values.length ? ` | ${values.join(" | ")}` : ""}`, { fontSize: 10, fill: 0xc8b698, fontWeight: "800", wordWrap: true, wordWrapWidth: width - 60 });
      cardMeta.position.set(x + 56, y + 29);
      container.addChild(cardName, cardMeta);
      y += rowHeight + 6;
    });
    if (cards.length > 2) {
      const more = text(`+${cards.length - 2} more granted cards`, { fontSize: 10, fill: 0x9f9276, fontWeight: "900" });
      more.position.set(x, y);
      container.addChild(more);
      y += 16;
    }
    return y + 6;
  }

  questsPanel(width, height) {
    const container = new PIXI.Container();
    const quests = this.state.player.quest_log || [];
    const active = this.activeStoryQuests();
    const done = quests.length - active.length;
    const activeErrands = this.state.player.active_errands || [];
    const readyErrands = activeErrands.filter((errand) => (errand.current || 0) >= (errand.required || 1));
    const activePill = this.summaryPill(`${active.length} active`, 0xe0b95b);
    const donePill = this.summaryPill(`${done} finished`, 0x7bd889);
    donePill.position.set(activePill.widthHint + 8, 0);
    const errandPill = this.summaryPill(`${activeErrands.length} errands`, 0x8fb7ff);
    errandPill.position.set(activePill.widthHint + donePill.widthHint + 16, 0);
    const trackerPill = this.summaryPill(this.state.questTrackerVisible ? "tracker on" : "tracker hidden", this.state.questTrackerVisible ? 0x7bd889 : 0x9f9276);
    trackerPill.position.set(activePill.widthHint + donePill.widthHint + errandPill.widthHint + 24, 0);
    container.addChild(activePill, donePill, errandPill, trackerPill);
    const filters = ["all", "story", "ready", "errands"];
    let filterX = 0;
    filters.forEach((filter) => {
      const selected = this.state.journalFilter === filter;
      const label = filter === "ready" ? `Ready ${readyErrands.length}` : this.formatIdLabel(filter);
      const item = button(label, filter === "ready" ? 86 : 74, 24, () => {
        this.state.journalFilter = filter;
        localStorage.setItem("vibe-journal-filter", filter);
        this.renderUi();
      }, { fontSize: 10, fill: selected ? 0x2d351c : 0x1b1712, stroke: selected ? 0x7bd889 : 0x6f5d3d });
      item.position.set(filterX, 34);
      filterX += item.widthHint + 6;
      container.addChild(item);
    });
    if (!this.state.questTrackerVisible) {
      const showTracker = button("Show tracker", 104, 28, () => {
        this.state.questTrackerVisible = true;
        localStorage.setItem("vibe-quest-tracker-visible", "true");
        this.renderUi();
      }, { fontSize: 11, fill: 0x1f2a19, stroke: 0x7bd889 });
      showTracker.position.set(width - 112, 2);
      container.addChild(showTracker);
    }
    const showStory = ["all", "story", "ready"].includes(this.state.journalFilter);
    const showErrands = ["all", "ready", "errands"].includes(this.state.journalFilter);
    const visibleStory = this.state.journalFilter === "ready" ? active.filter((quest) => (quest.objectives || []).some((objective) => objective.complete)) : active;
    const visibleErrands = this.state.journalFilter === "ready" ? readyErrands : activeErrands;
    let y = 70;
    if (showStory) {
      const storyBand = panel(width, 32, { fill: 0x241b0e, alpha: 0.94, stroke: 0xe0b95b, strokeAlpha: 0.8, radius: 4 });
      storyBand.position.set(0, y);
      const storyTitle = text("STORY QUESTS", { fontSize: 13, fill: 0xffdf7a, fontWeight: "900" });
      storyTitle.position.set(14, y + 8);
      container.addChild(storyBand, storyTitle);
      y += 42;
    }
    const rowHeight = 132;
    const visible = Math.max(0, Math.floor((height - y) / rowHeight));
    if (showStory) visibleStory.slice(0, visible).forEach((quest, index) => {
      const card = panel(width, 122, { fill: 0x17130f, alpha: 0.84, stroke: 0xa9854b, strokeAlpha: 0.62 });
      card.position.set(0, y + index * rowHeight);
      const title = text(quest.title || this.formatIdLabel(quest.quest_id), { fontSize: 16, fill: 0xf2e6c7, fontWeight: "900", wordWrap: true, wordWrapWidth: width - 180 });
      title.position.set(14, card.y + 10);
      const status = this.summaryPill(this.formatIdLabel(quest.status || "active"), 0xe0b95b);
      status.position.set(width - status.widthHint - 12, card.y + 10);
      const questId = quest.id || quest.quest_id;
      const pinned = questId && this.state.pinnedQuestId === questId;
      const pin = button(pinned ? "Pinned" : "Pin", 64, 26, () => {
        this.state.pinnedQuestId = pinned ? null : questId;
        if (this.state.pinnedQuestId) {
          localStorage.setItem("vibe-pinned-quest-id", this.state.pinnedQuestId);
        } else {
          localStorage.removeItem("vibe-pinned-quest-id");
        }
        this.state.questTrackerVisible = true;
        localStorage.setItem("vibe-quest-tracker-visible", "true");
        this.renderUi();
      }, { fontSize: 10, fill: pinned ? 0x2d351c : 0x201b14, stroke: pinned ? 0x7bd889 : 0x967341 });
      pin.position.set(width - status.widthHint - 84, card.y + 10);
      const stage = text(quest.stage_title || quest.current_stage || "Current step", { fontSize: 13, fill: 0xffe1a1, fontWeight: "900", wordWrap: true, wordWrapWidth: width - 28 });
      stage.position.set(14, card.y + 38);
      const note = text(quest.next_step || quest.description || quest.overview || "Progress is tracked by the story state.", { fontSize: 12, fill: 0xc8b698, wordWrap: true, wordWrapWidth: width - 28, lineHeight: 15 });
      note.position.set(14, card.y + 60);
      if (note.height > 31) {
        note.scale.set(Math.max(0.82, 31 / note.height));
      }
      const progress = this.questObjectiveSummary(quest);
      if (progress) {
        const objective = text(progress, { fontSize: 11, fill: 0x7bd889, fontWeight: "900", wordWrap: true, wordWrapWidth: width - 28 });
        objective.position.set(14, card.y + 86);
        container.addChild(objective);
      }
      const target = text(`Next: ${quest.target_location || quest.marker_map_name || quest.marker_label || "unknown location"}`, { fontSize: 12, fill: 0xffdf7a, fontWeight: "900", wordWrap: true, wordWrapWidth: width - 28 });
      target.position.set(14, card.y + 104);
      const rewards = (quest.reward_cards || []).map((card) => card.name || this.formatIdLabel(card.card_id)).slice(0, 2).join(", ");
      if (rewards) {
        const reward = text(`Rewards: ${rewards}`, { fontSize: 11, fill: 0xd49cff, fontWeight: "900", wordWrap: true, wordWrapWidth: width - 28 });
        reward.position.set(Math.max(14, width - 260), card.y + 86);
        container.addChild(reward);
      }
      container.addChild(card, title, status, pin, stage, note, target);
    });
    y += showStory ? visibleStory.slice(0, visible).length * rowHeight : 0;
    if (showStory && !visibleStory.length) {
      const empty = this.emptyState(this.state.journalFilter === "ready" ? "No ready story steps." : "No active story quests.", this.state.journalFilter === "ready" ? "Ready timed errands still appear below." : "Quest NPCs add story objectives here.");
      empty.position.set(0, y);
      container.addChild(empty);
      y += 88;
    }
    const remainingHeight = height - y;
    if (showErrands && remainingHeight > 86 && visibleErrands.length) {
      const divider = panel(width, 36, { fill: 0x101d2b, alpha: 0.96, stroke: 0x8fb7ff, strokeAlpha: 0.86, radius: 4 });
      divider.position.set(0, y + 4);
      const title = text("ERRANDS / ZLECENIA", { fontSize: 13, fill: 0xbfd8ff, fontWeight: "900" });
      title.position.set(14, y + 14);
      container.addChild(divider, title);
      y += 48;
      const rows = visibleErrands.slice(0, Math.max(1, Math.floor((height - y) / 90)));
      rows.forEach((errand, index) => {
        const rowY = y + index * 90;
        const canClaim = errand.status === "active" && (errand.current || 0) >= (errand.required || 1);
        const expiresSoon = errand.status === "active" && (errand.time_remaining_seconds || 0) <= 3600;
        const card = panel(width, 80, {
          fill: canClaim ? 0x142414 : expiresSoon ? 0x261713 : 0x101418,
          alpha: 0.86,
          stroke: canClaim ? 0x7bd889 : expiresSoon ? 0xffa07a : 0x8fb7ff,
          strokeAlpha: 0.72,
        });
        card.position.set(0, rowY);
        const titleText = text(errand.title || this.formatIdLabel(errand.id), { fontSize: 14, fill: 0xf2e6c7, fontWeight: "900", wordWrap: true, wordWrapWidth: width - 126 });
        titleText.position.set(12, rowY + 8);
        const current = `${errand.current || 0}/${errand.required || 1}`;
        const stateLabel = canClaim ? "Ready - return to NPC" : expiresSoon ? "Expires soon" : current;
        const detail = text(`${stateLabel} | ${this.formatDuration(errand.time_remaining_seconds)} | ${errand.target_location || "unknown location"}`, {
          fontSize: 11,
          fill: canClaim ? 0x7bd889 : expiresSoon ? 0xffa07a : 0x8fb7ff,
          fontWeight: "900",
          wordWrap: true,
          wordWrapWidth: width - 126,
        });
        detail.position.set(12, rowY + 30);
        const objective = text(errand.objective_text || errand.description || "Complete the listed objective.", {
          fontSize: 11,
          fill: 0xc8b698,
          wordWrap: true,
          wordWrapWidth: width - 126,
        });
        objective.position.set(12, rowY + 49);
        const reward = text(`${errand.reward_xp || 0} XP | ${errand.reward_gold || 0} gold`, { fontSize: 11, fill: 0xffdf7a, fontWeight: "900" });
        reward.position.set(width - 110, rowY + 10);
        const action = button("NPC", 82, 28, null, {
          fill: 0x1f2a19,
          stroke: canClaim ? 0x7bd889 : 0x5a4d37,
          fontSize: 11,
        });
        action.position.set(width - 94, rowY + 42);
        container.addChild(card, titleText, detail, objective, reward, action);
      });
    } else if (showErrands && !visibleStory.length) {
      const empty = this.emptyState(this.state.journalFilter === "ready" ? "Nothing ready to turn in." : "No active errands.", this.state.journalFilter === "ready" ? "Progress objectives, then return to the contractor NPC." : "Talk to contractor NPCs to accept timed errands.");
      empty.position.set(0, y);
      container.addChild(empty);
    }
    return container;
  }

  deckPanel(width, height) {
    const container = new PIXI.Container();
    const view = this.state.cardDeckView || this.fallbackCardDeckView();
    const selected = this.cardById(view, this.state.deckDetailCardId);
    if (selected) {
      container.addChild(this.deckDetailPanel(selected, width, height));
      return container;
    }

    const deckCounts = this.countById(view.active_deck || []);
    const collection = view.collection || [];
    const deckCount = (view.active_deck || []).length;
    const stats = this.deckSimpleStats(view);
    const status = panel(width, 70, { fill: 0x0b0f12, alpha: 0.88, stroke: 0x7f8aa0, strokeAlpha: 0.52, radius: 6 });
    const statusTitle = text(`Deck ${deckCount} cards`, { fontSize: 15, fill: 0xf2e6c7, fontWeight: "900" });
    statusTitle.position.set(16, 12);
    const statusLine = text(stats.join(" | "), { fontSize: 12, fill: 0xd8ccb0, fontWeight: "900", wordWrap: true, wordWrapWidth: width - 32 });
    statusLine.position.set(16, 38);
    container.addChild(status, statusTitle, statusLine);
    if (view.errors?.length) {
      const err = text(view.errors.join(" | "), { fontSize: 11, fill: 0xffa07a, wordWrap: true, wordWrapWidth: width - 32 });
      err.position.set(16, 58);
      container.addChild(err);
    }

    const activeCards = Object.entries(deckCounts).map(([cardId, count]) => {
      const card = this.cardById(view, cardId) || { card_id: cardId, name: this.formatIdLabel(cardId), owned_count: count };
      const ownedCount = Math.max(count, Number(card.owned_count || count || 0));
      return { card, count, ownedCount, availableCount: Math.max(0, ownedCount - count) };
    });
    const boardY = 90;
    const currentTitle = this.sectionTitle("Current deck");
    currentTitle.position.set(0, boardY);
    container.addChild(currentTitle);
    const deckGridHeight = Math.max(160, Math.min(260, Math.floor(height * 0.38)));
    container.addChild(this.deckCardGrid(activeCards, {
      width,
      height: deckGridHeight,
      cardWidth: width < 760 ? 106 : 132,
      cardHeight: width < 760 ? 146 : 176,
      y: boardY + 32,
      zone: "deck",
      scrollKey: "deck",
      view,
      emptyTitle: "Deck is empty.",
      emptyHint: "Add owned cards below to build the deck used in combat.",
    }));
    const collectionY = boardY + deckGridHeight + 44;
    const ownedTitle = this.sectionTitle("Owned cards");
    ownedTitle.position.set(0, collectionY);
    container.addChild(ownedTitle);
    container.addChild(this.deckCardGrid(collection.map((card) => {
      const count = deckCounts[card.card_id] || 0;
      const ownedCount = Math.max(count, Number(card.owned_count || 0));
      return { card, count, ownedCount, availableCount: Math.max(0, ownedCount - count) };
    }), {
      width,
      height: Math.max(120, height - collectionY - 34),
      cardWidth: width < 760 ? 106 : 128,
      cardHeight: width < 760 ? 146 : 170,
      y: collectionY + 32,
      zone: "collection",
      scrollKey: "collection",
      view,
      emptyTitle: "No owned cards.",
      emptyHint: "Skill unlocks, shops, equipment, and rewards add cards.",
    }));
    return container;
  }

  deckCardGrid(entries, options) {
    const container = new PIXI.Container();
    const scrollbarGutter = 14;
    const contentWidth = Math.max(1, options.width - scrollbarGutter);
    const gap = contentWidth < 760 ? 10 : 14;
    const columns = Math.max(1, Math.floor((contentWidth + gap) / (options.cardWidth + gap)));
    const rowCount = Math.max(1, Math.ceil((entries || []).length / columns));
    const contentHeight = rowCount * options.cardHeight + Math.max(0, rowCount - 1) * gap;
    const maxOffset = Math.max(0, contentHeight - options.height);
    const scrollKey = options.scrollKey || options.zone || "deck";
    this.state.deckScrollOffsets[scrollKey] = clamp(Number(this.state.deckScrollOffsets[scrollKey] || 0), 0, maxOffset);
    const offset = this.state.deckScrollOffsets[scrollKey];
    if (!(entries || []).length) {
      const empty = this.emptyState(options.emptyTitle, options.emptyHint);
      empty.position.set(0, options.y);
      container.addChild(empty);
      return container;
    }
    const viewport = new PIXI.Container();
    viewport.position.set(0, options.y);
    viewport.eventMode = "static";
    viewport.hitArea = new PIXI.Rectangle(0, 0, options.width, options.height);
    const content = new PIXI.Container();
    content.position.set(0, -offset);
    (entries || []).forEach((entry, index) => {
      const col = index % columns;
      const row = Math.floor(index / columns);
      const rowY = row * (options.cardHeight + gap);
      if (rowY + options.cardHeight < offset - gap || rowY > offset + options.height + gap) {
        return;
      }
      const tile = this.deckCardTile(entry.card, entry.count, {
        ...options,
        width: options.cardWidth,
        height: options.cardHeight,
        key: `${options.zone}:${entry.card.card_id}`,
        ownedCount: entry.ownedCount,
        availableCount: entry.availableCount,
      });
      tile.position.set(col * (options.cardWidth + gap), rowY);
      content.addChild(tile);
    });
    const mask = new PIXI.Graphics().roundRect(0, options.y, contentWidth, options.height, 5).fill(0xffffff);
    content.mask = mask;
    viewport.addChild(content);
    container.addChild(viewport, mask);
    if (maxOffset > 0) {
      const trackWidth = 8;
      const trackX = options.width - trackWidth;
      const track = new PIXI.Graphics()
        .roundRect(trackX, options.y, trackWidth, options.height, 4)
        .fill({ color: 0x1b1711, alpha: 0.82 });
      const thumbHeight = clamp((options.height / contentHeight) * options.height, 34, options.height);
      const thumbY = options.y + (options.height - thumbHeight) * (offset / maxOffset);
      const thumb = new PIXI.Graphics()
        .roundRect(trackX, thumbY, trackWidth, thumbHeight, 4)
        .fill({ color: 0xd7aa5f, alpha: 0.9 });
      container.addChild(track, thumb);
    }
    return container;
  }

  deckGridRegions(contentWidth, contentHeight) {
    const deckGridHeight = Math.max(160, Math.min(260, Math.floor(contentHeight * 0.38)));
    const boardY = 90;
    const collectionY = boardY + deckGridHeight + 44;
    return {
      deck: { x: 0, y: boardY + 32, width: contentWidth, height: deckGridHeight },
      collection: { x: 0, y: collectionY + 32, width: contentWidth, height: Math.max(120, contentHeight - collectionY - 34) },
    };
  }

  maxDeckGridScrollOffset(entries, width, height, cardWidth, cardHeight) {
    const contentWidth = Math.max(1, width - 14);
    const gap = contentWidth < 760 ? 10 : 14;
    const columns = Math.max(1, Math.floor((contentWidth + gap) / (cardWidth + gap)));
    const rows = Math.max(1, Math.ceil((entries || []).length / columns));
    const contentHeight = rows * cardHeight + Math.max(0, rows - 1) * gap;
    return Math.max(0, contentHeight - height);
  }

  deckCardTile(card, count, options) {
    const container = new PIXI.Container();
    const deck = this.state.cardDeckView?.active_deck || this.state.player?.card_deck || [];
    const ownedCount = Math.max(count, Number(options.ownedCount ?? card.owned_count ?? count ?? 0));
    const availableCount = Math.max(0, Number(options.availableCount ?? ownedCount - count));
    const canRemove = count > 0;
    const hovered = this.state.hoveredDeckCardKey === options.key;
    const unavailableReason = card.unavailable_reason || "";
    const unusable = Boolean(unavailableReason) || card.available === false;
    const addBlockedReason = this.deckAddRestriction(card, availableCount);
    const canAdd = !addBlockedReason;
    const allCopiesInDeck = options.zone === "collection" && count > 0 && availableCount <= 0;
    const grayed = allCopiesInDeck || (options.zone === "collection" && unusable);
    container._deckHoverKey = options.key;
    container.eventMode = "static";
    container.cursor = "pointer";
    container.hitArea = new PIXI.Rectangle(0, 0, options.width, options.height);
    container.on("pointerover", (event) => {
      event.stopPropagation?.();
      this.state.hoveredDeckCardKey = options.key;
      this.renderUi();
    });
    container.on("pointerout", (event) => {
      event.stopPropagation?.();
      if (this.state.hoveredDeckCardKey === options.key) {
        this.state.hoveredDeckCardKey = null;
        this.renderUi();
      }
    });
    container.on("pointerleave", (event) => {
      event.stopPropagation?.();
      if (this.state.hoveredDeckCardKey === options.key) {
        this.state.hoveredDeckCardKey = null;
        this.renderUi();
      }
    });
    this.drawDeckCardStack(container, count, options.width, options.height);
    const face = new PIXI.Container();
    face.position.set(options.width / 2, options.height / 2);
    this.drawCombatCardFace(face, card, { x: 0, y: 0, width: options.width, height: options.height, preview: true, compactText: true }, {});
    face.alpha = grayed ? 0.42 : 1;
    container.addChild(face);
    this.drawDeckTagChips(container, card, 8, options.height - 49, options.width - 16);
    if (options.zone === "collection" && unusable) {
      const unavailableVeil = new PIXI.Graphics()
        .roundRect(5, 5, options.width - 10, options.height - 10, 8)
        .fill({ color: 0x2a0808, alpha: 0.18 });
      const unavailableBorder = new PIXI.Graphics()
        .roundRect(1, 1, options.width - 2, options.height - 2, 9)
        .stroke({ color: 0xd24d3d, width: 3, alpha: 0.95 });
      container.addChild(unavailableVeil, unavailableBorder);
    }
    const badgeLabel = options.zone === "collection" ? `x${availableCount}` : count > 0 ? `x${count}` : "";
    if (badgeLabel) {
      const badge = panel(38, 24, { fill: 0x050505, alpha: 0.86, stroke: grayed ? 0x777777 : 0xffd46d, strokeAlpha: 0.95, radius: 12 });
      badge.position.set(options.width - 44, 6);
      const badgeText = text(badgeLabel, { fontSize: 13, fill: grayed ? 0xa0a0a0 : 0xfff1c7, fontWeight: "900" });
      badgeText.anchor.set(0.5);
      badgeText.position.set(options.width - 25, 18);
      container.addChild(badge, badgeText);
    }
    if (allCopiesInDeck || (options.zone === "collection" && unusable)) {
      const label = text(unusable ? "CANNOT USE" : "IN DECK", { fontSize: 12, fill: unusable ? 0xffd0c8 : 0xd8d8d8, fontWeight: "900", stroke: { color: 0x050505, width: 4 } });
      label.anchor.set(0.5);
      label.position.set(options.width / 2, options.height / 2);
      container.addChild(label);
    }
    if (hovered) {
      const overlay = new PIXI.Graphics()
        .roundRect(4, 4, options.width - 8, options.height - 8, 8)
        .fill({ color: 0x000000, alpha: 0.72 })
        .stroke({ color: 0xffd46d, width: 1.4, alpha: 0.72 });
      container.addChild(overlay);
      const buttonWidth = Math.max(64, options.width - 20);
      const details = this.deckOverlayButton("i", "Details", () => {
        this.state.deckDetailCardId = card.card_id;
        this.state.hoveredDeckCardKey = null;
        this.renderUi();
      }, 0x263247, 0x8fb7ff, buttonWidth);
      details.position.set(10, 12);
      container.addChild(details);
      if (unusable) {
        const reasonLines = this.cardRestrictionLines(card, availableCount);
        const reason = text(reasonLines.join("\n") || "Missing requirements", {
          fontSize: 11,
          fill: 0xffd0c8,
          fontWeight: "900",
          wordWrap: true,
          wordWrapWidth: options.width - 20,
          stroke: { color: 0x050505, width: 3 },
        });
        reason.position.set(10, 48);
        container.addChild(reason);
      }
      if (options.zone === "collection") {
        const addAction = canAdd
          ? () => this.updateCardDeck([...deck, card.card_id])
          : () => this.log(`[Deck] Cannot add ${card.name || this.formatIdLabel(card.card_id)}: ${addBlockedReason}.`);
        const add = this.deckOverlayButton("+", "Add", addAction, canAdd ? 0x1f2a19 : 0x222222, canAdd ? 0x7bd889 : 0x777777, buttonWidth);
        add.position.set(10, 46);
        if (unusable) {
          add.position.set(10, Math.min(options.height - 40, 100));
        }
        container.addChild(add);
      }
      if (options.zone === "deck") {
        const addAction = canAdd
          ? () => this.updateCardDeck([...deck, card.card_id])
          : () => this.log(`[Deck] Cannot add ${card.name || this.formatIdLabel(card.card_id)}: ${addBlockedReason}.`);
        const add = this.deckOverlayButton("+", "Add", addAction, canAdd ? 0x1f2a19 : 0x222222, canAdd ? 0x7bd889 : 0x777777, buttonWidth);
        add.position.set(10, 46);
        const remove = this.deckOverlayButton("-", "Remove", canRemove ? () => this.updateCardDeck(this.removeOneCard(deck, card.card_id)) : null, 0x2a1717, 0xb85a43, buttonWidth);
        remove.position.set(10, 80);
        container.addChild(add, remove);
      }
    }
    return container;
  }

  deckOverlayButton(icon, label, onPress, fill, stroke, width = 86) {
    return button(`${icon} ${label}`, width, 28, onPress, {
      fill: onPress ? fill : 0x1a1a1a,
      stroke: onPress ? stroke : 0x555555,
      fillText: onPress ? 0xf5e7c8 : 0x888888,
      fontSize: 11,
    });
  }

  drawDeckCardStack(container, count, width, height) {
    const copies = Math.min(3, Math.max(0, count - 1));
    for (let index = copies; index > 0; index -= 1) {
      const offset = index * 4;
      const back = new PIXI.Graphics()
        .roundRect(3 - offset, 5 - offset, width - 6, height - 6, 8)
        .fill({ color: 0x2b2319, alpha: 0.78 })
        .stroke({ color: 0xb99758, width: 1, alpha: 0.58 });
      back.rotation = -0.018 * index;
      container.addChild(back);
    }
  }

  drawDeckTagChips(container, card, x, y, maxWidth) {
    const tags = [
      [this.deckCardCategoryIconKey(card), this.formatIdLabel(card.card_category || "card"), 0xd8b971],
      [this.deckCardAreaIconKey(card.action_area || card.type), this.formatIdLabel(card.action_area || card.type || "any"), 0x8fb7ff],
      [this.deckTargetIconKey(card.target), this.formatIdLabel(card.target || "self"), 0x7bd889],
    ];
    let cursor = x;
    tags.forEach(([iconKey, label, color]) => {
      const chipWidth = this.deckTagChipWidth(label, 9);
      if (cursor + chipWidth > x + maxWidth) return;
      this.drawDeckTagChip(container, iconKey, label, color, cursor, y, chipWidth, 18, 9);
      cursor += chipWidth + 4;
    });
  }

  deckTagChipWidth(label, fontSize = 10) {
    return Math.min(94, Math.max(46, String(label || "").length * (fontSize * 0.58) + 30));
  }

  drawDeckTagChip(container, iconKey, label, color, x, y, width, height = 20, fontSize = 10) {
    const chip = panel(width, height, { fill: 0x0b0d0e, alpha: 0.74, stroke: color, strokeAlpha: 0.6, radius: Math.floor(height / 2) });
    chip.position.set(x, y);
    container.addChild(chip);
    const texture = this.assetTextures.get(iconKey);
    if (texture) {
      const icon = new PIXI.Sprite(texture);
      icon.anchor.set(0.5);
      icon.position.set(x + height / 2, y + height / 2);
      icon.width = height - 4;
      icon.height = height - 4;
      container.addChild(icon);
    }
    const labelText = text(String(label || ""), { fontSize, fill: 0xf2e6c7, fontWeight: "900" });
    labelText.anchor.set(0, 0.5);
    labelText.position.set(x + height + 3, y + height / 2);
    container.addChild(labelText);
  }

  deckCardCategoryIconKey(card) {
    const category = String(card.card_category || card.type || "").toLowerCase();
    if (category === "defense") return "card_tag_defense";
    if (category === "buff") return "card_tag_buff";
    if (category === "debuff") return "card_tag_debuff";
    return "card_tag_attack";
  }

  deckCardAreaIconKey(area) {
    const value = String(area || "").toLowerCase();
    if (value === "ranged") return "card_tag_ranged";
    if (value === "mental") return "card_tag_mental";
    return "card_tag_melee";
  }

  deckTargetIconKey(target) {
    const value = String(target || "").toLowerCase();
    if (value === "ally") return "card_tag_ally";
    if (value === "self") return "card_tag_self";
    return "card_tag_enemy";
  }

  deckDetailPanel(card, width, height) {
    const container = new PIXI.Container();
    const view = this.state.cardDeckView || this.fallbackCardDeckView();
    const deck = view.active_deck || this.state.player?.card_deck || [];
    const count = this.countById(deck)[card.card_id] || 0;
    const ownedCount = Math.max(count, Number(card.owned_count || 0));
    const availableCount = Math.max(0, ownedCount - count);
    const back = button("< Back", 86, 30, () => {
      this.state.deckDetailCardId = null;
      this.renderUi();
    }, { fill: 0x201b14, fontSize: 12 });
    back.position.set(0, 0);
    container.addChild(back);

    const compact = width < 760;
    const previewWidth = compact ? Math.min(220, Math.max(168, width * 0.48)) : Math.min(320, Math.max(250, width * 0.36));
    const previewHeight = Math.min(height - 58, Math.round(previewWidth * 1.38));
    const cardPreview = new PIXI.Container();
    cardPreview.position.set(previewWidth / 2, 58 + previewHeight / 2);
    this.drawCombatCardFace(cardPreview, card, { x: 0, y: 0, width: previewWidth, height: previewHeight, preview: true, compactText: true }, {});
    container.addChild(cardPreview);

    const textX = previewWidth + (compact ? 22 : 36);
    const rightWidth = Math.max(220, width - textX);
    const title = text(card.name || this.formatIdLabel(card.card_id), { fontSize: compact ? 18 : 23, fill: 0xf2e6c7, fontWeight: "900", wordWrap: true, wordWrapWidth: rightWidth });
    title.position.set(textX, 54);
    const flavor = text(card.description || "A reliable technique for the current build.", { fontSize: 12, fill: 0xc8b698, fontStyle: "italic", wordWrap: true, wordWrapWidth: rightWidth });
    flavor.position.set(textX, 86);
    container.addChild(title, flavor);
    this.drawDeckTagChips(container, card, textX, 122, rightWidth);
    const meta = text(`Deck ${count} | Owned ${ownedCount} | Available ${availableCount} | ${this.formatIdLabel(card.rarity)} | ${this.formatIdLabel(card.source_label || card.source || "unknown")}`, { fontSize: 11, fill: 0xa9c5e8, fontWeight: "900", wordWrap: true, wordWrapWidth: rightWidth });
    meta.position.set(textX, 148);
    container.addChild(meta);

    let formulaY = 178;
    const restrictionLines = this.cardRestrictionLines(card, availableCount);
    if (restrictionLines.length) {
      const restrictions = this.deckRestrictionBlock(restrictionLines, rightWidth);
      restrictions.position.set(textX, formulaY);
      container.addChild(restrictions);
      formulaY += restrictions.heightHint + 10;
    }
    const primary = this.deckFormulaBlock("Primary", card.primary_action, card, rightWidth);
    primary.position.set(textX, formulaY);
    container.addChild(primary);
    let nextY = formulaY + primary.heightHint + 10;
    if (card.secondary_action && Object.keys(card.secondary_action).length) {
      const secondary = this.deckFormulaBlock("Secondary", card.secondary_action, card, rightWidth);
      secondary.position.set(textX, nextY);
      container.addChild(secondary);
      nextY += secondary.heightHint + 10;
    }
    if (card.status) {
      const status = this.deckStatusBlock(card.status, card, rightWidth);
      status.position.set(textX, nextY);
      container.addChild(status);
      nextY += status.heightHint + 10;
    }
    const animation = this.deckAnimationPreview(card, Math.min(rightWidth, 420), Math.max(98, height - nextY - 76));
    animation.position.set(textX, nextY);
    container.addChild(animation);
    return container;
  }

  deckFormulaBlock(label, action, card, width) {
    const container = new PIXI.Container();
    const safeAction = action && typeof action === "object" ? action : {};
    const value = safeAction.kind === "heal" ? card.action_value || safeAction.base || 0 : card.damage_preview || card.action_value || safeAction.base || 0;
    const kind = this.formatIdLabel(safeAction.kind || card.card_category || "action");
    const area = this.formatIdLabel(safeAction.area || card.action_area || card.type || "any");
    const target = this.formatIdLabel(safeAction.target || card.target || "self");
    const height = 84;
    const frame = panel(width, height, { fill: 0x101418, alpha: 0.82, stroke: 0xd7aa5f, strokeAlpha: 0.58, radius: 6 });
    const title = text(`${label}: ${kind} ${value}`, { fontSize: 13, fill: 0xf2e6c7, fontWeight: "900" });
    title.position.set(12, 9);
    container.addChild(frame, title);
    const areaChipWidth = this.deckTagChipWidth(area, 10);
    this.drawDeckTagChip(container, this.deckCardAreaIconKey(safeAction.area || card.action_area), area, 0x8fb7ff, 12, 30, areaChipWidth, 20, 10);
    this.drawDeckTagChip(container, this.deckTargetIconKey(safeAction.target || card.target), target, 0x7bd889, 18 + areaChipWidth, 30, this.deckTagChipWidth(target, 10), 20, 10);
    const scaling = text(this.deckScalingFormulaText(safeAction, card), { fontSize: 11, fill: 0xffdf9a, fontFamily: "monospace", fontWeight: "900", wordWrap: true, wordWrapWidth: width - 24 });
    scaling.position.set(12, 54);
    container.addChild(scaling);
    container.heightHint = height;
    return container;
  }

  deckStatusBlock(status, card, width) {
    const container = new PIXI.Container();
    const safeStatus = status && typeof status === "object" ? status : {};
    const type = String(safeStatus.type || "status");
    const power = Math.max(0, Math.round(Number(safeStatus.power || 0)));
    const rounds = Math.max(0, Math.round(Number(safeStatus.rounds || 0)));
    const target = this.formatIdLabel(card.target || safeStatus.target || "target");
    const spec = this.combatStatusSpec?.(safeStatus) || { fill: 0x333333, stroke: 0xaaaaaa, icon: 0xffffff, name: this.formatIdLabel(type) };
    const height = 82;
    const frame = panel(width, height, { fill: 0x121018, alpha: 0.84, stroke: spec.stroke, strokeAlpha: 0.72, radius: 6 });
    container.addChild(frame);

    const icon = new PIXI.Container();
    icon.position.set(14, 14);
    this.drawCombatStatusIcon?.(icon, type, 14, 14, 10, spec.icon);
    container.addChild(icon);

    const title = text(`Applies ${spec.name}`, { fontSize: 13, fill: 0xf2e6c7, fontWeight: "900" });
    title.position.set(48, 10);
    const metaParts = [`Target ${target}`];
    if (rounds) metaParts.push(`${rounds} round${rounds === 1 ? "" : "s"}`);
    if (power) metaParts.push(`Power ${power}`);
    const meta = text(metaParts.join(" | "), { fontSize: 10, fill: 0xa9c5e8, fontWeight: "900", wordWrap: true, wordWrapWidth: width - 60 });
    meta.position.set(48, 30);
    const description = text(this.combatStatusDescription?.(safeStatus) || "Temporary combat effect.", {
      fontSize: 11,
      fill: 0xd8ccb0,
      wordWrap: true,
      wordWrapWidth: width - 24,
    });
    description.position.set(12, 52);
    container.addChild(title, meta, description);
    container.heightHint = height;
    return container;
  }

  deckScalingFormulaText(action, card = {}) {
    const base = Number(action?.base || 0);
    const multipliers = action?.stat_multipliers || {};
    const stats = this.state.player?.stats || {};
    const parts = Object.entries(multipliers).map(([stat, value]) => {
      const statValue = Number(stats[stat] || 0);
      const multiplier = Number(value);
      return `${statValue} ${this.statAbbreviation(stat)} x ${this.formatFormulaNumber(multiplier)}`;
    });
    const result = Number(card.damage_preview || card.action_value || 0);
    const symbol = action?.kind === "heal" ? "HEAL" : "DMG";
    if (!parts.length) {
      return `${symbol} = ${this.formatFormulaNumber(base)} = ${result}`;
    }
    return `${symbol} = ${this.formatFormulaNumber(base)} x (1 + ${parts.join(" + ")}) = ${result}`;
  }

  formatFormulaNumber(value) {
    return Number(value).toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
  }

  statAbbreviation(stat) {
    const labels = {
      strength: "STR",
      agility: "AGI",
      mind: "MND",
      magical_power: "POW",
      intelligence: "INT",
      endurance: "END",
      speed: "SPD",
    };
    return labels[stat] || String(stat || "").slice(0, 3).toUpperCase();
  }

  deckAnimationPreview(card, width, height) {
    const container = new PIXI.Container();
    const safeHeight = Math.max(92, Math.min(150, height));
    const frame = panel(width, safeHeight, { fill: 0x090b0d, alpha: 0.88, stroke: 0x6fa8ff, strokeAlpha: 0.5, radius: 6 });
    const title = text("Preview", { fontSize: 12, fill: 0xf2e6c7, fontWeight: "900" });
    title.position.set(12, 8);
    const scene = new PIXI.Container();
    container.addChild(frame, scene, title);
    this.drawDeckAnimationPreviewScene(scene, card, width, safeHeight, (performance.now() % 1800) / 1800);
    this.deckAnimationPreviewState = {
      layer: scene,
      card,
      width,
      safeHeight,
      lastFrameAt: performance.now(),
    };
    return container;
  }

  drawDeckAnimationPreviewScene(container, card, width, safeHeight, t) {
    const actorX = 58;
    const targetX = width - 58;
    const groundY = safeHeight - 12;
    const area = String(card.action_area || card.type || "melee");
    const actorShift = area === "melee" ? Math.sin(t * Math.PI) * 22 : 0;
    container.addChild(new PIXI.Graphics().ellipse(actorX + actorShift, groundY, 34, 8).fill({ color: 0x000000, alpha: 0.42 }));
    container.addChild(new PIXI.Graphics().ellipse(targetX, groundY, 34, 8).fill({ color: 0x000000, alpha: 0.42 }));
    this.drawSkeletalPlayerSprite(container, {
      equipment: this.state.player?.equipment || [],
    }, actorX + actorShift, groundY + 2, 0.34, {
      facing: 1,
      tilt: area === "mental" ? Math.sin(t * Math.PI * 2) * 0.035 : 0,
      pose: this.playerPoseForCard(card),
      animationProgress: t,
      actionAnimation: card.combat_animation || {},
      actionWeaponFamily: card.weapon_family || "",
    });
    this.drawDeckPreviewCombatSprite(container, "enemy_humanoid", "enemy", targetX, groundY + 2, 0.34, {
      facing: -1,
      impact: Math.sin(t * Math.PI) * (area === "melee" ? 0.07 : 0.035),
    });
    if (area === "ranged") {
      const p = clamp((t - 0.15) / 0.55, 0, 1);
      const x = actorX + (targetX - actorX) * p;
      const event = { action: card.card_id, action_type: "ranged", animation: card.combat_animation || {} };
      const projectileKey = this.combatProjectileKey(event);
      const projectileTexture = this.assetTextures.get(projectileKey);
      if (projectileTexture) {
        const projectile = new PIXI.Sprite(projectileTexture);
        projectile.anchor.set(0.5);
        projectile.position.set(x, groundY - 58 - Math.sin(p * Math.PI) * 24);
        projectile.width = projectileKey === "projectile_rock" ? 26 : 42;
        projectile.height = projectileKey === "projectile_rock" ? 26 : 22;
        projectile.rotation = -0.08;
        container.addChild(projectile);
      } else {
        container.addChild(new PIXI.Graphics().circle(x, groundY - 58 - Math.sin(p * Math.PI) * 24, 7).fill(0xffd46d));
      }
    } else if (area === "mental") {
      const pulse = 0.35 + Math.sin(t * Math.PI * 2) * 0.25;
      container.addChild(new PIXI.Graphics().circle(targetX, groundY - 68, 24 + pulse * 22).stroke({ color: 0x9b83e6, width: 4, alpha: 0.75 - pulse * 0.4 }));
    } else {
      const p = Math.sin(t * Math.PI);
      container.addChild(new PIXI.Graphics().moveTo(actorX + 28 + p * 30, groundY - 86).lineTo(actorX + 58 + p * 32, groundY - 42).stroke({ color: 0xffe59a, width: 5, alpha: 0.9 }));
    }
    const value = card.damage_preview || card.action_value || 0;
    if (value) {
      const float = Math.sin(t * Math.PI);
      const number = text(String(value), { fontSize: 22, fill: 0xffdf8a, fontWeight: "900", stroke: { color: 0x050505, width: 4 } });
      number.anchor.set(0.5);
      number.alpha = 0.35 + float * 0.65;
      number.position.set(targetX, groundY - 94 - float * 18);
      container.addChild(number);
    }
  }

  drawDeckPreviewCombatSprite(container, visualKey, side, x, y, scale, options = {}) {
    const texture = this.combatTexture(visualKey, side);
    if (!texture) {
      const color = side === "player" ? 0x5f8fd1 : 0x6a3229;
      container.addChild(new PIXI.Graphics().circle(x, y - 44, 18).fill(color).stroke({ color: 0xdfffea, width: 2 }));
      return;
    }
    const sprite = new PIXI.Sprite(texture);
    sprite.anchor.set(0.5, 1);
    sprite.width = 150 * scale;
    sprite.height = 220 * scale;
    sprite.position.set(x, y);
    sprite.scale.x *= options.facing || 1;
    sprite.rotation = Number(options.tilt || 0) + Number(options.impact || 0);
    container.addChild(sprite);
  }

  playerPoseForCard(card) {
    const configured = String(card?.combat_animation?.clip || "");
    if (this.playerSkeletonClipIds.has(configured)) return configured;
    const actionArea = String(card?.action_area || card?.type || "");
    const weaponFamily = String(card?.weapon_family || "");
    const category = String(card?.card_category || "");
    const primaryKind = String(card?.primary_action?.kind || "");
    if (card?.card_id === "flee") return "flee";
    if (category === "defense" || primaryKind === "heal" && actionArea !== "mental") return "guard";
    if (weaponFamily === "bow") return "bow_shot";
    if (weaponFamily === "sling") return "sling_shot";
    if (actionArea === "ranged") return "throw_overhand";
    if (actionArea === "mental" || primaryKind === "heal") return "cast_attack";
    return "melee_strike";
  }

  playerPoseForCombatEvent(event) {
    const configured = String(event?.animation?.clip || "");
    if (this.playerSkeletonClipIds.has(configured)) return configured;
    const actionType = String(event?.action_type || "");
    const key = String(event?.animation?.key || event?.action || "").toLowerCase();
    const result = String(event?.result || "");
    if (result === "escaped" || key === "flee") return "flee";
    if (result === "stunned") return "hurt";
    if (key.includes("block") || key.includes("defend") || key.includes("shield") || key.includes("guard") || key.includes("interpose")) return "guard";
    if (key.includes("bow") || key.includes("shoot") || key.includes("shot") || key.includes("arrow") || key.includes("falcon") || key.includes("snare")) return "bow_shot";
    if (actionType === "ranged") return "throw_overhand";
    if (actionType === "mental") return "cast_attack";
    return "melee_strike";
  }

  shouldDrawSkeletalEquipment(item, requestedPose, options = {}) {
    const slot = String(item?.slot || "");
    if (slot !== "weapon") {
      return true;
    }
    const actionWeaponFamily = String(options.actionWeaponFamily || "");
    if (actionWeaponFamily) {
      return String(item?.weapon_family || "") === actionWeaponFamily;
    }
    if (options.animationProgress !== null && options.animationProgress !== undefined && /^(bow|sling|throw|cast|shout)/.test(requestedPose)) {
      return false;
    }
    return true;
  }

  skeletalWorldTransforms(animationName, progress) {
    return skeletonWorldTransforms(playerSkeletalVisuals, sampleSkeletonPose(playerSkeletalVisuals, animationName, progress));
  }

  skeletalEquipmentPart(item) {
    if (!item?.item_id) {
      return null;
    }
    const slot = String(item.slot || "");
    const attachment = playerSkeletalVisuals.slotAttachments[slot];
    if (!attachment) {
      return null;
    }
    const assetKey = `skeletal_equipment_${item.item_id}_${attachment.bone}`;
    if (!this.assetTextures.has(assetKey)) {
      return null;
    }
    return {
      bone: attachment.bone,
      assetKey,
      anchor: attachment.anchor,
      order: attachment.order,
      item,
    };
  }

  legacyPlayerPose(requestedPose) {
    if (requestedPose === "idle" || requestedPose === "walk" || requestedPose === "hurt" || requestedPose === "guard") return requestedPose;
    if (requestedPose === "flee") return "hurt";
    if (requestedPose.startsWith("bow")) return "bow";
    if (requestedPose.startsWith("sling") || requestedPose.startsWith("throw")) return "throw";
    if (requestedPose.startsWith("cast") || requestedPose === "shout") return "cast";
    return "melee";
  }

  compiledClipProgress(clip, progress, animation = {}) {
    const action = clip.events.find((event) => event.name === "action");
    if (!action || clip.duration <= 0) return progress;
    const actionProgress = clamp(action.at / clip.duration, 0.001, 0.999);
    const phase = String(action.data?.phase || "effect");
    const configuredTarget = phase === "release"
      ? animation.projectile_start
      : animation.impact_at ?? animation.damage_at ?? animation.number_at;
    const fallbackTarget = phase === "release" ? 0.15 : phase === "escape" ? 0.5 : 0.68;
    const target = clamp(Number.isFinite(Number(configuredTarget)) ? Number(configuredTarget) : fallbackTarget, 0.001, 0.999);
    if (progress <= target) return progress / target * actionProgress;
    return actionProgress + (progress - target) / (1 - target) * (1 - actionProgress);
  }

  drawCompiledSkeletalPlayerSprite(container, fighter, x, y, scale, options = {}) {
    const template = this.playerSkeletonTemplate;
    if (!template) return false;
    const definition = template.definition;
    const requestedClip = String(options.pose || "idle");
    const clipId = this.playerSkeletonClipIds.has(requestedClip) ? requestedClip : "idle";
    const clip = definition.clips.find((candidate) => candidate.id === clipId);
    if (!clip) return false;
    const hasProgress = options.animationProgress !== null && options.animationProgress !== undefined;
    const progress = clamp(Number(options.animationProgress) || 0, 0, 1);
    const time = hasProgress
      ? this.compiledClipProgress(clip, progress, options.actionAnimation) * clip.duration
      : (performance.now() / 1000) % clip.duration;
    const frame = sampleClipPose(definition, clipId, time);
    const view = template.createInstance();
    view.apply(frame);
    const equipment = sortEquipmentForSkinning(fighter?.equipment || []);
    for (const item of equipment) {
      const slots = skeletalEquipmentSkins[item?.item_id];
      const textures = this.playerEquipmentSkinTextures.get(item?.item_id);
      if (!slots || !textures) continue;
      for (const slotId of slots) {
        const frameId = view.frameForSlot(slotId);
        const texture = frameId ? textures[`${item.item_id}/${frameId}`] : null;
        if (texture) view.setSlotTexture(slotId, texture);
      }
    }
    const skeleton = view.container;
    const skeletonScale = scale * 0.48;
    const facing = Number(options.facing || 1);
    skeleton.position.set(x, y - this.playerSkeletonGroundY * skeletonScale);
    skeleton.scale.set(skeletonScale * facing, skeletonScale);
    skeleton.rotation = Number(options.tilt || 0) + Number(options.impact || 0);
    skeleton.alpha = options.alpha ?? 1;

    const equipmentParts = equipment
      .filter((item) => this.shouldDrawSkeletalEquipment(item, clipId, options))
      .filter((item) => !skeletalEquipmentSkins[item?.item_id])
      .map((item) => this.skeletalEquipmentPart(item))
      .filter(Boolean)
      .sort((left, right) => Number(left.order || 0) - Number(right.order || 0));
    for (const part of equipmentParts) {
      const texture = this.assetTextures.get(part.assetKey);
      const bone = this.playerSkeletonBoneIndices.get(part.bone);
      if (!texture || bone === undefined) continue;
      const offset = bone * 6;
      const sprite = new PIXI.Sprite(texture);
      sprite.anchor.set(Number(part.anchor?.x ?? 0.5), Number(part.anchor?.y ?? 0.5));
      sprite.setFromMatrix(new PIXI.Matrix(
        frame.world[offset], frame.world[offset + 1], frame.world[offset + 2],
        frame.world[offset + 3], frame.world[offset + 4], frame.world[offset + 5],
      ));
      sprite.zIndex = Number(part.order || 0);
      skeleton.addChild(sprite);
    }
    skeleton.sortChildren();
    container.addChild(skeleton);
    return true;
  }

  drawCohesiveCombatPlayerSprite(container, fighter, x, y, scale, options = {}) {
    const texture = this.assetTextures.get("combat_player_adventurer_base");
    if (!texture) {
      return false;
    }
    const pose = String(options.pose || "idle");
    const progress = options.animationProgress === null || options.animationProgress === undefined ? 0 : clamp(Number(options.animationProgress) || 0, 0, 1);
    const strike = Math.sin(progress * Math.PI);
    const facing = Number(options.facing || 1);
    const sprite = new PIXI.Sprite(texture);
    sprite.anchor.set(0.5, 1);
    sprite.width = 150 * scale;
    sprite.height = 225 * scale;
    let offsetX = 0;
    let offsetY = 0;
    let rotation = 0;
    if (pose === "melee") {
      offsetX = 12 * strike * scale;
      offsetY = -2 * strike * scale;
      rotation = 0.035 * strike;
    } else if (pose === "throw" || pose === "bow") {
      offsetX = 5 * strike * scale;
      offsetY = -2 * strike * scale;
      rotation = -0.025 * strike;
    } else if (pose === "cast") {
      offsetY = -5 * strike * scale;
      rotation = 0.02 * Math.sin(progress * Math.PI * 2);
    } else if (pose === "hurt") {
      offsetX = -7 * strike * scale;
      rotation = -0.05 * strike;
    } else if (pose === "idle" && (options.animationProgress === null || options.animationProgress === undefined)) {
      offsetY = Math.sin(performance.now() / 450) * 0.8 * scale;
    }
    sprite.position.set(x + offsetX * facing, y + offsetY);
    sprite.scale.x *= facing;
    sprite.rotation = Number(options.tilt || 0) + Number(options.impact || 0) + rotation * facing;
    sprite.alpha = options.alpha ?? 1;
    container.addChild(sprite);
    return true;
  }

  drawSkeletalPlayerSprite(container, fighter, x, y, scale, options = {}) {
    const requestedPose = options.pose || "idle";
    if (this.drawCompiledSkeletalPlayerSprite(container, fighter, x, y, scale, options)) return;
    const legacyPose = this.legacyPlayerPose(requestedPose);
    const baseParts = playerSkeletalVisuals.slots
      .filter((part) => this.assetTextures.has(part.assetKey))
      .sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
    if (!baseParts.length) {
      if (!this.drawCohesiveCombatPlayerSprite(container, fighter, x, y, scale, { ...options, pose: legacyPose })) {
        this.drawDeckPreviewCombatSprite(container, this.heroVisualKeyForPlayer(fighter), "player", x, y, scale, options);
      }
      return;
    }
    const transforms = this.skeletalWorldTransforms(legacyPose, options.animationProgress);
    const equipmentParts = (fighter?.equipment || [])
        .filter((item) => this.shouldDrawSkeletalEquipment(item, requestedPose, options))
        .map((item) => this.skeletalEquipmentPart(item))
        .filter(Boolean)
        .sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
    const facing = Number(options.facing || 1);
    const skeletonScale = scale * Number(playerSkeletalVisuals.renderScale || 1);
    const drawPart = (part) => {
      const texture = this.assetTextures.get(part.assetKey);
      const bone = transforms[part.bone];
      if (!texture || !bone) {
        return;
      }
      const sprite = new PIXI.Sprite(texture);
      sprite.anchor.set(Number(part.anchor?.x ?? 0.5), Number(part.anchor?.y ?? 0.5));
      const partScale = skeletonScale * Number(part.scale || 1) * bone.scale;
      sprite.scale.set(partScale * facing, partScale);
      sprite.position.set(x + bone.x * skeletonScale * facing, y + bone.y * skeletonScale);
      sprite.rotation = Number(options.tilt || 0) + Number(options.impact || 0) + bone.rotation * facing;
      sprite.alpha = options.alpha ?? 1;
      container.addChild(sprite);
    };
    baseParts.forEach(drawPart);
    equipmentParts.forEach(drawPart);
  }

  deckPreviewHeroVisualKey() {
    const className = String(this.state.player?.class_name || "wanderer");
    if (["ranger", "assassin"].includes(className)) return "hero_ranged";
    if (["fire_wizard", "mystic", "priest", "necromancer", "alchemist"].includes(className)) return "hero_caster";
    return "hero_melee";
  }

  cardFormulaObjectText(value) {
    if (!value || typeof value !== "object" || !Object.keys(value).length) {
      return "none";
    }
    const parts = [];
    if (value.area) parts.push(`area ${value.area}`);
    if (Array.isArray(value.preferred_areas) && value.preferred_areas.length) parts.push(`areas ${value.preferred_areas.join("/")}`);
    if (value.value !== undefined) parts.push(`value ${value.value}`);
    if (value.formula) parts.push(String(value.formula));
    return parts.join(" | ") || "none";
  }

  fallbackCardDeckView() {
    const cards = (this.state.player.unlocked_card_ids || []).map((cardId) => ({
      card_id: cardId,
      name: this.formatIdLabel(cardId),
      description: "",
      type: "card",
      target: "enemy",
      effect: "",
      damage_preview: 0,
      damage_preview_by_target: {},
      action_value: 0,
      action_value_by_target: {},
      block_values: {},
      status: null,
      summary: "",
      rarity: "common",
      defense: {},
      copy_limit: 3,
      owned_count: 1,
      available: true,
      unavailable_reason: null,
    }));
    return {
      collection: cards,
      active_deck: this.state.player.card_deck || [],
      errors: [],
    };
  }

  countById(values) {
    return values.reduce((counts, value) => {
      counts[value] = (counts[value] || 0) + 1;
      return counts;
    }, {});
  }

  cardFieldSummary(cards, field) {
    const counts = (cards || []).reduce((acc, card) => {
      const value = card[field] || "unknown";
      acc[value] = (acc[value] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts)
      .sort((left, right) => right[1] - left[1] || String(left[0]).localeCompare(String(right[0])))
      .slice(0, 3)
      .map(([value, count]) => `${this.formatIdLabel(value)} ${count}`)
      .join(", ");
  }

  deckSimpleStats(view) {
    const activeDeck = view.active_deck || [];
    const deckCards = activeDeck.map((cardId) => this.cardById(view, cardId)).filter(Boolean);
    const attackCards = deckCards.filter((card) => card.card_category === "attack" || ["melee", "ranged", "mental", "physical"].includes(card.type)).length;
    const blockCards = deckCards.filter((card) => Object.values(card.defense || {}).some((value) => Number(value) > 0)).length;
    const buffs = deckCards.filter((card) => card.card_category === "buff").length;
    const debuffs = deckCards.filter((card) => card.card_category === "debuff").length;
    const areaCount = (area) => deckCards.filter((card) => (card.action_area || card.type) === area).length;
    return [
      `Attacks ${attackCards}`,
      `Blocks ${blockCards}`,
      `Melee ${areaCount("melee")}`,
      `Ranged ${areaCount("ranged")}`,
      `Mental ${areaCount("mental")}`,
      `Buffs ${buffs}`,
      `Debuffs ${debuffs}`,
    ];
  }

  cardById(view, cardId) {
    if (!cardId) return null;
    return (view.collection || []).find((card) => card.card_id === cardId) || null;
  }

  cardRestrictionLines(card, availableCount = null) {
    if (!card) {
      return [];
    }
    const lines = [];
    if (card.unavailable_reason) {
      lines.push(`Blocked: ${card.unavailable_reason}`);
    }
    if (Number(card.required_level || 1) > 1) {
      lines.push(`Requires level ${card.required_level}`);
    }
    if ((card.required_classes || []).length) {
      lines.push(`Requires class: ${card.required_classes.map((className) => this.formatIdLabel(className)).join(", ")}`);
    }
    if (card.weapon_family) {
      lines.push(`Requires equipped ${this.weaponFamilyLabel(card.weapon_family)}`);
    }
    if (availableCount != null && Number(availableCount) <= 0) {
      lines.push("No available copy outside the current deck");
    }
    return [...new Set(lines)];
  }

  deckAddRestriction(card, availableCount) {
    if (card?.unavailable_reason) {
      return card.unavailable_reason;
    }
    if (Number(availableCount || 0) <= 0) {
      return "no available copy outside the current deck";
    }
    return "";
  }

  deckRestrictionBlock(lines, width, title = "Restrictions") {
    const visibleLines = [...new Set(lines || [])].filter(Boolean);
    const container = new PIXI.Container();
    const height = visibleLines.length ? 34 + visibleLines.length * 18 : 0;
    if (!height) {
      container.heightHint = 0;
      return container;
    }
    container.addChild(panel(width, height, { fill: 0x1b0f0d, alpha: 0.88, stroke: 0xff8a6b, strokeAlpha: 0.7, radius: 6 }));
    const heading = text(title, { fontSize: 12, fill: 0xffd0c8, fontWeight: "900" });
    heading.position.set(12, 8);
    container.addChild(heading);
    visibleLines.slice(0, 5).forEach((line, index) => {
      const row = text(line, { fontSize: 10, fill: 0xf2e6c7, fontWeight: "800", wordWrap: true, wordWrapWidth: width - 24 });
      row.position.set(12, 28 + index * 18);
      container.addChild(row);
    });
    container.heightHint = height;
    return container;
  }

  removeOneCard(values, cardId) {
    const next = [...values];
    const index = next.indexOf(cardId);
    if (index >= 0) {
      next.splice(index, 1);
    }
    return next;
  }

  partyPanel(width, height) {
    const container = new PIXI.Container();
    const party = this.currentParty();
    const members = this.partyMemberPlayers(party);
    const isSolo = party?.solo || members.length <= 1;
    const isLeader = party?.leader_id === this.state.player?.id;
    const header = panel(width, 54, { fill: 0x111823, alpha: 0.92, stroke: 0x8fb7ff, strokeAlpha: 0.55, radius: 6 });
    const title = text(isSolo ? "You are not in a party" : `${members.length} party members`, { fontSize: 16, fill: 0xf2e6c7, fontWeight: "900" });
    title.position.set(16, 10);
    const detail = text(isSolo ? "Invite nearby players from Social or the map player menu." : `${isLeader ? "You are leading the party." : `${this.partyLeaderName(party, members)} is leading the party.`}`, {
      fontSize: 12,
      fill: 0xc8b698,
      wordWrap: true,
      wordWrapWidth: Math.max(160, width - 190),
    });
    detail.position.set(16, 32);
    container.addChild(header, title, detail);
    if (!isSolo) {
      const leave = button("Leave Party", 112, 32, () => this.leaveParty(), { fill: 0x2a1717, stroke: 0xb85a43, fontSize: 11 });
      leave.position.set(width - 128, 11);
      container.addChild(leave);
    }

    const sceneY = 72;
    const sceneHeight = Math.max(190, height - sceneY);
    const scene = panel(width, sceneHeight, { fill: 0x090b0d, alpha: 0.88, stroke: 0x6f5d3d, strokeAlpha: 0.42, radius: 6 });
    scene.position.set(0, sceneY);
    container.addChild(scene);
    if (!members.length) {
      const empty = this.emptyState("No party members.", "Party members will appear here.");
      empty.position.set(0, sceneY + 18);
      container.addChild(empty);
      return container;
    }
    const cardGap = width < 760 ? 12 : 18;
    const memberCount = Math.max(1, members.length);
    const cardWidth = clamp(Math.floor((width - 24 - cardGap * (memberCount - 1)) / memberCount), 128, 210);
    const totalWidth = memberCount * cardWidth + (memberCount - 1) * cardGap;
    const startX = Math.max(12, width / 2 - totalWidth / 2);
    const cardHeight = Math.min(sceneHeight - 24, 300);
    const cardY = sceneY + Math.max(12, (sceneHeight - cardHeight) / 2);
    members.forEach((member, index) => {
      const card = this.partyMemberCard(member, party, {
        width: cardWidth,
        height: cardHeight,
        canKick: isLeader && member.id !== this.state.player?.id && !party.solo,
      });
      card.position.set(startX + index * (cardWidth + cardGap), cardY);
      container.addChild(card);
    });
    return container;
  }

  partyLeaderName(party, members) {
    return members.find((member) => member.id === party?.leader_id)?.name || "Party leader";
  }

  partyMemberCard(member, party, options) {
    const container = new PIXI.Container();
    const width = options.width;
    const height = options.height;
    const isSelf = member.id === this.state.player?.id;
    const isLeader = member.id === party?.leader_id;
    const frame = panel(width, height, {
      fill: isLeader ? 0x111b2a : isSelf ? 0x111d17 : 0x14120f,
      alpha: 0.94,
      stroke: isLeader ? 0x8fb7ff : isSelf ? 0x7bd889 : 0x6f5d3d,
      strokeAlpha: 0.7,
      radius: 6,
    });
    container.addChild(frame);
    const name = text(`${member.name || this.formatIdLabel(member.id)}${isSelf ? " (You)" : ""}`, {
      fontSize: 14,
      fill: 0xf2e6c7,
      fontWeight: "900",
      wordWrap: true,
      wordWrapWidth: width - 20,
    });
    name.anchor.set(0.5, 0);
    name.position.set(width / 2, 10);
    const meta = text(`${isLeader ? "Leader" : "Member"} | Lv ${member.level || 1}`, { fontSize: 11, fill: isLeader ? 0xabc8ff : 0xc8b698, fontWeight: "800" });
    meta.anchor.set(0.5, 0);
    meta.position.set(width / 2, 34);
    const classLabel = text(member.class_summary?.label || this.formatIdLabel(member.class_name || "wanderer"), { fontSize: 10, fill: 0x9f9276, wordWrap: true, wordWrapWidth: width - 18 });
    classLabel.anchor.set(0.5, 0);
    classLabel.position.set(width / 2, 52);
    container.addChild(name, meta, classLabel);

    const groundY = Math.min(height - 108, 154);
    container.addChild(new PIXI.Graphics().ellipse(width / 2, groundY + 2, Math.min(46, width * 0.28), 10).fill({ color: 0x000000, alpha: 0.42 }));
    this.drawDeckPreviewCombatSprite(container, this.heroVisualKeyForPlayer(member), "player", width / 2, groundY, Math.min(0.44, Math.max(0.34, width / 420)), { facing: 1 });

    const barWidth = Math.max(104, width - 22);
    const bars = [
      ["HP", member.hp ?? 0, member.max_hp ?? 1, 0x62c461],
      ["Mana", member.mana ?? 0, member.max_mana ?? 1, 0x6292ff],
      ["Stamina", member.stamina ?? 0, member.max_stamina ?? 1, 0xe5b64f],
    ];
    const barsY = Math.max(groundY + 12, height - 104);
    bars.forEach(([label, current, max, color], index) => {
      const bar = resourceBar(label, current, max, color, barWidth);
      bar.scale.set(0.9);
      bar.position.set(11, barsY + index * 22);
      container.addChild(bar);
    });

    const inspect = button("i", 38, 28, () => this.inspectPlayer(member), { fill: 0x222026, stroke: 0xcdb8ff, fontSize: 16 });
    inspect.position.set(10, height - 36);
    const whisper = button("💬", 38, 28, () => this.openChatInput(member), { fill: 0x1d2536, stroke: 0x6fa8ff, fontSize: 15 });
    whisper.position.set(54, height - 36);
    container.addChild(inspect, whisper);
    if (options.canKick) {
      const kick = button("Kick", Math.max(52, width - 106), 28, () => this.kickPartyMember(member), { fill: 0x2a1717, stroke: 0xb85a43, fontSize: 10 });
      kick.position.set(98, height - 36);
      container.addChild(kick);
    }
    return container;
  }

  heroVisualKeyForPlayer(player) {
    const className = String(player?.class_name || "wanderer");
    if (["ranger", "assassin"].includes(className)) return "hero_ranged";
    if (["fire_wizard", "mystic", "priest", "necromancer", "alchemist"].includes(className)) return "hero_caster";
    return "hero_melee";
  }

  socialPanel(width, height) {
    const container = new PIXI.Container();
    const nearby = this.sortedNearbyPlayers((this.state.map?.players || []).filter((player) => player.id !== this.state.player.id));
    const party = this.currentParty();
    const friends = this.state.player.friends || [];
    const friendRequests = this.pendingFriendRequests();
    const compactHeader = width < 560;
    const tabWidth = compactHeader ? Math.max(84, Math.floor((width - 16) / 3)) : 112;
    const headerSecondRow = compactHeader && party;
    const contentTop = headerSecondRow ? 84 : 48;
    const nearbyTab = button(`Nearby ${nearby.length}`, tabWidth, 30, () => {
      this.state.socialTab = "nearby";
      this.renderUi();
    }, { fill: this.state.socialTab === "nearby" ? 0x3b2a17 : 0x201b14, stroke: this.state.socialTab === "nearby" ? 0xd7aa5f : 0x6f5d3d });
    const friendsTab = button(`Friends ${friends.length}`, tabWidth, 30, () => {
      this.state.socialTab = "friends";
      this.renderUi();
    }, { fill: this.state.socialTab === "friends" ? 0x3b2a17 : 0x201b14, stroke: this.state.socialTab === "friends" ? 0xd7aa5f : 0x6f5d3d });
    friendsTab.position.set(tabWidth + 6, 0);
    const partyTab = button(`Party ${party?.member_ids?.length || 0}`, tabWidth, 30, () => {
      this.state.socialTab = "party";
      this.renderUi();
    }, { fill: this.state.socialTab === "party" ? 0x1c2d3e : 0x201b14, stroke: this.state.socialTab === "party" ? 0x8fb7ff : 0x6f5d3d });
    partyTab.position.set((tabWidth + 6) * 2, 0);
    const onlineFriends = friends.filter((friend) => friend.online).length;
    const onlinePill = this.summaryPill(`${onlineFriends}/${friends.length} online`, onlineFriends ? 0x7bd889 : 0x9f9276);
    const leaveParty = this.state.player?.group_id ? button("Leave Party", 92, 30, () => this.leaveParty(), { fill: 0x2a1717, stroke: 0xb85a43, fontSize: 10 }) : null;
    if (leaveParty) {
      leaveParty.position.set(compactHeader ? 0 : (tabWidth + 6) * 3 + 8, compactHeader ? 40 : 0);
      onlinePill.position.set(compactHeader ? 100 : (tabWidth + 6) * 3 + 108, compactHeader ? 41 : 1);
    } else {
      onlinePill.position.set((tabWidth + 6) * 3 + 8, 1);
    }
    container.addChild(nearbyTab, friendsTab, partyTab);
    if (!compactHeader || party) {
      container.addChild(onlinePill);
    }
    if (leaveParty) {
      container.addChild(leaveParty);
    }

    if (this.state.socialTab === "party") {
      const members = this.partyMemberPlayers(party);
      let y = contentTop;
      members.forEach((member) => {
        if (y + 56 > height) return;
        const isLeader = member.id === party.leader_id;
        const isSelf = member.id === this.state.player?.id;
        const row = panel(width, 50, { fill: isLeader ? 0x172033 : isSelf ? 0x161d18 : 0x17130f, alpha: 0.84, stroke: isLeader ? 0x8fb7ff : isSelf ? 0x7bd889 : 0x6f5d3d, strokeAlpha: 0.58 });
        row.position.set(0, y);
        const role = isLeader ? "Leader" : "Member";
        const name = text(`${member.name}${isSelf ? " (You)" : ""}`, { fontSize: 14, fill: 0xf2e6c7, fontWeight: "900", wordWrap: true, wordWrapWidth: width - 132 });
        name.position.set(14, y + 8);
        const meta = text(`${role} | ${this.formatIdLabel(member.class_name)} | Lv ${member.level}${member.combat_id ? " | In combat" : ""}`, { fontSize: 12, fill: isLeader ? 0xabc8ff : 0xc8b698, wordWrap: true, wordWrapWidth: width - 132 });
        meta.position.set(14, y + 29);
        const inspect = button("i", 42, 34, () => this.inspectPlayer(member), { fill: 0x222026, stroke: 0xcdb8ff, fontSize: 18 });
        inspect.position.set(width - 100, y + 8);
        const talk = button("💬", 42, 34, () => this.openChatInput(member), { fill: 0x1d2536, stroke: 0x6fa8ff, fontSize: 18 });
        talk.position.set(width - 54, y + 8);
        container.addChild(row, name, meta, inspect, talk);
        y += 58;
      });
      return container;
    }

    if (this.state.socialTab === "friends") {
      if (!friends.length && !friendRequests.length) {
        const empty = this.emptyState("No friends yet.", "Friends will appear here when the social backend starts returning them.");
        empty.position.set(0, contentTop + 4);
        container.addChild(empty);
        return container;
      }
      let y = contentTop;
      if (friendRequests.length) {
        const heading = text("Pending Requests", { fontSize: 13, fill: 0xd7aa5f, fontWeight: "900" });
        heading.position.set(4, y);
        container.addChild(heading);
        y += 24;
        friendRequests.forEach((request) => {
          if (y + 50 > height) return;
          const incoming = request.target_id === this.state.player?.id;
          const row = panel(width, 48, { fill: 0x17130f, alpha: 0.86, stroke: 0xd7aa5f, strokeAlpha: 0.56 });
          row.position.set(0, y);
          const otherName = incoming ? request.requester_name : request.target_name;
          const name = text(otherName || "Player", { fontSize: 14, fill: 0xf2e6c7, fontWeight: "900", wordWrap: true, wordWrapWidth: incoming ? width - 128 : width - 28 });
          name.position.set(14, y + 8);
          const meta = text(incoming ? "Wants to add you" : "Request sent", { fontSize: 12, fill: 0xc8b698 });
          meta.position.set(14, y + 28);
          container.addChild(row, name, meta);
          if (incoming) {
            const accept = button("✓", 42, 34, () => this.answerFriendRequest(request, true), { fill: 0x1f2a19, stroke: 0x7bd889, fontSize: 18 });
            accept.position.set(width - 100, y + 7);
            const decline = button("×", 42, 34, () => this.answerFriendRequest(request, false), { fill: 0x2a1717, stroke: 0xb85a43, fontSize: 18 });
            decline.position.set(width - 54, y + 7);
            container.addChild(accept, decline);
          }
          y += 56;
        });
        y += 8;
      }
      if (friends.length && y + 24 <= height) {
        const heading = text("Friends", { fontSize: 13, fill: 0xd7aa5f, fontWeight: "900" });
        heading.position.set(4, y);
        container.addChild(heading);
        y += 24;
      }
      friends.forEach((friend) => {
        if (y + 50 > height) return;
        const row = panel(width, 48, { fill: 0x17130f, alpha: 0.8, stroke: 0x5c6f7c, strokeAlpha: 0.48 });
        row.position.set(0, y);
        const name = text(friend.name || this.formatIdLabel(friend.id || "Friend"), { fontSize: 14, fill: 0xf2e6c7, fontWeight: "900", wordWrap: true, wordWrapWidth: width - 28 });
        name.position.set(14, y + 8);
        const meta = text(friend.online ? "Online" : "Offline", { fontSize: 12, fill: friend.online ? 0x7bd889 : 0x9f9276 });
        meta.position.set(14, y + 28);
        container.addChild(row, name, meta);
        y += 56;
      });
      return container;
    }

    if (!nearby.length) {
      const empty = this.emptyState("No nearby players.", "Other adventurers on this map will be listed here.");
      empty.position.set(0, contentTop + 4);
      container.addChild(empty);
      return container;
    }
    const rowHeight = 56;
    const visible = Math.max(1, Math.floor((height - contentTop + 2) / rowHeight));
    nearby.slice(0, visible).forEach((player, index) => {
      const isFriend = this.isFriend(player.id);
      const sameParty = this.currentParty()?.member_ids?.includes(player.id);
      const playerDistance = this.state.player?.position && player.position ? Math.round(distance(this.state.player.position, player.position) * 10) / 10 : null;
      const relation = sameParty ? "Party" : isFriend ? "Friend" : "Nearby";
      const titleLabel = player.active_title?.label ? ` | ${player.active_title.label}` : "";
      const powerLabel = player.readiness?.power_score ? ` | Pwr ${player.readiness.power_score}` : "";
      const row = panel(width, 48, { fill: sameParty ? 0x111c22 : isFriend ? 0x151b13 : 0x17130f, alpha: 0.82, stroke: sameParty ? 0x8fb7ff : isFriend ? 0x7bd889 : 0x5c7c6a, strokeAlpha: 0.58 });
      row.position.set(0, contentTop + index * rowHeight);
      const name = text(player.name, { fontSize: 14, fill: 0xf2e6c7, fontWeight: "900", wordWrap: true, wordWrapWidth: width - 174 });
      name.position.set(14, row.y + 8);
      const meta = text(`${relation} | ${this.formatIdLabel(player.class_name)} | Lv ${player.level}${titleLabel}${powerLabel}${playerDistance !== null ? ` | ${playerDistance}m` : ""}`, { fontSize: 12, fill: sameParty ? 0x9fc2ff : isFriend ? 0x9de6a9 : 0xc8b698, wordWrap: true, wordWrapWidth: width - 174 });
      meta.position.set(14, row.y + 28);
      const inspect = button("i", 42, 34, () => this.inspectPlayer(player), { fill: 0x222026, stroke: 0xcdb8ff, fontSize: 18 });
      inspect.position.set(width - 146, row.y + 7);
      const talk = button("💬", 42, 34, () => this.openChatInput(player), { fill: 0x1d2536, stroke: 0x6fa8ff, fontSize: 18 });
      talk.position.set(width - 100, row.y + 7);
      const friend = button("☆", 42, 34, () => this.createFriendRequest(player), {
        fill: isFriend ? 0x27231c : 0x2b2212,
        stroke: isFriend ? 0x6f5d3d : 0xd7aa5f,
        fontSize: 18,
      });
      friend.position.set(width - 54, row.y + 7);
      container.addChild(row, name, meta, inspect, talk, friend);
    });
    return container;
  }

  sortedNearbyPlayers(players) {
    const party = this.currentParty();
    const position = this.state.player?.position;
    const score = (player) => {
      if (party?.member_ids?.includes(player.id)) return 0;
      if (this.isFriend(player.id)) return 1;
      return 2;
    };
    return [...players].sort((left, right) => {
      const relation = score(left) - score(right);
      if (relation) return relation;
      const leftDistance = position && left.position ? distance(position, left.position) : 9999;
      const rightDistance = position && right.position ? distance(position, right.position) : 9999;
      return leftDistance - rightDistance || String(left.name).localeCompare(String(right.name));
    });
  }

  partyMemberPlayers(party = this.currentParty()) {
    if (!party) return [];
    const sources = [
      ...(this.state.world?.players || []),
      ...(this.state.map?.players || []),
      this.state.player,
    ].filter(Boolean);
    const byId = new Map(sources.map((player) => [player.id, player]));
    return (party.member_ids || [])
      .map((memberId) => byId.get(memberId) || { id: memberId, name: this.formatIdLabel(memberId), class_name: "unknown", level: 1 })
      .sort((left, right) => (left.id === party.leader_id ? -1 : right.id === party.leader_id ? 1 : String(left.name).localeCompare(String(right.name))));
  }

  settingsPanel(width, height) {
    const container = new PIXI.Container();
    const intro = text("Audio", { fontSize: 16, fill: 0xf2e6c7, fontWeight: "900" });
    intro.position.set(0, 0);
    container.addChild(intro);

    const music = this.audioSlider("Muzyka tla", "musicVolume", Math.min(420, width - 26), 0xe0b95b);
    music.position.set(0, 44);
    const sfx = this.audioSlider("Efekty dzwiekowe", "sfxVolume", Math.min(420, width - 26), 0x6fa8ff);
    sfx.position.set(0, 118);
    const ui = this.audioSlider("Dzwieki UI", "uiVolume", Math.min(420, width - 26), 0xd7aa5f);
    ui.position.set(0, 192);
    container.addChild(music, sfx, ui);

    const interfaceTitle = text("Interface", { fontSize: 16, fill: 0xf2e6c7, fontWeight: "900" });
    interfaceTitle.position.set(0, 266);
    const scale = this.audioSlider("Skala UI", "uiScale", Math.min(420, width - 26), 0x7bd889, {
      min: 0.8,
      max: 1.2,
      format: (value) => `${Math.round(value * 100)}%`,
    });
    scale.position.set(0, 306);
    container.addChild(interfaceTitle, scale);

    const test = button("Test efektu", 130, 34, () => {
      this.unlockAudio();
      this.playSound(audioPaths.impact, 0.68);
    }, { fill: 0x1d2536, stroke: 0x6fa8ff });
    test.position.set(0, 388);
    const testUi = button("Test UI", 104, 34, () => {
      this.unlockAudio();
      this.playUiSound(audioPaths.uiClick, 0.68);
    }, { fill: 0x2b2212, stroke: 0xd7aa5f, sound: false });
    testUi.position.set(142, 388);
    container.addChild(test, testUi);

    const rightX = width > 760 ? 470 : 0;
    const rightY = width > 760 ? 0 : 438;
    const gameplayTitle = text("Gameplay UI", { fontSize: 16, fill: 0xf2e6c7, fontWeight: "900" });
    gameplayTitle.position.set(rightX, rightY);
    container.addChild(gameplayTitle);
    const toggleRows = [
      {
        label: this.state.questTrackerVisible ? "Quest tracker: On" : "Quest tracker: Off",
        color: this.state.questTrackerVisible ? 0x1f2a19 : 0x201b14,
        stroke: this.state.questTrackerVisible ? 0x7bd889 : 0x967341,
        action: () => {
          this.state.questTrackerVisible = !this.state.questTrackerVisible;
          localStorage.setItem("vibe-quest-tracker-visible", String(this.state.questTrackerVisible));
          this.renderUi();
        },
      },
      {
        label: this.state.worldMapTargetPins ? "Map targets: On" : "Map targets: Off",
        color: this.state.worldMapTargetPins ? 0x1f2a19 : 0x201b14,
        stroke: this.state.worldMapTargetPins ? 0x7bd889 : 0x967341,
        action: () => {
          this.state.worldMapTargetPins = !this.state.worldMapTargetPins;
          localStorage.setItem("vibe-world-map-target-pins", String(this.state.worldMapTargetPins));
          this.renderUi();
        },
      },
      {
        label: this.state.showCollisionObjects ? "Collision debug: On" : "Collision debug: Off",
        color: this.state.showCollisionObjects ? 0x1d2536 : 0x201b14,
        stroke: this.state.showCollisionObjects ? 0x6fa8ff : 0x967341,
        action: () => {
          this.state.showCollisionObjects = !this.state.showCollisionObjects;
          this.render();
        },
      },
    ];
    toggleRows.forEach((row, index) => {
      const item = button(row.label, 210, 30, row.action, { fill: row.color, stroke: row.stroke, fontSize: 11 });
      item.position.set(rightX, rightY + 38 + index * 38);
      container.addChild(item);
    });
    const resetPrefs = button("Reset local UI prefs", 210, 30, () => {
      ["vibe-pinned-quest-id", "vibe-inventory-tab", "vibe-inventory-filter", "vibe-inventory-sort", "vibe-craft-filter", "vibe-journal-filter", "vibe-world-map-zoom"].forEach((key) => localStorage.removeItem(key));
      this.state.pinnedQuestId = null;
      this.state.inventoryFilter = "all";
      this.state.inventorySort = "rarity";
      this.state.inventoryTab = "items";
      this.state.craftFilter = "ready";
      this.state.journalFilter = "all";
      this.state.worldMapZoom = 1;
      this.state.worldMapOffset = { x: 0, y: 0 };
      this.log("[System] Local UI preferences reset.");
      this.renderUi();
    }, { fill: 0x2b2212, stroke: 0xd7aa5f, fontSize: 11 });
    resetPrefs.position.set(rightX, rightY + 158);
    const help = button("Log command help", 210, 30, () => {
      this.log(this.state.devCheatsEnabled ? "[System] Commands: /help, /devhelp, /where, /quests, /inventory, /map." : "[System] Commands: /help, /where, /quests, /inventory, /map.");
    }, { fill: 0x1d2536, stroke: 0x6fa8ff, fontSize: 11 });
    help.position.set(rightX, rightY + 196);
    const resetPerf = button("Reset perf metrics", 210, 30, () => {
      Object.assign(this.state.performance, {
        worstFrameMs: 0,
        spikeCount: 0,
        lastSpikeAt: 0,
        lastLogAt: 0,
        worstApiMs: 0,
        slowApiCount: 0,
        lastApiLogAt: 0,
      });
      this.log("[System] Performance metrics reset.");
      this.renderUi();
    }, { fill: 0x1d2536, stroke: 0x7bd889, fontSize: 11 });
    resetPerf.position.set(rightX, rightY + 234);
    container.addChild(resetPrefs, help, resetPerf);
    const statusLines = [
      `Map zoom: ${Math.round(this.state.worldMapZoom * 100)}%`,
      `Inventory: ${this.formatIdLabel(this.state.inventoryFilter)} / ${this.formatIdLabel(this.state.inventorySort)}`,
      `Pinned: ${this.state.pinnedQuestId || "none"}`,
      `Discovered: ${(this.state.player?.discovered_map_ids || []).length || 0} regions`,
      `Perf: ${this.state.performance.fps || 0} FPS, worst ${this.state.performance.worstFrameMs || 0}ms, spikes ${this.state.performance.spikeCount || 0}`,
      `API: last ${this.state.performance.lastApiMs || 0}ms, worst ${this.state.performance.worstApiMs || 0}ms, slow ${this.state.performance.slowApiCount || 0}`,
    ];
    const status = text(statusLines.join("\n"), { fontSize: 11, fill: 0xc8b698, wordWrap: true, wordWrapWidth: 250, lineHeight: 16 });
    status.position.set(rightX, rightY + 276);
    container.addChild(status);

    const note = text("Ustawienia zapisuja sie automatycznie w tej przegladarce.", {
      fontSize: 12,
      fill: 0xb6a680,
      wordWrap: true,
      wordWrapWidth: width,
    });
    note.position.set(0, Math.min(height - 34, width > 760 ? 438 : rightY + 318));
    container.addChild(note);
    return container;
  }

  audioSlider(label, key, width, color, options = {}) {
    const container = new PIXI.Container();
    const trackY = 34;
    const trackWidth = Math.max(180, width);
    const labelText = text(label, { fontSize: 13, fill: 0xc8b698, fontWeight: "900" });
    const valueText = text("", { fontSize: 13, fill: 0xf0e7d0, fontWeight: "900" });
    valueText.anchor.set(1, 0);
    valueText.position.set(trackWidth, 0);
    const graphics = new PIXI.Graphics();
    const min = Number.isFinite(options.min) ? options.min : 0;
    const max = Number.isFinite(options.max) ? options.max : 1;
    const format = options.format || ((value) => `${Math.round(value * 100)}%`);
    const draw = () => {
      const value = clamp(Number(this.settings[key]) || min, min, max);
      const normalized = (value - min) / (max - min);
      valueText.text = format(value);
      graphics.clear();
      graphics.roundRect(0, trackY, trackWidth, 10, 5)
        .fill({ color: 0x2d241e, alpha: 0.98 })
        .stroke({ color: 0x090806, width: 1 });
      graphics.roundRect(1, trackY + 1, Math.max(8, (trackWidth - 2) * normalized), 8, 4).fill(color);
      graphics.circle(trackWidth * normalized, trackY + 5, 10)
        .fill(0xf6e4bc)
        .stroke({ color, width: 3 });
    };
    const setFromEvent = (event) => {
      event.stopPropagation?.();
      this.unlockAudio();
      const local = container.toLocal(event.global);
      const normalized = clamp(local.x / trackWidth, 0, 1);
      this.setAudioSetting(key, min + normalized * (max - min), false);
      draw();
    };
    let dragging = false;
    container.eventMode = "static";
    container.cursor = "pointer";
    container.hitArea = new PIXI.Rectangle(-6, 0, trackWidth + 12, 56);
    container.on("pointerdown", (event) => {
      dragging = true;
      setFromEvent(event);
    });
    container.on("pointermove", (event) => {
      if (dragging) {
        setFromEvent(event);
      }
    });
    container.on("pointerup", (event) => {
      if (dragging) {
        setFromEvent(event);
        if (key === "uiScale") {
          this.renderUi();
        }
      }
      dragging = false;
    });
    container.on("pointerupoutside", () => {
      dragging = false;
    });
    draw();
    container.addChild(graphics, labelText, valueText);
    return container;
  }

  infoGrid(rows, width) {
    const container = new PIXI.Container();
    rows.forEach(([label, value], index) => {
      const row = panel(width, 34, { fill: index % 2 ? 0x16140f : 0x1d1912, alpha: 0.72, strokeAlpha: 0.18 });
      row.position.set(0, index * 38);
      const left = text(label, { fontSize: 13, fill: 0xc8b698, fontWeight: "900" });
      left.position.set(12, index * 38 + 9);
      const right = text(String(value), { fontSize: 13, fill: 0xf0e7d0, wordWrap: true, wordWrapWidth: width - 140 });
      right.position.set(126, index * 38 + 9);
      container.addChild(row, left, right);
    });
    return container;
  }

  compactStatGrid(rows, width, height) {
    const container = new PIXI.Container();
    const columns = width >= 520 ? 3 : 2;
    const gap = 8;
    const cellWidth = Math.floor((width - gap * (columns - 1)) / columns);
    const rowHeight = 58;
    const cellHeight = 50;
    const visibleRows = rows.slice(0, Math.floor(height / rowHeight) * columns);
    visibleRows.forEach((row, index) => {
      const x = (index % columns) * (cellWidth + gap);
      const y = Math.floor(index / columns) * rowHeight;
      const hovered = this.state.hoveredCharacterStat === row.key;
      const cell = panel(cellWidth, cellHeight, {
        fill: hovered ? 0x242015 : 0x18140f,
        alpha: 0.86,
        stroke: hovered ? 0xe0b95b : 0x5a4730,
        strokeAlpha: hovered ? 0.78 : 0.2,
      });
      cell.position.set(x, y);
      cell.eventMode = "static";
      cell.cursor = "help";
      cell.hitArea = new PIXI.Rectangle(0, 0, cellWidth, cellHeight);
      cell.on("pointerover", (event) => {
        event.stopPropagation?.();
        if (this.state.hoveredCharacterStat !== row.key) {
          this.state.hoveredCharacterStat = row.key;
          this.renderUi();
        }
      });
      cell.on("pointerout", (event) => {
        event.stopPropagation?.();
        if (this.state.hoveredCharacterStat === row.key) {
          this.state.hoveredCharacterStat = null;
          this.renderUi();
        }
      });
      container.addChild(cell);
      const iconTexture = this.assetTextures.get(row.iconKey || this.characterStatIconKey(row.key));
      if (iconTexture) {
        const icon = new PIXI.Sprite(iconTexture);
        icon.width = 34;
        icon.height = 34;
        icon.position.set(x + 8, y + 8);
        container.addChild(icon);
      }
      const statLabel = text(row.label, { fontSize: 11, fill: 0xb6a680, fontWeight: "800", wordWrap: true, wordWrapWidth: Math.max(60, cellWidth - 88) });
      statLabel.position.set(x + 48, y + 8);
      const valueRight = row.allocatable && this.state.player?.unspent_stat_points > 0 ? cellWidth - 46 : cellWidth - 10;
      const statValue = text(row.value, { fontSize: 17, fill: 0xf2e6c7, fontWeight: "900" });
      statValue.anchor.set(1, 0);
      statValue.position.set(x + valueRight, y + 16);
      container.addChild(statLabel, statValue);
      if (row.allocatable && this.state.player?.unspent_stat_points > 0) {
        const add = button("+", 28, 26, () => this.allocateStat(row.key), {
          fill: 0x1f2a19,
          stroke: 0x7bd889,
          fontSize: 16,
        });
        add.position.set(x + cellWidth - 36, y + 12);
        container.addChild(add);
      }
    });
    const hoveredRow = visibleRows.find((row) => row.key === this.state.hoveredCharacterStat && row.description);
    if (hoveredRow) {
      const index = visibleRows.indexOf(hoveredRow);
      const cellX = (index % columns) * (cellWidth + gap);
      const cellY = Math.floor(index / columns) * rowHeight;
      const tooltipWidth = Math.min(360, width);
      const tooltipX = Math.min(Math.max(0, cellX), Math.max(0, width - tooltipWidth));
      const tooltipY = Math.min(cellY + cellHeight + 4, Math.max(0, height - 104));
      const tooltip = panel(tooltipWidth, 98, { fill: 0x0f0d0a, alpha: 0.98, stroke: 0xe0b95b, strokeAlpha: 0.85 });
      tooltip.position.set(tooltipX, tooltipY);
      const title = text(hoveredRow.label, { fontSize: 13, fill: 0xf2e6c7, fontWeight: "900" });
      title.position.set(tooltipX + 12, tooltipY + 9);
      const freePoints = this.state.player?.unspent_stat_points || 0;
      const body = text(`${hoveredRow.description}${freePoints ? `\nFree stat points: ${freePoints}` : ""}`, { fontSize: 12, fill: 0xc8b698, wordWrap: true, wordWrapWidth: tooltipWidth - 24 });
      body.position.set(tooltipX + 12, tooltipY + 30);
      container.addChild(tooltip, title, body);
    }
    return container;
  }

  characterStatIconKey(key) {
    return {
      strength: "character_stat_strength",
      agility: "character_stat_agility",
      mind: "character_stat_mind",
      magical_power: "character_stat_magical_power",
      intelligence: "character_stat_intelligence",
      endurance: "character_stat_endurance",
      speed: "character_stat_speed",
      max_hp: "character_stat_max_hp",
      max_mana: "character_stat_max_mana",
      max_stamina: "character_stat_max_stamina",
    }[key] || "item";
  }

  characterStatDescriptions() {
    return {
      strength: "Melee power and force checks. Offensive cards with Strength scaling hit harder.",
      agility: "Ranged precision, dodging feel and initiative support. Ranged cards often scale from Agility.",
      mind: "Mental resilience and focus. Mental cards and will-based effects scale from Mind.",
      magical_power: "Raw spell force. Magical and arcane cards use it for stronger effects.",
      intelligence: "Tactical knowledge and complex card scaling. Utility and advanced cards can use it.",
      endurance: "Universal defensive resilience. Blocks and defensive formulas often scale from Endurance.",
      speed: "Turn initiative and movement tempo in combat calculations.",
      max_hp: "Maximum health. Each allocated point gives +10 Health and immediately heals that extra capacity.",
      max_mana: "Maximum mana. Each allocated point gives +10 Mana and immediately restores that extra capacity.",
      max_stamina: "Maximum stamina. Each allocated point gives +10 Stamina and immediately restores that extra capacity.",
    };
  }

  sectionTitle(label) {
    return text(label, { fontSize: 15, fill: 0xf2e6c7, fontWeight: "900" });
  }

  summaryPill(label, color) {
    const labelText = text(label, { fontSize: 12, fill: 0xfff0c8, fontWeight: "900" });
    const width = Math.max(92, labelText.width + 24);
    const container = new PIXI.Container();
    const bg = panel(width, 28, { fill: 0x17130f, alpha: 0.9, stroke: color, strokeAlpha: 0.72 });
    labelText.anchor.set(0.5);
    labelText.position.set(width / 2, 14);
    container.addChild(bg, labelText);
    container.widthHint = width;
    return container;
  }

  emptyState(title, detail) {
    const container = new PIXI.Container();
    container.addChild(panel(420, 104, { fill: 0x15120f, alpha: 0.84, stroke: 0x6f5d3d, strokeAlpha: 0.55 }));
    const heading = text(title, { fontSize: 16, fill: 0xf2e6c7, fontWeight: "900" });
    heading.position.set(18, 18);
    const body = text(detail, { fontSize: 13, fill: 0xb6a680, wordWrap: true, wordWrapWidth: 380 });
    body.position.set(18, 48);
    container.addChild(heading, body);
    return container;
  }

  formatIdLabel(value) {
    return String(value || "")
      .replaceAll("_", " ")
      .replaceAll("-", " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  formatWeight(value) {
    const numeric = Number(value || 0);
    return `${numeric.toFixed(numeric >= 10 ? 1 : 2)} kg`;
  }

  formatDuration(seconds) {
    const total = Math.max(0, Number(seconds || 0));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${Math.max(1, minutes)}m`;
  }

  formatClockDuration(seconds) {
    const total = Math.max(0, Math.floor(Number(seconds || 0)));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const secs = total % 60;
    return [hours, minutes, secs].map((part) => String(part).padStart(2, "0")).join(":");
  }

  worldMapTargetEntries(worldMapParents = {}) {
    const normalizeMapId = (mapId) => worldMapParents[mapId] || mapId;
    const quests = this.activeStoryQuests()
      .filter((quest) => quest.marker_map_id)
      .map((quest) => ({
        kind: "quest",
        id: quest.id || quest.quest_id,
        mapId: normalizeMapId(quest.marker_map_id),
        label: quest.title || quest.marker_label || this.formatIdLabel(quest.id || quest.quest_id),
      }));
    const errands = (this.state.player?.active_errands || [])
      .filter((errand) => errand.marker_map_id)
      .map((errand) => ({
        kind: "errand",
        id: errand.id,
        mapId: normalizeMapId(errand.marker_map_id),
        label: errand.title || errand.marker_label || this.formatIdLabel(errand.id),
      }));
    return [...quests, ...errands].filter((entry) => entry.mapId);
  }

  currentMapServiceEntries() {
    const map = this.state.map;
    if (!map) return [];
    const entries = [];
    (map.portals || []).forEach((portal) => {
      entries.push({ kind: "route", label: `Route to ${this.formatIdLabel(portal.to_map_id || "instance")}` });
    });
    (map.npcs || []).forEach((npc) => {
      const effects = new Set((npc.conversation_options || []).map((option) => option.effect));
      if ((npc.actions || []).includes("shop") || effects.has("shop")) {
        entries.push({ kind: "shop", label: npc.name });
      }
      if (npc.kind === "quest" || effects.has("quest")) {
        entries.push({ kind: "quest", label: npc.name });
      }
      if (effects.has("errand")) {
        entries.push({ kind: "errand", label: npc.name });
      }
    });
    (map.gathering_areas || []).forEach((area) => {
      entries.push({ kind: "resource", label: area.item_name || area.name });
    });
    (map.interactables || []).forEach((interactable) => {
      if (interactable.label_visible !== false) {
        entries.push({ kind: "interactable", label: interactable.name });
      }
    });
    (map.enemy_groups || []).forEach((group) => {
      const difficulty = group.difficulty_label && group.difficulty !== "unknown" ? ` | ${group.difficulty_label}` : "";
      entries.push({ kind: "threat", label: `${group.name} Lv ${group.level || 1}${difficulty}` });
    });
    const priority = { quest: 0, errand: 1, shop: 2, resource: 3, route: 4, threat: 5, interactable: 6 };
    return entries.sort((left, right) => (priority[left.kind] ?? 9) - (priority[right.kind] ?? 9) || left.label.localeCompare(right.label));
  }

  listPanel(items, width, height) {
    const container = new PIXI.Container();
    items.slice(0, Math.floor(height / 32)).forEach((item, index) => {
      const row = panel(width, 28, { fill: index % 2 ? 0x15130f : 0x1c1811, alpha: 0.74, strokeAlpha: 0.16 });
      row.position.set(0, index * 32);
      const label = text(item, { fontSize: 13, fill: 0xe9ddc2, wordWrap: true, wordWrapWidth: width - 20 });
      label.position.set(10, index * 32 + 7);
      container.addChild(row, label);
    });
    return container;
  }

  worldMapGraphic(width, height) {
    const container = new PIXI.Container();
    const controlsHeight = 38;
    const mapHeight = Math.max(180, height - controlsHeight - 10);
    const mapWidth = width;
    const viewport = new PIXI.Container();
    viewport.position.set(0, controlsHeight + 10);
    viewport.eventMode = "static";
    viewport.cursor = "grab";
    viewport.hitArea = new PIXI.Rectangle(0, 0, mapWidth, mapHeight);
    const viewportBg = new PIXI.Graphics().roundRect(0, 0, mapWidth, mapHeight, 8).fill(0x1a2d35).stroke({ color: 0x78613c, width: 1 });
    const mask = new PIXI.Graphics().roundRect(0, 0, mapWidth, mapHeight, 8).fill(0xffffff);
    const content = new PIXI.Container();
    content.mask = mask;
    viewport.addChild(viewportBg, content, mask);
    container.addChild(viewport);

    const texture = this.assetTextures.get("world_map_asterfall");
    const zoom = this.state.worldMapZoom;
    const minScale = texture ? Math.max(mapWidth / texture.width, mapHeight / texture.height) : 1;
    const imageWidth = texture ? texture.width * minScale * zoom : mapWidth;
    const imageHeight = texture ? texture.height * minScale * zoom : mapHeight;
    const offset = this.clampedWorldMapOffset(mapWidth, mapHeight, imageWidth, imageHeight, this.state.worldMapOffset);
    this.state.worldMapOffset = offset;
    if (texture) {
      const sprite = new PIXI.Sprite(texture);
      sprite.position.set(offset.x, offset.y);
      sprite.width = imageWidth;
      sprite.height = imageHeight;
      content.addChild(sprite);
    }
    // Keep this as a regional world map: do not add small service interiors like city shops, inns, or taverns as nodes.
    const nodes = [
      ["City Wharf", "city_wharf", 0.22, 0.7, 0, 18],
      ["City Centre", "city_centre", 0.28, 0.6, -20, 18],
      ["Outer Fields", "outer_fields", 0.38, 0.34, 0, 18],
      ["Northwood", "northwood_grove", 0.35, 0.18, -12, -28],
      ["Highridge", "highridge_pass", 0.5, 0.12, -12, -28],
      ["Abandoned Village", "abandoned_village", 0.4, 0.76, -36, 18],
      ["Last Light Temple", "last_light_temple", 0.46, 0.86, 34, 20],
      ["City Sewers", "city_sewers", 0.3, 0.68, -28, 20],
      ["Deep Sewers", "deep_sewers", 0.43, 0.72, 0, 20],
      ["River Beach", "river_beach", 0.56, 0.8, 0, -28],
      ["Mistwood", "mistwood_crossing", 0.62, 0.31, 0, 18],
      ["Old Watchtower", "old_watchtower", 0.62, 0.18, 0, 18],
      ["Smugglers Cove", "smugglers_cove", 0.78, 0.72, 0, 18],
    ];
    const nodeByMapId = Object.fromEntries(nodes.map((node) => [node[1], node]));
    const routes = [
      ["city_wharf", "city_centre"],
      ["city_centre", "outer_fields"],
      ["city_centre", "northwood_grove"],
      ["city_centre", "abandoned_village"],
      ["city_centre", "city_sewers"],
      ["city_sewers", "deep_sewers"],
      ["deep_sewers", "river_beach"],
      ["northwood_grove", "highridge_pass"],
      ["northwood_grove", "mistwood_crossing"],
      ["highridge_pass", "abandoned_village"],
      ["abandoned_village", "last_light_temple"],
      ["abandoned_village", "outer_fields"],
      ["outer_fields", "river_beach"],
      ["river_beach", "mistwood_crossing"],
      ["mistwood_crossing", "old_watchtower"],
      ["old_watchtower", "smugglers_cove"],
    ];
    routes.forEach(([fromMapId, toMapId]) => {
      const from = nodeByMapId[fromMapId];
      const to = nodeByMapId[toMapId];
      if (!from || !to) return;
      const fromX = offset.x + from[2] * imageWidth;
      const fromY = offset.y + from[3] * imageHeight;
      const toX = offset.x + to[2] * imageWidth;
      const toY = offset.y + to[3] * imageHeight;
      content.addChild(new PIXI.Graphics()
        .moveTo(fromX, fromY)
        .lineTo(toX, toY)
        .stroke({ color: 0xf5d58a, width: 3, alpha: 0.78 }));
    });
    const worldMapParents = {
      market_hall: "city_centre",
      city_tavern: "city_centre",
      blacksmith_shop: "city_centre",
      general_shop: "city_centre",
      city_inn: "city_centre",
      highridge_mine: "highridge_pass",
      smuggler_hideout: "outer_fields",
      watchtower_cellar: "old_watchtower",
      dry_wall_sanctum: "old_watchtower",
      black_wax_foundry: "smugglers_cove",
      "instance:highridge_mine": "highridge_pass",
      "instance:smuggler_hideout": "outer_fields",
      "instance:watchtower_cellar": "old_watchtower",
      "instance:dry_wall_sanctum": "old_watchtower",
      "instance:black_wax_foundry": "smugglers_cove",
    };
    const rawCurrentMap = (this.state.player?.instance_id && this.state.map?.base_map_id) || this.state.player?.map_id || this.state.map?.id;
    const currentMap = worldMapParents[rawCurrentMap] || rawCurrentMap;
    const discoveredMaps = new Set(this.state.player?.discovered_map_ids || [currentMap].filter(Boolean));
    const targetEntries = this.state.worldMapTargetPins ? this.worldMapTargetEntries(worldMapParents) : [];
    const targetsByMapId = targetEntries.reduce((acc, entry) => {
      acc[entry.mapId] = acc[entry.mapId] || [];
      acc[entry.mapId].push(entry);
      return acc;
    }, {});
    nodes.forEach(([label, mapId, nx, ny, labelX, labelY]) => {
      const x = offset.x + nx * imageWidth;
      const y = offset.y + ny * imageHeight;
      if (x < -80 || y < -60 || x > mapWidth + 80 || y > mapHeight + 80) return;
      const active = mapId === currentMap;
      const discovered = discoveredMaps.has(mapId) || active;
      const mapTargets = targetsByMapId[mapId] || [];
      if (mapTargets.length) {
        content.addChild(new PIXI.Graphics()
          .circle(x, y, active ? 25 : 21)
          .stroke({ color: mapTargets.some((entry) => entry.kind === "quest") ? 0xffdf7a : 0x8fb7ff, width: 4, alpha: 0.78 }));
      }
      content.addChild(new PIXI.Graphics()
        .circle(x, y, active ? 17 : 13)
        .fill(active ? 0x7bd889 : discovered ? 0xe0b95b : 0x5c5446)
        .stroke({ color: active ? 0xeaffd6 : 0x201810, width: active ? 4 : 3, alpha: 0.92 }));
      if (mapTargets.length) {
        const badge = text(String(mapTargets.length), { fontSize: 10, fill: 0x130f08, fontWeight: "900" });
        badge.anchor.set(0.5);
        badge.position.set(x + 17, y - 16);
        content.addChild(new PIXI.Graphics().circle(x + 17, y - 16, 10).fill(0xffdf7a).stroke({ color: 0x2a1f12, width: 2 }), badge);
      }
      const nodeLabel = text(label, {
        fontSize: active ? 13 : 12,
        fill: active ? 0xeaffd6 : discovered ? 0xf3e3bd : 0x9f9276,
        fontWeight: "900",
        stroke: { color: 0x060403, width: 4 },
      });
      nodeLabel.anchor.set(0.5);
      nodeLabel.position.set(x + labelX, y + labelY);
      content.addChild(nodeLabel);
    });
    const legend = panel(Math.min(300, width - 24), 168, { fill: 0x0e0d0b, alpha: 0.8, stroke: 0x6f5d3d });
    legend.position.set(12, controlsHeight + 22);
    const title = text("Current Area", { fontSize: 13, fill: 0xf2e6c7, fontWeight: "900" });
    title.position.set(26, controlsHeight + 34);
    const detail = text(this.formatIdLabel(currentMap || "unknown"), { fontSize: 12, fill: 0xc8b698, wordWrap: true, wordWrapWidth: legend.width - 28 });
    detail.position.set(26, controlsHeight + 58);
    const population = text(`${(this.state.map?.players || []).length} players | ${(this.state.map?.npcs || []).length} NPCs | ${(this.state.map?.enemy_groups || []).length} enemy groups`, {
      fontSize: 11,
      fill: 0x9f9276,
      wordWrap: true,
      wordWrapWidth: legend.width - 28,
    });
    population.position.set(26, controlsHeight + 76);
    const discoveredSummary = text(`${discoveredMaps.size}/${nodes.length} regions discovered`, {
      fontSize: 11,
      fill: 0x7bd889,
      fontWeight: "900",
      wordWrap: true,
      wordWrapWidth: legend.width - 28,
    });
    discoveredSummary.position.set(26, controlsHeight + 94);
    const targetSummary = text(`${targetEntries.length} tracked map targets`, {
      fontSize: 11,
      fill: this.state.worldMapTargetPins ? 0xffdf7a : 0x9f9276,
      fontWeight: "900",
      wordWrap: true,
      wordWrapWidth: legend.width - 28,
    });
    const threat = this.state.map?.threat || {};
    const threatColor = {
      safe: 0x7bd889,
      low: 0x9de6a9,
      even: 0xffdf7a,
      dangerous: 0xffa07a,
      deadly: 0xff6b6b,
      unknown: 0x9f9276,
    }[threat.status || "unknown"] || 0x9f9276;
    const threatSummary = text(`${threat.label || "Unknown"} | Rec Lv ${threat.recommended_level || 1}`, {
      fontSize: 11,
      fill: threatColor,
      fontWeight: "900",
      wordWrap: true,
      wordWrapWidth: legend.width - 28,
    });
    threatSummary.position.set(26, controlsHeight + 109);
    targetSummary.position.set(26, controlsHeight + 126);
    const targetList = text(targetEntries.slice(0, 3).map((entry) => `${this.formatIdLabel(entry.kind)}: ${entry.label}`).join("\n") || "No tracked targets.", {
      fontSize: 10,
      fill: 0xc8b698,
      wordWrap: true,
      wordWrapWidth: legend.width - 28,
      lineHeight: 13,
    });
    targetList.position.set(26, controlsHeight + 143);
    container.addChild(legend, title, detail, population, discoveredSummary, threatSummary, targetSummary, targetList);
    const activitySummary = this.state.map?.activity_summary?.summary || "";
    const serviceEntries = this.currentMapServiceEntries();
    const servicesWidth = Math.min(320, width - 24);
    if (width > 700 && serviceEntries.length) {
      const servicesX = width - servicesWidth - 12;
      const servicesPanel = panel(servicesWidth, 156, { fill: 0x0e0d0b, alpha: 0.8, stroke: 0x7bd889, strokeAlpha: 0.62 });
      servicesPanel.position.set(servicesX, controlsHeight + 22);
      const servicesTitle = text("Map Services", { fontSize: 13, fill: 0xf2e6c7, fontWeight: "900" });
      servicesTitle.position.set(servicesX + 14, controlsHeight + 34);
      const counts = this.countById(serviceEntries.map((entry) => entry.kind));
      const servicesSummary = text(activitySummary || Object.entries(counts).map(([kind, count]) => `${this.formatIdLabel(kind)} ${count}`).join(" | "), {
        fontSize: 10,
        fill: 0x9de6a9,
        fontWeight: "900",
        wordWrap: true,
        wordWrapWidth: servicesWidth - 28,
      });
      servicesSummary.position.set(servicesX + 14, controlsHeight + 56);
      container.addChild(servicesPanel, servicesTitle, servicesSummary);
      serviceEntries.slice(0, 5).forEach((entry, index) => {
        const rowY = controlsHeight + 80 + index * 20;
        const label = text(`${this.formatIdLabel(entry.kind)}: ${entry.label}`, {
          fontSize: 10,
          fill: 0xc8b698,
          wordWrap: true,
          wordWrapWidth: servicesWidth - 28,
        });
        label.position.set(servicesX + 14, rowY);
        container.addChild(label);
      });
    }

    const zoomLabel = text(`Zoom ${Math.round(zoom * 100)}%`, { fontSize: 12, fill: 0xf2e6c7, fontWeight: "900" });
    zoomLabel.position.set(0, 10);
    const targets = button(this.state.worldMapTargetPins ? "Targets On" : "Targets Off", 96, 30, () => {
      this.state.worldMapTargetPins = !this.state.worldMapTargetPins;
      localStorage.setItem("vibe-world-map-target-pins", String(this.state.worldMapTargetPins));
      this.renderUi();
    }, { fill: this.state.worldMapTargetPins ? 0x1f2a19 : 0x201b14, stroke: this.state.worldMapTargetPins ? 0x7bd889 : 0x967341, fontSize: 11 });
    const reset = button("Reset", 72, 30, () => {
      this.state.worldMapZoom = 1;
      this.state.worldMapOffset = { x: 0, y: 0 };
      localStorage.setItem("vibe-world-map-zoom", "1");
      this.renderUi();
    }, { fill: 0x201b14, fontSize: 12 });
    const minus = button("-", 34, 30, () => this.setWorldMapZoom(this.state.worldMapZoom - 0.2, mapWidth, mapHeight), { fontSize: 18, fill: 0x201b14 });
    const plus = button("+", 34, 30, () => this.setWorldMapZoom(this.state.worldMapZoom + 0.2, mapWidth, mapHeight), { fontSize: 18, fill: 0x201b14 });
    plus.position.set(width - 34, 0);
    minus.position.set(width - 76, 0);
    reset.position.set(width - 156, 0);
    targets.position.set(Math.max(120, width - 260), 0);
    container.addChild(zoomLabel, targets, reset, minus, plus);

    const dragStart = (event) => {
      event.stopPropagation?.();
      viewport.cursor = "grabbing";
      this.state.worldMapDrag = {
        pointer: { x: event.global.x, y: event.global.y },
        offset: { ...this.state.worldMapOffset },
        bounds: { mapWidth, mapHeight, imageWidth, imageHeight },
      };
    };
    viewport.on("pointerdown", dragStart);
    viewport.on("pointermove", (event) => {
      if (!this.state.worldMapDrag) return;
      event.stopPropagation?.();
      const drag = this.state.worldMapDrag;
      this.state.worldMapOffset = this.clampedWorldMapOffset(
        drag.bounds.mapWidth,
        drag.bounds.mapHeight,
        drag.bounds.imageWidth,
        drag.bounds.imageHeight,
        {
          x: drag.offset.x + event.global.x - drag.pointer.x,
          y: drag.offset.y + event.global.y - drag.pointer.y,
        },
      );
      this.renderUi();
    });
    const endDrag = (event) => {
      event?.stopPropagation?.();
      viewport.cursor = "grab";
      this.state.worldMapDrag = null;
    };
    viewport.on("pointerup", endDrag);
    viewport.on("pointerupoutside", endDrag);
    viewport.on("wheel", (event) => {
      event.stopPropagation?.();
      this.setWorldMapZoom(this.state.worldMapZoom + (event.deltaY < 0 ? 0.15 : -0.15), mapWidth, mapHeight);
    });
    return container;
  }

  setWorldMapZoom(value, mapWidth = 1, mapHeight = 1) {
    this.state.worldMapZoom = clamp(value, 0.7, 2.6);
    localStorage.setItem("vibe-world-map-zoom", String(this.state.worldMapZoom));
    const texture = this.assetTextures.get("world_map_asterfall");
    if (texture) {
      const minScale = Math.max(mapWidth / texture.width, mapHeight / texture.height);
      this.state.worldMapOffset = this.clampedWorldMapOffset(
        mapWidth,
        mapHeight,
        texture.width * minScale * this.state.worldMapZoom,
        texture.height * minScale * this.state.worldMapZoom,
        this.state.worldMapOffset,
      );
    }
    this.renderUi();
  }

  clampedWorldMapOffset(mapWidth, mapHeight, imageWidth, imageHeight, offset) {
    const minX = Math.min(0, mapWidth - imageWidth);
    const minY = Math.min(0, mapHeight - imageHeight);
    return {
      x: imageWidth <= mapWidth ? (mapWidth - imageWidth) / 2 : clamp(offset.x || 0, minX, 0),
      y: imageHeight <= mapHeight ? (mapHeight - imageHeight) / 2 : clamp(offset.y || 0, minY, 0),
    };
  }

  handlePointerMove(event) {
    if (this.updateEquipmentDrag(event)) {
      return;
    }
    if (this.updateCombatCardDrag(event)) {
      return;
    }
    this.clearStaleCardHover(event);
    const previousCombatTargetId = this.state.hoveredCombatTargetId || "";
    const combatTarget = this.hoveredCombatTargetAt(event.global);
    this.state.hoveredCombatTargetId = combatTarget?.id || null;
    if (previousCombatTargetId !== (this.state.hoveredCombatTargetId || "")) {
      this.renderUi();
    }
    if (!this.state.map || event.global.y < this.mapViewportTop()) return;
    const previousThreatId = this.state.hoveredAggroThreat?.enemyGroup?.id || "";
    const previousTarget = this.state.hoveredMoveTarget;
    const previousInteractableId = this.state.hoveredInteractable?.id || "";
    const world = this.screenToWorld(event.global);
    const enemyGroup = this.findEnemyGroupAt(world.x, world.y);
    const interactable = !enemyGroup ? this.findInteractableAt(world.x, world.y) : null;
    const moveTarget = this.inMap(world) ? { x: Number(world.x.toFixed(2)), y: Number(world.y.toFixed(2)) } : null;
    const aggroThreat = !enemyGroup && moveTarget ? this.aggroThreatForPath(moveTarget) : null;
    this.state.hoveredEnemyGroup = enemyGroup || null;
    this.state.hoveredInteractable = interactable || null;
    this.state.hoveredAggroThreat = aggroThreat;
    this.state.hoveredMoveTarget = moveTarget;
    const nextThreatId = aggroThreat?.enemyGroup?.id || "";
    const nextInteractableId = interactable?.id || "";
    const targetChanged = (previousTarget?.x ?? null) !== (moveTarget?.x ?? null) || (previousTarget?.y ?? null) !== (moveTarget?.y ?? null);
    if (previousThreatId !== nextThreatId || previousInteractableId !== nextInteractableId || targetChanged) {
      this.renderEffects();
      if (previousInteractableId !== nextInteractableId) {
        this.renderMap();
      }
    }
  }

  clearStaleCardHover(event) {
    const deckKey = this.eventAncestorValue(event, "_deckHoverKey");
    const combatCardId = this.eventAncestorValue(event, "_combatCardInstanceId");
    let changed = false;
    if (this.state.hoveredDeckCardKey && this.state.hoveredDeckCardKey !== deckKey) {
      this.state.hoveredDeckCardKey = null;
      changed = true;
    }
    if (this.state.hoveredCombatCardInstanceId && this.state.hoveredCombatCardInstanceId !== combatCardId) {
      this.state.hoveredCombatCardInstanceId = null;
      this.state.hoveredCombatTargetId = null;
      changed = true;
    }
    if (changed) {
      this.renderUi();
    }
  }

  eventAncestorValue(event, key) {
    let node = event?.target || null;
    while (node) {
      if (node[key]) {
        return node[key];
      }
      node = node.parent || null;
    }
    return null;
  }

  handleWheel(event) {
    const rect = this.app.canvas.getBoundingClientRect();
    const point = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
    if (this.state.selectedCardFusion) {
      const handled = this.handleCardFusionWheel(point, event.deltaY);
      if (handled) {
        event.preventDefault();
        this.renderUi();
        return;
      }
    }
    if (this.state.selectedShop) {
      const handled = this.handleShopWheel(point, event.deltaY);
      if (handled) {
        event.preventDefault();
        this.renderUi();
        return;
      }
    }
    const chat = this.chatLayout();
    const overChat = point.x >= chat.x && point.x <= chat.x + chat.width && point.y >= chat.y && point.y <= chat.y + chat.height;
    if (overChat) {
      event.preventDefault();
      const direction = event.deltaY > 0 ? -1 : 1;
      const channel = this.effectiveChatChannel();
      this.state.chatScrollOffsets[channel] = clamp((this.state.chatScrollOffsets[channel] || 0) + direction * 2, 0, this.maxChatScrollOffset());
      this.renderUi();
      return;
    }
    if (this.state.activeWindow === "character") {
      const contentRect = this.activeWindowLayout().content;
      const overCharacter = point.x >= contentRect.x && point.x <= contentRect.x + contentRect.width && point.y >= contentRect.y && point.y <= contentRect.y + contentRect.height;
      if (overCharacter) {
        event.preventDefault();
        this.state.characterScrollOffset = Math.max(0, Number(this.state.characterScrollOffset || 0) + event.deltaY);
        this.renderUi();
        return;
      }
    }
    if (this.state.activeWindow === "inventory") {
      const contentRect = this.activeWindowLayout().content;
      if (this.handleInventoryWheel(point, contentRect, event.deltaY)) {
        event.preventDefault();
        this.renderUi();
        return;
      }
    }
    if (this.state.activeWindow === "deck" && !this.state.deckDetailCardId) {
      const contentRect = this.activeWindowLayout().content;
      const handled = this.handleDeckWheel(point, contentRect, event.deltaY);
      if (handled) {
        event.preventDefault();
        this.renderUi();
        return;
      }
    }
    if (!this.state.map || this.state.activeWindow || this.state.selectedCardFusion || this.state.selectedNpc || this.state.selectedInteractable || this.state.selectedEnemyGroup) {
      return;
    }
    event.preventDefault();
    const delta = event.deltaY > 0 ? -0.1 : 0.1;
    this.setMapZoom(this.state.mapZoom + delta);
  }

  setMapZoom(value) {
    const next = clamp(Number(value) || 1, 0.7, 1.8);
    if (Math.abs(next - this.state.mapZoom) < 0.001) {
      return;
    }
    this.state.mapZoom = Number(next.toFixed(2));
    localStorage.setItem("vibe-map-zoom", String(this.state.mapZoom));
    this.render();
  }

  handleInventoryWheel(point, contentRect, deltaY) {
    if (this.state.inventoryTab === "crafting") {
      return false;
    }
    const tabHeight = 42;
    const inventory = this.filteredInventory(this.state.player?.inventory || []);
    const layout = this.inventoryGridLayout(contentRect.width, Math.max(1, contentRect.height - tabHeight), inventory.length);
    const screenRegion = {
      x: contentRect.x + layout.gridX,
      y: contentRect.y + tabHeight + layout.gridTop,
      width: layout.gridWidth,
      height: layout.gridHeight,
    };
    const overGrid = point.x >= screenRegion.x && point.x <= screenRegion.x + screenRegion.width && point.y >= screenRegion.y && point.y <= screenRegion.y + screenRegion.height;
    if (!overGrid) {
      return false;
    }
    if (layout.maxOffset <= 0) {
      return true;
    }
    this.state.inventoryScrollOffset = clamp(Number(this.state.inventoryScrollOffset || 0) + deltaY, 0, layout.maxOffset);
    this.state.hoveredInventoryItemId = null;
    return true;
  }

  handleDeckWheel(point, contentRect, deltaY) {
    const insideContent = point.x >= contentRect.x && point.x <= contentRect.x + contentRect.width && point.y >= contentRect.y && point.y <= contentRect.y + contentRect.height;
    if (!insideContent) return false;
    const view = this.state.cardDeckView || this.fallbackCardDeckView();
    const deckCounts = this.countById(view.active_deck || []);
    const activeCards = Object.entries(deckCounts).map(([cardId, count]) => {
      const card = this.cardById(view, cardId) || { card_id: cardId, name: this.formatIdLabel(cardId), owned_count: count };
      const ownedCount = Math.max(count, Number(card.owned_count || count || 0));
      return { card, count, ownedCount, availableCount: Math.max(0, ownedCount - count) };
    });
    const collectionCards = (view.collection || []).map((card) => {
      const count = deckCounts[card.card_id] || 0;
      const ownedCount = Math.max(count, Number(card.owned_count || 0));
      return { card, count, ownedCount, availableCount: Math.max(0, ownedCount - count) };
    });
    const regions = this.deckGridRegions(contentRect.width, contentRect.height);
    const regionEntries = {
      deck: activeCards,
      collection: collectionCards,
    };
    for (const key of ["collection", "deck"]) {
      const region = regions[key];
      const screenRegion = {
        x: contentRect.x + region.x,
        y: contentRect.y + region.y,
        width: region.width,
        height: region.height,
      };
      const overRegion = point.x >= screenRegion.x && point.x <= screenRegion.x + screenRegion.width && point.y >= screenRegion.y && point.y <= screenRegion.y + screenRegion.height;
      if (!overRegion) continue;
      const cardWidth = contentRect.width < 760 ? 106 : key === "deck" ? 132 : 128;
      const cardHeight = contentRect.width < 760 ? 146 : key === "deck" ? 176 : 170;
      const maxOffset = this.maxDeckGridScrollOffset(regionEntries[key], region.width, region.height, cardWidth, cardHeight);
      if (maxOffset <= 0) return true;
      const current = Number(this.state.deckScrollOffsets[key] || 0);
      this.state.deckScrollOffsets[key] = clamp(current + deltaY, 0, maxOffset);
      if (this.state.hoveredDeckCardKey?.startsWith(`${key}:`)) {
        this.state.hoveredDeckCardKey = null;
      }
      return true;
    }
    return false;
  }

  handleShopWheel(point, deltaY) {
    const shop = this.state.selectedShop;
    if (!shop) return false;
    const width = Math.max(360, Math.min(900, this.state.viewport.width - 32));
    const height = Math.max(300, Math.min(660, this.state.viewport.height - this.topNavHeight() - 36));
    const x = this.state.viewport.width / 2 - width / 2;
    const y = this.topNavHeight() + 18;
    const contentX = x + 18;
    const contentY = y + 98;
    const contentWidth = width - 36;
    const contentHeight = height - 118;
    const inside = point.x >= contentX && point.x <= contentX + contentWidth && point.y >= contentY && point.y <= contentY + contentHeight;
    if (!inside) return false;
    const activeTab = this.state.shopTab === "sell" ? "sell" : "buy";
    if (activeTab === "sell") {
      const sellItems = this.sortedSellItems(shop.can_sell || []);
      const gridY = contentY + 54;
      const gridHeight = contentHeight - 54;
      const overGrid = point.y >= gridY && point.y <= gridY + gridHeight;
      if (!overGrid) return false;
      const layout = this.shopItemGridLayout(contentWidth, gridHeight, sellItems.length, "sell");
      if (layout.maxOffset <= 0) return true;
      this.state.shopScrollOffsets.sell = clamp(Number(this.state.shopScrollOffsets.sell || 0) + deltaY, 0, layout.maxOffset);
      this.state.hoveredShopItemKey = null;
      return true;
    }
    const items = this.sortedShopItems(shop.for_sale || [], shop.player_gold ?? this.state.player?.gold ?? 0);
    const cards = this.sortedShopCards(shop.cards_for_sale || [], shop.player_gold ?? this.state.player?.gold ?? 0);
    const hasCards = cards.length > 0;
    const itemWidth = hasCards ? Math.floor(contentWidth * 0.5) - 9 : contentWidth;
    const cardX = contentX + itemWidth + 18;
    const overCards = hasCards && point.x >= cardX && point.x <= cardX + (contentWidth - itemWidth - 18);
    if (overCards) {
      const cardHeight = contentHeight - 36;
      const maxOffset = Math.max(0, cards.length * 82 - cardHeight);
      if (maxOffset <= 0) return true;
      this.state.shopScrollOffsets.buyCards = clamp(Number(this.state.shopScrollOffsets.buyCards || 0) + deltaY, 0, maxOffset);
      return true;
    }
    const overItems = point.x >= contentX && point.x <= contentX + itemWidth;
    if (!overItems) return false;
    const itemHeight = contentHeight - 42;
    const layout = this.shopItemGridLayout(itemWidth, itemHeight, items.length, "buy");
    if (layout.maxOffset <= 0) return true;
    this.state.shopScrollOffsets.buyItems = clamp(Number(this.state.shopScrollOffsets.buyItems || 0) + deltaY, 0, layout.maxOffset);
    this.state.hoveredShopItemKey = null;
    return true;
  }

  handleCardFusionWheel(point, deltaY) {
    const fusion = this.state.selectedCardFusion;
    if (!fusion) return false;
    const width = Math.max(380, Math.min(980, this.state.viewport.width - 32));
    const height = Math.max(340, Math.min(680, this.state.viewport.height - this.topNavHeight() - 36));
    const x = this.state.viewport.width / 2 - width / 2;
    const y = this.topNavHeight() + 18;
    const contentX = x + 18;
    const contentY = y + 76;
    const contentWidth = width - 36;
    const contentHeight = height - 96;
    const listWidth = contentWidth < 760 ? Math.floor(contentWidth * 0.46) : Math.floor(contentWidth * 0.42);
    const listY = contentY + 34;
    const listHeight = Math.max(1, contentHeight - 34);
    const overList = point.x >= contentX && point.x <= contentX + listWidth && point.y >= listY && point.y <= listY + listHeight;
    if (!overList) return false;
    const maxOffset = Math.max(0, (fusion.stacks || []).length * 86 - listHeight);
    if (maxOffset <= 0) return true;
    this.state.cardFusionScrollOffset = clamp(Number(this.state.cardFusionScrollOffset || 0) + deltaY, 0, maxOffset);
    this.state.hoveredCardFusionKey = null;
    return true;
  }

  handlePointerTap(event) {
    if (performance.now() < Number(this.state.suppressWorldTapUntil || 0)) return;
    if (this.state.tutorialIntroSlide !== null) return;
    if (!this.state.map || event.global.y < this.mapViewportTop()) return;
    if (event.target && event.target !== this.app.stage) return;
    if (this.state.activeWindow || this.state.selectedCardFusion || this.state.selectedNpc || this.state.selectedInteractable || this.state.selectedEnemyGroup) {
      return;
    }
    const position = this.screenToWorld(event.global);
    const enemy = this.findEnemyGroupAt(position.x, position.y);
    if (enemy) {
      if (!this.tutorialAllowsTarget("enemy", enemy.id)) return this.showTutorialBlocked();
      this.state.selectedEnemyGroup = enemy;
      this.renderUi();
      return;
    }
    const npc = this.findNpcAt(position.x, position.y);
    if (npc) {
      if (!this.tutorialAllowsTarget("npc", npc.id)) return this.showTutorialBlocked();
      if (this.canReachPoint(npc.position)) {
        this.openNpc(npc);
      } else {
        this.moveToWorld(npc.position.x, npc.position.y, { afterMove: () => {
          if (this.canReachPoint(npc.position)) {
            this.openNpc(npc);
          }
        } });
      }
      return;
    }
    const player = this.findPlayerAt(position.x, position.y);
    if (player && player.id !== this.state.player?.id) {
      if (this.state.player?.tutorial) return this.showTutorialBlocked();
      this.state.selectedPlayer = player;
      this.renderUi();
      return;
    }
    const interactable = this.findInteractableAt(position.x, position.y);
    if (interactable) {
      if (!this.tutorialAllowsTarget("interactable", interactable.id)) return this.showTutorialBlocked();
      if (this.canReachPoint(interactable.position)) {
        this.openInteractable(interactable);
      } else {
        this.moveToWorld(interactable.position.x, interactable.position.y, { afterMove: () => {
          if (this.canReachPoint(interactable.position)) {
            this.openInteractable(interactable);
          }
        } });
      }
      return;
    }
    const portal = this.findPortalAt(position.x, position.y);
    if (portal) {
      if (!this.tutorialAllowsTarget("portal", portal.id)) return this.showTutorialBlocked();
      this.moveToWorld(portal.at.x, portal.at.y, { portalId: portal.id });
      return;
    }
    if (this.inMap(position)) {
      this.moveToWorld(Number(position.x.toFixed(2)), Number(position.y.toFixed(2)));
    }
  }

  tutorialAllowsTarget(kind, id) {
    const target = this.state.player?.tutorial?.target;
    return !target || (target.kind === kind && target.id === id);
  }

  showTutorialBlocked() {
    const now = performance.now();
    if (now - Number(this.state.tutorialBlockedPulseAt || 0) > 900) {
      this.state.tutorialBlockedPulseAt = now;
      this.log(currentLanguage() === "pl" ? "[Tutorial] Najpierw wykonaj krok wskazany świecącą strzałką." : "[Tutorial] Follow the glowing arrow first.");
    }
    this.renderEffects();
  }

  openNpc(npc) {
    if (!this.tutorialAllowsTarget("npc", npc.id)) return this.showTutorialBlocked();
    if (!this.isPartyLeader()) {
      this.log("[Party] Only the party leader can talk for the party.");
      return;
    }
    this.state.selectedNpc = npc;
    this.state.selectedInteractable = null;
    this.state.selectedShop = null;
    this.state.selectedCardFusion = null;
    this.state.cardFusionSlots = [];
    this.state.cardFusionResult = null;
    this.state.hoveredShopItemKey = null;
    this.state.shopTab = "buy";
    this.state.shopScrollOffsets = { buyItems: 0, buyCards: 0, sell: 0 };
    this.state.questBranch = null;
    this.state.conversationLine = npc.dialogue?.[0] || npc.description || "";
    this.renderUi();
  }

  openInteractable(interactable) {
    if (!this.tutorialAllowsTarget("interactable", interactable.id)) return this.showTutorialBlocked();
    if (!this.isPartyLeader()) {
      this.log("[Party] Only the party leader can interact for the party.");
      return;
    }
    this.state.selectedInteractable = interactable;
    this.state.selectedNpc = null;
    this.state.selectedShop = null;
    this.state.selectedCardFusion = null;
    this.state.cardFusionSlots = [];
    this.state.cardFusionResult = null;
    this.state.hoveredShopItemKey = null;
    this.state.shopTab = "buy";
    this.state.shopScrollOffsets = { buyItems: 0, buyCards: 0, sell: 0 };
    this.state.questBranch = null;
    this.state.conversationLine = interactable.description || interactable.name || "";
    this.renderUi();
  }

  startMovementSession() {
    this.state.movementToken += 1;
    this.state.movementActive = true;
    this.state.movementAbort = null;
    return this.state.movementToken;
  }

  isActiveMovement(token) {
    return this.state.movementActive && this.state.movementToken === token;
  }

  finishMovementSession() {
    this.state.movementAbort = null;
    this.state.movementActive = false;
  }

  cancelMovement({ snapToServer = false } = {}) {
    if (!this.state.player) {
      return null;
    }
    this.state.movementToken += 1;
    this.state.movementActive = false;
    if (this.state.movementAbort) {
      this.state.movementAbort.abort();
      this.state.movementAbort = null;
    }
    if (snapToServer) {
      this.setRenderPosition(this.state.player.id, this.state.player.position);
      return { ...this.state.player.position };
    }
    const current = this.getPlayerRenderPosition(this.state.player);
    this.state.renderPositions.set(this.state.player.id, { ...current });
    this.state.tweens.delete(this.state.player.id);
    return { x: Number(current.x.toFixed(2)), y: Number(current.y.toFixed(2)) };
  }

  async syncServerPositionToRenderPosition(position) {
    if (!this.state.player || !position || this.state.player.combat_id) {
      return;
    }
    if (distance(this.state.player.position, position) <= 0.05) {
      this.setRenderPosition(this.state.player.id, this.state.player.position);
      return;
    }
    this.state.movementSequence += 1;
    const stoppedPlayer = await this.api.request(`/players/${this.state.player.id}/move-to`, {
      method: "POST",
      body: JSON.stringify({ position, sequence: this.state.movementSequence }),
    });
    this.state.player = stoppedPlayer;
    this.syncPlayer(this.state.player);
    this.setRenderPosition(this.state.player.id, this.state.player.position);
  }

  async moveToWorld(x, y, options = {}) {
    if (!this.state.player || this.state.player.combat_id) return;
    if (!this.isPartyLeader()) {
      this.log("[Party] Only the party leader can move the party. Leave the party to move alone.");
      return;
    }
    this.state.selectedPlayer = null;
    this.state.partyInvite = null;
    this.cancelGathering();
    const interruptedMovement = this.state.movementActive;
    const interruptedPosition = this.cancelMovement();
    if (interruptedMovement && interruptedPosition) {
      await this.syncServerPositionToRenderPosition(interruptedPosition);
    }
    const movementToken = this.startMovementSession();
    const start = interruptedPosition || this.getPlayerRenderPosition(this.state.player);
    const target = { x, y };
    const startingMapId = this.state.player.map_id;
    this.state.player.is_resting = false;
    try {
      const waypoints = this.expandMovementWaypoints(start, this.findNavigationPath(start, target) || this.movementWaypoints(start, target));
      for (const waypoint of waypoints) {
        if (!this.isActiveMovement(movementToken)) {
          return;
        }
        if (this.state.player.map_id !== startingMapId) {
          break;
        }
        const beforeWaypoint = this.state.player;
        const renderStart = this.getPlayerRenderPosition(this.state.player);
        this.startRenderPath(this.state.player.id, renderStart, [waypoint]);
        const controller = new AbortController();
        this.state.movementAbort = controller;
        this.state.movementSequence += 1;
        const movedPlayer = await this.api.request(`/players/${this.state.player.id}/move-to`, {
          method: "POST",
          signal: controller.signal,
          body: JSON.stringify({ position: waypoint, sequence: this.state.movementSequence }),
        });
        if (!this.isActiveMovement(movementToken)) {
          return;
        }
        this.state.player = movedPlayer;
        this.syncPlayer(this.state.player);
        if (distance(this.state.player.position, waypoint) > 0.12) {
          this.syncPlayerRenderPosition(this.state.player, beforeWaypoint);
          break;
        }
        await this.waitForTweenToFinish(this.state.player.id);
      }
      if (options.portalId && this.state.player.map_id === startingMapId && distance(this.state.player.position, target) <= 0.35) {
        await this.waitForTweenToFinish(this.state.player.id);
        if (!this.isActiveMovement(movementToken)) {
          return;
        }
        const controller = new AbortController();
        this.state.movementAbort = controller;
        this.state.movementSequence += 1;
        this.state.player = await this.api.request(`/players/${this.state.player.id}/enter-portal`, {
          method: "POST",
          signal: controller.signal,
          body: JSON.stringify({ portal_id: options.portalId, sequence: this.state.movementSequence }),
        });
        if (!this.isActiveMovement(movementToken)) {
          return;
        }
        this.syncPlayer(this.state.player);
        this.setRenderPosition(this.state.player.id, this.state.player.position);
      }
      if (!this.isActiveMovement(movementToken)) {
        return;
      }
      await this.refreshWorld();
      if (!this.isActiveMovement(movementToken)) {
        return;
      }
      await this.loadMap(this.state.player.map_id);
      if (!options.portalId && this.state.player.map_id === startingMapId) {
        this.syncPlayerRenderPosition(this.state.player);
        await this.waitForTweenToFinish(this.state.player.id);
      }
      if (this.isActiveMovement(movementToken)) {
        options.afterMove?.();
      }
    } catch (error) {
      if (!this.isActiveMovement(movementToken) || error.name === "AbortError") {
        return;
      }
      this.log(error.message);
      this.setRenderPosition(this.state.player.id, this.state.player.position);
    } finally {
      if (this.isActiveMovement(movementToken)) {
        this.finishMovementSession();
      }
    }
  }

  movementWaypoints(start, target) {
    const totalDistance = distance(start, target);
    const steps = Math.max(1, Math.ceil(totalDistance / 9.5));
    return Array.from({ length: steps }, (_, index) => {
      const progress = (index + 1) / steps;
      return {
        x: Number((start.x + (target.x - start.x) * progress).toFixed(2)),
        y: Number((start.y + (target.y - start.y) * progress).toFixed(2)),
      };
    });
  }

  expandMovementWaypoints(start, path) {
    const expanded = [];
    let cursor = start;
    path.forEach((waypoint) => {
      const segment = this.movementWaypoints(cursor, waypoint);
      expanded.push(...segment);
      cursor = waypoint;
    });
    return expanded;
  }

  findNavigationPath(start, target) {
    if (!this.state.map) {
      return [{ x: target.x, y: target.y }];
    }
    if (!this.isNavigationLineBlocked(start, target)) {
      return [{ x: Number(target.x.toFixed(2)), y: Number(target.y.toFixed(2)) }];
    }
    const startCell = this.nearestWalkableCell(this.positionToNavigationCell(start));
    const targetCell = this.nearestWalkableCell(this.positionToNavigationCell(target));
    if (!startCell || !targetCell) {
      return null;
    }
    const targetKey = this.navigationCellKey(targetCell);
    const open = [startCell];
    const cameFrom = new Map();
    const gScore = new Map([[this.navigationCellKey(startCell), 0]]);
    const fScore = new Map([[this.navigationCellKey(startCell), this.navigationCellDistance(startCell, targetCell)]]);
    const closed = new Set();
    let iterations = 0;

    while (open.length > 0 && iterations < 12000) {
      iterations += 1;
      open.sort((left, right) => (fScore.get(this.navigationCellKey(left)) ?? Infinity) - (fScore.get(this.navigationCellKey(right)) ?? Infinity));
      const current = open.shift();
      const currentKey = this.navigationCellKey(current);
      if (currentKey === targetKey) {
        return this.compressNavigationPath(this.reconstructNavigationCells(cameFrom, current).map((cell) => this.navigationCellToPosition(cell)), target);
      }
      closed.add(currentKey);
      this.navigationNeighbors(current).forEach((neighbor) => {
        const neighborKey = this.navigationCellKey(neighbor);
        if (closed.has(neighborKey)) {
          return;
        }
        const tentative = (gScore.get(currentKey) ?? Infinity) + this.navigationCellDistance(current, neighbor);
        if (tentative >= (gScore.get(neighborKey) ?? Infinity)) {
          return;
        }
        cameFrom.set(neighborKey, current);
        gScore.set(neighborKey, tentative);
        fScore.set(neighborKey, tentative + this.navigationCellDistance(neighbor, targetCell));
        if (!open.some((cell) => cell.x === neighbor.x && cell.y === neighbor.y)) {
          open.push(neighbor);
        }
      });
    }
    return null;
  }

  positionToNavigationCell(position) {
    return {
      x: Math.round(position.x / navigationGridSize),
      y: Math.round(position.y / navigationGridSize),
    };
  }

  navigationCellToPosition(cell) {
    return {
      x: Number((cell.x * navigationGridSize).toFixed(2)),
      y: Number((cell.y * navigationGridSize).toFixed(2)),
    };
  }

  navigationCellKey(cell) {
    return `${cell.x},${cell.y}`;
  }

  navigationCellDistance(left, right) {
    return Math.hypot(left.x - right.x, left.y - right.y);
  }

  nearestWalkableCell(origin) {
    if (this.isNavigationCellWalkable(origin)) {
      return origin;
    }
    for (let radius = 1; radius <= 8; radius += 1) {
      for (let dx = -radius; dx <= radius; dx += 1) {
        for (let dy = -radius; dy <= radius; dy += 1) {
          if (Math.abs(dx) !== radius && Math.abs(dy) !== radius) {
            continue;
          }
          const candidate = { x: origin.x + dx, y: origin.y + dy };
          if (this.isNavigationCellWalkable(candidate)) {
            return candidate;
          }
        }
      }
    }
    return null;
  }

  navigationNeighbors(cell) {
    const neighbors = [];
    for (let dx = -1; dx <= 1; dx += 1) {
      for (let dy = -1; dy <= 1; dy += 1) {
        if (dx === 0 && dy === 0) {
          continue;
        }
        const candidate = { x: cell.x + dx, y: cell.y + dy };
        if (!this.isNavigationCellWalkable(candidate)) {
          continue;
        }
        if (dx !== 0 && dy !== 0) {
          if (!this.isNavigationCellWalkable({ x: cell.x + dx, y: cell.y }) || !this.isNavigationCellWalkable({ x: cell.x, y: cell.y + dy })) {
            continue;
          }
        }
        neighbors.push(candidate);
      }
    }
    return neighbors;
  }

  isNavigationCellWalkable(cell) {
    const position = this.navigationCellToPosition(cell);
    return position.x >= 0
      && position.y >= 0
      && position.x < this.state.map.width
      && position.y < this.state.map.height
      && !this.isNavigationBlocked(position);
  }

  isNavigationBlocked(position) {
    const walkableAreas = this.state.map.collision_map?.walkable_areas || [];
    if (walkableAreas.length && !walkableAreas.some((area) => this.isPointInWalkableArea(position, area))) {
      return true;
    }
    if ((this.state.map.collision_map?.objects || []).some((collisionObject) => this.isCollisionObjectBlocking(collisionObject, position))) {
      return true;
    }
    return (this.state.map.features || []).some((feature) => {
      if (feature.kind === "tree") {
        const trunk = {
          x: feature.position.x + feature.width / 2,
          y: feature.position.y + feature.height * 0.82,
        };
        return Math.hypot(position.x - trunk.x, position.y - trunk.y) <= 0.42;
      }
      if (!feature.blocks_movement && !collidableRectFeatureKinds.has(feature.kind)) {
        return false;
      }
      return position.x > feature.position.x
        && position.x < feature.position.x + feature.width
        && position.y > feature.position.y
        && position.y < feature.position.y + feature.height;
    });
  }

  isCollisionObjectBlocking(collisionObject, position) {
    if (collisionObject.shape === "rect") {
      return collisionObject.position
        && position.x >= collisionObject.position.x - playerCollisionRadius
        && position.x <= collisionObject.position.x + collisionObject.width + playerCollisionRadius
        && position.y >= collisionObject.position.y - playerCollisionRadius
        && position.y <= collisionObject.position.y + collisionObject.height + playerCollisionRadius;
    }
    if (collisionObject.shape === "circle") {
      return collisionObject.position
        && Math.hypot(position.x - collisionObject.position.x, position.y - collisionObject.position.y) <= collisionObject.radius + playerCollisionRadius;
    }
    if (collisionObject.shape === "polygon") {
      const points = collisionObject.points || [];
      const inHole = (collisionObject.holes || []).some((hole) => this.pointInPolygon(position, hole));
      return (this.pointInPolygon(position, points) && !inHole) || this.isNearPolygonEdge(position, points, playerCollisionRadius);
    }
    return false;
  }

  isPointInWalkableArea(position, walkableArea) {
    return this.pointInPolygon(position, walkableArea.points || [])
      && !(walkableArea.holes || []).some((hole) => this.pointInPolygon(position, hole));
  }

  pointInPolygon(position, points) {
    if (points.length < 3) {
      return false;
    }
    let inside = false;
    let previous = points[points.length - 1];
    points.forEach((current) => {
      if ((current.y > position.y) !== (previous.y > position.y)) {
        const intersectionX = ((previous.x - current.x) * (position.y - current.y)) / (previous.y - current.y) + current.x;
        if (position.x < intersectionX) {
          inside = !inside;
        }
      }
      previous = current;
    });
    return inside;
  }

  isNearPolygonEdge(position, points, radius) {
    if (points.length < 2) {
      return false;
    }
    return points.some((point, index) => {
      const next = points[(index + 1) % points.length];
      return this.distancePointToSegment(position, point, next) <= radius;
    });
  }

  isNavigationLineBlocked(start, target) {
    const length = distance(start, target);
    const samples = Math.max(1, Math.ceil(length / (navigationGridSize * 0.5)));
    for (let index = 1; index <= samples; index += 1) {
      const progress = index / samples;
      const position = {
        x: start.x + (target.x - start.x) * progress,
        y: start.y + (target.y - start.y) * progress,
      };
      if (this.isNavigationBlocked(position)) {
        return true;
      }
    }
    return false;
  }

  distancePointToSegment(point, start, end) {
    const segmentX = end.x - start.x;
    const segmentY = end.y - start.y;
    const lengthSquared = segmentX * segmentX + segmentY * segmentY;
    if (lengthSquared === 0) {
      return distance(point, start);
    }
    const progress = Math.max(0, Math.min(1, ((point.x - start.x) * segmentX + (point.y - start.y) * segmentY) / lengthSquared));
    return distance(point, {
      x: start.x + segmentX * progress,
      y: start.y + segmentY * progress,
    });
  }

  reconstructNavigationCells(cameFrom, current) {
    const path = [current];
    let cursor = current;
    while (cameFrom.has(this.navigationCellKey(cursor))) {
      cursor = cameFrom.get(this.navigationCellKey(cursor));
      path.unshift(cursor);
    }
    return path;
  }

  compressNavigationPath(path, target) {
    if (!path.length) {
      return [{ x: Number(target.x.toFixed(2)), y: Number(target.y.toFixed(2)) }];
    }
    const result = [];
    let anchor = path[0];
    for (let index = 1; index < path.length; index += 1) {
      const candidate = path[index];
      if (this.isNavigationLineBlocked(anchor, candidate)) {
        const previous = path[index - 1];
        result.push(previous);
        anchor = previous;
      }
    }
    const finalTarget = { x: Number(target.x.toFixed(2)), y: Number(target.y.toFixed(2)) };
    result.push(this.isNavigationLineBlocked(anchor, finalTarget) ? path[path.length - 1] : finalTarget);
    return result;
  }

  startTween(playerId, from, to) {
    const duration = Math.max(120, (distance(from, to) / movementSpeed) * 1000);
    this.state.tweens.set(playerId, { from, to, elapsed: 0, duration });
    this.state.playerFacings.set(playerId, directionFromDelta(to.x - from.x, to.y - from.y));
  }

  setRenderPosition(playerId, position) {
    this.state.renderPositions.set(playerId, { x: position.x, y: position.y });
    this.state.tweens.delete(playerId);
  }

  startRenderPath(playerId, start, waypoints) {
    const points = waypoints
      .filter((point) => Math.hypot(point.x - start.x, point.y - start.y) > 0.01)
      .map((point) => ({ x: point.x, y: point.y }));
    if (!points.length) {
      this.setRenderPosition(playerId, start);
      return;
    }
    const path = [{ x: start.x, y: start.y }, ...points];
    const segmentLengths = [];
    let totalLength = 0;
    for (let index = 1; index < path.length; index += 1) {
      const segmentLength = Math.hypot(path[index].x - path[index - 1].x, path[index].y - path[index - 1].y);
      segmentLengths.push(segmentLength);
      totalLength += segmentLength;
    }
    this.state.playerFacings.set(playerId, directionFromDelta(path[1].x - path[0].x, path[1].y - path[0].y));
    this.state.tweens.set(playerId, {
      path,
      segmentLengths,
      totalLength,
      elapsed: 0,
      duration: Math.max(120, (totalLength / movementSpeed) * 1000),
    });
  }

  tweenFinalTarget(tween) {
    return tween.path?.[tween.path.length - 1] || tween.to;
  }

  positionsMatch(left, right, tolerance = 0.01) {
    return Boolean(left && right && Math.abs(left.x - right.x) <= tolerance && Math.abs(left.y - right.y) <= tolerance);
  }

  activeTweenEndsAt(playerId, target) {
    const activeTween = this.state.tweens.get(playerId);
    return Boolean(activeTween && this.positionsMatch(this.tweenFinalTarget(activeTween), target));
  }

  waitForTweenToFinish(playerId) {
    return new Promise((resolve) => {
      const check = () => {
        if (!this.state.tweens.has(playerId)) {
          resolve();
          return;
        }
        requestAnimationFrame(check);
      };
      check();
    });
  }

  syncPlayerRenderPosition(player, previous = null) {
    const target = player.position;
    if (player.map_id !== this.state.map?.id) {
      this.state.tweens.delete(player.id);
      return;
    }
    if (this.activeTweenEndsAt(player.id, target)) {
      return;
    }
    const current = this.state.renderPositions.get(player.id) || previous?.position || player.position;
    if (this.positionsMatch(current, target)) {
      this.setRenderPosition(player.id, target);
      return;
    }
    const path = this.findNavigationPath(current, target);
    if (path?.length) {
      this.startRenderPath(player.id, current, this.expandMovementWaypoints(current, path));
    } else {
      this.startTween(player.id, current, target);
    }
  }

  positionAlongTweenPath(tween, travelled) {
    let remaining = travelled;
    for (let index = 1; index < tween.path.length; index += 1) {
      const segmentLength = tween.segmentLengths[index - 1];
      if (remaining <= segmentLength || index === tween.path.length - 1) {
        const from = tween.path[index - 1];
        const to = tween.path[index];
        const progress = segmentLength > 0 ? Math.min(1, remaining / segmentLength) : 1;
        return {
          x: from.x + (to.x - from.x) * progress,
          y: from.y + (to.y - from.y) * progress,
        };
      }
      remaining -= segmentLength;
    }
    return { ...this.tweenFinalTarget(tween) };
  }

  nearbyGatherable() {
    if (!this.state.map || !this.state.player) return null;
    const position = this.getPlayerRenderPosition(this.state.player);
    return (this.state.map.gathering_areas || [])
      .filter((area) => this.pointInsideGatheringArea(position, area))
      .sort((left, right) => this.gatheringAreaDistance(left, position) - this.gatheringAreaDistance(right, position))[0] || null;
  }

  pointInsideGatheringArea(position, area) {
    return position.x >= area.position.x
      && position.y >= area.position.y
      && position.x <= area.position.x + area.width
      && position.y <= area.position.y + area.height;
  }

  gatheringAreaDistance(area, position) {
    const center = { x: area.position.x + area.width / 2, y: area.position.y + area.height / 2 };
    return distance(center, position);
  }

  toggleGathering(resource) {
    if (!resource?.item_id) return;
    if (this.state.gathering?.resourceId === resource.id) {
      this.cancelGathering();
      this.render();
      return;
    }
    if (!this.isPartyLeader()) {
      this.log("[Party] Only the party leader can gather for the party.");
      return;
    }
    this.cancelMovement();
    if (this.state.player?.is_resting) {
      this.toggleResting().catch((error) => this.log(error.message));
    }
    this.state.gathering = {
      resourceId: resource.id,
      itemId: resource.item_id,
      itemName: resource.item_name || resource.name,
      startedAt: performance.now(),
      duration: 2400,
    };
    this.playSound(this.gatheringStartSound(resource), 0.48);
    this.render();
  }

  gatheringStartSound(resource) {
    const label = `${resource?.id || ""} ${resource?.item_id || ""} ${resource?.item_name || ""} ${resource?.name || ""}`.toLowerCase();
    const mining = ["ore", "ruda", "iron", "copper", "stone", "rock", "min"].some((token) => label.includes(token));
    return mining ? audioPaths.sfx?.mineStart || audioPaths.sfx?.gatherStart : audioPaths.sfx?.gatherStart;
  }

  cancelGathering() {
    if (!this.state.gathering) return;
    this.state.gathering = null;
    this.state.gatheringRequestPending = false;
  }

  gatheringProgress(gathering) {
    const raw = (performance.now() - gathering.startedAt) / gathering.duration;
    return clamp(raw, 0, this.state.gatheringRequestPending ? 0.98 : 1);
  }

  async gatherOnce(resourceId) {
    if (!this.state.player || !this.state.gathering || this.state.gathering.resourceId !== resourceId) return;
    const gathering = { ...this.state.gathering };
    const result = await this.api.request(`/players/${this.state.player.id}/gather/${resourceId}`, { method: "POST" });
    this.state.player = result.player;
    this.syncPlayer(this.state.player);
    const granted = result.granted_items?.[0];
    const name = result.gathering_area?.item_name || gathering.itemName || this.formatIdLabel(granted?.item_id);
    const quantity = granted?.quantity || 1;
    this.queueItemTakePopup(this.grantedItemsFromResult(result), `${name}: gathered`);
    this.state.gatheringPopups.push({
      label: `+${quantity} ${name}`,
      createdAt: performance.now(),
      duration: 1300,
      worldOffsetX: (Math.random() - 0.5) * 0.8,
      worldOffsetY: -0.85 - Math.random() * 0.2,
    });
    if (this.state.gathering?.resourceId === resourceId) {
      this.state.gathering.startedAt = performance.now();
    }
    await this.refreshWorld();
    this.render();
  }

  async toggleResting() {
    if (!this.state.player) return;
    if (!this.isPartyLeader()) {
      this.log("[Party] Only the party leader can start or stop rest for the party.");
      return;
    }
    const resting = !this.state.player.is_resting;
    let stoppedAt = null;
    if (resting) {
      this.cancelGathering();
      stoppedAt = this.cancelMovement();
    }
    try {
      if (resting) {
        await this.syncServerPositionToRenderPosition(stoppedAt);
      }
      this.state.player = await this.api.request(`/players/${this.state.player.id}/rest`, {
        method: "POST",
        body: JSON.stringify({ resting }),
      });
      this.syncPlayer(this.state.player);
      this.playSound(resting ? audioPaths.restStart : audioPaths.restEnd, resting ? 0.58 : 0.5);
      if (resting) {
        this.setRenderPosition(this.state.player.id, this.state.player.position);
      }
      this.state.nextRestRefreshAt = 0;
      this.state.nextRestPopupAt = 0;
      this.render();
    } catch (error) {
      this.log(error.message);
    }
  }

  async startEnemyCombat(enemyGroup) {
    if (!this.isPartyLeader()) {
      this.log("[Party] Only the party leader can start a fight for the party.");
      return;
    }
    try {
      this.state.combat = await this.api.request(`/players/${this.state.player.id}/enemy-groups/${enemyGroup.id}/combats`, { method: "POST" });
      this.state.selectedEnemyGroup = null;
      this.clearConversation({ render: false });
      this.state.activeWindow = "combat";
      this.render();
    } catch (error) {
      this.log(error.message);
    }
  }

  async sendChat(value) {
    const parsed = this.parseWhisperInput(value);
    const textValue = parsed.text;
    if (!textValue) return;
    if (await this.handleLocalChatCommand(textValue)) {
      return;
    }
    if (this.state.socket?.readyState === WebSocket.OPEN) {
      const targetPlayerId = parsed.targetPlayerId || this.state.chatTargetPlayerId;
      if (targetPlayerId) {
        const target = this.privateChatCandidates().find((player) => player.id === targetPlayerId);
        if (target) {
          this.state.chatTargetPlayerId = target.id;
          this.rememberPrivateChatPlayer(target);
        }
        this.state.socket.send(JSON.stringify({ type: "chat", channel: "whisper", target_player_id: targetPlayerId, text: textValue }));
        return;
      }
      const channel = this.effectiveChatChannel();
      if (channel === "private") {
        this.log("[Private] Select a player or use /w name message.");
        return;
      }
      this.state.socket.send(JSON.stringify({ type: "chat", channel, text: textValue }));
    } else {
      this.log("Chat is not connected.");
    }
  }

  async sendCombatChat(value) {
    const textValue = String(value || "").trim();
    if (!textValue) return;
    if (await this.handleLocalChatCommand(textValue)) {
      return;
    }
    if (this.state.socket?.readyState === WebSocket.OPEN) {
      this.state.socket.send(JSON.stringify({ type: "chat", channel: "combat", text: textValue }));
    } else {
      this.log("Chat is not connected.");
    }
  }

  async createPartyInvite(player) {
    if (!this.state.player || !player?.id) return;
    try {
      const invite = await this.api.request(`/players/${this.state.player.id}/party-invites/${player.id}`, { method: "POST" });
      this.state.selectedPlayer = null;
      this.log(`[System] Party invite sent to ${invite.invitee_name}.`);
      await this.refreshWorld();
      this.render();
    } catch (error) {
      this.log(error.message);
    }
  }

  async leaveParty() {
    if (!this.state.player?.group_id) return;
    try {
      await this.api.request(`/players/${this.state.player.id}/groups/leave`, { method: "POST" });
      await this.refreshWorldAndCurrentMap();
      this.log("[Party] You left the party.");
    } catch (error) {
      this.log(error.message);
    }
  }

  async kickPartyMember(member) {
    if (!this.state.player?.group_id || !member?.id || member.id === this.state.player.id) return;
    const party = this.currentParty();
    if (party?.leader_id !== this.state.player.id) {
      this.log("[Party] Only the party leader can kick members.");
      return;
    }
    if (!window.confirm(`Remove ${member.name || "this player"} from the party?`)) {
      return;
    }
    try {
      await this.api.request(`/players/${this.state.player.id}/groups/kick/${member.id}`, { method: "POST" });
      await this.refreshWorldAndCurrentMap();
      this.log(`[Party] Removed ${member.name || this.formatIdLabel(member.id)} from the party.`);
      this.renderUi();
    } catch (error) {
      this.log(error.message);
    }
  }

  async createFriendRequest(player) {
    if (!this.state.player || !player?.id) return;
    if (this.isFriend(player.id)) {
      this.log(`[System] ${player.name} is already your friend.`);
      this.state.selectedPlayer = null;
      this.renderUi();
      return;
    }
    try {
      const request = await this.api.request(`/players/${this.state.player.id}/friend-requests/${player.id}`, { method: "POST" });
      this.state.selectedPlayer = null;
      this.mergePendingFriendRequest(request);
      this.log(`[System] Friend request sent to ${request.target_name}.`);
      await this.refreshWorld();
      this.render();
    } catch (error) {
      this.log(error.message);
    }
  }

  async inspectPlayer(playerOrId) {
    if (!this.state.player || !playerOrId) return;
    const targetId = typeof playerOrId === "string" ? playerOrId : playerOrId.id;
    if (!targetId) return;
    try {
      const profile = await this.api.request(`/players/${this.state.player.id}/inspect/${targetId}`);
      const gear = (profile.equipment || [])
        .filter((item) => item.item_id)
        .slice(0, 6)
        .map((item) => `${this.formatIdLabel(item.slot)}: ${item.name || this.formatIdLabel(item.item_id)}`)
        .join(" | ") || "no visible gear";
      const guild = profile.guild_name ? `${profile.guild_name} Lv ${profile.guild_level || 1}` : "no guild";
      const title = profile.active_title?.label ? ` | ${profile.active_title.label}` : "";
      this.log(`[Inspect] ${profile.name} Lv ${profile.level} ${this.formatIdLabel(profile.class_name)}${title} | ${guild} | power ${profile.readiness?.power_score || 0} | ${gear}.`);
      this.state.selectedPlayer = null;
      this.renderUi();
    } catch (error) {
      this.log(error.message);
      this.renderUi();
    }
  }

  async loadCardDeck() {
    if (!this.state.player) return;
    try {
      this.state.cardDeckView = await this.api.request(`/players/${this.state.player.id}/card-deck`);
      this.renderUi();
    } catch (error) {
      this.log(error.message);
    }
  }

  startEquipmentDrag(event, source, item, slot = null) {
    if (!item?.item_id || this.state.combatDrag) return;
    const tutorialTarget = this.state.player?.tutorial?.target;
    if (tutorialTarget && (source !== "inventory" || tutorialTarget.kind !== "item" || tutorialTarget.id !== item.item_id)) {
      this.showTutorialBlocked();
      return;
    }
    event.stopPropagation?.();
    this.state.equipmentDrag = {
      source,
      item,
      slot,
      x: event.global.x,
      y: event.global.y,
    };
  }

  updateEquipmentDrag(event) {
    if (!this.state.equipmentDrag) return false;
    this.state.equipmentDrag.x = event.global.x;
    this.state.equipmentDrag.y = event.global.y;
    this.renderUi();
    return true;
  }

  finishEquipmentDrag(event) {
    const drag = this.state.equipmentDrag;
    if (!drag) return false;
    const targetSlot = this.eventAncestorValue(event, "_equipmentSlotId");
    const inventoryDrop = Boolean(this.eventAncestorValue(event, "_equipmentInventoryDrop"));
    this.state.equipmentDrag = null;
    if (drag.source === "inventory" && targetSlot) {
      if (drag.item.slot !== targetSlot) {
        this.log(`[Equipment] ${drag.item.name || this.formatIdLabel(drag.item.item_id)} belongs in ${this.equipmentSlotLabel(drag.item.slot)}, not ${this.equipmentSlotLabel(targetSlot)}.`);
        this.renderUi();
        return true;
      }
      this.equipInventoryItem(drag.item);
      return true;
    }
    if (drag.source === "equipped" && inventoryDrop) {
      this.unequipInventorySlot(drag.slot);
      return true;
    }
    this.renderUi();
    return true;
  }

  cancelEquipmentDrag() {
    if (!this.state.equipmentDrag) return;
    this.state.equipmentDrag = null;
    this.renderUi();
  }

  drawEquipmentDragGhost() {
    const drag = this.state.equipmentDrag;
    if (!drag) return;
    const ghost = new PIXI.Container();
    ghost.eventMode = "none";
    const width = 154;
    const height = 54;
    ghost.position.set(clamp(drag.x + 14, 6, this.state.viewport.width - width - 6), clamp(drag.y + 14, 6, this.state.viewport.height - height - 6));
    ghost.addChild(panel(width, height, { fill: 0x241b10, alpha: 0.94, stroke: 0xffdf7a, strokeAlpha: 0.96, radius: 5 }));
    const texture = this.itemIconTexture(drag.item);
    if (texture) {
      const icon = new PIXI.Sprite(texture);
      icon.position.set(7, 7);
      icon.width = 40;
      icon.height = 40;
      ghost.addChild(icon);
    }
    const label = text(drag.item.name || this.formatIdLabel(drag.item.item_id), { fontSize: 11, fill: 0xffefc2, fontWeight: "900", wordWrap: true, wordWrapWidth: width - 58 });
    label.position.set(54, 10);
    const hint = text(drag.source === "equipped" ? "→ Inventory" : `→ ${this.equipmentSlotLabel(drag.item.slot)}`, { fontSize: 10, fill: 0x9de6a9, fontWeight: "900" });
    hint.position.set(54, 34);
    ghost.addChild(label, hint);
    this.layers.tooltip.addChild(ghost);
  }

  async updateCardDeck(cardIds) {
    if (!this.state.player) return;
    try {
      this.state.player = await this.api.request(`/players/${this.state.player.id}/card-deck`, {
        method: "POST",
        body: JSON.stringify({ card_ids: cardIds }),
      });
      this.syncPlayer(this.state.player);
      this.state.cardDeckView = await this.api.request(`/players/${this.state.player.id}/card-deck`);
      this.render();
    } catch (error) {
      this.log(error.message);
    }
  }

  async equipInventoryItem(item) {
    if (!this.state.player || !item?.item_id) return;
    try {
      this.state.player = await this.api.request(`/players/${this.state.player.id}/equip`, {
        method: "POST",
        body: JSON.stringify({ item_id: item.item_id }),
      });
      this.syncPlayer(this.state.player);
      if (this.state.player?.tutorial?.target?.ui !== "inventory") this.state.activeWindow = null;
      await this.refreshWorld();
      this.render();
    } catch (error) {
      this.log(error.message);
    }
  }

  async unequipInventorySlot(slot) {
    if (!this.state.player || !slot) return;
    try {
      this.state.player = await this.api.request(`/players/${this.state.player.id}/unequip`, {
        method: "POST",
        body: JSON.stringify({ slot }),
      });
      this.syncPlayer(this.state.player);
      this.state.hoveredEquipmentSlot = null;
      if (this.state.cardDeckView) {
        this.state.cardDeckView = await this.api.request(`/players/${this.state.player.id}/card-deck`);
      }
      await this.refreshWorld();
      this.render();
    } catch (error) {
      this.log(`[Equipment] ${error.message}`);
      this.renderUi();
    }
  }

  async useInventoryItem(item) {
    if (!this.state.player || !item?.item_id) return;
    try {
      this.state.player = await this.api.request(`/players/${this.state.player.id}/items/${item.item_id}/use`, { method: "POST" });
      this.syncPlayer(this.state.player);
      if (this.state.player?.tutorial?.target?.ui !== "inventory") this.state.activeWindow = null;
      this.log(`[Inventory] Used ${item.name || this.formatIdLabel(item.item_id)}.`);
      await this.refreshWorld();
      this.render();
    } catch (error) {
      this.log(error.message);
      this.renderUi();
    }
  }

  async bankTransfer(action, itemId, quantity = 1) {
    if (!this.state.player || !itemId) return;
    try {
      const qty = Math.max(1, Number.parseInt(quantity, 10) || 1);
      this.state.player = await this.api.request(`/players/${this.state.player.id}/bank/${action}`, {
        method: "POST",
        body: JSON.stringify({ item_id: itemId, quantity: qty }),
      });
      this.syncPlayer(this.state.player);
      this.log(`[Bank] ${action === "deposit" ? "Deposited" : "Withdrew"} ${qty}x ${this.formatIdLabel(itemId)}.`);
      await this.refreshWorld();
      this.render();
    } catch (error) {
      this.log(error.message);
      this.renderUi();
    }
  }

  async marketListItem(itemId, quantity = 1, priceEach = 1) {
    if (!this.state.player || !itemId) return;
    try {
      const qty = Math.max(1, Number.parseInt(quantity, 10) || 1);
      const price = Math.max(1, Number.parseInt(priceEach, 10) || 1);
      this.state.player = await this.api.request(`/players/${this.state.player.id}/market/list`, {
        method: "POST",
        body: JSON.stringify({ item_id: itemId, quantity: qty, price_each: price }),
      });
      this.syncPlayer(this.state.player);
      this.log(`[Market] Listed ${qty}x ${this.formatIdLabel(itemId)} for ${price}g each.`);
      await this.refreshWorld();
      this.render();
    } catch (error) {
      this.log(error.message);
      this.renderUi();
    }
  }

  async marketBuyListing(listingId) {
    if (!this.state.player || !listingId) return;
    try {
      this.state.player = await this.api.request(`/players/${this.state.player.id}/market/buy`, {
        method: "POST",
        body: JSON.stringify({ listing_id: listingId }),
      });
      this.syncPlayer(this.state.player);
      this.log(`[Market] Bought listing ${listingId}.`);
      await this.refreshWorld();
      this.render();
    } catch (error) {
      this.log(error.message);
      this.renderUi();
    }
  }

  async createGuild(name) {
    if (!this.state.player || !name) return;
    try {
      this.state.player = await this.api.request(`/players/${this.state.player.id}/guilds`, {
        method: "POST",
        body: JSON.stringify({ name }),
      });
      this.syncPlayer(this.state.player);
      this.log(`[Guild] Founded ${this.state.player.guild?.name || name}.`);
      await this.refreshWorld();
      this.render();
    } catch (error) {
      this.log(error.message);
      this.renderUi();
    }
  }

  async joinGuild(guildId) {
    if (!this.state.player || !guildId) return;
    try {
      this.state.player = await this.api.request(`/players/${this.state.player.id}/guilds/join`, {
        method: "POST",
        body: JSON.stringify({ guild_id: guildId }),
      });
      this.syncPlayer(this.state.player);
      this.log(`[Guild] Joined ${this.state.player.guild?.name || guildId}.`);
      await this.refreshWorld();
      this.render();
    } catch (error) {
      this.log(error.message);
      this.renderUi();
    }
  }

  async leaveGuild() {
    if (!this.state.player) return;
    try {
      const previous = this.state.player.guild?.name || "guild";
      this.state.player = await this.api.request(`/players/${this.state.player.id}/guilds/leave`, { method: "POST" });
      this.syncPlayer(this.state.player);
      this.log(`[Guild] Left ${previous}.`);
      await this.refreshWorld();
      this.render();
    } catch (error) {
      this.log(error.message);
      this.renderUi();
    }
  }

  async guildBankTransfer(action, itemId, quantity = 1) {
    if (!this.state.player || !itemId) return;
    try {
      const qty = Math.max(1, Number.parseInt(quantity, 10) || 1);
      this.state.player = await this.api.request(`/players/${this.state.player.id}/guilds/bank/${action}`, {
        method: "POST",
        body: JSON.stringify({ item_id: itemId, quantity: qty }),
      });
      this.syncPlayer(this.state.player);
      this.log(`[Guild] ${action === "deposit" ? "Deposited" : "Withdrew"} ${qty}x ${this.formatIdLabel(itemId)}.`);
      await this.refreshWorld();
      this.render();
    } catch (error) {
      this.log(error.message);
      this.renderUi();
    }
  }

  async craftRecipe(recipeId) {
    if (!this.state.player || !recipeId) return;
    try {
      this.state.player = await this.api.request(`/players/${this.state.player.id}/craft/${recipeId}`, { method: "POST" });
      this.syncPlayer(this.state.player);
      this.log(`[Crafting] Crafted ${this.formatIdLabel(recipeId)}.`);
      await this.refreshWorld();
      this.render();
    } catch (error) {
      this.log(error.message);
      this.renderUi();
    }
  }

  async allocateStat(stat) {
    if (!this.state.player || !stat || this.state.player.unspent_stat_points <= 0) return;
    try {
      this.state.player = await this.api.request(`/players/${this.state.player.id}/stats/allocate`, {
        method: "POST",
        body: JSON.stringify({ stat, points: 1 }),
      });
      this.syncPlayer(this.state.player);
      this.render();
    } catch (error) {
      this.log(error.message);
    }
  }

  async acceptErrand(errandId) {
    if (!this.state.player || !errandId) return;
    try {
      this.state.player = await this.api.request(`/players/${this.state.player.id}/errands/${errandId}/accept`, { method: "POST" });
      this.syncPlayer(this.state.player);
      this.log(`[Errand] Accepted ${this.formatIdLabel(errandId)}.`);
      await this.refreshWorld();
      this.render();
    } catch (error) {
      this.log(error.message);
      this.renderUi();
    }
  }

  async completeErrand(errandId) {
    if (!this.state.player || !errandId) return;
    try {
      const result = await this.api.request(`/players/${this.state.player.id}/errands/${errandId}/complete`, { method: "POST" });
      this.state.player = result.player;
      this.syncPlayer(this.state.player);
      this.showErrandReward(result.errand_event);
      await this.refreshWorld();
      this.render();
    } catch (error) {
      this.log(error.message);
      this.renderUi();
    }
  }

  async claimActivityReward(period, activityId) {
    if (!this.state.player || !period || !activityId) return;
    try {
      const result = await this.api.request(`/players/${this.state.player.id}/activities/${period}/${activityId}/claim`, { method: "POST" });
      this.state.player = result.player;
      this.syncPlayer(this.state.player);
      this.showActivityReward(result.activity_event);
      await this.refreshWorld();
      this.render();
    } catch (error) {
      this.log(error.message);
      this.renderUi();
    }
  }

  async markMailRead() {
    if (!this.state.player) return;
    try {
      this.state.player = await this.api.request(`/players/${this.state.player.id}/mail/read`, { method: "POST" });
      this.syncPlayer(this.state.player);
      await this.refreshWorld();
      this.log("[System] Mail marked as read.");
      this.render();
    } catch (error) {
      this.log(error.message);
      this.renderUi();
    }
  }

  async answerPartyInvite(invite, accept) {
    if (!this.state.player || !invite?.id) return;
    try {
      await this.api.request(`/players/${this.state.player.id}/party-invites/${invite.id}/${accept ? "accept" : "decline"}`, { method: "POST" });
      this.state.partyInvite = null;
      await this.refreshWorld();
      this.render();
    } catch (error) {
      this.log(error.message);
    }
  }

  async answerFriendRequest(request, accept) {
    if (!this.state.player || !request?.id) return;
    const playerId = this.state.player.id;
    try {
      const response = await this.api.request(`/players/${playerId}/friend-requests/${request.id}/${accept ? "accept" : "decline"}`, { method: "POST" });
      if (accept) {
        this.state.player = response;
        this.syncPlayer(this.state.player);
      }
      this.removePendingFriendRequest(request.id);
      this.state.friendRequest = null;
      await this.refreshWorld();
      this.render();
    } catch (error) {
      this.log(error.message);
    }
  }

  parseWhisperInput(value) {
    const raw = String(value || "").trim();
    const target = this.chatTargetPlayer();
    if (target) {
      const prefix = `/w ${target.name}`;
      if (raw.toLowerCase().startsWith(prefix.toLowerCase())) {
        return {
          text: raw.slice(prefix.length).trim(),
          targetPlayerId: target.id,
        };
      }
    }
    const match = raw.match(/^\/(?:w|whisper)\s+(.+?)\s+(.+)$/i);
    if (!match) {
      return { text: raw, targetPlayerId: null };
    }
    const [, targetName, textValue] = match;
    const typedTarget = this.privateChatCandidates().find((player) => String(player.name || "").toLowerCase() === targetName.toLowerCase());
    return {
      text: textValue.trim(),
      targetPlayerId: typedTarget?.id || this.state.chatTargetPlayerId,
    };
  }

  chatTargetPlayer() {
    if (!this.state.chatTargetPlayerId) {
      return null;
    }
    return this.privateChatCandidates().find((player) => player.id === this.state.chatTargetPlayerId) || null;
  }

  privateChatCandidates() {
    const byId = new Map();
    const add = (player) => {
      if (!player?.id || player.id === this.state.player?.id || byId.has(player.id)) return;
      byId.set(player.id, {
        id: player.id,
        name: player.name || this.formatIdLabel(player.id),
        online: Boolean(player.online ?? (this.state.map?.players || []).some((candidate) => candidate.id === player.id)),
      });
    };
    (this.state.privateChatRecentPlayers || []).forEach(add);
    (this.state.player?.friends || []).forEach(add);
    (this.state.map?.players || []).forEach(add);
    return [...byId.values()];
  }

  isDevCheatCommand(command, options = {}) {
    const allowIncomplete = Boolean(options.allowIncomplete);
    const normalized = command.trim().toLowerCase();
    const exactCommands = new Set([
      "/devhelp",
      "/debug help",
      "/help debug",
      "/debug heal",
      "/heal",
      "/debug outfit",
      "/debug outfit skeletal",
      "/debug outfit layered",
      "/debug dress",
      "/dress",
      "/debug items",
      "/debug allcards",
      "/allcards",
      "/debug all-cards",
      "/all-cards",
      "/commonitems",
      "/uncommonitems",
      "/rareitems",
      "/epicitems",
      "/allitems",
      "/fight",
      "/combat",
      "/collisions",
      "/collision",
      "/kolizje",
      "/kolizja",
      "/allowmap",
      "/map-editor",
      "/walkable-editor",
    ]);
    const reservedPrefixes = [
      "/debug",
      "/gold",
      "/xp",
      "/level",
      "/statpoint",
      "/statpoints",
      "/item",
      "/items",
      "/commonitems",
      "/uncommonitems",
      "/rareitems",
      "/epicitems",
      "/allitems",
      "/card",
      "/allcards",
      "/all-cards",
      "/teleport",
      "/profession",
      "/quest",
      "/errand",
      "/fight",
      "/combat",
    ];
    if (allowIncomplete && reservedPrefixes.some((prefix) => normalized === prefix || normalized.startsWith(`${prefix} `))) {
      return true;
    }
    return normalized === "/devhelp"
      || normalized === "/debug help"
      || normalized === "/help debug"
      || exactCommands.has(normalized)
      || normalized.startsWith("/fight ")
      || normalized.startsWith("/combat ")
      || /^\/(?:debug\s+)?(?:gold|xp|level)\s+\d+$/i.test(command)
      || /^\/(?:debug\s+)?(?:outfit|dress)(?:\s+(?:skeletal|layered))?$/i.test(command)
      || /^\/(?:debug\s+)?statpoints?\s+\d+$/i.test(command)
      || /^\/(?:debug\s+)?item\s+[a-zA-Z0-9_-]+(?:\s+\d+)?$/i.test(command)
      || /^\/(?:debug\s+)?items\s+(common|uncommon|rare|epic|all)(?:\s+\d+)?$/i.test(command)
      || /^\/(?:common|uncommon|rare|epic|all)items(?:\s+\d+)?$/i.test(command)
      || /^\/(?:debug\s+)?card\s+[a-zA-Z0-9_-]+(?:\s+\d+)?$/i.test(command)
      || /^\/(?:debug\s+)?all-?cards\s+\d+$/i.test(command)
      || /^\/(?:debug\s+)?teleport\s+[a-zA-Z0-9_-]+(?:\s+[0-9]+(?:\.[0-9]+)?\s+[0-9]+(?:\.[0-9]+)?)?$/i.test(command)
      || /^\/(?:debug\s+)?reputation\s+[a-zA-Z0-9_-]+\s+-?\d+$/i.test(command)
      || /^\/(?:debug\s+)?profession\s+(herbalism|smithing|cooking|alchemy)\s+\d+$/i.test(command)
      || /^\/(?:debug\s+)?quest\s+[a-zA-Z0-9_-]+\s+(active|completed|failed|abandoned|unstarted)(?:\s+[a-zA-Z0-9_-]+)?$/i.test(command)
      || /^\/(?:debug\s+)?errand\s+[a-zA-Z0-9_-]+\s+(active|completed|expired|unstarted)$/i.test(command);
  }

  async handleLocalChatCommand(command) {
    const normalized = command.toLowerCase();
    if (!this.state.devCheatsEnabled && this.isDevCheatCommand(command, { allowIncomplete: true })) {
      this.log("[Debug] Dev cheat commands are disabled for this deployment.");
      return true;
    }
    if (["/collisions", "/collision", "/kolizje", "/kolizja"].includes(normalized)) {
      this.state.showCollisionObjects = !this.state.showCollisionObjects;
      const blockedCount = this.state.map?.collision_map?.objects?.length || 0;
      const walkableCount = this.state.map?.collision_map?.walkable_areas?.length || 0;
      this.log(`[System] Collision overlay ${this.state.showCollisionObjects ? "on" : "off"} (${blockedCount} blocked, ${walkableCount} walkable).`);
      this.renderMap();
      return true;
    }
    if (["/allowmap", "/map-editor", "/walkable-editor"].includes(normalized)) {
      const editor = window.open("/static/allowmap-editor.html", "_blank", "noopener");
      this.log(editor ? "[System] Opening allowmap editor." : "[System] Popup blocked. Open /static/allowmap-editor.html manually.");
      return true;
    }
    if (normalized === "/help" || normalized === "/commands") {
      const devHint = this.state.devCheatsEnabled ? " Use /devhelp for development cheat commands." : "";
      this.log(`[System] Commands: /where, /who, /inspect, /time, /weather, /ambience, /event, /quests, /ready, /readiness, /errands, /daily, /weekly, /claimdaily, /claimweekly, /mail, /readmail, /bank, /deposit, /withdraw, /market, /listitem, /buylisting, /guild, /guildbank, /createguild, /joinguild, /leaveguild, /gdeposit, /gwithdraw, /leaderboard, /summary, /unlocks, /route, /inventory, /loot, /selltips, /repairtips, /weight, /reputation, /professions, /crafting, /target, /next, /journey, /bestiary, /services, /map.${devHint}`);
      return true;
    }
    if (normalized === "/where") {
      const player = this.state.player;
      this.log(player ? `[System] ${this.formatIdLabel(player.map_id)} at ${player.position?.x ?? "?"}, ${player.position?.y ?? "?"}.` : "[System] Create a character first.");
      return true;
    }
    if (normalized === "/who") {
      const players = Math.max(0, (this.state.map?.players || []).length - (this.state.player ? 1 : 0));
      this.log(`[System] Nearby: ${players} players, ${(this.state.map?.npcs || []).length} NPCs, ${(this.state.map?.enemy_groups || []).length} enemy groups.`);
      return true;
    }
    if (normalized.startsWith("/inspect ")) {
      if (!this.state.player) {
        this.log("[System] Create a character first.");
        return true;
      }
      const query = command.trim().replace(/^\/inspect\s+/i, "").trim().toLowerCase();
      const target = (this.state.map?.players || []).find((player) => player.id.toLowerCase() === query || player.name.toLowerCase() === query);
      if (!target) {
        this.log("[Inspect] Player not found on this map.");
        return true;
      }
      await this.inspectPlayer(target);
      return true;
    }
    if (normalized === "/time") {
      const worldTime = this.state.player?.world_time;
      this.log(worldTime ? `[System] World time: ${worldTime.label}.` : "[System] Create a character first.");
      return true;
    }
    if (normalized === "/weather") {
      const weather = this.state.player?.weather;
      this.log(weather ? `[System] Weather: ${weather.label}. ${weather.description}` : "[System] Create a character first.");
      return true;
    }
    if (normalized === "/ambience") {
      const worldTime = this.state.player?.world_time;
      const weather = this.state.player?.weather;
      this.log(worldTime && weather ? `[System] Ambience: ${worldTime.label}; ${weather.label}. ${weather.description}` : "[System] Create a character first.");
      return true;
    }
    if (normalized === "/event") {
      const event = this.state.player?.world_event;
      this.log(event ? `[System] World event: ${event.label}. ${event.description} Bonus: +${event.bonus_xp_percent}% ${this.formatIdLabel(event.bonus_activity_id)} activity XP. Ends in ${event.ends_in_hours}h.` : "[System] Create a character first.");
      return true;
    }
    if (normalized === "/quests") {
      this.state.activeWindow = "quests";
      const target = this.activeQuestOrErrandTarget();
      this.log(target ? `[System] Tracking ${target.kind}: ${target.entry.title || this.formatIdLabel(target.entry.id)}.` : "[System] No active quest or errand target.");
      this.renderUi();
      return true;
    }
    if (normalized === "/errands") {
      this.state.activeWindow = "quests";
      this.state.journalFilter = "errands";
      localStorage.setItem("vibe-journal-filter", "errands");
      const active = this.state.player?.active_errands || [];
      const ready = active.filter((errand) => (errand.current || 0) >= (errand.required || 1)).length;
      this.log(`[System] Errands: ${active.length} active, ${ready} ready. Talk to contractor NPCs for new errands.`);
      this.renderUi();
      return true;
    }
    if (normalized === "/daily") {
      const entries = this.state.player?.daily_activities || [];
      this.log(entries.length ? `[System] Daily focus: ${entries.map((entry) => `${entry.label} ${entry.current}/${entry.required}${entry.claimed ? " claimed" : entry.claimable ? " ready" : ""}`).join(" | ")}.` : "[System] No daily focus available.");
      return true;
    }
    if (normalized === "/weekly") {
      const entries = this.state.player?.weekly_activities || [];
      this.log(entries.length ? `[System] Weekly goals: ${entries.map((entry) => `${entry.label} ${entry.current}/${entry.required}${entry.claimed ? " claimed" : entry.claimable ? " ready" : ""}`).join(" | ")}.` : "[System] No weekly goals available.");
      return true;
    }
    if (normalized.startsWith("/claimdaily") || normalized.startsWith("/claimweekly")) {
      const period = normalized.startsWith("/claimweekly") ? "weekly" : "daily";
      const entries = period === "weekly" ? this.state.player?.weekly_activities || [] : this.state.player?.daily_activities || [];
      const requestedId = command.trim().split(/\s+/)[1] || "";
      const target = requestedId
        ? entries.find((entry) => entry.id === requestedId || entry.label.toLowerCase() === requestedId.toLowerCase())
        : entries.find((entry) => entry.claimable);
      if (!this.state.player) {
        this.log("[System] Create a character first.");
        return true;
      }
      if (!target) {
        this.log(`[System] No ${period} activity reward is ready to claim.`);
        return true;
      }
      await this.claimActivityReward(period, target.id);
      return true;
    }
    if (normalized === "/mail" || normalized === "/inbox") {
      const entries = this.state.player?.mail || [];
      this.log(entries.length ? `[System] Mail: ${entries.slice(0, 3).map((entry) => `${entry.read ? "" : "*"}${entry.sender}: ${entry.subject}`).join(" | ")}.` : "[System] Mailbox is empty.");
      return true;
    }
    if (normalized === "/readmail") {
      if (!this.state.player) {
        this.log("[System] Create a character first.");
        return true;
      }
      await this.markMailRead();
      return true;
    }
    if (normalized === "/ready") {
      this.state.activeWindow = "quests";
      this.state.journalFilter = "ready";
      localStorage.setItem("vibe-journal-filter", "ready");
      const ready = (this.state.player?.active_errands || []).filter((errand) => (errand.current || 0) >= (errand.required || 1)).length;
      this.log(`[System] Ready journal opened: ${ready} claimable errands.`);
      this.renderUi();
      return true;
    }
    if (normalized === "/readiness" || normalized === "/power") {
      const readiness = this.state.player?.readiness;
      this.log(readiness ? `[System] Readiness: power ${readiness.power_score}, gear ${readiness.gear_score}, deck ${readiness.deck_score}, survival ${readiness.survival_score}. ${readiness.warnings?.length ? `Warnings: ${readiness.warnings.join(", ")}` : "No warnings."}` : "[System] Create a character first.");
      return true;
    }
    if (normalized === "/inventory" || normalized === "/inv") {
      this.state.activeWindow = "inventory";
      this.log(`[System] Inventory opened: ${(this.state.player?.inventory || []).length} item stacks.`);
      this.renderUi();
      return true;
    }
    if (normalized === "/bank" || normalized === "/stash") {
      const entries = this.state.player?.bank || [];
      this.log(entries.length ? `[System] Bank: ${entries.slice(0, 5).map((entry) => `${entry.name} x${entry.quantity}`).join(" | ")} (${this.formatWeight(this.state.player?.bank_weight_kg || 0)}).` : "[System] Bank is empty. Use /deposit item_id quantity.");
      return true;
    }
    if (normalized === "/market" || normalized === "/auction") {
      const entries = this.state.player?.market_listings || [];
      this.log(entries.length ? `[Market] ${entries.slice(0, 6).map((entry) => `#${entry.id} ${entry.name} x${entry.quantity} ${entry.total_price}g by ${entry.seller_name}${entry.is_self ? " (yours)" : ""}`).join(" | ")}.` : "[Market] No active listings. Use /listitem item_id quantity price_each.");
      return true;
    }
    if (normalized === "/leaderboard" || normalized === "/rank") {
      const entries = this.state.player?.leaderboard || [];
      this.log(entries.length ? `[System] Leaderboard: ${entries.slice(0, 5).map((entry) => `#${entry.rank} ${entry.name} L${entry.level} ${entry.score}`).join(" | ")}.` : "[System] Leaderboard is empty.");
      return true;
    }
    if (normalized === "/guild") {
      const guild = this.state.player?.guild;
      const activePerks = (guild?.perks || []).filter((perk) => perk.active).map((perk) => perk.label).join(", ") || "none";
      this.log(guild ? `[Guild] ${guild.name} Lv ${guild.level || 1} (${guild.xp || 0}/${guild.xp_to_next || 100}): ${guild.member_count} members, perks ${activePerks}, bank ${(guild.bank || []).length} stacks ${this.formatWeight(guild.bank_weight_kg || 0)}. Members: ${(guild.members || []).slice(0, 6).map((member) => `${member.name}${member.role === "leader" ? " (leader)" : ""} ${member.contribution_xp || 0}xp`).join(", ")}.` : "[Guild] You are not in a guild. Use /createguild name or /joinguild guild_id.");
      return true;
    }
    if (normalized === "/guildbank" || normalized === "/gvault") {
      const guild = this.state.player?.guild;
      const entries = guild?.bank || [];
      this.log(guild ? (entries.length ? `[Guild] Bank: ${entries.slice(0, 6).map((entry) => `${entry.name} x${entry.quantity}`).join(" | ")} (${this.formatWeight(guild.bank_weight_kg || 0)}).` : "[Guild] Bank is empty. Use /gdeposit item_id quantity.") : "[Guild] Join a guild first.");
      return true;
    }
    if (normalized.startsWith("/createguild ")) {
      if (!this.state.player) {
        this.log("[System] Create a character first.");
        return true;
      }
      const name = command.trim().replace(/^\/createguild\s+/i, "").trim();
      if (!name) {
        this.log("[Guild] Usage: /createguild guild name.");
        return true;
      }
      await this.createGuild(name);
      return true;
    }
    if (normalized.startsWith("/joinguild ")) {
      if (!this.state.player) {
        this.log("[System] Create a character first.");
        return true;
      }
      const guildId = command.trim().split(/\s+/)[1];
      if (!guildId) {
        this.log("[Guild] Usage: /joinguild guild_id.");
        return true;
      }
      await this.joinGuild(guildId);
      return true;
    }
    if (normalized === "/leaveguild") {
      if (!this.state.player) {
        this.log("[System] Create a character first.");
        return true;
      }
      await this.leaveGuild();
      return true;
    }
    if (normalized.startsWith("/gdeposit ") || normalized.startsWith("/gwithdraw ")) {
      if (!this.state.player) {
        this.log("[System] Create a character first.");
        return true;
      }
      const [actionCommand, itemId, quantity = "1"] = command.trim().split(/\s+/);
      if (!itemId) {
        this.log(`[Guild] Usage: ${actionCommand} item_id quantity.`);
        return true;
      }
      await this.guildBankTransfer(actionCommand === "/gdeposit" ? "deposit" : "withdraw", itemId, quantity);
      return true;
    }
    if (normalized.startsWith("/deposit ") || normalized.startsWith("/withdraw ")) {
      if (!this.state.player) {
        this.log("[System] Create a character first.");
        return true;
      }
      const [actionCommand, itemId, quantity = "1"] = command.trim().split(/\s+/);
      if (!itemId) {
        this.log(`[System] Usage: ${actionCommand} item_id quantity.`);
        return true;
      }
      await this.bankTransfer(actionCommand.slice(1), itemId, quantity);
      return true;
    }
    if (normalized.startsWith("/listitem ")) {
      if (!this.state.player) {
        this.log("[System] Create a character first.");
        return true;
      }
      const [, itemId, quantity = "1", priceEach = "1"] = command.trim().split(/\s+/);
      if (!itemId) {
        this.log("[Market] Usage: /listitem item_id quantity price_each.");
        return true;
      }
      await this.marketListItem(itemId, quantity, priceEach);
      return true;
    }
    if (normalized.startsWith("/buylisting ")) {
      if (!this.state.player) {
        this.log("[System] Create a character first.");
        return true;
      }
      const [, listingId] = command.trim().split(/\s+/);
      if (!listingId) {
        this.log("[Market] Usage: /buylisting listing_id.");
        return true;
      }
      await this.marketBuyListing(listingId);
      return true;
    }
    if (normalized === "/loot") {
      this.state.activeWindow = "inventory";
      this.state.inventoryFilter = "all";
      this.state.inventorySort = "density";
      localStorage.setItem("vibe-inventory-filter", "all");
      localStorage.setItem("vibe-inventory-sort", "density");
      const totalValue = (this.state.player?.inventory || []).reduce((sum, item) => sum + Number(item.value || 0) * Number(item.quantity || 0), 0);
      this.log(`[System] Loot view opened. Inventory value: ${totalValue}g.`);
      this.renderUi();
      return true;
    }
    if (normalized === "/selltips") {
      const entries = this.state.player?.sell_recommendations || [];
      this.log(entries.length ? `[System] Sell tips: ${entries.slice(0, 3).map((entry) => `${entry.name} x${entry.quantity} ${entry.total_value}g (${entry.value_density}g/kg)`).join(" | ")}.` : "[System] No sellable recommendations.");
      return true;
    }
    if (normalized === "/repairtips" || normalized === "/repair") {
      const entries = this.state.player?.repair_recommendations || [];
      this.log(entries.length ? `[System] Repair tips: ${entries.slice(0, 3).map((entry) => `${entry.name} ${entry.condition} ${entry.durability}% ${entry.repair_cost}g`).join(" | ")}.` : "[System] No repair recommendations.");
      return true;
    }
    if (normalized === "/weight") {
      const player = this.state.player;
      this.log(player ? `[System] Carry weight: ${this.formatWeight(player.inventory_weight_kg)} / ${this.formatWeight(player.carry_capacity_kg)}.` : "[System] Create a character first.");
      return true;
    }
    if (normalized === "/reputation") {
      this.state.activeWindow = "character";
      const entries = this.state.player?.reputation_standings || Object.entries(this.state.player?.reputation || {}).map(([faction_id, value]) => ({
        faction_id,
        value,
        label: "Neutral",
      }));
      this.log(entries.length ? `[System] Reputation: ${entries.map((entry) => `${this.formatIdLabel(entry.faction_id)} ${entry.value} ${entry.label}`).join(", ")}.` : "[System] No faction reputation yet.");
      this.renderUi();
      return true;
    }
    if (normalized === "/professions") {
      const professions = this.state.player?.professions || [];
      this.log(professions.length ? `[System] Professions: ${professions.map((entry) => `${entry.name || this.formatIdLabel(entry.id)} Lv ${entry.level}`).join(", ")}.` : "[System] No profession progress yet.");
      return true;
    }
    if (normalized === "/crafting" || normalized === "/craft") {
      this.state.activeWindow = "inventory";
      const entries = this.state.player?.crafting_recommendations || [];
      this.log(entries.length ? `[System] Crafting: ${entries.map((entry) => `${entry.name}: ${entry.reason}`).join(" | ")}.` : "[System] No crafting recommendations.");
      this.renderUi();
      return true;
    }
    if (normalized === "/target") {
      const target = this.activeQuestOrErrandTarget();
      this.log(target ? `[System] ${this.formatIdLabel(target.kind)} target: ${target.entry.title || this.formatIdLabel(target.entry.id)} -> ${target.entry.target_location || "unknown location"}.` : "[System] No tracked target.");
      return true;
    }
    if (normalized === "/next") {
      const entries = this.state.player?.recommendations || [];
      this.log(entries.length ? `[System] Next: ${entries.slice(0, 3).map((entry) => `${entry.label}${entry.target_location ? ` -> ${entry.target_location}` : ""}`).join(" | ")}.` : "[System] No recommendations yet.");
      return true;
    }
    if (normalized === "/summary") {
      const summary = this.state.player?.adventure_summary;
      this.log(summary ? `[System] Summary: ${summary.total_kills} kills, ${summary.completed_quests} quests, ${summary.completed_errands} errands, ${summary.discovered_regions} regions, ${summary.titles_earned} titles, daily ${summary.daily_completed}/${summary.daily_total}, weekly ${summary.weekly_completed}/${summary.weekly_total}.` : "[System] Create a character first.");
      return true;
    }
    if (normalized === "/unlocks") {
      const entries = this.state.player?.next_unlocks || [];
      this.log(entries.length ? `[System] Next unlocks: ${entries.slice(0, 5).map((entry) => `${entry.label} Lv ${entry.required_level} ${entry.kind}`).join(" | ")}.` : "[System] No upcoming unlocks.");
      return true;
    }
    if (normalized === "/route" || normalized === "/path") {
      const entries = this.state.player?.route_hints || [];
      this.log(entries.length ? `[System] Route: ${entries.slice(0, 3).map((entry) => `${entry.label}: ${entry.instruction}`).join(" | ")}.` : "[System] No route hints.");
      return true;
    }
    if (normalized === "/journey" || normalized === "/progress") {
      this.state.activeWindow = "character";
      const entries = this.state.player ? this.journeySummaryEntries(this.state.player).slice(0, 4).map((entry) => entry.label).join(" | ") : "Create a character first.";
      this.log(`[System] Journey: ${entries}`);
      this.renderUi();
      return true;
    }
    if (normalized === "/bestiary") {
      this.state.activeWindow = "character";
      const bestiary = this.state.player?.bestiary || [];
      this.log(bestiary.length ? `[System] Bestiary: ${bestiary.slice(0, 4).map((entry) => `${entry.name} x${entry.kills}`).join(", ")}.` : "[System] Bestiary is empty. Defeat enemies to learn entries.");
      this.renderUi();
      return true;
    }
    if (normalized === "/services" || normalized === "/map services") {
      this.state.activeWindow = "world-map";
      const counts = this.countById(this.currentMapServiceEntries().map((entry) => entry.kind));
      const summary = Object.entries(counts).map(([kind, count]) => `${this.formatIdLabel(kind)} ${count}`).join(", ") || "no known services";
      const activity = this.state.map?.activity_summary?.summary;
      this.log(`[System] Current map services: ${activity || summary}.`);
      this.renderUi();
      return true;
    }
    if (normalized === "/map" || normalized === "/worldmap" || normalized === "/world-map") {
      this.state.activeWindow = "world-map";
      this.log("[System] World map opened.");
      this.renderUi();
      return true;
    }
    if (normalized === "/fight help" || normalized === "/combat help") {
      this.log(`[System] Fight command: /fight [enemy]. Examples: /fight, /fight wolf, /fight cave_rat. Enemies: ${debugFightEnemies.map((enemy) => enemy.id).join(", ")}.`);
      return true;
    }
    if (normalized === "/debug help" || normalized === "/help debug" || normalized === "/devhelp") {
      this.log("[Debug] Dev cheat commands: /gold N, /xp N, /level N, /debug heal, /debug outfit skeletal, /debug statpoints N, /debug item ID [N], /debug items RARITY [N], /debug card ID [N], /debug allcards N, /debug teleport MAP [X Y], /debug reputation FACTION N, /debug profession PROFESSION N, /debug quest ID STATUS [STAGE], /debug errand ID STATUS, /fight [enemy], /collisions, /allowmap.");
      return true;
    }
    if (normalized === "/debug heal" || normalized === "/heal") {
      await this.runDebugPost("heal", {}, "healed to full");
      return true;
    }
    if (normalized === "/debug outfit" || normalized === "/debug outfit skeletal" || normalized === "/debug outfit layered" || normalized === "/debug dress" || normalized === "/dress") {
      await this.runDebugLayeredOutfit();
      return true;
    }
    const debugMatch = command.match(/^\/(?:debug\s+)?(gold|xp|level)\s+(\d+)$/i);
    if (debugMatch) {
      await this.runDebugGrant(debugMatch[1].toLowerCase(), Number(debugMatch[2]));
      return true;
    }
    const debugStatPointsMatch = command.match(/^\/(?:debug\s+)?statpoints?\s+(\d+)$/i);
    if (debugStatPointsMatch) {
      await this.runDebugPost("stat-points", { amount: Number(debugStatPointsMatch[1]) }, `stat points granted: ${debugStatPointsMatch[1]}`);
      return true;
    }
    const debugItemMatch = command.match(/^\/(?:debug\s+)?item\s+([a-zA-Z0-9_-]+)(?:\s+(\d+))?$/i);
    if (debugItemMatch) {
      await this.runDebugItemGrant(debugItemMatch[1], Number(debugItemMatch[2] || 1));
      return true;
    }
    const debugItemsMatch = command.match(/^\/(?:debug\s+)?items\s+(common|uncommon|rare|epic|all)(?:\s+(\d+))?$/i);
    if (debugItemsMatch) {
      await this.runDebugItemsGrant(debugItemsMatch[1].toLowerCase(), Number(debugItemsMatch[2] || 1));
      return true;
    }
    const debugRarityItemsMatch = command.match(/^\/(common|uncommon|rare|epic|all)items(?:\s+(\d+))?$/i);
    if (debugRarityItemsMatch) {
      await this.runDebugItemsGrant(debugRarityItemsMatch[1].toLowerCase(), Number(debugRarityItemsMatch[2] || 1));
      return true;
    }
    const debugCardMatch = command.match(/^\/(?:debug\s+)?card\s+([a-zA-Z0-9_-]+)(?:\s+(\d+))?$/i);
    if (debugCardMatch) {
      await this.runDebugCardGrant(debugCardMatch[1], Number(debugCardMatch[2] || 1));
      return true;
    }
    const debugAllCardsMatch = command.match(/^\/(?:(?:debug\s+)?all-?cards)(?:\s+(\d+))?$/i);
    if (debugAllCardsMatch) {
      await this.runDebugAllCards(Number(debugAllCardsMatch[1] || 1));
      return true;
    }
    const debugTeleportMatch = command.match(/^\/(?:debug\s+)?teleport\s+([a-zA-Z0-9_-]+)(?:\s+([0-9]+(?:\.[0-9]+)?)\s+([0-9]+(?:\.[0-9]+)?))?$/i);
    if (debugTeleportMatch) {
      await this.runDebugTeleport(debugTeleportMatch[1], debugTeleportMatch[2] ? Number(debugTeleportMatch[2]) : null, debugTeleportMatch[3] ? Number(debugTeleportMatch[3]) : null);
      return true;
    }
    const debugReputationMatch = command.match(/^\/(?:debug\s+)?reputation\s+([a-zA-Z0-9_-]+)\s+(-?\d+)$/i);
    if (debugReputationMatch) {
      await this.runDebugPost("reputation", { faction_id: debugReputationMatch[1], amount: Number(debugReputationMatch[2]) }, `reputation ${debugReputationMatch[1]} changed by ${debugReputationMatch[2]}`);
      return true;
    }
    const debugProfessionMatch = command.match(/^\/(?:debug\s+)?profession\s+(herbalism|smithing|cooking|alchemy)\s+(\d+)$/i);
    if (debugProfessionMatch) {
      await this.runDebugPost("profession", { profession_id: debugProfessionMatch[1].toLowerCase(), amount: Number(debugProfessionMatch[2]) }, `profession XP granted: ${debugProfessionMatch[1]} +${debugProfessionMatch[2]}`);
      return true;
    }
    const debugQuestMatch = command.match(/^\/(?:debug\s+)?quest\s+([a-zA-Z0-9_-]+)\s+(active|completed|failed|abandoned|unstarted)(?:\s+([a-zA-Z0-9_-]+))?$/i);
    if (debugQuestMatch) {
      await this.runDebugQuestState(debugQuestMatch[1], debugQuestMatch[2].toLowerCase(), debugQuestMatch[3] || null);
      return true;
    }
    const debugErrandMatch = command.match(/^\/(?:debug\s+)?errand\s+([a-zA-Z0-9_-]+)\s+(active|completed|expired|unstarted)$/i);
    if (debugErrandMatch) {
      await this.runDebugPost("errand", { errand_id: debugErrandMatch[1], status: debugErrandMatch[2].toLowerCase() }, `errand ${debugErrandMatch[1]}: ${debugErrandMatch[2].toLowerCase()}`);
      return true;
    }
    if (normalized === "/fight" || normalized === "/combat" || normalized.startsWith("/fight ") || normalized.startsWith("/combat ")) {
      const query = command.replace(/^\/(?:fight|combat)\s*/i, "").trim();
      await this.startCommandCombat(query);
      return true;
    }
    return false;
  }

  ensureDevCheatsEnabled() {
    if (this.state.devCheatsEnabled) {
      return true;
    }
    this.log("[Debug] Dev cheat commands are disabled for this deployment.");
    return false;
  }

  async runDebugGrant(kind, amount) {
    if (!this.ensureDevCheatsEnabled()) {
      return;
    }
    if (!this.state.player) {
      this.log("[System] Create a character before using debug commands.");
      return;
    }
    try {
      this.state.player = await this.api.request(`/players/${this.state.player.id}/debug/${kind}`, {
        method: "POST",
        body: JSON.stringify({ amount }),
      });
      this.syncPlayer(this.state.player);
      await this.refreshWorld();
      this.log(`[Debug] ${kind} set/granted: ${amount}.`);
      this.render();
    } catch (error) {
      this.log(error.message);
    }
  }

  async runDebugPost(endpoint, body, label) {
    if (!this.ensureDevCheatsEnabled()) {
      return;
    }
    if (!this.state.player) {
      this.log("[System] Create a character before using debug commands.");
      return;
    }
    try {
      this.state.player = await this.api.request(`/players/${this.state.player.id}/debug/${endpoint}`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      this.syncPlayer(this.state.player);
      await this.refreshWorld();
      this.log(`[Debug] ${label}.`);
      this.render();
    } catch (error) {
      this.log(error.message);
    }
  }

  async runDebugTeleport(mapId, x = null, y = null) {
    if (!this.ensureDevCheatsEnabled()) {
      return;
    }
    if (!this.state.player) {
      this.log("[System] Create a character before using debug commands.");
      return;
    }
    try {
      this.state.player = await this.api.request(`/players/${this.state.player.id}/debug/teleport`, {
        method: "POST",
        body: JSON.stringify({ map_id: mapId, x, y }),
      });
      this.syncPlayer(this.state.player);
      await this.refreshWorld();
      await this.loadMap(this.state.player.map_id);
      this.log(`[Debug] teleported to ${mapId}${x !== null && y !== null ? ` at ${x}, ${y}` : ""}.`);
      this.render();
    } catch (error) {
      this.log(error.message);
    }
  }

  async runDebugItemGrant(itemId, quantity) {
    if (!this.ensureDevCheatsEnabled()) {
      return;
    }
    if (!this.state.player) {
      this.log("[System] Create a character before using debug commands.");
      return;
    }
    try {
      this.state.player = await this.api.request(`/players/${this.state.player.id}/debug/item`, {
        method: "POST",
        body: JSON.stringify({ item_id: itemId, quantity }),
      });
      this.syncPlayer(this.state.player);
      await this.refreshWorld();
      this.log(`[Debug] item granted: ${quantity}x ${itemId}.`);
      this.render();
    } catch (error) {
      this.log(error.message);
    }
  }

  async runDebugLayeredOutfit() {
    if (!this.ensureDevCheatsEnabled()) {
      return;
    }
    if (!this.state.player) {
      this.log("[System] Create a character before using debug commands.");
      return;
    }
    const itemIds = [
      "training_sword",
      "patched_cloth_armor",
      "plank_guard",
      "militia_iron_cap",
      "dockhand_belt",
      "wolfstep_boots",
    ];
    const playerId = this.state.player.id;
    const equipped = [];
    try {
      for (const itemId of itemIds) {
        this.state.player = await this.api.request(`/players/${playerId}/debug/item`, {
          method: "POST",
          body: JSON.stringify({ item_id: itemId, quantity: 1 }),
        });
      }
      for (const itemId of itemIds) {
        this.state.player = await this.api.request(`/players/${playerId}/equip`, {
          method: "POST",
          body: JSON.stringify({ item_id: itemId }),
        });
        equipped.push(itemId);
      }
      this.syncPlayer(this.state.player);
      await this.refreshWorld();
      this.log(`[Debug] skeletal outfit equipped: ${equipped.join(", ")}.`);
      this.render();
    } catch (error) {
      this.syncPlayer(this.state.player);
      await this.refreshWorld().catch(() => {});
      this.log(`[Debug] skeletal outfit stopped after ${equipped.length}/${itemIds.length} equips: ${error.message}`);
      this.render();
    }
  }

  async runDebugItemsGrant(rarity, quantity = 1) {
    if (!this.ensureDevCheatsEnabled()) {
      return;
    }
    if (!this.state.player) {
      this.log("[System] Create a character before using debug commands.");
      return;
    }
    try {
      this.state.player = await this.api.request(`/players/${this.state.player.id}/debug/items`, {
        method: "POST",
        body: JSON.stringify({ rarity, quantity }),
      });
      this.syncPlayer(this.state.player);
      await this.refreshWorld();
      this.log(`[Debug] ${rarity} items granted: ${quantity}x each.`);
      this.render();
    } catch (error) {
      this.log(error.message);
    }
  }

  async runDebugCardGrant(cardId, quantity = 1) {
    if (!this.ensureDevCheatsEnabled()) {
      return;
    }
    if (!this.state.player) {
      this.log("[System] Create a character before using debug commands.");
      return;
    }
    try {
      this.state.player = await this.api.request(`/players/${this.state.player.id}/debug/card`, {
        method: "POST",
        body: JSON.stringify({ card_id: cardId, quantity }),
      });
      this.syncPlayer(this.state.player);
      this.state.cardDeckView = await this.api.request(`/players/${this.state.player.id}/card-deck`);
      await this.refreshWorld();
      this.log(`[Debug] card granted: ${quantity}x ${cardId}.`);
      this.render();
    } catch (error) {
      this.log(error.message);
    }
  }

  async runDebugAllCards(quantity = 1) {
    if (!this.ensureDevCheatsEnabled()) {
      return;
    }
    if (!this.state.player) {
      this.log("[System] Create a character before using debug commands.");
      return;
    }
    try {
      this.state.player = await this.api.request(`/players/${this.state.player.id}/debug/all-cards`, {
        method: "POST",
        body: JSON.stringify({ quantity }),
      });
      this.syncPlayer(this.state.player);
      this.state.cardDeckView = await this.api.request(`/players/${this.state.player.id}/card-deck`);
      await this.refreshWorld();
      this.log(`[Debug] all cards granted: ${quantity}x each.`);
      this.render();
    } catch (error) {
      this.log(error.message);
    }
  }

  async runDebugQuestState(questId, status, stageId = null) {
    if (!this.ensureDevCheatsEnabled()) {
      return;
    }
    if (!this.state.player) {
      this.log("[System] Create a character before using debug commands.");
      return;
    }
    try {
      this.state.player = await this.api.request(`/players/${this.state.player.id}/debug/quest`, {
        method: "POST",
        body: JSON.stringify({ quest_id: questId, status, stage_id: stageId }),
      });
      this.syncPlayer(this.state.player);
      await this.refreshWorld();
      this.log(`[Debug] quest ${questId}: ${status}${stageId ? ` at ${stageId}` : ""}.`);
      this.render();
    } catch (error) {
      this.log(error.message);
    }
  }

  enemyTemplateForCommand(query) {
    const normalized = query.trim().toLowerCase().replace(/\s+/g, " ");
    if (!normalized || ["any", "random", "losowo"].includes(normalized)) {
      return debugFightEnemies[Math.floor(Math.random() * debugFightEnemies.length)];
    }
    const comparable = normalized.replace(/[\s-]+/g, "_");
    return debugFightEnemies.find((enemy) => enemy.id === comparable)
      || debugFightEnemies.find((enemy) => enemy.name.toLowerCase() === normalized)
      || debugFightEnemies.find((enemy) => enemy.aliases.some((alias) => alias === normalized || alias.replace(/[\s-]+/g, "_") === comparable))
      || debugFightEnemies.find((enemy) => enemy.id.includes(comparable) || enemy.name.toLowerCase().includes(normalized));
  }

  async startCommandCombat(query) {
    if (!this.ensureDevCheatsEnabled()) {
      return;
    }
    if (!this.state.player) {
      this.log("[System] Create a character before starting combat.");
      return;
    }
    const enemy = this.enemyTemplateForCommand(query);
    if (!enemy) {
      this.log(`[System] Unknown enemy "${query}". Try /fight help.`);
      return;
    }
    try {
      this.state.combat = await this.api.request(`/players/${this.state.player.id}/combats`, {
        method: "POST",
        body: JSON.stringify({ enemy_template_id: enemy.id, random_arena: true }),
      });
      await this.refreshWorld();
      this.clearConversation({ render: false });
      this.state.activeWindow = "combat";
      this.log(`[System] Started fight: ${enemy.name}.`);
      this.render();
    } catch (error) {
      this.log(error.message);
    }
  }

  async interact(interactable) {
    try {
      const result = await this.api.request(`/players/${this.state.player.id}/interact/${interactable.id}`, { method: "POST" });
      if (result.player) {
        this.state.player = result.player;
        this.syncPlayer(result.player);
      }
      this.showQuestEventRewards(result.quest_events || []);
      this.queueItemTakePopup(this.grantedItemsFromResult(result), `${interactable.name}: found`);
      if (result.errand_events?.some((event) => event.type === "errand_completed")) {
        result.errand_events.filter((event) => event.type === "errand_completed").forEach((event) => this.showErrandReward(event));
      }
      const dialogue = (result.dialogue || []).join(" ");
      this.state.selectedInteractable = result.interactable || interactable;
      this.state.selectedNpc = null;
      this.state.selectedShop = null;
      this.state.questBranch = null;
      this.state.conversationLine = dialogue || result.message || interactable.description || interactable.name || "";
      this.log(this.state.conversationLine || interactable.name);
      await this.refreshWorld();
      if (this.state.player?.tutorial) {
        this.state.rewardSummary = {
          title: interactable.name,
          lines: [this.state.conversationLine],
          createdAt: performance.now(),
          duration: 4200,
        };
        this.clearConversation({ render: false });
      }
      this.renderUi();
    } catch (error) {
      this.log(error.message);
    }
  }

  async confirmSacrificeLevel() {
    if (!this.state.player) return;
    const level = Number(this.state.player.level || 1);
    const confirmed = window.confirm(
      `Sacrifice your latest level?\n\nThis will return you from level ${level} to level ${Math.max(1, level - 1)}, remove the stats and gains from that level, and grant +1 free stat point. This can only be done once per day.`,
    );
    if (!confirmed) return;
    try {
      this.state.player = await this.api.request(`/players/${this.state.player.id}/temple/sacrifice`, { method: "POST" });
      this.syncPlayer(this.state.player);
      this.log(`[Temple] Sacrificed level ${level}. Gained +1 permanent stat point.`);
      this.clearConversation({ render: false });
      await this.refreshWorldAndCurrentMap();
      this.render();
    } catch (error) {
      this.log(`[Temple] ${error.message}`);
      if (String(error.message || "").includes("cannot sacrifice level 1")) {
        this.showTempleFizzle();
      }
      this.renderUi();
    }
  }

  showTempleFizzle() {
    this.state.rewardPopups.push({
      label: "Fizzled",
      color: 0xb8a7ff,
      createdAt: performance.now(),
      duration: 1800,
      worldOffsetX: 0,
      worldOffsetY: -1.15,
    });
    this.log("[Temple] The altar flares, but level one cannot be taken.");
  }

  async chooseNpcOption(npc, option) {
    if (option?.effect === "end") {
      this.clearConversation();
      return;
    }
    if (option?.effect === "shop") {
      await this.openShop(npc);
      return;
    }
    if (option?.effect === "card_fusion") {
      await this.openCardFusion(npc);
      return;
    }
    try {
      const previousConversationLine = this.state.conversationLine;
      const isBranchChoice = Boolean(this.state.questBranch && option?.id);
      const query = option?.id && !isBranchChoice ? `?option_id=${encodeURIComponent(option.id)}` : "";
      const result = await this.api.request(`/players/${this.state.player.id}/talk/${npc.id}${query}`, {
        method: "POST",
        body: isBranchChoice ? JSON.stringify({
          interaction_id: this.state.questBranch.interactionId,
          revision: this.state.questBranch.revision,
          choice_id: option.id,
        }) : undefined,
      });
      if (result.dialogue?.length) {
        this.log(result.dialogue[0]);
      }
      if (result.player) {
        this.state.player = result.player;
        this.syncPlayer(this.state.player);
      }
      if (result.accepted_errand_id) {
        this.log(`[Errand] Accepted ${result.errand_event?.title || this.formatIdLabel(result.accepted_errand_id)}.`);
      }
      if (result.errand_event?.type === "errand_completed") {
        this.showErrandReward(result.errand_event);
      }
      this.showQuestEventRewards(result.quest_events || []);
      this.queueItemTakePopup(this.grantedItemsFromResult(result), "Quest items");
      if (this.questEventsInclude(result.quest_events || [], "combat_started")) {
        this.state.selectedNpc = null;
        this.state.selectedInteractable = null;
        this.state.selectedShop = null;
        this.state.shopTab = "buy";
        this.state.shopScrollOffsets = { buyItems: 0, buyCards: 0, sell: 0 };
        this.state.questBranch = null;
        this.state.conversationLine = "";
        await this.refreshWorld();
        await this.syncActiveCombat(true);
        this.render();
        return;
      }
      this.state.selectedNpc = result.npc || npc;
      const nextConversationLine = (result.dialogue || []).join(" ");
      this.state.conversationLine = nextConversationLine
        || (result.closed && this.state.itemTakePopup ? previousConversationLine : "");
      this.state.questBranch = (result.dialogue_options || []).length
        ? { options: result.dialogue_options, interactionId: result.interaction_id, revision: result.revision }
        : null;
      if (result.shop) {
        this.state.selectedShop = result.shop;
        this.state.shopTab = "buy";
        this.state.shopScrollOffsets = { buyItems: 0, buyCards: 0, sell: 0 };
      }
      await this.refreshWorld();
      const tutorialConversationEnded = this.state.player?.tutorial
        && (result.closed || this.state.player.tutorial.target?.kind !== "npc" || this.state.player.tutorial.target?.id !== npc.id);
      if (tutorialConversationEnded) {
        if (this.state.itemTakePopup) {
          this.state.itemTakePopup.alongsideStory = true;
          this.state.itemTakePopup.closeConversationOnTake = true;
        } else {
          this.clearConversation({ render: false });
        }
      }
      this.renderUi();
    } catch (error) {
      this.log(error.message);
    }
  }

  async openCombatFollowupDialogue(followup) {
    if (!this.state.player || !followup?.npc_id || !followup?.option_id) {
      return false;
    }
    try {
      const result = await this.api.request(`/players/${this.state.player.id}/talk/${followup.npc_id}?option_id=${encodeURIComponent(followup.option_id)}`, {
        method: "POST",
      });
      this.state.selectedNpc = result.npc || (this.state.map?.npcs || []).find((npc) => npc.id === followup.npc_id) || null;
      this.state.selectedInteractable = null;
      this.state.selectedEnemyGroup = null;
      this.state.selectedShop = null;
      this.state.shopTab = "buy";
      this.state.shopScrollOffsets = { buyItems: 0, buyCards: 0, sell: 0 };
      this.state.conversationLine = (result.dialogue || []).join(" ");
      this.state.questBranch = (result.dialogue_options || []).length
        ? { options: result.dialogue_options, interactionId: result.interaction_id, revision: result.revision }
        : null;
      this.showQuestEventRewards(result.quest_events || []);
      this.queueItemTakePopup(this.grantedItemsFromResult(result), "Quest items");
      this.log(this.state.conversationLine || `[Quest] ${followup.label || "Continue the quest"}.`);
      await this.refreshWorldAndCurrentMap();
      return true;
    } catch (error) {
      this.log(`[Quest] ${error.message}`);
      return false;
    }
  }

  async finishResolvedCombatView(combat) {
    if (this.state.player?.combat_id && this.state.combat?.status === "active") {
      this.state.activeWindow = "combat";
      return;
    }
    this.state.combat = combat;
    this.state.pendingCombatFollowup = combat?.status === "won" ? combat?.followup_dialogue || null : null;
    this.state.activeWindow = "combat";
    this.render();
  }

  flattenQuestEvents(events) {
    const flattened = [];
    const visit = (event) => {
      if (!event) return;
      if (Array.isArray(event.events)) {
        event.events.forEach(visit);
      }
      flattened.push(event);
    };
    events.forEach(visit);
    return flattened;
  }

  questEventsInclude(events, type) {
    return this.flattenQuestEvents(events).some((event) => event.type === type);
  }

  showQuestEventRewards(events) {
    const flattened = this.flattenQuestEvents(events);
    const now = performance.now();
    const labels = [];
    const summaryLines = [];
    let summaryTitle = "";
    flattened.forEach((event) => {
      if (event.type === "started") {
        const questTitle = event.title || this.formatIdLabel(event.quest_id || "quest");
        labels.push({ label: `Quest accepted: ${questTitle}`, color: 0xffdf7a });
        summaryLines.push(questTitle);
        summaryTitle = "New Quest";
      }
      if (event.type === "xp_granted") {
        labels.push({ label: `+${event.amount || 0} XP`, color: 0x8fb7ff });
        summaryLines.push(`+${event.amount || 0} XP`);
        (event.level_events || []).forEach((levelEvent) => this.showLevelUpEffect(levelEvent.level));
      }
      if (event.type === "gold_granted") {
        labels.push({ label: `+${event.amount || 0} gold`, color: 0xe0b95b });
        summaryLines.push(`+${event.amount || 0} gold`);
      }
      if (event.type === "item_granted" && event.item_id) {
        labels.push({ label: `+${event.quantity || 1} ${this.formatIdLabel(event.item_id)}`, color: 0x7bd889 });
        summaryLines.push(`Item: ${this.formatIdLabel(event.item_id)}`);
      }
      if (event.type === "card_granted" && event.card_id) {
        labels.push({ label: `Card: ${this.formatIdLabel(event.card_id)}`, color: 0xd49cff });
        summaryLines.push(`Card: ${this.formatIdLabel(event.card_id)}`);
      }
      if (event.type === "completed") {
        labels.push({ label: "Quest complete", color: 0xffdf7a });
        summaryLines.unshift(`Quest: ${this.formatIdLabel(event.quest_id || "complete")}`);
        summaryTitle = "Quest Complete";
      }
    });
    if (summaryTitle && summaryLines.length) {
      this.state.rewardSummary = {
        title: summaryTitle,
        lines: summaryLines.slice(0, 6),
        createdAt: now,
        duration: 12000,
      };
    }
    labels.slice(0, 5).forEach((popup, index) => {
      this.state.rewardPopups.push({
        ...popup,
        createdAt: now + index * 120,
        duration: 1800,
        worldOffsetX: (index - labels.length / 2) * 0.28,
        worldOffsetY: -1.1 - index * 0.18,
      });
      this.log(`[Reward] ${popup.label}`);
    });
  }

  showLevelUpEffect(level, playerId = this.state.player?.id, options = {}) {
    const now = performance.now();
    const isLocalPlayer = !playerId || playerId === this.state.player?.id;
    this.state.levelUpPopups.push({
      level: Number(level || this.state.player?.level || 0),
      playerId,
      createdAt: now,
      duration: 2600,
    });
    this.playSound(audioPaths.sfx?.levelUp || audioPaths.impact, isLocalPlayer ? 0.66 : 0.34);
    if (options.log !== false && isLocalPlayer) {
      this.log(`[Level] Level up${level ? `: ${level}` : ""}.`);
    }
  }

  showErrandReward(event) {
    if (!event) return;
    const now = performance.now();
    const lines = [];
    if (event.xp) lines.push(`+${event.xp} XP`);
    if (event.gold) lines.push(`+${event.gold} gold`);
    this.state.rewardSummary = {
      title: event.title || "Errand Complete",
      lines,
      createdAt: now,
      duration: 12000,
    };
    [
      event.xp ? { label: `+${event.xp} XP`, color: 0x8fb7ff } : null,
      event.gold ? { label: `+${event.gold} gold`, color: 0xe0b95b } : null,
      { label: "Errand complete", color: 0xffdf7a },
    ].filter(Boolean).forEach((popup, index) => {
      this.state.rewardPopups.push({
        ...popup,
        createdAt: now + index * 120,
        duration: 1800,
        worldOffsetX: (index - 1) * 0.22,
        worldOffsetY: -1.1 - index * 0.18,
      });
      this.log(`[Reward] ${popup.label}`);
    });
    this.renderUi();
  }

  showActivityReward(event) {
    if (!event) return;
    const now = performance.now();
    const xp = Number(event.xp || 0);
    this.state.rewardSummary = {
      title: `${event.period === "weekly" ? "Weekly" : "Daily"} Goal Complete`,
      lines: [`${event.title || this.formatIdLabel(event.activity_id)}: +${xp} XP`],
      createdAt: now,
      duration: 12000,
    };
    [
      xp ? { label: `+${xp} XP`, color: 0x8fb7ff } : null,
      { label: `${event.period === "weekly" ? "Weekly" : "Daily"} claimed`, color: 0xffdf7a },
    ].filter(Boolean).forEach((popup, index) => {
      this.state.rewardPopups.push({
        ...popup,
        createdAt: now + index * 120,
        duration: 1800,
        worldOffsetX: (index - 0.5) * 0.22,
        worldOffsetY: -1.1 - index * 0.18,
      });
      this.log(`[Reward] ${popup.label}`);
    });
    this.renderUi();
  }

  async openShop(npc) {
    if (!this.state.player || !npc?.id) return;
    try {
      this.state.selectedShop = await this.api.request(`/players/${this.state.player.id}/shops/${npc.id}`);
      this.state.selectedCardFusion = null;
      this.state.shopTab = "buy";
      this.state.shopScrollOffsets = { buyItems: 0, buyCards: 0, sell: 0 };
      this.state.selectedNpc = npc;
      this.state.selectedInteractable = null;
      this.state.questBranch = null;
      this.renderUi();
    } catch (error) {
      this.log(error.message);
    }
  }

  async openCardFusion(npc) {
    if (!this.state.player || !npc?.id) return;
    try {
      this.state.selectedCardFusion = await this.api.request(`/players/${this.state.player.id}/card-fusion`);
      this.state.selectedShop = null;
      this.state.cardFusionSlots = [];
      this.state.cardFusionResult = null;
      this.state.cardFusionScrollOffset = 0;
      this.state.hoveredCardFusionKey = null;
      this.state.hoveredCardFusionButton = false;
      this.state.selectedNpc = npc;
      this.state.selectedInteractable = null;
      this.state.questBranch = null;
      this.renderUi();
    } catch (error) {
      this.log(error.message);
    }
  }

  async refreshCardFusion() {
    if (!this.state.player || !this.state.selectedCardFusion) return;
    this.state.selectedCardFusion = await this.api.request(`/players/${this.state.player.id}/card-fusion`);
  }

  cardFusionStackByKey(cardKey) {
    return (this.state.selectedCardFusion?.stacks || []).find((stack) => stack.card_key === cardKey) || null;
  }

  addCardToFusionSlots(cardKey) {
    const stack = this.cardFusionStackByKey(cardKey);
    if (!stack || !stack.can_fuse) return;
    const current = this.state.cardFusionSlots || [];
    if (current.length && current.some((value) => value !== cardKey)) {
      this.state.cardFusionSlots = [];
    }
    if ((this.state.cardFusionSlots || []).length >= 3) return;
    this.state.cardFusionSlots = [...(this.state.cardFusionSlots || []), cardKey];
    this.state.cardFusionResult = null;
    this.renderUi();
  }

  removeCardFusionSlot(index) {
    this.state.cardFusionSlots = (this.state.cardFusionSlots || []).filter((_, slotIndex) => slotIndex !== index);
    this.state.cardFusionResult = null;
    this.renderUi();
  }

  async fuseSelectedCards() {
    const slots = this.state.cardFusionSlots || [];
    if (slots.length !== 3 || new Set(slots).size !== 1 || !this.state.player) return;
    const stack = this.cardFusionStackByKey(slots[0]);
    if (!stack?.can_fuse) return;
    try {
      const result = await this.api.request(`/players/${this.state.player.id}/card-fusion`, {
        method: "POST",
        body: JSON.stringify({ card_key: stack.card_key, tier: stack.tier }),
      });
      this.state.player = result.player;
      this.syncPlayer(this.state.player);
      this.state.selectedCardFusion = result.fusion;
      this.state.cardDeckView = result.card_deck;
      this.state.cardFusionResult = result;
      this.state.cardFusionSlots = [];
      this.log(result.message || (result.success ? "Fusion succeeded." : "Fusion failed."));
      await this.refreshWorld();
      this.render();
    } catch (error) {
      this.log(error.message);
      this.renderUi();
    }
  }

  async refreshSelectedShop() {
    const shop = this.state.selectedShop;
    if (!this.state.player || !shop?.npc_id) return;
    this.state.selectedShop = await this.api.request(`/players/${this.state.player.id}/shops/${shop.npc_id}`);
  }

  async buyShopItem(item) {
    const shop = this.state.selectedShop;
    if (!this.state.player || !shop?.npc_id || !item?.item_id) return;
    try {
      this.state.player = await this.api.request(`/players/${this.state.player.id}/shops/${shop.npc_id}/buy`, {
        method: "POST",
        body: JSON.stringify({ item_id: item.item_id, quantity: 1 }),
      });
      this.syncPlayer(this.state.player);
      await this.refreshWorld();
      await this.refreshSelectedShop();
      this.render();
    } catch (error) {
      this.log(error.message);
      this.renderUi();
    }
  }

  async buyShopCard(card) {
    const shop = this.state.selectedShop;
    if (!this.state.player || !shop?.npc_id || !card?.card_id) return;
    try {
      this.state.player = await this.api.request(`/players/${this.state.player.id}/shops/${shop.npc_id}/buy-card`, {
        method: "POST",
        body: JSON.stringify({ card_id: card.card_id }),
      });
      this.syncPlayer(this.state.player);
      await this.refreshWorld();
      await this.refreshSelectedShop();
      if (this.state.activeWindow === "deck") {
        this.state.cardDeckView = await this.api.request(`/players/${this.state.player.id}/card-deck`);
      }
      this.render();
    } catch (error) {
      this.log(error.message);
      this.renderUi();
    }
  }

  async sellShopItem(item, quantity = 1) {
    const shop = this.state.selectedShop;
    if (!this.state.player || !shop?.npc_id || !item?.item_id) return;
    try {
      this.state.player = await this.api.request(`/players/${this.state.player.id}/shops/${shop.npc_id}/sell`, {
        method: "POST",
        body: JSON.stringify({ item_id: item.item_id, quantity: Math.max(1, quantity) }),
      });
      this.syncPlayer(this.state.player);
      await this.refreshWorld();
      await this.refreshSelectedShop();
      this.render();
    } catch (error) {
      this.log(error.message);
      this.renderUi();
    }
  }

  visibleConversationOptions(npc) {
    if (this.state.questBranch) {
      return this.state.questBranch.options;
    }
    const options = (npc.conversation_options || []).filter((option) => option.available !== false);
    if (this.state.player?.tutorial) {
      return options.filter((option) => option.effect === "quest").slice(0, 7);
    }
    return options.slice(0, 7);
  }

  clearConversation(options = {}) {
    this.state.selectedNpc = null;
    this.state.selectedInteractable = null;
    this.state.selectedShop = null;
    this.state.selectedCardFusion = null;
    this.state.cardFusionSlots = [];
    this.state.cardFusionResult = null;
    this.state.cardFusionDrag = null;
    this.state.hoveredShopItemKey = null;
    this.state.shopTab = "buy";
    this.state.shopScrollOffsets = { buyItems: 0, buyCards: 0, sell: 0 };
    this.state.questBranch = null;
    this.state.conversationLine = "";
    this.state.conversationScrollOffset = 0;
    if (options.render !== false) {
      this.renderUi();
    }
  }

  closeTopSurface() {
    if (this.state.selectedEnemyGroup || this.state.selectedNpc || this.state.selectedInteractable) {
      this.state.selectedEnemyGroup = null;
      this.clearConversation();
      return;
    }
    this.state.activeWindow = null;
    this.renderUi();
  }

  currentParty() {
    if (!this.state.player) return null;
    if (!this.state.player.group_id || !this.state.world) {
      return { id: `solo:${this.state.player.id}`, leader_id: this.state.player.id, member_ids: [this.state.player.id], solo: true };
    }
    return this.state.world.groups.find((group) => group.id === this.state.player.group_id) || {
      id: `solo:${this.state.player.id}`,
      leader_id: this.state.player.id,
      member_ids: [this.state.player.id],
      solo: true,
    };
  }

  isPartyLeader() {
    const party = this.currentParty();
    return !party || party.leader_id === this.state.player?.id;
  }

  pendingFriendRequests() {
    const playerId = this.state.player?.id;
    if (!playerId) return [];
    return (this.state.player.friend_requests || [])
      .filter((request) => request.requester_id === playerId || request.target_id === playerId)
      .sort((a, b) => {
        const aIncoming = a.target_id === playerId ? 0 : 1;
        const bIncoming = b.target_id === playerId ? 0 : 1;
        return aIncoming - bIncoming || (a.created_at || 0) - (b.created_at || 0);
      });
  }

  mergePendingFriendRequest(request) {
    if (!request?.id || !this.state.player) return;
    const requests = this.state.player.friend_requests || [];
    const index = requests.findIndex((candidate) => candidate.id === request.id);
    if (index >= 0) {
      requests[index] = { ...requests[index], ...request };
    } else {
      requests.push(request);
    }
    this.state.player.friend_requests = requests;
  }

  removePendingFriendRequest(requestId) {
    if (!this.state.player?.friend_requests) return;
    this.state.player.friend_requests = this.state.player.friend_requests.filter((request) => request.id !== requestId);
  }

  isFriend(playerId) {
    return Boolean((this.state.player?.friends || []).some((friend) => friend.id === playerId));
  }

  inMap(position) {
    return position.x >= 0 && position.y >= 0 && position.x < this.state.map.width && position.y < this.state.map.height;
  }

  canReachPoint(position) {
    if (!this.state.player?.position) {
      return false;
    }
    const player = this.state.player.position;
    return Math.abs(player.x - position.x) + Math.abs(player.y - position.y) <= 1.5;
  }

  findNpcAt(x, y) {
    return this.state.map.npcs.find((npc) => distance(npc.position, { x, y }) <= 0.75);
  }

  findEnemyGroupAt(x, y) {
    return this.state.map.enemy_groups.find((enemyGroup) => distance(enemyGroup.position, { x, y }) <= enemyGroupHitRadius);
  }

  findPortalAt(x, y) {
    return (this.state.map.portals || []).find((portal) => distance(portal.at, { x, y }) <= 1.25);
  }

  findInteractableAt(x, y) {
    return (this.state.map.interactables || []).find((interactable) => {
      const radius = interactable.kind === "sacrifice_altar" ? 1.45 : 0.75;
      return distance(interactable.position, { x, y }) <= radius;
    });
  }

  findPlayerAt(x, y) {
    const entry = this.partyActorEntries().find((candidate) => distance(this.getPlayerRenderPosition(candidate.leader), { x, y }) <= 0.75);
    return entry?.leader || null;
  }

  aggroThreatForPath(target) {
    if (!this.state.player || !this.state.map) return null;
    return this.state.map.enemy_groups.find((enemyGroup) => enemyGroup.is_aggressive_for_player && enemyGroup.aggro_radius > 0 && distance(enemyGroup.position, target) <= enemyGroup.aggro_radius)
      ? { enemyGroup: this.state.map.enemy_groups.find((enemyGroup) => enemyGroup.is_aggressive_for_player && enemyGroup.aggro_radius > 0 && distance(enemyGroup.position, target) <= enemyGroup.aggro_radius) }
      : null;
  }

  iconSprite(iconId, size, camera) {
    const texture = this.assetTextures.get(iconId) || this.assetTextures.get("map_feature");
    const sprite = new PIXI.Sprite(texture);
    sprite.anchor.set(0.5);
    sprite.width = camera.scale * size;
    sprite.height = camera.scale * size;
    return sprite;
  }

  textureRegion(textureId, x, y, width, height) {
    const sourceTexture = this.assetTextures.get(textureId);
    if (!sourceTexture) {
      return PIXI.Texture.EMPTY;
    }
    const key = `${textureId}:${x}:${y}:${width}:${height}`;
    if (!this.textureRegions.has(key)) {
      this.textureRegions.set(key, new PIXI.Texture({
        source: sourceTexture.source,
        frame: new PIXI.Rectangle(x, y, width, height),
      }));
    }
    return this.textureRegions.get(key);
  }

  drawMapObjectPresence(layer, screen, camera, options = {}) {
    const {
      color = 0xd7b063,
      scale = 1,
      ring = false,
      pulse = false,
      danger = false,
    } = options;
    const pulseValue = pulse ? 0.5 + Math.sin(performance.now() / 320) * 0.5 : 0;
    const base = camera.scale * scale;
    const shadow = new PIXI.Graphics()
      .ellipse(screen.x, screen.y + base * 0.23, base * 0.44, base * 0.16)
      .fill({ color: 0x030202, alpha: danger ? 0.56 : 0.46 });
    const halo = new PIXI.Graphics()
      .circle(screen.x, screen.y, base * 0.47)
      .fill({ color, alpha: 0.12 + pulseValue * 0.04 })
      .stroke({ color: 0x050505, width: 3, alpha: 0.5 });
    const rim = new PIXI.Graphics()
      .circle(screen.x, screen.y, base * 0.42)
      .stroke({ color, width: 2, alpha: ring ? 0.74 + pulseValue * 0.16 : 0.36 });
    layer.addChild(shadow, halo, rim);
    if (ring) {
      const actionRing = new PIXI.Graphics()
        .circle(screen.x, screen.y, base * 0.52)
        .stroke({ color, width: 3, alpha: 0.72 + pulseValue * 0.18 });
      layer.addChild(actionRing);
    }
  }

  interactableAccentColor(interactable) {
    const kind = String(interactable?.kind || "");
    if (["chest", "crate", "cache", "supply_sack", "barrel"].includes(kind)) return 0xf0c45c;
    if (["herb", "trace", "camp"].includes(kind)) return 0x7bd889;
    if (["document", "notice"].includes(kind)) return 0x8fb7ff;
    return 0x78d7a7;
  }

  label(label, x, y, layer, fontSize = 11, color = 0xe8ddc3, options = {}) {
    const value = text(label, {
      fontSize,
      fill: color,
      fontWeight: options.fontWeight || "700",
      stroke: { color: 0x050505, width: options.strokeWidth ?? 3 },
    });
    value.anchor.set(0.5, 0);
    value.position.set(x, y);
    if (options.background) {
      const paddingX = options.paddingX ?? 5;
      const paddingY = options.paddingY ?? 2;
      const background = new PIXI.Graphics()
        .roundRect(x - value.width / 2 - paddingX, y - paddingY, value.width + paddingX * 2, value.height + paddingY * 2, 4)
        .fill({ color: options.backgroundFill ?? 0x050403, alpha: options.backgroundAlpha ?? 0.56 })
        .stroke({ color: options.backgroundStroke ?? 0x000000, width: 1, alpha: 0.28 });
      layer.addChild(background);
    }
    layer.addChild(value);
  }

  npcIconId(npc) {
    if (npc.kind === "guard") return "guard";
    if (npc.kind === "merchant") return "merchant";
    if (npc.kind === "quest") return "quest_npc";
    return "npc";
  }

  enemyIconId(enemyGroup) {
    const templateId = enemyGroup.enemy_template_ids?.[0] || "";
    if (templateId.includes("snake") || templateId.includes("asp") || templateId.includes("viper") || templateId.includes("constrictor")) return "enemy_snake";
    if (templateId.includes("wolf")) return "wolf";
    if (templateId.includes("rat")) return "rat";
    if (templateId.includes("rabbit")) return "rabbit";
    return "enemy";
  }

  interactableIconId(interactable) {
    return this.assetTextures.has(interactable.kind) ? interactable.kind : "map_feature";
  }

  featureIconId(feature) {
    if (this.assetTextures.has(feature.kind)) return feature.kind;
    return new Set(["field", "floor", "wall", "water", "dock", "counter", "road"]).has(feature.kind) ? null : "map_feature";
  }
}

Object.assign(GameClient.prototype, combatMethods);

const game = new GameClient();
window.vibePixiGame = game;
game.start().catch((error) => {
  console.error(error);
  document.body.dataset.pixiFailed = "true";
  const fallback = document.createElement("div");
  fallback.className = "pixi-fatal-error";
  fallback.textContent = `Pixi client failed to start: ${error.message || error}`;
  document.body.appendChild(fallback);
});
